import React, { useState, useEffect, useRef } from 'react';
import CoinIcon from '../components/CoinIcon';
import { useParams, useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import api from '../api';
import { FiArrowLeft, FiArrowRight, FiArrowUpCircle, FiChevronDown, FiChevronUp, FiChevronRight, FiCheckCircle, FiCalendar, FiCreditCard, FiMapPin, FiUsers, FiPlus, FiCheck, FiArchive, FiEdit2, FiTrash2, FiEdit, FiUpload, FiHeart, FiMessageCircle, FiSend, FiShare2, FiClock, FiCheckSquare, FiGift, FiCamera, FiMessageSquare, FiUserCheck, FiUserX, FiBarChart2, FiX, FiMic, FiAlertCircle, FiAlertTriangle, FiStar, FiShoppingBag, FiExternalLink, FiMail, FiPhone, FiMoreHorizontal, FiSettings, FiBell, FiZap, FiImage, FiSearch, FiDownload, FiTruck, FiCopy, FiInfo, FiShield, FiDollarSign, FiTrendingUp, FiHelpCircle } from 'react-icons/fi';
import { HiOutlineBuildingStorefront } from 'react-icons/hi2';
import { IoTicketOutline } from 'react-icons/io5';
import { Html5Qrcode } from 'html5-qrcode';
import GigiBot from '../components/GigiBot';
import LoadingScreen from '../components/LoadingScreen';
import ErrorBoundary from '../components/ErrorBoundary';
import ImageCropper from '../components/ImageCropper';
import AIInvitationDesigner from '../components/AIInvitationDesigner';
import GalleryWall, { GALLERY_LAYOUTS } from '../components/GalleryLayouts';
import useIsMobile from '../hooks/useIsMobile';
import cleanAiText from '../utils/cleanAiText';

const STANDARD_EVENT_TYPES = ['Wedding','Lobola','Umembeso','Birthday','Kids Party','Graduation','Gender Reveal','Engagement','Memorial / Unveiling','Funeral','Matric Dance','Corporate Event'];

// Normalize a ZA phone number to international digits (278...)
const normalizePhone = (phone) => {
  const digits = String(phone || '').replace(/\D/g, '');
  if (!digits) return null;
  if (digits.startsWith('0')) return '27' + digits.slice(1);
  if (digits.startsWith('27')) return digits;
  return '27' + digits;
};

// Build a wa.me link to open WhatsApp with a pre-filled message.
const buildWaLink = (phone, message) => {
  const num = normalizePhone(phone);
  if (!num) return null;
  return `https://wa.me/${num}?text=${encodeURIComponent(message)}`;
};

// Open WhatsApp with a pre-filled message in a new tab — the same behaviour as
// a native <a target="_blank"> link, which Chrome uses to hand off to WhatsApp.
const openWhatsApp = (phone, message) => {
  const link = buildWaLink(phone, message);
  if (!link) return false;
  window.open(link, '_blank', 'noopener');
  return true;
};

const isLightColor = (hex) => {
  if (!hex) return true;
  const c = hex.replace('#', '');
  const r = parseInt(c.substr(0, 2), 16) || 0;
  const g = parseInt(c.substr(2, 2), 16) || 0;
  const b = parseInt(c.substr(4, 2), 16) || 0;
  return (r * 299 + g * 587 + b * 114) / 1000 > 150;
};

// Real stock images for gift registry items (from Pexels, free to use)
const GIFT_IMAGES = [
  { keywords: ['flower', 'flowers', 'bouquet', 'floral', 'rose'], image: '/uploads/gift-defaults/flowers.jpg' },
  { keywords: ['wine', 'champagne', 'prosecco', 'bubbles', 'celebration box'], image: '/uploads/gift-defaults/wine.jpg' },
  { keywords: ['hamper', 'hampers'], image: '/uploads/gift-defaults/hamper.jpg' },
  { keywords: ['basket', 'mini'], image: '/uploads/gift-defaults/basket.jpg' },
  { keywords: ['chocolate', 'choc'], image: '/uploads/gift-defaults/chocolate.jpg' },
  { keywords: ['candle', 'spa', 'bath'], image: '/uploads/gift-defaults/candle-set.jpg' },
  { keywords: ['fragrance', 'perfume', 'cologne', 'scent'], image: '/uploads/gift-defaults/perfume.jpg' },
  { keywords: ['table', 'chair', 'furniture', 'decor'], image: '/uploads/gift-defaults/glass-table.jpg' },
  { keywords: ['snack', 'gourmet', 'food', 'tower', 'platter', 'biltong', 'nuts'], image: '/uploads/gift-defaults/snacks.jpg' },
  { keywords: ['welcome', 'pack', 'tote', 'bag'], image: '/uploads/gift-defaults/tote-bag.jpg' },
  { keywords: ['luxury', 'grand', 'premium'], image: '/uploads/gift-defaults/luxury-gift.jpg' },
  { keywords: ['set', 'thank you', 'thank-you'], image: '/uploads/gift-defaults/gift-box-elegant.jpg' },
  { keywords: ['box', 'wrapped', 'wrap'], image: '/uploads/gift-defaults/gift-wrapped.jpg' },
  { keywords: ['contribution', 'contribute', 'cash', 'money', 'monetary', 'donate', 'voucher'], image: '/uploads/gift-defaults/money-envelope.jpg' },
  { keywords: ['kitchen', 'appliance', 'cookware', 'plate', 'cutlery'], image: '/uploads/gift-defaults/food-platter.jpg' },
];
const DEFAULT_GIFT_IMAGE = '/uploads/gift-defaults/gift-box.jpg';

const getGiftDefaultImage = (title, type) => {
  const lower = (title || '').toLowerCase();
  for (const entry of GIFT_IMAGES) {
    if (entry.keywords.some(k => lower.includes(k))) return entry.image;
  }
  if (type === 'group' || type === 'monetary') return '/uploads/gift-defaults/money-envelope.jpg';
  if (type === 'store') return '/uploads/gift-defaults/gift-box-elegant.jpg';
  return DEFAULT_GIFT_IMAGE;
};

const EVENT_TYPE_COLORS = {
  'Wedding': { bg: '#2D1F2B', accent: '#E8A0BF', text: '#FFF5F8' },
  'Lobola': { bg: '#3B1F0B', accent: '#D4A574', text: '#FFF3E6' },
  'Umembeso': { bg: '#1A2E1A', accent: '#8FBC8F', text: '#F0FFF0' },
  'Birthday': { bg: '#1A1A3E', accent: '#FFB347', text: '#FFF8E7' },
  'Kids Party': { bg: '#2D1B4E', accent: '#FF69B4', text: '#FFF0F5' },
  'Graduation': { bg: '#0D1B2A', accent: '#4FC3F7', text: '#E8F4FD' },
  'Gender Reveal': { bg: '#2B1F3D', accent: '#DDA0DD', text: '#FFF0FF' },
  'Engagement': { bg: '#2D1F2B', accent: '#FF6B6B', text: '#FFF5F5' },
  'Memorial / Unveiling': { bg: '#1A1A1A', accent: '#9E9E9E', text: '#F5F5F5' },
  'Funeral': { bg: '#1A1A1A', accent: '#8B8B8B', text: '#E8E8E8' },
  'Matric Dance': { bg: '#1A0A2E', accent: '#C084FC', text: '#F3E8FF' },
  'Corporate Event': { bg: '#0F172A', accent: '#38BDF8', text: '#F0F9FF' },
};

function getEventTypeColors(eventType) {
  return EVENT_TYPE_COLORS[eventType] || { bg: '#2D1F2B', accent: '#E8A0BF', text: '#FFF5F8' };
}

const DEFAULT_BANNERS = {
  'Wedding': '/hero/wedding-default.jpg',
  'Lobola': '/hero/lobola-default.jpg',
  'Umembeso': '/hero/umembeso-default.jpg',
  'Birthday': '/hero/birthday-default.jpg',
  'Kids Party': '/hero/kids-party-default.jpg',
  'Graduation': '/hero/graduation-default.jpg',
  'Gender Reveal': '/hero/gender-reveal-default.jpg',
  'Engagement': '/hero/engagement-default.jpg',
  'Memorial / Unveiling': '/hero/memorial-default.jpg',
  'Funeral': '/hero/funeral-default.jpg',
  'Matric Dance': '/hero/matric-dance-default.jpg',
  'Corporate Event': '/hero/corporate-default.jpg',
};

function getDefaultEventBanner(eventType) {
  return DEFAULT_BANNERS[eventType] || null;
}

function getEventCoverImage(event) {
  return event?.cover_image || getDefaultEventBanner(event?.event_type);
}

function getEventBannerStyle(event, overlayAlpha = ['0.35', '0.55']) {
  const colors = getEventTypeColors(event?.event_type);
  const cssBg = `linear-gradient(135deg, ${colors.bg} 0%, ${colors.bg}dd 40%, ${colors.accent}44 100%)`;
  const imageUrl = getEventCoverImage(event);
  if (imageUrl) {
    return { background: `linear-gradient(rgba(0,0,0,${overlayAlpha[0]}), rgba(0,0,0,${overlayAlpha[1]})), url(${imageUrl}) center/cover`, color: 'white' };
  }
  return { background: cssBg, color: colors.text };
}

const INVITATION_PALETTES = [
  { key: 'elegant', label: 'Elegant', bg: '#2D1F2B', accent: '#E8A0BF', text: '#FFF5F8' },
  { key: 'traditional', label: 'African', bg: '#8B2252', accent: '#F2C4D6', text: '#FFF8F0' },
  { key: 'modern', label: 'Modern', bg: '#0F0F0F', accent: '#FFFFFF', text: '#FFFFFF' },
  { key: 'floral', label: 'Floral', bg: '#2D1B2E', accent: '#F0B6C5', text: '#FFF8F0' },
  { key: 'minimal', label: 'Minimal', bg: '#FFF5F8', accent: '#C97191', text: '#2D1F2B' },
  { key: 'gold', label: 'Gold', bg: '#2D1F2B', accent: '#E8A0BF', text: '#FFF5F8' },
  { key: 'eternal-light', label: 'Eternal', bg: '#0d0d1a', accent: '#E8D5A3', text: '#FFF8E8' },
  { key: 'peaceful-dove', label: 'Peaceful', bg: '#1a1a2a', accent: '#B8C4D4', text: '#F0F4F8' },
  { key: 'gentle-rest', label: 'Gentle', bg: '#1a0f1e', accent: '#C9A0D4', text: '#F8F0FF' },
  { key: 'sacred-cross', label: 'Sacred', bg: '#0a0a14', accent: '#A8B8C8', text: '#F0F4F8' },
  { key: 'sunset-farewell', label: 'Sunset', bg: '#1a0a05', accent: '#D4956B', text: '#FFF3E8' },
];

function hexToRgb(hex) {
  const c = hex.replace('#', '');
  return [parseInt(c.substr(0,2),16), parseInt(c.substr(2,2),16), parseInt(c.substr(4,2),16)];
}

function findClosestPalette(eventType) {
  const target = EVENT_TYPE_COLORS[eventType];
  if (!target) return 'elegant';
  const [tr, tg, tb] = hexToRgb(target.bg);
  let best = 'elegant', bestDist = Infinity;
  INVITATION_PALETTES.forEach(p => {
    const [pr, pg, pb] = hexToRgb(p.bg);
    const dist = Math.abs(tr-pr) + Math.abs(tg-pg) + Math.abs(tb-pb);
    if (dist < bestDist) { bestDist = dist; best = p.key; }
  });
  return best;
}

// Extract dominant colors from an image for auto-palette generation
function extractImageColors(imageUrl, callback) {
  const img = new Image();
  img.crossOrigin = 'anonymous';
  img.onload = () => {
    const canvas = document.createElement('canvas');
    const size = 100; // sample at 100x100 for performance
    canvas.width = size; canvas.height = size;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(img, 0, 0, size, size);
    const data = ctx.getImageData(0, 0, size, size).data;
    const pixels = [];
    for (let i = 0; i < data.length; i += 4) {
      const r = data[i], g = data[i+1], b = data[i+2];
      const lum = 0.299*r + 0.587*g + 0.114*b; // perceived brightness
      const sat = Math.max(r,g,b) - Math.min(r,g,b); // simple saturation
      pixels.push({ r, g, b, lum, sat });
    }
    // Sort by brightness to find dark and light clusters
    pixels.sort((a, b) => a.lum - b.lum);
    // Darkest 30% average = background
    const darkSlice = pixels.slice(0, Math.floor(pixels.length * 0.3));
    const bg = avgColor(darkSlice);
    // Lightest 20% average = text
    const lightSlice = pixels.slice(Math.floor(pixels.length * 0.8));
    const textColor = avgColor(lightSlice);
    // Most saturated 10% = accent
    pixels.sort((a, b) => b.sat - a.sat);
    const vibrantSlice = pixels.slice(0, Math.floor(pixels.length * 0.1));
    const accent = avgColor(vibrantSlice);
    // Ensure accent is visibly different from bg
    const accLum = 0.299*accent.r + 0.587*accent.g + 0.114*accent.b;
    const bgLum = 0.299*bg.r + 0.587*bg.g + 0.114*bg.b;
    if (Math.abs(accLum - bgLum) < 40) {
      // Boost accent saturation if too close to bg
      accent.r = Math.min(255, accent.r + 40);
      accent.g = Math.min(255, accent.g + 20);
      accent.b = Math.min(255, accent.b + 20);
    }
    callback({
      bg: rgbToHex(bg.r, bg.g, bg.b),
      accent: rgbToHex(accent.r, accent.g, accent.b),
      text: rgbToHex(textColor.r, textColor.g, textColor.b),
    });
  };
  img.onerror = () => callback(null);
  img.src = imageUrl;
}
function avgColor(arr) {
  if (!arr.length) return {r:45,g:31,b:43};
  let r=0,g=0,b=0;
  arr.forEach(p => { r+=p.r; g+=p.g; b+=p.b; });
  return {r:Math.round(r/arr.length), g:Math.round(g/arr.length), b:Math.round(b/arr.length)};
}
function rgbToHex(r,g,b) { return '#' + [r,g,b].map(x => Math.min(255,Math.max(0,x|0)).toString(16).padStart(2,'0')).join(''); }


/* Mini SVG decorations per theme for ThankYouCard preview */
const THANK_YOU_DECO = {
  elegant: (
    <>
      {['topLeft','topRight','bottomLeft','bottomRight'].map(c => {
        const isT = c.includes('top'), isL = c.includes('Left');
        return <div key={c} style={{ position:'absolute', [isT?'top':'bottom']:8, [isL?'left':'right']:8, width:28, height:28, borderTop: isT?'2px solid #C9A84C':'none', borderBottom:!isT?'2px solid #C9A84C':'none', borderLeft:isL?'2px solid #C9A84C':'none', borderRight:!isL?'2px solid #C9A84C':'none', opacity:0.7 }} />;
      })}
      <div style={{ position:'absolute', top:0, left:'10%', right:'10%', height:2, background:'linear-gradient(90deg,transparent,#C9A84C,transparent)', opacity:0.5 }} />
      <div style={{ position:'absolute', bottom:0, left:'10%', right:'10%', height:2, background:'linear-gradient(90deg,transparent,#C9A84C,transparent)', opacity:0.5 }} />
      <div style={{ position:'absolute', top:4, left:'50%', transform:'translateX(-50)', pointerEvents:'none', opacity:0.5 }}>
        <svg width="40" height="20" viewBox="0 0 60 30"><polygon points="30,2 38,15 30,28 22,15" fill="none" stroke="#C9A84C" strokeWidth="1.5" /><polygon points="30,6 35,15 30,24 25,15" fill="#C9A84C" opacity="0.25" /></svg>
      </div>
    </>
  ),
  floral: (
    <>
      <div style={{ position:'absolute', bottom:0, right:0, pointerEvents:'none', opacity:0.7 }}>
        <svg width="80" height="85" viewBox="0 0 140 150"><circle cx="90" cy="110" r="18" fill="#F48FB1" opacity="0.5"/><circle cx="90" cy="110" r="12" fill="#E91E63" opacity="0.45"/><circle cx="90" cy="110" r="6" fill="#C2185B" opacity="0.4"/><path d="M120 95 C130 80,138 65,135 72 C130 78,125 88,120 95Z" fill="#4CAF50" opacity="0.45"/><path d="M60 105 C45 90,30 80,25 88 C35 92,48 98,60 105Z" fill="#66BB6A" opacity="0.4"/>{[[25,110],[70,95],[105,90],[75,140]].map(([x,y],i) => <circle key={i} cx={x} cy={y} r="2" fill="white" opacity="0.5"/>)}</svg>
      </div>
      <div style={{ position:'absolute', top:4, left:4, pointerEvents:'none', opacity:0.5 }}>
        <svg width="35" height="35" viewBox="0 0 50 50"><circle cx="25" cy="25" r="8" fill="#E91E63" opacity="0.5"/><circle cx="25" cy="25" r="4" fill="#E91E63" opacity="0.7"/>{[0,72,144,216,288].map((a,i)=><ellipse key={i} cx={25+Math.cos(a*Math.PI/180)*12} cy={25+Math.sin(a*Math.PI/180)*12} rx="4" ry="7" fill="#F48FB1" opacity="0.4" transform={`rotate(${a},${25+Math.cos(a*Math.PI/180)*12},${25+Math.sin(a*Math.PI/180)*12})`}/>)}</svg>
      </div>
      <div style={{ position:'absolute', top:4, right:4, pointerEvents:'none', opacity:0.5 }}>
        <svg width="28" height="28" viewBox="0 0 40 40"><circle cx="20" cy="14" r="5" fill="#F48FB1"/><circle cx="14" cy="20" r="5" fill="#F48FB1"/><circle cx="26" cy="20" r="5" fill="#F48FB1"/><circle cx="17" cy="26" r="5" fill="#F48FB1"/><circle cx="23" cy="26" r="5" fill="#F48FB1"/><circle cx="20" cy="20" r="3" fill="#FFD700"/></svg>
      </div>
    </>
  ),
  golden: (
    <>
      {['topLeft','topRight','bottomLeft','bottomRight'].map(c => {
        const isT = c.includes('top'), isL = c.includes('Left');
        return <div key={c} style={{ position:'absolute', [isT?'top':'bottom']:6, [isL?'left':'right']:6, width:24, height:24, border:'1.5px solid rgba(255,255,255,0.5)', borderRadius:'2px', opacity:0.6, borderTop:isT?undefined:'none', borderBottom:!isT?undefined:'none', borderLeft:isL?undefined:'none', borderRight:!isL?undefined:'none' }} />;
      })}
      <div style={{ position:'absolute', inset:8, border:'1px solid rgba(255,255,255,0.15)', borderRadius:12, pointerEvents:'none' }} />
    </>
  ),
  festive: (
    <>
      {Array.from({length:12}).map((_,i) => {
        const colors = ['#FFD700','#FF6B6B','#4FC3F7','#81C784','#FF8A65','#BA68C8','#FFF'];
        return <div key={i} style={{ position:'absolute', top:`${5+Math.random()*85}%`, left:`${5+Math.random()*85}%`, width:`${4+Math.random()*6}px`, height:`${4+Math.random()*6}px`, background:colors[i%colors.length], borderRadius:i%3===0?'50%':'1px', opacity:0.3+Math.random()*0.15, transform:`rotate(${Math.random()*360}deg)`, pointerEvents:'none' }} />;
      })}
      <div style={{ position:'absolute', top:6, right:8, pointerEvents:'none', opacity:0.6 }}>
        <svg width="24" height="36" viewBox="0 0 24 40"><ellipse cx="12" cy="18" rx="9" ry="14" fill="#FFD700" opacity="0.5"/><ellipse cx="12" cy="18" rx="9" ry="14" fill="none" stroke="#FFF" strokeWidth="0.5" opacity="0.3"/><line x1="12" y1="32" x2="12" y2="40" stroke="#FFF" strokeWidth="0.8" opacity="0.4"/></svg>
      </div>
      <div style={{ position:'absolute', top:10, left:8, pointerEvents:'none', opacity:0.5 }}>
        <svg width="20" height="30" viewBox="0 0 24 40"><ellipse cx="12" cy="18" rx="9" ry="14" fill="#4FC3F7" opacity="0.5"/><line x1="12" y1="32" x2="12" y2="40" stroke="#FFF" strokeWidth="0.8" opacity="0.4"/></svg>
      </div>
    </>
  ),
  botanical: (
    <>
      <div style={{ position:'absolute', bottom:0, right:0, pointerEvents:'none', opacity:0.6 }}>
        <svg width="70" height="70" viewBox="0 0 100 100"><path d="M80 90 C90 70,95 45,80 30 C75 40,72 60,80 90Z" fill="#5C7A5A" opacity="0.5"/><path d="M60 95 C50 75,38 55,45 35 C50 48,55 70,60 95Z" fill="#6B8F6B" opacity="0.4"/><path d="M90 85 C100 65,98 40,85 35 C85 48,88 68,90 85Z" fill="#4A6B4A" opacity="0.35"/><line x1="80" y1="90" x2="80" y2="35" stroke="#5C7A5A" strokeWidth="0.8" opacity="0.3"/></svg>
      </div>
      <div style={{ position:'absolute', top:0, left:0, pointerEvents:'none', opacity:0.5 }}>
        <svg width="50" height="50" viewBox="0 0 80 80"><path d="M10 10 C10 10,25 5,35 15 C45 25,30 40,20 35 C12 30,10 20,10 10Z" fill="#5C7A5A" opacity="0.4"/><path d="M10 10 C10 10,5 25,15 35 C25 45,40 30,35 20 C30 12,20 10,10 10Z" fill="#6B8F6B" opacity="0.35"/></svg>
      </div>
    </>
  ),
  roseGold: (
    <>
      <div style={{ position:'absolute', bottom:0, right:0, pointerEvents:'none', opacity:0.55 }}>
        <svg width="65" height="70" viewBox="0 0 100 110"><circle cx="60" cy="75" r="16" fill="#E8B4B8" opacity="0.45"/><circle cx="60" cy="75" r="10" fill="#B76E79" opacity="0.4"/><circle cx="60" cy="75" r="5" fill="#8B4A52" opacity="0.35"/><path d="M78 62 C86 50,90 38,85 42 C82 48,80 56,78 62Z" fill="#6B8F6B" opacity="0.35"/></svg>
      </div>
      {[[15,20],[80,12],[25,85],[75,80],[50,15],[10,50],[88,55]].map(([l,t],i) => (
        <div key={i} style={{ position:'absolute', left:`${l}%`, top:`${t}%`, width:'4px', height:'4px', borderRadius:'50%', background:'radial-gradient(circle,rgba(232,180,184,0.6),transparent)', pointerEvents:'none' }} />
      ))}
      <div style={{ position:'absolute', inset:10, border:'1px solid rgba(255,255,255,0.12)', borderRadius:12, pointerEvents:'none' }} />
    </>
  ),
  african: (
    <>
      <div style={{ position:'absolute', top:0, left:0, right:0, height:'14px', pointerEvents:'none', opacity:0.6 }}>
        <svg width="100%" height="14" viewBox="0 0 400 14" preserveAspectRatio="none">{Array.from({length:20}).map((_,i)=>{const cols=['#CC6633','#E8A87C','#85461E'];return <rect key={i} x={i*20} y="2" width="18" height="10" fill={cols[i%3]} opacity="0.6" rx="1"/>;})}</svg>
      </div>
      <div style={{ position:'absolute', bottom:0, left:0, right:0, height:'14px', pointerEvents:'none', opacity:0.6 }}>
        <svg width="100%" height="14" viewBox="0 0 400 14" preserveAspectRatio="none">{Array.from({length:20}).map((_,i)=>{const cols=['#CC6633','#E8A87C','#85461E'];return <rect key={i} x={i*20} y="2" width="18" height="10" fill={cols[i%3]} opacity="0.6" rx="1"/>;})}</svg>
      </div>
      {Array.from({length:6}).map((_,i)=>{const cols=['#CC6633','#E8A87C','#85461E'];return <div key={`l${i}`} style={{position:'absolute',top:`${18+i*14}%`,left:6,width:'7px',height:'7px',background:cols[i%3],transform:'rotate(45deg)',opacity:0.5,pointerEvents:'none'}}/>;
      })}
      {Array.from({length:6}).map((_,i)=>{const cols=['#CC6633','#E8A87C','#85461E'];return <div key={`r${i}`} style={{position:'absolute',top:`${18+i*14}%`,right:6,width:'7px',height:'7px',background:cols[i%3],transform:'rotate(45deg)',opacity:0.5,pointerEvents:'none'}}/>;
      })}
      <div style={{ position:'absolute', inset:'14px 14px', border:'1px solid rgba(204,102,51,0.25)', pointerEvents:'none' }} />
    </>
  ),
  midnight: (
    <>
      {Array.from({length:18}).map((_,i)=>{const cols=['#FFD700','#FFA500','#C0C0FF','#E0E0FF','#FFF','#87CEEB','#DDA0DD'];const s=1.5+Math.random()*2.5;return <div key={i} style={{position:'absolute',top:`${5+Math.random()*88}%`,left:`${5+Math.random()*88}%`,width:`${s}px`,height:`${s}px`,background:cols[i%cols.length],borderRadius:'50%',opacity:0.4+Math.random()*0.4,boxShadow:s>2.5?`0 0 ${s*2}px ${cols[i%cols.length]}`:'none',pointerEvents:'none'}}/>;
      })}
      <div style={{ position:'absolute', top:6, right:8, pointerEvents:'none', opacity:0.6 }}>
        <svg width="32" height="32" viewBox="0 0 50 50"><defs><radialGradient id="pmg" cx="50%" cy="50%" r="70%"><stop offset="0%" stopColor="#FFFACD" stopOpacity="0.25"/><stop offset="100%" stopColor="transparent" stopOpacity="0"/></radialGradient></defs><circle cx="25" cy="25" r="20" fill="url(#pmg)"/><path d="M30 5 A20 20 0 1 0 30 45 A14 14 0 1 1 30 5Z" fill="#FFFACD" opacity="0.55"/></svg>
      </div>
      <div style={{ position:'absolute', bottom:'-5px', left:'-5px', pointerEvents:'none', opacity:0.4 }}>
        <svg width="70" height="70" viewBox="0 0 120 120"><defs><radialGradient id="pgg" cx="50%" cy="50%" r="50%"><stop offset="0%" stopColor="#7B68EE" stopOpacity="0.35"/><stop offset="50%" stopColor="#4B0082" stopOpacity="0.15"/><stop offset="100%" stopColor="transparent" stopOpacity="0"/></radialGradient></defs><circle cx="60" cy="60" r="55" fill="url(#pgg)"/><circle cx="60" cy="60" r="3" fill="#E0E0FF" opacity="0.5"/></svg>
      </div>
    </>
  ),
  garden: (
    <>
      <div style={{ position:'absolute', top:'-10px', left:'-10px', width:'80px', height:'80px', borderRadius:'50%', background:'radial-gradient(circle,rgba(107,175,107,0.12),transparent 70%)', pointerEvents:'none' }} />
      <div style={{ position:'absolute', bottom:'-10px', right:'-10px', width:'90px', height:'90px', borderRadius:'50%', background:'radial-gradient(circle,rgba(107,175,107,0.15),transparent 70%)', pointerEvents:'none' }} />
      <div style={{ position:'absolute', bottom:0, right:0, pointerEvents:'none', opacity:0.6 }}>
        <svg width="70" height="70" viewBox="0 0 100 100"><path d="M80 90 C90 70,85 45,70 35 C68 50,72 70,80 90Z" fill="#6BAF6B" opacity="0.45"/><path d="M60 95 C50 78,42 55,50 38 C55 50,58 72,60 95Z" fill="#7BC47B" opacity="0.4"/>{[0,45,90,135,180,225,270,315].map((a,i)=><ellipse key={i} cx="45" cy="25" rx="3" ry="7" fill="white" opacity="0.45" transform={`rotate(${a},45,25)`}/>)}<circle cx="45" cy="25" r="4" fill="#FFD700" opacity="0.5"/></svg>
      </div>
    </>
  ),
};

const BG_MAP = { elegant: '#1C1C1E', floral: '#FFF0F3', golden: 'linear-gradient(135deg, #C9A84C, #E8D59A)', minimal: '#FAFAF9', festive: '#E3425C', botanical: '#E8F0E8', roseGold: 'linear-gradient(135deg, #B76E79, #E8B4B8)', african: '#4A2C1A', midnight: 'linear-gradient(135deg, #0D1B2A, #1A0A2E)', garden: '#F0F7F0' };
const TITLE_MAP = { elegant: '#C9A84C', floral: '#C2185B', golden: '#FFF', minimal: '#1C1C1E', festive: '#FFF', botanical: '#3D5A3D', roseGold: '#FFF', african: '#CC6633', midnight: '#C0C0FF', garden: '#3A6B3A' };
const SUBTITLE_MAP = { elegant: 'rgba(255,255,255,0.6)', floral: '#C2185B', golden: 'rgba(255,255,255,0.7)', minimal: '#999', festive: 'rgba(255,255,255,0.7)', botanical: '#5C7A5A', roseGold: 'rgba(255,255,255,0.7)', african: 'rgba(204,102,51,0.7)', midnight: 'rgba(192,192,255,0.6)', garden: '#5C7A5A' };
const BORDER_MAP = { elegant: '1px solid rgba(201,168,76,0.3)', african: '1px solid rgba(204,102,51,0.3)', midnight: '1px solid rgba(192,192,255,0.2)', roseGold: '1px solid rgba(255,255,255,0.15)', floral: '1px solid rgba(233,30,99,0.2)', botanical: '1px solid rgba(92,122,90,0.2)', garden: '1px solid rgba(92,122,90,0.2)', festive: '1px solid rgba(255,215,0,0.2)' };
const ACCENT_MAP = { elegant: '#C9A84C', floral: '#E91E63', golden: '#FFF', minimal: '#E0E0E0', festive: '#FFD700', botanical: '#5C7A5A', roseGold: '#FFF', african: '#CC6633', midnight: '#C0C0FF', garden: '#5C7A5A' };
const DARK_STYLES = ['elegant', 'golden', 'roseGold', 'african', 'midnight', 'festive'];

function ThankYouCardPreview({ cardStyle, message, signature, eventDate, eventTitle, userName }) {
  const cs = cardStyle || 'elegant';
  const isDark = DARK_STYLES.includes(cs);
  const previewDate = eventDate ? (() => { try { const d = new Date(String(eventDate).split('T')[0] + 'T00:00:00'); return isNaN(d.getTime()) ? '' : d.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }); } catch { return ''; } })() : '';

  return (
    <div style={{ marginBottom: '16px' }}>
      <div style={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '2px', color: 'var(--gray)', marginBottom: '8px', textAlign: 'center' }}>
        Card Preview — {cs.charAt(0).toUpperCase() + cs.slice(1).replace(/([A-Z])/g, ' $1')} Theme
      </div>
      <div key={cs} style={{
        borderRadius: '16px', overflow: 'hidden',
        background: BG_MAP[cs] || '#FAFAF9',
        border: BORDER_MAP[cs] || '1px solid rgba(0,0,0,0.06)',
        padding: '32px 24px',
        maxWidth: '400px', margin: '0 auto',
        boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
        position: 'relative',
        transition: 'background 0.4s ease',
      }}>
        {THANK_YOU_DECO[cs] || null}

        <div style={{ textAlign: 'center', marginBottom: '16px', position: 'relative', zIndex: 1 }}>
          <img src="/uploads/gift-icon-v2.png" alt="GGR" style={{ width: '36px', height: '36px', objectFit: 'contain', opacity: 0.7 }}
            onError={e => { e.target.style.display = 'none'; }} />
        </div>

        <div style={{ width: '40px', height: '1px', background: ACCENT_MAP[cs] || '#C9A84C', margin: '0 auto 20px', opacity: 0.4, position: 'relative', zIndex: 1 }} />

        <div style={{ textAlign: 'center', fontFamily: "'DM Serif Display', serif", fontSize: '26px', color: TITLE_MAP[cs] || '#1C1C1E', marginBottom: '6px', letterSpacing: '0.5px', position: 'relative', zIndex: 1 }}>
          Thank You
        </div>

        <div style={{ textAlign: 'center', fontSize: '14px', color: SUBTITLE_MAP[cs] || 'var(--gray)', marginBottom: '20px', fontStyle: 'italic', position: 'relative', zIndex: 1 }}>
          Dear Guest Name
        </div>

        {message && (
        <div style={{
          textAlign: 'center', fontSize: '13px',
          color: isDark ? 'rgba(255,255,255,0.8)' : '#666',
          lineHeight: 1.8, whiteSpace: 'pre-line',
          maxWidth: '320px', margin: '0 auto',
          padding: '0 8px', position: 'relative', zIndex: 1,
        }}>
          {message.replace('{guest_name}', 'Guest Name').replace('{event_name}', eventTitle || 'Your Event')}
        </div>
        )}

        <div style={{ textAlign: 'center', marginTop: '20px', position: 'relative', zIndex: 1 }}>
          <div style={{ width: '30px', height: '1px', background: ACCENT_MAP[cs] || '#C9A84C', margin: '0 auto 12px', opacity: 0.3 }} />
          <div style={{ fontStyle: 'italic', fontSize: '14px', color: isDark ? 'rgba(255,255,255,0.85)' : '#555', fontWeight: 500 }}>
            — {signature || userName || 'Host'}
          </div>
        </div>

        <div style={{
          marginTop: '24px', padding: '12px 16px',
          borderRadius: '10px',
          background: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)',
          border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)'}`,
          textAlign: 'center', position: 'relative', zIndex: 1,
        }}>
          {eventTitle && (
            <div style={{ fontSize: '12px', fontWeight: 600, color: SUBTITLE_MAP[cs] || 'var(--gray)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              {eventTitle}
            </div>
          )}
          {previewDate && (
            <div style={{ fontSize: '11px', color: SUBTITLE_MAP[cs] || 'var(--gray)', marginTop: '4px', opacity: 0.8 }}>
              {previewDate}
            </div>
          )}
        </div>

        <div style={{ textAlign: 'center', marginTop: '16px', fontSize: '9px', color: isDark ? 'rgba(255,255,255,0.25)' : 'rgba(0,0,0,0.2)', letterSpacing: '1px', textTransform: 'uppercase', position: 'relative', zIndex: 1 }}>
          Powered by Grand Gather Registry
        </div>
      </div>
    </div>
  );
}

export default function EventWorkspace() {
  const { id } = useParams();
  const { user, showToast } = useAuth();
  const navigate = useNavigate();
  const hostPlan = user?.host_plan || 'free';
  const HOST_PLAN_RANK = { free: 0, standard: 1, premium: 2 };
  const HOST_PLAN_LIMITS = {
    free: { rsvp: 50, designs: 1, ticketSales: 5000, whatsapp: 50 },
    standard: { rsvp: 200, designs: 5, ticketSales: 50000, whatsapp: 200 },
    premium: { rsvp: Infinity, designs: Infinity, ticketSales: Infinity, whatsapp: 500 },
  };
  const hostLimits = HOST_PLAN_LIMITS[hostPlan] || HOST_PLAN_LIMITS.free;
  const hostPlanLabel = hostPlan.charAt(0).toUpperCase() + hostPlan.slice(1);
  const [searchParams] = useSearchParams();
  const [event, setEvent] = useState(null);
  const [newGiftContributions, setNewGiftContributions] = useState(0);
  const [tab, setTabRaw] = useState('overview');
  const [autoOpenConvId, setAutoOpenConvId] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [budget, setBudget] = useState([]);
  const [guests, setGuests] = useState([]);
  const [checklist, setChecklist] = useState([]);
  const [registry, setRegistry] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [wallPosts, setWallPosts] = useState([]);
  const [delegations, setDelegations] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [polls, setPolls] = useState([]);
  const [qa, setQa] = useState([]);
  const [speakers, setSpeakers] = useState([]);

  const [deliveries, setDeliveries] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [ticketTiers, setTicketTiers] = useState([]);
  const [ticketSales, setTicketSales] = useState(null);
  const [ticketView, setTicketView] = useState('setup'); // setup | sales | checkin
  const [showTierForm, setShowTierForm] = useState(false);
  const [editingTier, setEditingTier] = useState(null);
  const [tierForm, setTierForm] = useState({ name: '', price: '', quantity: '', description: '', color: '#E3425C', target_count: '', notional_price: '' });
  const [ticketsEnabled, setTicketsEnabled] = useState(false);
  const [rsvpDeadline, setRsvpDeadline] = useState('');
  const [ticketDeadline, setTicketDeadline] = useState('');
  const [checkinSearch, setCheckinSearch] = useState('');
  const [scanResult, setScanResult] = useState(null);
  const [qrScanning, setQrScanning] = useState(false);
  const qrScannerRef = useRef(null);
  const [tables, setTables] = useState([]);
  const [seatingSearch, setSeatingSearch] = useState('');
  const [giftVendors, setGiftVendors] = useState([]);
  const [giftStores, setGiftStores] = useState(false);
  const [giftBoxOpen, setGiftBoxOpen] = useState(false);
  const [giftBoxAnimating, setGiftBoxAnimating] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [storeTab, setStoreTab] = useState('online'); // 'online' | 'vendors'
  const [pasteUrl, setPasteUrl] = useState('');
  const [scrapedProduct, setScrapedProduct] = useState(null);
  const [scraping, setScraping] = useState(false);
  const [scrapeError, setScrapeError] = useState('');
  const [gigiPicks, setGigiPicks] = useState(false);
  const [showModal, setShowModal] = useState(null);
  const [showMoreTabs, setShowMoreTabs] = useState(false);
  const [formData, setFormData] = useState({});
  const [cropperImage, setCropperImage] = useState(null); // { src, field, aspect }
  const [showAIDesigner, setShowAIDesigner] = useState(false);
  const [srPhotos, setSrPhotos] = useState([]);
  const [srSubmitting, setSrSubmitting] = useState(false);
  const [generatingExtra, setGeneratingExtra] = useState(null); // 'programme' | 'welcome_board'
  const [genDesc, setGenDesc] = useState(false);
  const [descTone, setDescTone] = useState('warm'); // 'warm' | 'professional' | 'funny' | 'formal'
  const [csvContacts, setCsvContacts] = useState(null); // parsed CSV contacts for vCard export
  const [programmeImage, setProgrammeImage] = useState(null);
  const [welcomeBoardImage, setWelcomeBoardImage] = useState(null);
  const fileInputRef = useRef();
  const csvInputRef = useRef();
  // Auto messaging states
  const [autoMsg, setAutoMsg] = useState(null);
  const [autoMsgHistory, setAutoMsgHistory] = useState([]);
  const [autoMsgPanel, setAutoMsgPanel] = useState(false);
  const [autoMsgStep, setAutoMsgStep] = useState(1);
  const [showAddGuestDropdown, setShowAddGuestDropdown] = useState(false);
  const [showBulkStatusDropdown, setShowBulkStatusDropdown] = useState(false);
  const [guestFilter, setGuestFilter] = useState(null); // null=all, 'attending', 'maybe', 'not_attending', 'tickets'
  // Vendor chat states
  const [conversations, setConversations] = useState([]);
  const [activeChat, setActiveChat] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [thankYouData, setThankYouData] = useState({ message: '', card_style: 'elegant', host_signature: '', auto_send: true, send_delay_days: 1, post_event_followup: true });
  const [thankYouSaved, setThankYouSaved] = useState(false);
  const [thankYouSending, setThankYouSending] = useState(false);
  const [gigiWriting, setGigiWriting] = useState(false);
  const [thankYouFilter, setThankYouFilter] = useState('attending');
  const [thankYouSentGuests, setThankYouSentGuests] = useState([]);
  const [thankYouSent, setThankYouSent] = useState(false);
  // Host wallet & bookings states
  const [hostWallet, setHostWallet] = useState(null);
  const [hostWalletTxns, setHostWalletTxns] = useState([]);
  const [hostBanking, setHostBanking] = useState(null);
  const [hostBookings, setHostBookings] = useState([]);
  const [cancelBookingId, setCancelBookingId] = useState(null);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelCustomReason, setCancelCustomReason] = useState('');
  const [cancelling, setCancelling] = useState(false);
  const [rescheduleBookingId, setRescheduleBookingId] = useState(null);
  const [rescheduleDate, setRescheduleDate] = useState('');
  const [rescheduleNotes, setRescheduleNotes] = useState('');
  const [hostBookingsFilter, setHostBookingsFilter] = useState('active');
  const [bookedRateCards, setBookedRateCards] = useState({});
  const [deliveryAddressText, setDeliveryAddressText] = useState('');
  const [savingDeliveryId, setSavingDeliveryId] = useState(null);
  const [editingAddressId, setEditingAddressId] = useState(null);
  const [hostDeliveryInfo, setHostDeliveryInfo] = useState(null);
  const [editDeliveryFields, setEditDeliveryFields] = useState({
    street: '', town: '', region: '', province: '', postal_code: '',
    contact_person: '', contact_mobile: ''
  });
  const [verifyingPayment, setVerifyingPayment] = useState(false);

  const [rescheduling, setRescheduling] = useState(false);
  const [disputes, setDisputes] = useState([]);
  const [reportModal, setReportModal] = useState(null); // { booking_id, vendor_name }
  const [reportSubject, setReportSubject] = useState('');
  const [reportDescription, setReportDescription] = useState('');
  const [reporting, setReporting] = useState(false);
  const [expandedGroup, setExpandedGroup] = useState(null);
  const [isMobile] = useIsMobile();
  const chatEndRef = useRef(null);
  const chatPollRef = useRef(null);
  const gearRef = useRef(null);
  const lastAppliedParams = useRef({ tab: null, convId: null });
  const setTabRef = useRef(null);
  const ozowHandledRef = useRef(false);

  // Close gear dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (showModal === 'eventSettings' && gearRef.current && !gearRef.current.contains(e.target)) {
        setShowModal(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showModal]);

  // Auto-populate thank you message with event name when event loads
  useEffect(() => {
    if (event?.title && !thankYouData.message) {
      setThankYouData(d => ({
        ...d,
        message: `Hi {guest_name},\n\nThank you so much for celebrating with us at ${event.title}. Your presence made our day truly special.\n\nWith all our love and appreciation.`
      }));
    }
  }, [event?.title]);

  useEffect(() => { loadEvent(); }, [id]);
  useEffect(() => { if (event) loadTabData(); }, [tab, event?.id]);
  // Load conversations on mount for notification badge
  useEffect(() => { api.get('/chat/conversations', { params: { event_id: id, role: 'client' } }).then(r => setConversations(r.data)).catch(() => {}); }, [id]);
  // Load host's saved delivery info for structured address form
  useEffect(() => { api.get('/host/delivery-info').then(r => { if (r.data) { setHostDeliveryInfo(r.data); setEditDeliveryFields({ street: r.data.street || '', town: r.data.town || '', region: r.data.region || '', province: r.data.province || '', postal_code: r.data.postal_code || '', contact_person: r.data.contact_person || '', contact_mobile: r.data.contact_mobile || '' }); } }).catch(() => {}); }, [id]);
  // Auto-clear gift contribution badge when Registry tab opens
  useEffect(() => {
    if (tab === 'registry' && newGiftContributions > 0) {
      api.post(`/events/${id}/mark-contributions-viewed`).then(() => setNewGiftContributions(0)).catch(() => {});
    }
  }, [tab, id, newGiftContributions]);

  // Auto-refresh ticket sales for live updates
  useEffect(() => {
    if (tab !== 'tickets' || ticketView !== 'sales' || !event?.id) return;
    const interval = setInterval(async () => {
      try {
        const sales = await api.get(`/events/${id}/ticket-sales`);
        setTicketSales(sales.data);
      } catch {}
    }, 15000);
    return () => clearInterval(interval);
  }, [tab, ticketView, id, event?.id]);

  // Listen for GiGi quick-chip navigation events
  useEffect(() => {
    const handler = (e) => {
      console.log('[EventWorkspace gigi-action] Received event:', e.detail);
      const { action, tab: targetTab, filter, vendorName } = e.detail || {};
      if (action === 'navigate' && targetTab) {
        console.log('[EventWorkspace gigi-action] Navigating to tab:', targetTab, 'filter:', filter);
        if (setTabRef.current) setTabRef.current(targetTab);
        if (filter) {
          sessionStorage.setItem('ggr_vendor_filter', filter);
        }
      } else if (action === 'open_vendor_chat' && vendorName) {
        console.log('[EventWorkspace gigi-action] Opening vendor chat:', vendorName);
        if (setTabRef.current) setTabRef.current('vendors');
        sessionStorage.setItem('ggr_open_vendor_chat', vendorName);
      } else {
        console.log('[EventWorkspace gigi-action] No matching handler. action:', action, 'tab:', targetTab, 'vendorName:', vendorName);
      }
    };
    console.log('[EventWorkspace gigi-action] Listener registered');
    window.addEventListener('gigi-action', handler);
    return () => {
      console.log('[EventWorkspace gigi-action] Listener removed');
      window.removeEventListener('gigi-action', handler);
    };
  }, []);

  // Open chat with vendor from booking card
  const openChatWithVendor = async (vendorId, vendorName) => {
    try {
      // Check if conversation already exists
      const existing = conversations.find(c => c.other_user_id === vendorId);
      let convId = existing?.id;
      if (!convId) {
        const r = await api.post('/chat/conversations', { vendor_id: vendorId, subject: `Booking inquiry for ${event?.title || 'event'}`, event_id: id });
        convId = r.data.id;
        // Refresh conversations
        api.get('/chat/conversations', { params: { event_id: id, role: 'client' } }).then(r2 => setConversations(r2.data)).catch(() => {});
      }
      setAutoOpenConvId(convId);
      setTab('vendors');
    } catch { showToast('Failed to open chat', 'error'); }
  };

  const sortedHostBookings = React.useMemo(() => {
    if (!Array.isArray(hostBookings)) return [];
    const filtered = hostBookingsFilter === 'all' ? hostBookings
      : hostBookingsFilter === 'archived' ? hostBookings.filter(b => b.is_archived)
      : hostBookingsFilter === 'paid' ? hostBookings.filter(b => b.status === 'confirmed' && b.payment_status === 'paid' && !b.is_archived)
      : hostBookingsFilter === 'confirmed' ? hostBookings.filter(b => b.status === 'confirmed' && b.payment_status !== 'paid' && !b.is_archived)
      : hostBookingsFilter === 'pending' ? hostBookings.filter(b => b.status === 'pending' && !b.is_archived)
      : hostBookingsFilter === 'cancelled' ? hostBookings.filter(b => (b.status === 'cancelled' || b.status === 'declined') && !b.is_archived)
      : /* active */ hostBookings.filter(b => !b.is_archived && !['cancelled', 'declined'].includes(b.status));
    const priority = { pending: 0, confirmed: 1, cancelled: 2, declined: 3 };
    return [...filtered].sort((a, b) => {
      const pa = priority[a.status] ?? 4;
      const pb = priority[b.status] ?? 4;
      if (pa !== pb) return pa - pb;
      return new Date(b.created_at) - new Date(a.created_at);
    });
  }, [hostBookings, hostBookingsFilter]);

  const saveDeliveryAddress = async (bkId) => {
    const fields = editDeliveryFields;
    if (!fields.street && !fields.town && !fields.region && !fields.province && !fields.postal_code) {
      return showToast('Please enter at least your street and town', 'error');
    }
    setSavingDeliveryId(bkId);
    try {
      // Save structured delivery info for future bookings
      await api.put('/host/delivery-info', fields);
      setHostDeliveryInfo(fields);

      // Build formatted address string
      const parts = [fields.street, fields.town, fields.region, fields.province, fields.postal_code].filter(Boolean);
      if (fields.contact_person) parts.push(`Contact: ${fields.contact_person}`);
      if (fields.contact_mobile) parts.push(`Mobile: ${fields.contact_mobile}`);
      const formattedAddress = parts.join(', ');

      // Update this booking's delivery address
      await api.put(`/host/bookings/${bkId}/delivery-address`, { delivery_address: formattedAddress });
      setHostBookings(prev => prev.map(b => b.id === bkId ? { ...b, delivery_address: formattedAddress } : b));
      setEditingAddressId(null);
      showToast('Address saved for all future bookings.', 'success');
    } catch (err) {
      showToast(err.response?.data?.error || 'Failed to save address', 'error');
    } finally { setSavingDeliveryId(null); }
  };

  const toggleHostArchive = async (bkId) => {
    try {
      await api.post(`/vendor/bookings/${bkId}/archive`);
      setHostBookings(prev => prev.map(b => b.id === bkId ? { ...b, is_archived: !b.is_archived } : b));
      showToast('Booking archived');
    } catch { showToast('Failed to archive', 'error'); }
  };

  // Auto-open chat when navigated from vendor profile with convid param
  useEffect(() => {
    // Wait until event is loaded — setTab depends on MAIN_TABS which needs event
    if (!event) return;
    const convId = searchParams.get('convid');
    const tabParam = searchParams.get('tab');
    if (convId && String(convId) !== String(lastAppliedParams.current.convId)) {
      lastAppliedParams.current = { tab: null, convId: String(convId) };
      setAutoOpenConvId(parseInt(convId));
      setTab('vendors');
    } else if (tabParam && tabParam !== lastAppliedParams.current.tab) {
      lastAppliedParams.current = { tab: tabParam, convId: null };
      // Map legacy tab names to current tabs
      const tabMap = { messages: 'vendors', orders: 'deliveries' };
      setTab(tabMap[tabParam] || tabParam);
    }
  }, [searchParams, event]);

  // Handle Ozow payment redirect: ?ozow=success&ref=XXX (only once)
  useEffect(() => {
    if (!event || ozowHandledRef.current) return;
    const ozowStatus = searchParams.get('ozow');
    const ozowRef = searchParams.get('ref');
    if (ozowStatus === 'success') {
      ozowHandledRef.current = true;
      showToast('Payment successful! Your booking has been confirmed.', 'success');
      // Show the payer their receipt
      if (ozowRef) {
        navigate(`/receipt/${ozowRef}`);
      }
    } else if (ozowStatus === 'cancel' || ozowStatus === 'error') {
      ozowHandledRef.current = true;
      showToast('Payment was cancelled.', 'error');
    }
  }, [searchParams, event, showToast, navigate]);

  // Listen for notification bell clicks from AppLayout
  useEffect(() => {
    if (id) {
      api.get(`/events/${id}/thank-you`).then(r => {
        if (r.data) {
          setThankYouData(r.data);
          setThankYouSaved(true);
          if (r.data.sent) setThankYouSent(true);
        }
      }).catch(() => {});
    }
  }, [id]);

  const loadEvent = async () => {
    try { const r = await api.get(`/events/${id}`); setEvent(r.data); setNewGiftContributions(parseInt(r.data.new_gift_contributions) || 0); setRsvpDeadline(r.data.rsvp_deadline || ''); setTicketDeadline(r.data.ticket_deadline || ''); setTicketsEnabled(r.data.tickets_enabled || false); } catch { navigate('/dashboard'); }
  };

  const generateExtra = async (type) => {
    setGeneratingExtra(type);
    try {
      const r = await api.post(`/events/${id}/ai-generate-extra`, { type });
      if (r.data.image_url) {
        if (type === 'programme') setProgrammeImage(r.data.image_url);
        else setWelcomeBoardImage(r.data.image_url);
        showToast(`${type === 'programme' ? 'Programme card' : 'Welcome board'} generated!`);
      }
    } catch (err) {
      showToast(err.response?.data?.error || 'Failed to generate', 'error');
    }
    setGeneratingExtra(null);
  };

  const downloadExtraImage = async (url, name) => {
    try {
      const r = await fetch(url);
      const blob = await r.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `${name}-${event?.title?.replace(/\s+/g, '-') || 'event'}.png`;
      a.click(); URL.revokeObjectURL(a.href);
    } catch { showToast('Download failed', 'error'); }
  };

  const loadTabData = async () => {
    try {
      switch(tab) {
        case 'overview': {
          const [b, g, c, r, inv] = await Promise.all([
            api.get(`/events/${id}/budget`), api.get(`/events/${id}/guests`),
            api.get(`/events/${id}/checklist`), api.get(`/events/${id}/registry`),
            api.get(`/events/${id}/invitations`)
          ]);
          setBudget(b.data); setGuests(g.data); setChecklist(c.data); setRegistry(r.data); setInvitations(inv.data);
          break;
        }
        case 'timeline': { const r = await api.get(`/events/${id}/timeline`); setTimeline(r.data); break; }
        case 'budget': { const r = await api.get(`/events/${id}/budget`); setBudget(r.data); break; }
        case 'guests': { const [r, t] = await Promise.all([api.get(`/events/${id}/guests`), api.get(`/events/${id}/tickets`)]); setGuests(r.data); setTickets(t.data); break; }
        case 'tickets': { const [r, tiers, sales, inv] = await Promise.all([api.get(`/events/${id}/tickets`), api.get(`/events/${id}/ticket-tiers`), api.get(`/events/${id}/ticket-sales`).catch(() => ({ data: null })), api.get(`/events/${id}/invitations`)]); setTickets(r.data); setTicketTiers(tiers.data); setTicketSales(sales.data); setInvitations(inv.data); setTicketsEnabled(event?.tickets_enabled || false); break; }
        case 'checklist': { const r = await api.get(`/events/${id}/checklist`); setChecklist(r.data); break; }
        case 'registry': { const [r, gv] = await Promise.all([api.get(`/events/${id}/registry`), api.get('/gift-vendors')]); setRegistry(r.data); setGiftVendors(gv.data); break; }
        case 'gallery': { const r = await api.get(`/events/${id}/gallery`); setGallery(r.data); break; }
        case 'wall': { const r = await api.get(`/events/${id}/wall`); setWallPosts(r.data); break; }
        case 'delegation': { const r = await api.get(`/events/${id}/delegations`); setDelegations(r.data); break; }
        case 'invitations': { const r = await api.get(`/events/${id}/invitations`); setInvitations(r.data); break; }
        case 'speakers': { const [r, dl] = await Promise.all([api.get(`/events/${id}/speakers`), api.get(`/events/${id}/delegations`)]); setSpeakers(r.data); setDelegations(dl.data); break; }

        case 'corporate': { const [p, q] = await Promise.all([api.get(`/events/${id}/polls`), api.get(`/events/${id}/qa`)]); setPolls(p.data); setQa(q.data); break; }
        case 'seating': { const [t, g] = await Promise.all([api.get(`/events/${id}/tables`), api.get(`/events/${id}/guests`)]); setTables(t.data); setGuests(g.data); break; }
        case 'vendors': { const c = await api.get('/chat/conversations', { params: { event_id: id, role: 'client' } }).catch(() => ({ data: [] })); setConversations(c.data); break; }
        case 'deliveries': { const r = await api.get('/guest/deliveries'); setDeliveries(r.data); break; }
        case 'bookings': { const [r, d] = await Promise.all([api.get('/host/bookings', { params: { event_id: id } }).catch(() => ({ data: [] })), api.get('/disputes', { params: { event_id: id } }).catch(() => ({ data: [] }))]); setHostBookings(r.data); setDisputes(d.data); break; }
        default: break;
      }
    } catch {}
  };

  const confirmDelivery = async (deliveryId) => {
    try {
      await api.put(`/guest/deliveries/${deliveryId}/confirm`);
      showToast('Delivery confirmed! Vendor has been notified.');
      loadTabData();
    } catch (e) { showToast(e.response?.data?.error || 'Failed to confirm', 'error'); }
  };

  if (!event) return <LoadingScreen />;

  // Main tab groups with expandable sub-tabs
  const MAIN_TABS = [
    { key: 'plan', label: 'Plan', icon: <FiCheckSquare />, sub: [
      { key: 'checklist', label: 'Checklist', icon: <FiCheckSquare /> },
      { key: 'timeline', label: 'Timeline', icon: <FiClock /> },
      { key: 'budget', label: 'Budget', icon: <CoinIcon /> },
      { key: 'gallery', label: 'Gallery', icon: <FiCamera /> },
      { key: 'wall', label: 'Wall', icon: <FiMessageSquare /> },
    ]},
    { key: 'guests', label: 'Guests', icon: <FiUsers />, sub: [
      { key: 'guests', label: 'Guests', icon: <FiUsers /> },
      { key: 'invitations', label: 'Invites', icon: <FiShare2 /> },
      { key: 'seating', label: 'Seating', icon: <FiMapPin /> },
      { key: 'speakers', label: 'Speakers', icon: <FiMic /> },
      { key: 'tickets', label: 'Tickets', icon: <IoTicketOutline /> },
    ]},
    { key: 'registry', label: 'Registry', icon: <FiGift />, sub: [
      { key: 'registry', label: 'Registry', icon: <FiGift /> },
    ]},
    { key: 'vendors', label: 'Vendors', icon: <HiOutlineBuildingStorefront />, sub: [
      { key: 'vendors', label: 'Chats', icon: <FiMessageCircle /> },
      { key: 'bookings', label: 'Bookings', icon: <CoinIcon /> },
      { key: 'deliveries', label: 'Deliveries', icon: <FiTruck /> },
    ]},
  ];
  if (event.event_type === 'Corporate Event') {
    MAIN_TABS[0].sub.push({ key: 'corporate', label: 'Corporate', icon: <FiBarChart2 /> });
  }

  const handleMainTab = (groupKey) => {
    if (expandedGroup === groupKey) {
      // Collapse — keep current tab
      setExpandedGroup(null);
    } else {
      // Expand and switch to its first sub-tab
      const group = MAIN_TABS.find(g => g.key === groupKey);
      setExpandedGroup(groupKey);
      if (group?.sub[0]) setTabRaw(group.sub[0].key);
    }
  };

  const handleSubTab = (subKey) => {
    setTabRaw(subKey);
    // Ensure the parent group is expanded (syncs sub-tab row with active tab)
    const group = MAIN_TABS.find(g => g.sub.some(s => s.key === subKey));
    if (group) setExpandedGroup(group.key);
  };

  const handleDashboard = () => {
    setExpandedGroup(null);
    setTabRaw('overview');
  };

  // Wrapper for external setTab calls — auto-expands the correct group
  const setTab = (key) => {
    setTabRaw(key);
    if (key === 'overview') {
      setExpandedGroup(null);
    } else {
      const group = MAIN_TABS.find(g => g.sub.some(s => s.key === key));
      if (group) setExpandedGroup(group.key);
    }
  };
  setTabRef.current = setTab;

  const expandedSubs = MAIN_TABS.find(g => g.key === expandedGroup)?.sub || [];
  const vendorUnread = conversations.reduce((a, c) => a + (parseInt(c.unread_count) || 0), 0);

  const rsvpStats = {
    attending: guests.filter(g => g.rsvp_status === 'attending').length,
    maybe: guests.filter(g => g.rsvp_status === 'maybe').length,
    not_attending: guests.filter(g => g.rsvp_status === 'not_attending').length,
    pending: guests.filter(g => g.rsvp_status === 'pending').length,
  };
  const totalChildren = guests.reduce((sum, g) => sum + (g.children ? g.children.length : 0), 0);
  const isKidsParty = event?.event_type && event.event_type.toLowerCase().includes('kids');

  const totalBudgetAllocated = budget.reduce((a, b) => a + Number(b.allocated || 0), 0);
  const totalBudgetSpent = budget.reduce((a, b) => a + Number(b.spent || 0), 0);
  const checklistDone = checklist.filter(c => c.completed).length;

  // GiGi Tip Card — shown on empty tabs to prompt one-click setup
  const GigiTipCard = ({ message, action }) => (
    <div className="card" style={{
      marginBottom: '20px', padding: '18px 20px',
      background: 'linear-gradient(135deg, rgba(227,66,92,0.06), rgba(227,66,92,0.02))',
      border: '1px solid rgba(227,66,92,0.15)',
      borderRadius: '14px',
      display: 'flex', alignItems: 'center', gap: '14px', flexWrap: 'wrap'
    }}>
      <div style={{
        width: '44px', height: '44px', borderRadius: '50%',
        background: 'linear-gradient(135deg, rgba(227,66,92,0.2), rgba(227,66,92,0.05))',
        border: '2px solid rgba(227,66,92,0.2)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        overflow: 'hidden', flexShrink: 0
      }}>
        <img src="/gigi-mascot.png" alt="GiGi" style={{ width: '36px', height: '36px', objectFit: 'cover', objectPosition: 'center top', borderRadius: '50%' }} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 600, fontSize: '14px', color: 'var(--dark)', marginBottom: '4px' }}>{message}</div>
        <div style={{ fontSize: '12px', color: 'var(--gray)' }}>One tap and GiGi handles it for you</div>
      </div>
      <button onClick={() => {
        window.dispatchEvent(new CustomEvent('gigi-ask', { detail: { message: action } }));
      }} style={{
        padding: '10px 18px', borderRadius: '10px',
        background: 'linear-gradient(135deg, #E3425C, #cf4354)',
        color: 'white', border: 'none', fontWeight: 600, fontSize: '13px',
        cursor: 'pointer', whiteSpace: 'nowrap',
        boxShadow: '0 2px 8px rgba(227,66,92,0.25)'
      }}>
        Set It Up
      </button>
    </div>
  );

  return (
    <div style={{ minHeight: '100vh', background: 'var(--cream)' }}>
      <style>{`
        .ew-tab-row { display:flex; gap:6px; align-items:center; }
        .ew-subtab-row { display:flex; gap:6px; align-items:center; }
        .ew-tab { display:flex; flex-direction:column; align-items:center; justify-content:center; gap:6px; padding:14px 20px; border-radius:14px; min-width:0; cursor:pointer; font-size:14px; font-weight:500; transition:all 0.2s ease; white-space:nowrap; border:1px solid transparent; background:transparent; color:var(--gray); flex:1 1 0; }
        .ew-tab .ew-tab-icon { font-size:24px; line-height:1; }
        .ew-dashboard { display:flex; flex-direction:column; align-items:center; justify-content:center; gap:6px; padding:14px 20px; border-radius:14px; min-width:0; cursor:pointer; font-size:14px; font-weight:500; transition:all 0.2s ease; white-space:nowrap; border:1px solid transparent; background:transparent; color:var(--gray); flex:1 1 0; }
        .ew-dashboard .ew-tab-icon { font-size:24px; line-height:1; }
        .ew-subtab { display:flex; align-items:center; justify-content:center; gap:6px; padding:10px 18px; border-radius:8px; cursor:pointer; font-size:13px; font-weight:500; transition:all 0.2s ease; flex:1 1 0; white-space:nowrap; border:1px solid var(--cream-dark); background:var(--cream); color:var(--dark); }
        [data-theme="dark"] .ew-subtab { background:#2C2C2E; border-color:#3A3A3C; color:#e8e8ea; }
        .ew-subtab .ew-subtab-icon { font-size:17px; }
        .ew-divider { width:1px; height:28px; background:var(--cream-dark); border-radius:1px; flex-shrink:0; margin:0 4px; }
        .ew-logo { height:48px; filter:brightness(0) invert(1); opacity:0.95; }
        @media (max-width: 600px) {
          .ew-logo { height:36px; }
          .budget-row { flex-direction: column !important; align-items: flex-start !important; gap: 12px !important; }
          .budget-row > * { flex: none !important; width: 100% !important; }
          .budget-row > div:last-child { text-align: left !important; }
          .event-settings-dropdown { right: auto !important; left: 0 !important; min-width: '200px' !important; }
          .ew-tab-row { overflow-x:auto; flex-wrap:nowrap; -webkit-overflow-scrolling:touch; }
          .ew-subtab-row { overflow-x:auto; flex-wrap:nowrap; -webkit-overflow-scrolling:touch; }
          .ew-tab { flex:0 0 auto; padding:8px 11px; border-radius:10px; min-width:48px; gap:3px; font-size:10px; }
          .ew-tab .ew-tab-icon { font-size:16px; }
          .ew-dashboard { flex:0 0 auto; padding:8px 11px; border-radius:10px; min-width:48px; gap:3px; font-size:10px; }
          .ew-dashboard .ew-tab-icon { font-size:16px; }
          .ew-subtab { flex:0 0 auto; padding:7px 12px; gap:5px; font-size:11px; }
          .ew-subtab .ew-subtab-icon { font-size:14px; }
          .ew-divider { height:24px; margin:0 2px; }
        }
      `}</style>
      {/* Header */}
      <div style={{ ...getEventBannerStyle(event), padding: '24px 0' }}>
        <div className="container">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', marginBottom: '16px', gap: '12px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexShrink: 0 }}>
              <Link to="/"><img src="/ggr-logo-deck.png" alt="Grand Gather Registry" className="ew-logo" /></Link>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
              <Link to="/dashboard" style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', height: '38px', padding: '0 14px', borderRadius: '10px', border: '1px solid rgba(255,255,255,0.35)', background: 'rgba(255,255,255,0.1)', color: 'white', fontSize: '13px', fontWeight: 500, textDecoration: 'none', whiteSpace: 'nowrap' }}><FiArrowLeft size={14} /> Back</Link>
              <button style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', height: '38px', padding: '0 14px', borderRadius: '10px', border: '1px solid rgba(255,255,255,0.35)', background: 'rgba(255,255,255,0.1)', color: 'white', fontSize: '13px', fontWeight: 500, cursor: 'pointer', whiteSpace: 'nowrap' }} onClick={() => { const isStandard = STANDARD_EVENT_TYPES.includes(event.event_type); setFormData({ title: event.title, event_type: isStandard ? event.event_type : 'Custom Event', custom_event_type: isStandard ? (event.event_type || '') : (event.event_type || ''), event_date: event.event_date ? event.event_date.split('T')[0] : '', event_time: event.event_time || '', end_time: event.end_time || '', location: event.location || '', estimated_guests: event.estimated_guests || '', budget: event.budget || '', description: event.description || '', dress_code: event.dress_code || '', theme_color: event.theme_color || '', existingImage: event.cover_image || '', show_host_contact: event.show_host_contact || false, host_contact_name: event.host_contact_name || '', host_contact_phone: event.host_contact_phone || '', host_contact_email: event.host_contact_email || '' }); setShowModal('editEvent'); }}><FiEdit size={14} /> Edit</button>
              <div style={{ position: 'relative' }} ref={gearRef}>
                <button style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '38px', height: '38px', padding: 0, borderRadius: '10px', border: '1px solid rgba(255,255,255,0.35)', background: 'rgba(255,255,255,0.1)', color: 'white', cursor: 'pointer' }}
                  onClick={() => setShowModal(showModal === 'eventSettings' ? null : 'eventSettings')}>
                  <FiSettings size={16} />
                </button>
                {showModal === 'eventSettings' && (
                  <div className="event-settings-dropdown" style={{ position: 'absolute', right: 0, top: '100%', marginTop: '6px', background: 'var(--white)', borderRadius: '12px', boxShadow: '0 8px 32px rgba(0,0,0,0.15)', border: '1px solid rgba(0,0,0,0.06)', padding: '6px', minWidth: '220px', zIndex: 100 }}>
                    <button style={{ width: '100%', display: 'flex', alignItems: 'center', gap: '10px', padding: '12px 14px', borderRadius: '8px', border: 'none', cursor: 'pointer', background: 'transparent', fontSize: '13px', fontWeight: 500, color: event.discontinued ? '#10B981' : '#e65100', textAlign: 'left' }}
                      onMouseEnter={e => e.currentTarget.style.background = 'var(--cream)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                      onClick={async () => {
                        setShowModal(null);
                        if (event.discontinued) {
                          if (window.confirm('Re-activate this event page?')) { await api.put(`/events/${id}/discontinue`, { discontinued: false }); loadEvent(); }
                        } else {
                          if (window.confirm('Discontinue this event page? Guests will no longer access the invitation link.')) { await api.put(`/events/${id}/discontinue`, { discontinued: true }); loadEvent(); }
                        }
                      }}>
                      <FiAlertCircle size={14} /> {event.discontinued ? 'Re-activate Page' : 'Discontinue Page'}
                    </button>
                    <div style={{ height: '1px', background: 'rgba(0,0,0,0.06)', margin: '4px 8px' }} />
                    <button style={{ width: '100%', display: 'flex', alignItems: 'center', gap: '10px', padding: '12px 14px', borderRadius: '8px', border: 'none', cursor: 'pointer', background: 'transparent', fontSize: '13px', fontWeight: 500, color: '#d32f2f', textAlign: 'left' }}
                      onMouseEnter={e => e.currentTarget.style.background = 'rgba(244,67,54,0.05)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                      onClick={async () => {
                        setShowModal(null);
                        if (window.confirm('Permanently delete this event? This removes all guests, timeline, budget, gallery, and everything. This CANNOT be undone.')) {
                          if (window.confirm('Final confirmation — delete "' + event.title + '" permanently?')) {
                            try { await api.delete(`/events/${id}`); navigate('/dashboard'); } catch { alert('Failed to delete event.'); }
                          }
                        }
                      }}>
                      <FiTrash2 size={14} /> Delete Event
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '16px' }}>
            <div>
              <span className="tag" style={{ background: 'rgba(255,255,255,0.2)', color: 'white', marginBottom: '8px', display: 'inline-block' }}>{event.event_type}</span>
              <h1 style={{ fontSize: '32px', marginBottom: '8px' }}>{event.title}</h1>
              <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap', fontSize: '14px', opacity: 0.9 }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}><FiCalendar /> {event.event_date ? new Date(event.event_date).toLocaleDateString('en-ZA', { day:'numeric', month:'long', year:'numeric' }) : 'Date TBD'}{event.event_time && <><FiClock style={{ marginLeft: '8px' }} /> {event.event_time}{event.end_time ? ` - ${event.end_time}` : ''}</>}</span>
                {event.location ? (
                  <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(event.location)}`} target="_blank" rel="noopener noreferrer" style={{ display: 'flex', alignItems: 'center', gap: '6px', color: 'inherit', textDecoration: 'underline', textDecorationStyle: 'dotted', textUnderlineOffset: '3px' }}><FiMapPin /> {event.location}</a>
                ) : (
                  <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}><FiMapPin /> Location TBD</span>
                )}
                <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}><FiUsers /> {event.estimated_guests} Guests</span>
                <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}><CoinIcon /> R{Number(event.budget || 0).toLocaleString()}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Host Plan Banner */}
      <div className="container" style={{ marginTop: '16px' }}>
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px',
          padding: '14px 20px', borderRadius: '14px',
          background: 'var(--white)',
          border: '1px solid var(--cream-dark)',
          boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
            <span style={{
              fontSize: '12px', fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase',
              padding: '4px 12px', borderRadius: '20px',
              background: hostPlan === 'premium' ? '#E8A0BF' : hostPlan === 'standard' ? 'var(--rose)' : 'var(--cream-dark)',
              color: hostPlan === 'premium' ? '#2D1F2B' : hostPlan === 'standard' ? 'white' : 'var(--dark)',
            }}>{hostPlanLabel} Plan</span>
            <span style={{ fontSize: '13px', color: 'var(--gray)', fontFamily: 'Inter, sans-serif' }}>
              {hostLimits.rsvp === Infinity ? (
                <>Unlimited RSVPs &middot; Unlimited designs &middot; Priority vendor matching</>
              ) : hostPlan === 'standard' ? (
                <>{hostLimits.rsvp} RSVPs &middot; {hostLimits.designs} designs &middot; Seating &amp; Budget</>
              ) : (
                <>{hostLimits.rsvp} RSVPs &middot; {hostLimits.designs} design &middot; No seating/budget</>
              )}
            </span>
          </div>
          {hostPlan !== 'premium' && (
            <Link to="/upgrade?type=host" style={{
              display: 'inline-flex', alignItems: 'center', gap: '6px',
              padding: '8px 18px', borderRadius: '10px',
              background: 'linear-gradient(135deg, var(--rose-deep), #b13d4e)', color: 'white',
              fontSize: '13px', fontWeight: 700, fontFamily: 'Inter, sans-serif',
              textDecoration: 'none', transition: 'opacity 0.2s',
            }} onMouseEnter={e => e.currentTarget.style.opacity = '0.85'} onMouseLeave={e => e.currentTarget.style.opacity = '1'}>
              <FiZap size={14} /> Upgrade
            </Link>
          )}
        </div>
      </div>

      {/* Tabs — Two-row collapsible */}
      <div className="container" style={{ marginTop: '20px' }}>
        <div style={{
          background: 'var(--white)',
          borderRadius: '16px',
          padding: '10px 12px',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
          border: '1px solid var(--cream-dark)'
        }}>
          {/* Row 1: Dashboard + Main tabs */}
          <div className="ew-tab-row" style={{
            paddingBottom: expandedSubs.length > 0 ? '8px' : '0',
            borderBottom: expandedSubs.length > 0 ? '1px solid var(--cream-dark)' : 'none'
          }}>
            {/* Dashboard — always visible, standalone */}
            <button onClick={handleDashboard} className="ew-dashboard"
              style={{
                borderColor: tab === 'overview' ? '#C94D5C' : 'transparent',
                borderWidth: tab === 'overview' ? '1.5px' : '1px',
                fontWeight: tab === 'overview' ? 700 : 500,
                background: tab === 'overview' ? '#C94D5C' : 'transparent',
                color: tab === 'overview' ? 'white' : 'var(--gray)',
              }}
            >
              <span className="ew-tab-icon" style={{ opacity: tab === 'overview' ? 1 : 0.65 }}><FiBarChart2 /></span>
              Dashboard
            </button>

            {/* Divider */}
            <div className="ew-divider" />

            {/* Main category tabs */}
            {MAIN_TABS.map(g => {
              const isExpanded = expandedGroup === g.key;
              const hasActive = g.sub.some(s => s.key === tab);
              return (
                <button key={g.key} onClick={() => handleMainTab(g.key)} className="ew-tab"
                  style={{
                    borderColor: isExpanded ? '#C94D5C' : 'transparent',
                    borderWidth: isExpanded ? '1.5px' : '1px',
                    fontWeight: isExpanded ? 700 : 500,
                    background: isExpanded ? '#C94D5C' : 'transparent',
                    color: isExpanded ? 'white' : hasActive && !isExpanded ? '#C94D5C' : 'var(--gray)',
                    position: 'relative',
                  }}
                >
                  <span className="ew-tab-icon" style={{ opacity: isExpanded ? 1 : 0.65 }}>{g.icon}</span>
                  {g.label}
                  {g.key === 'media' && vendorUnread > 0 && !isExpanded && (
                    <span style={{ position: 'absolute', top: '-4px', right: '-4px', background: '#E3425C', color: 'white', borderRadius: '10px', padding: '1px 6px', fontSize: '10px', fontWeight: 700, lineHeight: '16px', minWidth: '18px', textAlign: 'center' }}>{vendorUnread}</span>
                  )}
                  {g.key === 'registry' && newGiftContributions > 0 && !isExpanded && (
                    <span style={{ position: 'absolute', top: '-4px', right: '-4px', background: '#EC4899', color: 'white', borderRadius: '10px', padding: '1px 6px', fontSize: '10px', fontWeight: 700, lineHeight: '16px', minWidth: '18px', textAlign: 'center' }}>{newGiftContributions}</span>
                  )}
                  {hasActive && !isExpanded && (
                    <div style={{ position: 'absolute', bottom: '4px', left: '50%', transform: 'translateX(-50%)', width: '6px', height: '6px', borderRadius: '50%', background: 'var(--rose-deep)' }} />
                  )}
                </button>
              );
            })}
          </div>

          {/* Row 2: Sub-tabs (only when a group is expanded) */}
          {expandedSubs.length > 0 && (
            <div className="ew-subtab-row" style={{ paddingTop: '10px', paddingBottom: '2px' }}>
              {expandedSubs.map(t => (
                <button key={t.key} onClick={() => handleSubTab(t.key)} className="ew-subtab"
                  style={{
                    borderColor: tab === t.key ? '#C94D5C' : 'var(--cream-dark)',
                    borderWidth: tab === t.key ? '1.5px' : '1px',
                    fontWeight: tab === t.key ? 700 : 500,
                    background: tab === t.key ? 'var(--white)' : 'var(--cream)',
                    color: tab === t.key ? '#C94D5C' : 'var(--dark)',
                  }}
                >
                  <span className="ew-subtab-icon" style={{ opacity: tab === t.key ? 1 : 0.6 }}>{t.icon}</span>
                  {t.label}
                  {t.key === 'registry' && newGiftContributions > 0 && (
                    <span style={{ marginLeft: '6px', background: '#EC4899', color: 'white', borderRadius: '10px', padding: '1px 7px', fontSize: '10px', fontWeight: 700, lineHeight: '16px', minWidth: '18px', textAlign: 'center', animation: 'rsvpPulse 1.2s ease-in-out infinite' }}>{newGiftContributions}</span>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Tab Content */}
        <div style={{ paddingBottom: '40px' }}>

          {/* OVERVIEW */}
          {tab === 'overview' && (<>
            {/* Getting Started Guide - always visible */}
              <div className="card" style={{ marginBottom: '20px', background: 'linear-gradient(135deg, rgba(232,160,191,0.08), rgba(201,168,76,0.06))', border: '1px solid rgba(232,160,191,0.15)' }}>
                <h3 style={{ fontSize: '18px', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <FiZap style={{ color: 'var(--rose)' }} /> {[!!(event.event_date && event.location), guests.length > 0, checklist.length > 0, registry.length > 0, invitations.length > 0].every(Boolean) ? 'Event Setup Complete' : 'Getting Started'}
                </h3>
                <p style={{ fontSize: '13px', color: 'var(--gray)', marginBottom: '16px' }}>{[!!(event.event_date && event.location), guests.length > 0, checklist.length > 0, registry.length > 0, invitations.length > 0].every(Boolean) ? 'All steps completed! You can still adjust your event anytime.' : 'Complete these steps to make the most of your event'}</p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {[
                    { done: !!(event.event_date && event.location), label: 'Add event date & venue', action: () => { setFormData({ title: event.title, event_type: event.event_type, event_date: event.event_date ? event.event_date.split('T')[0] : '', event_time: event.event_time || '', end_time: event.end_time || '', location: event.location || '', estimated_guests: event.estimated_guests || '', budget: event.budget || '', description: event.description || '', dress_code: event.dress_code || '', theme_color: event.theme_color || '', existingImage: event.cover_image || '' }); setShowModal('editEvent'); }, icon: <FiCalendar /> },
                    { done: guests.length > 0, label: 'Add your first guests', action: () => setTab('guests'), icon: <FiUsers /> },
                    { done: checklist.length > 0, label: 'Create your planning checklist', action: () => setTab('checklist'), icon: <FiCheckSquare /> },
                    { done: registry.length > 0, label: 'Set up gift registry', action: () => setTab('registry'), icon: <FiGift /> },
                    { done: invitations.length > 0, label: 'Share your invitation link', action: () => setTab('invitations'), icon: <FiShare2 /> },
                  ].map((step, i) => (
                    <button key={i} onClick={step.action} style={{
                      display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 16px',
                      background: step.done ? 'rgba(16,185,129,0.06)' : 'var(--white)',
                      borderRadius: '10px', border: step.done ? '1px solid rgba(16,185,129,0.15)' : '1px solid var(--cream-dark)',
                      cursor: 'pointer', width: '100%', textAlign: 'left', transition: 'all 0.2s',
                    }}>
                      <div style={{
                        width: '28px', height: '28px', minWidth: '28px', minHeight: '28px', maxWidth: '28px', maxHeight: '28px', borderRadius: '50%',
                        background: step.done ? 'var(--green)' : 'var(--cream)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        color: step.done ? 'white' : 'var(--gray)', fontSize: '14px', flexShrink: 0, aspectRatio: '1 / 1',
                      }}>
                        {step.done ? <FiCheck size={14} /> : step.icon}
                      </div>
                      <span style={{ fontSize: '14px', fontWeight: 500, color: step.done ? 'var(--green)' : 'var(--dark)', textDecoration: step.done ? 'line-through' : 'none' }}>
                        {step.label}
                      </span>
                      {!step.done && <FiArrowRight size={14} style={{ marginLeft: 'auto', color: 'var(--gray)' }} />}
                    </button>
                  ))}
                </div>
                {/* Progress */}
                <div style={{ marginTop: '16px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', marginBottom: '6px' }}>
                    <span style={{ color: 'var(--gray)' }}>Setup Progress</span>
                    <span style={{ fontWeight: 600 }}>{[!!(event.event_date && event.location), guests.length > 0, checklist.length > 0, registry.length > 0, invitations.length > 0].filter(Boolean).length}/5</span>
                  </div>
                  <div className="progress-bar" style={{ height: '6px' }}>
                    <div className="progress-fill" style={{ width: `${([!!(event.event_date && event.location), guests.length > 0, checklist.length > 0, registry.length > 0, invitations.length > 0].filter(Boolean).length / 5) * 100}%` }}></div>
                  </div>
                </div>
              </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '20px' }}>
              <div className="card">
                <h3 style={{ fontSize: '16px', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}><FiCheckSquare /> Checklist Progress</h3>
                <div style={{ textAlign: 'center', marginBottom: '12px' }}>
                  <span style={{ fontSize: '36px', fontWeight: 700, color: 'var(--gold-dark)' }}>{checklistDone}</span>
                  <span style={{ fontSize: '20px', color: 'var(--gray)' }}> / {checklist.length}</span>
                </div>
                <div className="progress-bar" style={{ height: '10px' }}>
                  <div className="progress-fill" style={{ width: `${checklist.length ? (checklistDone/checklist.length)*100 : 0}%` }}></div>
                </div>
                <button className="btn btn-outline btn-sm btn-full" style={{ marginTop: '16px' }} onClick={() => setTab('checklist')}>View Checklist</button>
              </div>
              <div className="card">
                <h3 style={{ fontSize: '16px', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}><CoinIcon /> Budget Overview</h3>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', fontSize: '14px' }}>
                  <span style={{ color: 'var(--gray)' }}>Total Budget</span><span style={{ fontWeight: 600 }}>R{Number(event.budget || 0).toLocaleString()}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', fontSize: '14px' }}>
                  <span style={{ color: 'var(--gray)' }}>Allocated</span><span style={{ fontWeight: 600, color: 'var(--blue)' }}>R{totalBudgetAllocated.toLocaleString()}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', fontSize: '14px' }}>
                  <span style={{ color: 'var(--gray)' }}>Spent</span><span style={{ fontWeight: 600, color: 'var(--red)' }}>R{totalBudgetSpent.toLocaleString()}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px' }}>
                  <span style={{ color: 'var(--gray)' }}>Remaining</span><span style={{ fontWeight: 600, color: 'var(--green)' }}>R{(Number(event.budget || 0) - totalBudgetSpent).toLocaleString()}</span>
                </div>
                <button className="btn btn-outline btn-sm btn-full" style={{ marginTop: '16px' }} onClick={() => setTab('budget')}>Manage Budget</button>
              </div>
              <div className="card">
                <h3 style={{ fontSize: '16px', marginBottom: '6px', display: 'flex', alignItems: 'center', gap: '8px' }}><FiUsers /> Guest RSVPs</h3>
                <p style={{ fontSize: '12px', color: 'var(--gray)', marginBottom: '14px' }}>
                  Tap a toggle to view &amp; manage guests
                </p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {[
                    { key: 'attending', icon: <FiUserCheck size={13} />, count: rsvpStats.attending, label: 'Attending', color: '#10B981', desc: 'Going' },
                    { key: 'not_attending', icon: <FiUserX size={13} />, count: rsvpStats.not_attending, label: 'Declined', color: '#EF4444', desc: 'Not coming' },
                    { key: 'maybe', icon: <FiAlertCircle size={13} />, count: rsvpStats.maybe, label: 'Maybe', color: '#F59E0B', desc: 'Not sure yet' },
                    { key: 'tickets', icon: <IoTicketOutline size={13} />, count: tickets.length, label: 'Tickets', color: '#3B82F6', desc: 'Ticket holders' },
                    ...(isKidsParty ? [{ key: 'children', icon: <FiHeart size={13} />, count: totalChildren || 0, label: 'Children', color: '#EC4899', desc: 'Under 12' }] : []),
                  ].map((stat, i) => {
                    const isActive = guestFilter === stat.key;
                    return (
                    <button key={i}
                      onClick={() => { setGuestFilter(stat.key); setTab('guests'); }}
                      style={{
                        display: 'flex', alignItems: 'center', gap: '7px',
                        padding: '8px 14px',
                        background: isActive ? stat.color : 'var(--white)',
                        borderRadius: '24px',
                        border: isActive ? `2px solid ${stat.color}` : '1px solid var(--cream-dark)',
                        cursor: 'pointer', textAlign: 'left',
                        transition: 'all 0.15s ease',
                        position: 'relative',
                      }}>
                        <span style={{ display: 'flex', alignItems: 'center', color: isActive ? '#fff' : stat.color, transition: 'color 0.15s' }}>
                          {stat.icon}
                        </span>
                        <div>
                          <div style={{
                            fontSize: '12px', fontWeight: 600,
                            color: isActive ? '#fff' : 'var(--dark)',
                            lineHeight: 1.2, transition: 'color 0.15s',
                          }}>{stat.label} <span style={{
                            fontWeight: 700, fontSize: '11px',
                            color: isActive ? 'rgba(255,255,255,0.8)' : stat.color,
                          }}>{stat.count}</span></div>
                          <div style={{
                            fontSize: '10px',
                            color: isActive ? 'rgba(255,255,255,0.7)' : 'var(--gray)',
                            lineHeight: 1, marginTop: '1px', transition: 'color 0.15s',
                          }}>{stat.desc}</div>
                        </div>
                      </button>
                    );
                  })}
                </div>
                {/* Guest summary — manage in Guests tab */}
                <div style={{ marginTop: '14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span style={{ fontSize: '13px', color: 'var(--gray)' }}>
                    {guests.length === 0 ? 'No guests added yet' : `${guests.length} ${guests.length === 1 ? 'guest' : 'guests'} total`}
                  </span>
                  <button onClick={() => setTab('guests')} style={{ background: 'none', border: 'none', color: 'var(--rose)', cursor: 'pointer', fontSize: '13px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '4px' }}>
                    {guests.length === 0 ? <><FiPlus size={14} /> Add Guests</> : <>Manage Guests <FiChevronRight size={13} /></>}
                  </button>
                </div>
                {/* RSVP limit indicator */}
                {hostLimits.rsvp !== Infinity && (() => {
                  const totalRsvps = rsvpStats.attending + rsvpStats.maybe + rsvpStats.not_attending + rsvpStats.pending;
                  const pct = Math.min(100, Math.round((totalRsvps / hostLimits.rsvp) * 100));
                  const nearLimit = totalRsvps >= hostLimits.rsvp * 0.8;
                  const atLimit = totalRsvps >= hostLimits.rsvp;
                  return (
                    <div style={{ marginTop: '14px', paddingTop: '14px', borderTop: '1px solid var(--cream-dark)' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', marginBottom: '6px' }}>
                        <span style={{ color: atLimit ? 'var(--red)' : nearLimit ? '#e65100' : 'var(--gray)' }}>
                          {totalRsvps} / {hostLimits.rsvp} RSVPs
                        </span>
                        <span style={{ color: 'var(--gray)' }}>{pct}% used</span>
                      </div>
                      <div className="progress-bar" style={{ height: '6px', background: 'var(--cream-dark)' }}>
                        <div className="progress-fill" style={{ width: `${pct}%`, background: atLimit ? 'var(--red)' : nearLimit ? '#e65100' : 'var(--gold-dark)' }}></div>
                      </div>
                      {nearLimit && hostPlan !== 'premium' && (
                        <Link to="/upgrade?type=host" style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', marginTop: '8px', fontSize: '12px', color: 'var(--rose)', fontWeight: 600, textDecoration: 'none' }}>
                          <FiZap size={12} /> {atLimit ? 'RSVP limit reached — Upgrade now' : 'Nearly at limit — Upgrade'}
                        </Link>
                      )}
                    </div>
                  );
                })()}
              </div>
              <div className="card">
                <h3 style={{ fontSize: '16px', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}><FiGift /> Gift Registry</h3>
                {registry.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '16px 0' }}>
                    <FiGift size={28} style={{ color: 'var(--gray)', marginBottom: '8px', opacity: 0.4 }} />
                    <p style={{ fontSize: '13px', color: 'var(--gray)', marginBottom: '12px' }}>No gifts added yet</p>
                    <button className="btn btn-primary btn-sm" onClick={() => setTab('registry')}><FiPlus /> Set Up Registry</button>
                  </div>
                ) : (
                  <>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
                      <div style={{ textAlign: 'center', padding: '12px', background: 'rgba(232,160,191,0.1)', borderRadius: '8px' }}>
                        <div style={{ fontSize: '24px', fontWeight: 700, color: 'var(--rose-deep)' }}>{registry.length}</div>
                        <div style={{ fontSize: '12px', color: 'var(--gray)' }}>Items</div>
                      </div>
                      <div style={{ textAlign: 'center', padding: '12px', background: 'rgba(16,185,129,0.1)', borderRadius: '8px' }}>
                        <div style={{ fontSize: '24px', fontWeight: 700, color: 'var(--green)' }}>{registry.filter(r => r.is_fulfilled).length}</div>
                        <div style={{ fontSize: '12px', color: 'var(--gray)' }}>Claimed</div>
                      </div>
                    </div>
                    {registry.filter(r => Number(r.current_amount) > 0).length > 0 && (
                      <div style={{ fontSize: '14px', marginBottom: '8px', display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ color: 'var(--gray)' }}>Cash raised</span>
                        <span style={{ fontWeight: 600, color: 'var(--green)' }}>R{registry.reduce((a, r) => a + Number(r.current_amount || 0), 0).toLocaleString()}</span>
                      </div>
                    )}
                    <button className="btn btn-outline btn-sm btn-full" style={{ marginTop: '8px' }} onClick={() => setTab('registry')}>Manage Registry</button>
                  </>
                )}
              </div>
            </div>
          </>)}

          {/* GUESTS */}
          {tab === 'guests' && (
            <>
              {/* Guest filter toggle pills */}
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '16px' }}>
                {[
                  { key: 'attending', icon: <FiUserCheck size={13} />, count: rsvpStats.attending, label: 'Attending', color: '#10B981' },
                  { key: 'not_attending', icon: <FiUserX size={13} />, count: rsvpStats.not_attending, label: 'Declined', color: '#EF4444' },
                  { key: 'maybe', icon: <FiAlertCircle size={13} />, count: rsvpStats.maybe, label: 'Maybe', color: '#F59E0B' },
                  { key: 'tickets', icon: <IoTicketOutline size={13} />, count: tickets.length, label: 'Tickets', color: '#3B82F6' },
                  ...(isKidsParty ? [{ key: 'children', icon: <FiHeart size={13} />, count: totalChildren || 0, label: 'Children', color: '#EC4899' }] : []),
                ].map((stat, i) => {
                  const isActive = guestFilter === stat.key;
                  return (
                  <button key={i}
                    onClick={() => setGuestFilter(isActive ? null : stat.key)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '6px',
                      padding: '7px 14px',
                      background: isActive ? stat.color : 'var(--white)',
                      borderRadius: '24px',
                      border: isActive ? `2px solid ${stat.color}` : '1px solid var(--cream-dark)',
                      cursor: 'pointer',
                      transition: 'all 0.15s ease',
                    }}>
                    <span style={{ display: 'flex', alignItems: 'center', color: isActive ? '#fff' : stat.color }}>
                      {stat.icon}
                    </span>
                    <span style={{
                      fontSize: '12px', fontWeight: 600,
                      color: isActive ? '#fff' : 'var(--dark)',
                      lineHeight: 1.2,
                    }}>{stat.label} <span style={{
                      fontWeight: 700, fontSize: '11px',
                      color: isActive ? 'rgba(255,255,255,0.8)' : stat.color,
                    }}>{stat.count}</span></span>
                  </button>
                  );
                })}
              </div>
                <div style={{ display: 'flex', gap: '8px', marginBottom: '20px', flexWrap: 'wrap' }}>
                  <button className="btn btn-sm" style={{ background: 'linear-gradient(135deg, #8B2252, #C97191)', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 14px', fontSize: '13px', fontWeight: 600 }} onClick={async () => {
                    try {
                      const r = await api.get(`/events/${id}/auto-messages`);
                      setAutoMsg(r.data.settings || { enabled: true, use_whatsapp: true, use_email: true, schedule_days: [30,14,7,3,1], interval_days: 3, message_style: 'hype', custom_message: '', target_audience: 'attending' });
                      setAutoMsgHistory(r.data.history || []);
                      setAutoMsgPanel(true); setAutoMsgStep(1);
                    } catch { showToast('Failed to load messaging settings', 'error'); }
                  }}><FiZap size={14} /> Auto Messages</button>
                  <button className="btn btn-sm" style={{ background: 'var(--white)', color: 'var(--dark)', border: '1px solid var(--gray-light)', borderRadius: '8px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 14px', fontSize: '13px', fontWeight: 600 }} onClick={async () => {
                    try {
                      const token = localStorage.getItem('ggr_token');
                      const r = await fetch(`${process.env.REACT_APP_API_URL || ''}/api/events/${id}/guests/export-pdf`, { headers: { Authorization: `Bearer ${token}` } });
                      const blob = await r.blob();
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url; a.download = `guest-report-${(event?.title || 'event-' + id).replace(/[^a-zA-Z0-9]/g, '-')}.pdf`;
                      document.body.appendChild(a); a.click(); a.remove();
                      URL.revokeObjectURL(url);
                    } catch(e) { console.error('Download failed:', e); }
                  }}>
                    <FiDownload size={14} /> Download Report (PDF)
                  </button>
                  <div style={{ position: 'relative' }}>
                    <button className="btn btn-primary btn-sm" onClick={() => setShowAddGuestDropdown(!showAddGuestDropdown)} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <FiPlus /> Add Guest <FiChevronDown size={14} style={{ transform: showAddGuestDropdown ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
                    </button>
                    {showAddGuestDropdown && (
                      <>
                        <div style={{ position: 'fixed', inset: 0, zIndex: 98 }} onClick={() => setShowAddGuestDropdown(false)} />
                        <div style={{ position: 'absolute', top: '100%', right: 0, marginTop: '6px', background: '#fff', borderRadius: '12px', boxShadow: '0 8px 30px rgba(0,0,0,0.12)', border: '1px solid var(--gray-light)', zIndex: 99, minWidth: '210px', overflow: 'hidden' }}>
                          <button onClick={() => { setShowAddGuestDropdown(false); setFormData({ name: '', phone: '', email: '', rsvp_status: 'pending', dietary_requirements: '', children: [] }); setShowModal('guest'); }} style={{ display: 'flex', alignItems: 'center', gap: '10px', width: '100%', padding: '12px 16px', border: 'none', background: 'none', cursor: 'pointer', fontSize: '14px', fontWeight: 500, borderBottom: '1px solid var(--gray-light)' }}>
                            <FiUserCheck size={16} style={{ color: 'var(--rose-deep)' }} /> Add Manually
                          </button>
                          {'contacts' in navigator && (
                            <button onClick={async () => {
                              setShowAddGuestDropdown(false);
                              try {
                                const contacts = await navigator.contacts.select(['name', 'tel', 'email'], { multiple: true });
                                let added = 0;
                                for (const c of contacts) {
                                  const name = c.name?.[0] || '';
                                  const phone = c.tel?.[0] || '';
                                  const email = c.email?.[0] || '';
                                  if (name || phone) {
                                    await api.post(`/events/${id}/guests`, { name, phone, email, rsvp_status: 'pending' });
                                    added++;
                                  }
                                }
                                if (added > 0) { loadTabData(); showToast(`${added} guest${added > 1 ? 's' : ''} added from contacts!`); }
                              } catch (e) { if (e.name !== 'TypeError') showToast('Could not access contacts', 'error'); }
                            }} style={{ display: 'flex', alignItems: 'center', gap: '10px', width: '100%', padding: '12px 16px', border: 'none', background: 'none', cursor: 'pointer', fontSize: '14px', fontWeight: 500, borderBottom: '1px solid var(--gray-light)' }}>
                              <FiUsers size={16} style={{ color: 'var(--gold)' }} /> Import from Contacts
                            </button>
                          )}
                          <button onClick={() => { setShowAddGuestDropdown(false); csvInputRef.current?.click(); }} style={{ display: 'flex', alignItems: 'center', gap: '10px', width: '100%', padding: '12px 16px', border: 'none', background: 'none', cursor: 'pointer', fontSize: '14px', fontWeight: 500 }}>
                            <FiDownload size={16} style={{ color: 'var(--green)' }} /> Import CSV
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                  <div style={{ position: 'relative' }}>
                    <button className="btn btn-sm" style={{ background: 'linear-gradient(135deg, #6366f1, #8b5cf6)', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 14px', fontSize: '13px', fontWeight: 600 }} onClick={() => setShowBulkStatusDropdown(!showBulkStatusDropdown)}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 11 12 14 22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/></svg> Status All
                    </button>
                    {showBulkStatusDropdown && (
                      <>
                        <div style={{ position: 'fixed', inset: 0, zIndex: 98 }} onClick={() => setShowBulkStatusDropdown(false)} />
                        <div style={{ position: 'absolute', top: '100%', right: 0, marginTop: '6px', background: '#fff', borderRadius: '12px', boxShadow: '0 8px 30px rgba(0,0,0,0.12)', border: '1px solid var(--gray-light)', zIndex: 99, minWidth: '200px', overflow: 'hidden' }}>
                          <button onClick={async () => {
                            setShowBulkStatusDropdown(false);
                            for (const g of guests) {
                              await api.put(`/events/${id}/guests/${g.id}`, { rsvp_status: 'attending' });
                            }
                            loadTabData(); showToast(`All ${guests.length} guests marked as Attending`);
                          }} style={{ display: 'flex', alignItems: 'center', gap: '10px', width: '100%', padding: '12px 16px', border: 'none', background: 'none', cursor: 'pointer', fontSize: '13px', fontWeight: 500, borderBottom: '1px solid var(--gray-light)' }}>
                            <span className="tag tag-green" style={{ width: '10px', height: '10px', borderRadius: '50%', padding: 0, minWidth: '10px' }} /> Mark All Attending
                          </button>
                          <button onClick={async () => {
                            setShowBulkStatusDropdown(false);
                            for (const g of guests) {
                              await api.put(`/events/${id}/guests/${g.id}`, { rsvp_status: 'not_attending' });
                            }
                            loadTabData(); showToast(`All ${guests.length} guests marked as Not Attending`);
                          }} style={{ display: 'flex', alignItems: 'center', gap: '10px', width: '100%', padding: '12px 16px', border: 'none', background: 'none', cursor: 'pointer', fontSize: '13px', fontWeight: 500, borderBottom: '1px solid var(--gray-light)' }}>
                            <span className="tag tag-red" style={{ width: '10px', height: '10px', borderRadius: '50%', padding: 0, minWidth: '10px' }} /> Mark All Not Attending
                          </button>
                          <button onClick={async () => {
                            setShowBulkStatusDropdown(false);
                            for (const g of guests) {
                              await api.put(`/events/${id}/guests/${g.id}`, { rsvp_status: 'maybe' });
                            }
                            loadTabData(); showToast(`All ${guests.length} guests marked as Maybe`);
                          }} style={{ display: 'flex', alignItems: 'center', gap: '10px', width: '100%', padding: '12px 16px', border: 'none', background: 'none', cursor: 'pointer', fontSize: '13px', fontWeight: 500 }}>
                            <span className="tag tag-gold" style={{ width: '10px', height: '10px', borderRadius: '50%', padding: 0, minWidth: '10px' }} /> Mark All Maybe
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                  <input
                    type="file"
                    ref={csvInputRef}
                    accept=".csv"
                    style={{ display: 'none' }}
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      try {
                        const text = await file.text();
                        const lines = text.split(/\r?\n/).filter(l => l.trim());
                        if (lines.length < 2) { showToast('CSV file is empty or has no data rows', 'error'); return; }
                        const header = lines[0].split(',').map(h => h.trim().toLowerCase().replace(/^"|"$/g, ''));
                        const nameIdx = header.findIndex(h => h === 'name' || h === 'first name' || h === 'full name');
                        const phoneIdx = header.findIndex(h => h === 'phone' || h === 'tel' || h === 'telephone' || h === 'mobile' || h === 'phone number');
                        const emailIdx = header.findIndex(h => h === 'email' || h === 'e-mail' || h === 'mail');
                        if (nameIdx === -1 && phoneIdx === -1 && emailIdx === -1) {
                          showToast('CSV must have a name, phone, or email column', 'error'); return;
                        }
                        let added = 0;
                        const contacts = [];
                        for (let i = 1; i < lines.length; i++) {
                          const cols = lines[i].split(',').map(c => c.trim().replace(/^"|"$/g, ''));
                          const name = nameIdx >= 0 ? cols[nameIdx] : '';
                          const phone = phoneIdx >= 0 ? cols[phoneIdx] : '';
                          const email = emailIdx >= 0 ? cols[emailIdx] : '';
                          if (name || phone || email) {
                            await api.post(`/events/${id}/guests`, { name, phone, email, rsvp_status: 'pending' });
                            contacts.push({ name, phone, email });
                            added++;
                          }
                        }
                        if (added > 0) {
                          loadTabData();
                          setCsvContacts(contacts.filter(c => c.name && c.phone));
                          showToast(`${added} guest${added > 1 ? 's' : ''} imported from CSV!`);
                        }
                        else { showToast('No valid contacts found in CSV', 'error'); }
                      } catch (err) {
                        showToast('Failed to parse CSV file', 'error');
                      }
                      e.target.value = '';
                    }}
                  />
                </div>

                {/* vCard export banner */}
                {csvContacts && csvContacts.length > 0 && (
                  <div style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px',
                    padding: '14px 18px', marginBottom: '20px',
                    background: 'linear-gradient(135deg, rgba(37,211,102,0.08), rgba(37,211,102,0.03))',
                    border: '1px solid rgba(37,211,102,0.25)', borderRadius: '12px',
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <div style={{
                        width: '36px', height: '36px', borderRadius: '50%',
                        background: 'var(--green)', display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                        <FiUsers size={16} style={{ color: 'white' }} />
                      </div>
                      <div>
                        <div style={{ fontSize: '13px', fontWeight: 700, color: 'var(--green)' }}>
                          {csvContacts.length} contact{csvContacts.length > 1 ? 's' : ''} ready
                        </div>
                        <div style={{ fontSize: '11px', color: 'var(--gray)' }}>
                          Save to your phone's address book
                        </div>
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button
                        onClick={() => {
                          let vcf = '';
                          for (const c of csvContacts) {
                            vcf += 'BEGIN:VCARD\r\nVERSION:3.0\r\n';
                            vcf += `FN:${c.name}\r\n`;
                            if (c.phone) vcf += `TEL;TYPE=CELL:${c.phone}\r\n`;
                            if (c.email) vcf += `EMAIL:${c.email}\r\n`;
                            vcf += 'END:VCARD\r\n';
                          }
                          const blob = new Blob([vcf], { type: 'text/vcard;charset=utf-8' });
                          const url = URL.createObjectURL(blob);
                          const a = document.createElement('a');
                          a.href = url;
                          a.download = csvContacts.length === 1
                            ? `${csvContacts[0].name.replace(/\s+/g, '_')}.vcf`
                            : `ggr_contacts_${csvContacts.length}.vcf`;
                          document.body.appendChild(a);
                          a.click();
                          document.body.removeChild(a);
                          URL.revokeObjectURL(url);
                          showToast(`${csvContacts.length} contact${csvContacts.length > 1 ? 's' : ''} saved to phone!`);
                        }}
                        style={{
                          padding: '8px 18px', borderRadius: '20px', border: 'none',
                          background: 'var(--green)', color: 'white', fontWeight: 700, fontSize: '12px',
                          cursor: 'pointer', whiteSpace: 'nowrap',
                        }}
                      >
                        <FiDownload size={13} style={{ marginRight: '5px', verticalAlign: 'middle' }} />
                        Save to Phone
                      </button>
                      <button
                        onClick={() => setCsvContacts(null)}
                        style={{
                          padding: '8px', borderRadius: '50%', border: 'none',
                          background: 'transparent', color: 'var(--gray)', cursor: 'pointer',
                          fontSize: '16px', lineHeight: 1,
                        }}
                        title="Dismiss"
                      >
                        <FiX size={16} />
                      </button>
                    </div>
                  </div>
                )}

                {/* AUTO MESSAGING PANEL */}
                {autoMsgPanel && autoMsg && (
                  <div className="card" style={{ marginBottom: '20px', padding: '24px', background: 'linear-gradient(135deg, rgba(139,34,82,0.05), rgba(212,165,116,0.05))', border: '1px solid var(--rose-light)' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <FiBell size={18} style={{ color: 'var(--rose-deep)' }} />
                        <h4 style={{ margin: 0 }}>GiGi Auto Hype Messages</h4>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '14px' }}>
                          <input type="checkbox" checked={autoMsg.enabled} onChange={e => setAutoMsg({...autoMsg, enabled: e.target.checked})} style={{ accentColor: 'var(--rose-deep)' }} />
                          <span style={{ fontWeight: 600, color: autoMsg.enabled ? 'var(--rose-deep)' : 'var(--gray)' }}>{autoMsg.enabled ? 'Active' : 'Off'}</span>
                        </label>
                        <button onClick={() => setAutoMsgPanel(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--gray)' }}><FiX size={16} /></button>
                      </div>
                    </div>
                    {/* Step indicators */}
                    <div style={{ display: 'flex', gap: '8px', marginBottom: '20px' }}>
                      {['Schedule', 'Style', 'Review'].map((s, i) => (
                        <button key={s} onClick={() => setAutoMsgStep(i + 1)} style={{
                          flex: 1, padding: '10px', borderRadius: '10px', border: autoMsgStep === i + 1 ? '2px solid var(--rose-deep)' : '1px solid var(--gray-light)',
                          background: autoMsgStep === i + 1 ? 'rgba(139,34,82,0.08)' : 'var(--white)',
                          fontWeight: autoMsgStep === i + 1 ? 700 : 500, fontSize: '13px', cursor: 'pointer',
                          color: autoMsgStep === i + 1 ? 'var(--rose-deep)' : 'var(--gray)',
                        }}>{s}</button>
                      ))}
                    </div>
                    {autoMsgStep === 1 && (
                      <div style={{ display: 'grid', gap: '16px' }}>
                        <div>
                          <label style={{ fontSize: '13px', fontWeight: 600, display: 'block', marginBottom: '6px' }}>Who should receive messages?</label>
                          <select value={autoMsg.target_audience} onChange={e => setAutoMsg({...autoMsg, target_audience: e.target.value})} style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid var(--gray-light)', fontSize: '14px' }}>
                            <option value="attending">Attending guests only</option>
                            <option value="all">All guests</option>
                            <option value="pending">Pending guests only</option>
                          </select>
                        </div>
                        <div>
                          <label style={{ fontSize: '13px', fontWeight: 600, display: 'block', marginBottom: '6px' }}>Send a reminder every</label>
                          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                            {[2,3,5,7,8].map(d => {
                              const active = (autoMsg.interval_days || 3) === d;
                              return (
                                <button key={d} onClick={() => setAutoMsg({...autoMsg, interval_days: d})} style={{
                                  padding: '6px 14px', borderRadius: '20px', border: active ? '2px solid var(--rose-deep)' : '1px solid var(--gray-light)',
                                  background: active ? 'rgba(139,34,82,0.08)' : 'var(--white)',
                                  fontWeight: active ? 700 : 500, fontSize: '13px', cursor: 'pointer',
                                  color: active ? 'var(--rose-deep)' : 'var(--gray)',
                                }}>{d} days</button>
                              );
                            })}
                          </div>
                          <p style={{ fontSize: '12px', color: 'var(--gray)', margin: '8px 0 0', lineHeight: 1.5 }}>
                            Reminders are sent automatically every {autoMsg.interval_days || 3} days, plus a final reminder 1 day before the event. Each message shows the real days remaining.
                          </p>
                        </div>
                        <div>
                          <label style={{ fontSize: '13px', fontWeight: 600, display: 'block', marginBottom: '6px' }}>Channels</label>
                          <div style={{ display: 'flex', gap: '12px' }}>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '14px', cursor: 'pointer' }}>
                              <input type="checkbox" checked={autoMsg.use_whatsapp} onChange={e => setAutoMsg({...autoMsg, use_whatsapp: e.target.checked})} style={{ accentColor: 'var(--rose-deep)' }} />
                              WhatsApp
                            </label>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '14px', cursor: 'pointer' }}>
                              <input type="checkbox" checked={autoMsg.use_email} onChange={e => setAutoMsg({...autoMsg, use_email: e.target.checked})} style={{ accentColor: 'var(--rose-deep)' }} />
                              Email
                            </label>
                          </div>
                        </div>
                        <button className="btn btn-primary btn-sm" onClick={() => setAutoMsgStep(2)} style={{ alignSelf: 'flex-end' }}>Next: Style</button>
                      </div>
                    )}
                    {autoMsgStep === 2 && (
                      <div style={{ display: 'grid', gap: '16px' }}>
                        <div>
                          <label style={{ fontSize: '13px', fontWeight: 600, display: 'block', marginBottom: '6px' }}>Message Style</label>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                            {['hype','classy','chill','family'].map(style => (
                              <button key={style} onClick={() => setAutoMsg({...autoMsg, message_style: style})} style={{
                                padding: '10px', borderRadius: '10px', border: autoMsg.message_style === style ? '2px solid var(--rose-deep)' : '1px solid var(--gray-light)',
                                background: autoMsg.message_style === style ? 'rgba(139,34,82,0.08)' : 'var(--white)',
                                fontWeight: autoMsg.message_style === style ? 700 : 500, fontSize: '13px', cursor: 'pointer',
                                color: autoMsg.message_style === style ? 'var(--rose-deep)' : 'var(--gray)',
                                textTransform: 'capitalize',
                              }}>{style}</button>
                            ))}
                          </div>
                        </div>
                        <div>
                          <label style={{ fontSize: '13px', fontWeight: 600, display: 'block', marginBottom: '6px' }}>Custom message (optional)</label>
                          <textarea value={autoMsg.custom_message} onChange={e => setAutoMsg({...autoMsg, custom_message: e.target.value})} placeholder="Add a personal touch..." rows={3} style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid var(--gray-light)', fontSize: '14px', resize: 'vertical' }} />
                        </div>
                        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                          <button className="btn btn-outline btn-sm" onClick={() => setAutoMsgStep(1)}>Back</button>
                          <button className="btn btn-primary btn-sm" onClick={() => setAutoMsgStep(3)}>Review</button>
                        </div>
                      </div>
                    )}
                    {autoMsgStep === 3 && (
                      <div>
                        <div style={{ padding: '16px', background: 'var(--white)', borderRadius: '10px', border: '1px solid var(--gray-light)', marginBottom: '16px' }}>
                          <div style={{ fontSize: '12px', color: 'var(--gray)', marginBottom: '8px' }}>Preview</div>
                          <p style={{ fontSize: '14px', lineHeight: 1.6, margin: 0 }}>
                            🎉 Hey {autoMsg.target_audience === 'all' ? 'everyone' : 'there'}! {autoMsg.custom_message || `Getting excited for ${event.title}! Just ${autoMsg.interval_days || 3} days to go...`}
                          </p>
                        </div>
                        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                          <button className="btn btn-outline btn-sm" onClick={() => setAutoMsgStep(2)}>Back</button>
                          <button className="btn btn-primary btn-sm" onClick={async () => {
                            try {
                              await api.post(`/events/${id}/auto-messages`, autoMsg);
                              showToast('Auto messages saved! GiGi will handle the rest.');
                              setAutoMsgPanel(false);
                            } catch { showToast('Failed to save', 'error'); }
                          }}>Save & Activate</button>
                        </div>
                      </div>
                    )}
                    {/* Send Now / History */}
                    <div style={{ marginTop: '20px', paddingTop: '16px', borderTop: '1px solid var(--gray-light)' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px', gap: '8px', flexWrap: 'wrap' }}>
                        <div style={{ fontSize: '13px', fontWeight: 600 }}>Recent Activity</div>
                        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                          <button className="btn btn-sm" style={{ background: '#25D366', color: '#fff', border: 'none', fontSize: '12px', fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }} onClick={() => {
                            const audience = autoMsg.target_audience || 'attending';
                            const eligible = guests.filter(g => g.phone && (audience === 'attending' ? g.rsvp_status === 'attending' : audience === 'pending' ? g.rsvp_status === 'pending' : true));
                            if (eligible.length === 0) { showToast('No guests with phone numbers in this group', 'error'); return; }
                            if (!window.confirm(`Open WhatsApp for ${eligible.length} guests? You'll send each one individually.`)) return;
                            const dateStr = event.event_date ? new Date(event.event_date).toLocaleDateString('en-ZA', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }) : 'TBC';
                            const inviteLink = invitations[0]?.share_link ? `${window.location.origin}/invite/${invitations[0].share_link}` : null;
                            eligible.forEach((g, i) => {
                              const name = (g.name || 'there').split(' ')[0];
                              const base = autoMsg.custom_message || `We're counting down to ${event.title}! Save the date: ${dateStr}${event.location ? ` at ${event.location}` : ''}.`;
                              const msg = `🎉 Hi ${name}! ${base}${inviteLink ? `\n\n📩 View your invitation & RSVP:\n${inviteLink}` : ''}\n\n— Grand Gather Registry ✦`;
                              if (i === 0) { openWhatsApp(g.phone, msg); }
                              else { setTimeout(() => openWhatsApp(g.phone, msg), i * 1200); }
                            });
                            showToast(`Opening WhatsApp for ${eligible.length} guests...`);
                          }}><FiSend size={12} /> Send on WhatsApp</button>
                          <button className="btn btn-outline btn-sm" onClick={async () => {
                            try {
                              const r = await api.post(`/events/${id}/auto-messages/send-now`);
                              showToast(`Sent! ${r.data.sent} delivered, ${r.data.failed} failed`);
                              const h = await api.get(`/events/${id}/auto-messages`);
                              setAutoMsgHistory(h.data.history || h.data.settings?.history || []);
                            } catch { showToast('Failed to send', 'error'); }
                          }} style={{ fontSize: '12px', fontWeight: 700 }}>Auto-send</button>
                        </div>
                      </div>
                      {autoMsgHistory.length > 0 ? (
                        autoMsgHistory.slice(0, 5).map((h, i) => (
                          <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', padding: '4px 0', color: 'var(--gray)' }}>
                            <span>{h.sent_at ? new Date(h.sent_at).toLocaleDateString() : 'Pending'}</span>
                            <span>{h.channel === 'whatsapp' ? 'WhatsApp' : 'Email'} — {h.recipient_count || 0} guests</span>
                          </div>
                        ))
                      ) : (
                        <div style={{ fontSize: '12px', color: 'var(--gray)' }}>No sends yet. Tap <b>Send Now</b> to message all guests now.</div>
                      )}

                      {/* Send reminder to individual guests (like thank-you cards) */}
                      <details style={{ marginTop: '12px' }}>
                        <summary style={{ fontSize: '13px', fontWeight: 600, cursor: 'pointer', color: 'var(--text)', padding: '8px 0', userSelect: 'none' }}>
                          Send to Individual Guests ({guests.filter(g => g.phone && ((autoMsg.target_audience || 'attending') === 'attending' ? g.rsvp_status === 'attending' : (autoMsg.target_audience || 'attending') === 'pending' ? g.rsvp_status === 'pending' : true)).length})
                        </summary>
                        <div style={{ marginTop: '8px' }}>
                          <div style={{ maxHeight: '320px', overflowY: 'auto', borderRadius: '10px', border: '1px solid rgba(0,0,0,0.06)' }}>
                            {guests.filter(g => g.phone && ((autoMsg.target_audience || 'attending') === 'attending' ? g.rsvp_status === 'attending' : (autoMsg.target_audience || 'attending') === 'pending' ? g.rsvp_status === 'pending' : true)).map(guest => {
                              const name = (guest.name || 'there').split(' ')[0];
                              const dateStr = event.event_date ? new Date(event.event_date).toLocaleDateString('en-ZA', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }) : 'TBC';
                              const inviteLink = invitations[0]?.share_link ? `${window.location.origin}/invite/${invitations[0].share_link}` : null;
                              const base = autoMsg.custom_message || `We're counting down to ${event.title}! Save the date: ${dateStr}${event.location ? ` at ${event.location}` : ''}.`;
                              const msg = `🎉 Hi ${name}! ${base}${inviteLink ? `\n\n📩 View your invitation & RSVP:\n${inviteLink}` : ''}\n\n— Grand Gather Registry ✦`;
                              const waLink = buildWaLink(guest.phone, msg);
                              return (
                                <div key={guest.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 12px', borderBottom: '1px solid rgba(0,0,0,0.04)' }}>
                                  <div style={{ flex: 1, minWidth: 0, paddingRight: '8px' }}>
                                    <div style={{ fontSize: '13px', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{guest.name}</div>
                                    <div style={{ fontSize: '11px', color: 'var(--gray)' }}>{guest.phone} · {guest.rsvp_status}</div>
                                  </div>
                                  <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
                                    <a href={waLink} target="_blank" rel="noreferrer" className="btn btn-sm" style={{ background: '#25D366', color: '#fff', border: 'none', fontSize: '11px', padding: '5px 10px', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                                      <FiSend size={11} /> Send
                                    </a>
                                    <button className="btn btn-sm btn-outline" style={{ fontSize: '11px', padding: '5px 10px' }} onClick={() => { navigator.clipboard?.writeText(msg); showToast('Message copied!'); }}>
                                      Copy
                                    </button>
                                  </div>
                                </div>
                              );
                            })}
                            {guests.filter(g => g.phone && ((autoMsg.target_audience || 'attending') === 'attending' ? g.rsvp_status === 'attending' : (autoMsg.target_audience || 'attending') === 'pending' ? g.rsvp_status === 'pending' : true)).length === 0 && (
                              <div style={{ padding: '12px', fontSize: '12px', color: 'var(--gray)' }}>No guests with phone numbers in this group.</div>
                            )}
                          </div>
                        </div>
                      </details>
                    </div>
                  </div>
                )}

                {/* Filtered Guest Cards */}
                {(() => {
                  const filteredGuests = guestFilter
                    ? guestFilter === 'tickets'
                      ? guests.filter(g => tickets.some(t => t.guest_id === g.id))
                      : guestFilter === 'children'
                        ? guests.filter(g => g.children && g.children.length > 0)
                        : guests.filter(g => g.rsvp_status === guestFilter)
                    : guests;
                  if (filteredGuests.length === 0 && guestFilter) {
                    return (
                      <div style={{ textAlign: 'center', padding: '40px 20px' }}>
                        <div style={{
                          width: '64px', height: '64px', borderRadius: '50%',
                          background: 'var(--cream)', display: 'flex', alignItems: 'center', justifyContent: 'center',
                          margin: '0 auto 16px', fontSize: '28px', opacity: 0.6,
                        }}>
                          <FiUsers size={28} style={{ color: 'var(--gray)' }} />
                        </div>
                        <p style={{ fontSize: '14px', color: 'var(--gray)', marginBottom: '8px' }}>
                          No {guestFilter === 'tickets' ? 'ticket holders' : guestFilter === 'not_attending' ? 'declined' : guestFilter === 'maybe' ? 'maybe' : guestFilter === 'children' ? 'children' : 'attending'} guests found
                        </p>
                        <button onClick={() => setGuestFilter(null)} style={{ background: 'none', border: 'none', color: 'var(--rose)', cursor: 'pointer', fontSize: '13px', fontWeight: 600 }}>
                          Clear filter to see all {guests.length} guests
                        </button>
                      </div>
                    );
                  }
                  return (
                    <div style={{ display: 'grid', gap: '8px' }}>
                      {guestFilter && (
                        <div style={{ fontSize: '13px', color: 'var(--gray)', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                          Showing {filteredGuests.length} of {guests.length} guests
                          <button onClick={() => setGuestFilter(null)} style={{ background: 'none', border: 'none', color: 'var(--rose)', cursor: 'pointer', fontSize: '12px', fontWeight: 600 }}>Show all</button>
                        </div>
                      )}
                      {filteredGuests.map(g => {
                        const guestTicket = tickets.find(t => t.guest_id === g.id);
                        const statusIcons = { attending: { icon: <FiUserCheck size={11} />, bg: '#10B98115', color: '#10B981', label: 'Attending' }, maybe: { icon: <FiAlertCircle size={11} />, bg: '#F59E0B15', color: '#F59E0B', label: 'Maybe' }, not_attending: { icon: <FiUserX size={11} />, bg: '#EF444415', color: '#EF4444', label: 'Not Attending' }, pending: { icon: <FiClock size={11} />, bg: '#6B728015', color: '#6B7280', label: 'Pending' } };
                        const sc = statusIcons[g.rsvp_status] || statusIcons.pending;
                        return (
                          <div key={g.id} className="card" style={{ padding: '16px 20px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
                              <div style={{ width: '44px', height: '44px', borderRadius: '50%', background: 'linear-gradient(135deg, var(--gold-light), var(--gold))', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontWeight: 600, fontSize: '16px', flexShrink: 0 }}>{g.name[0]}</div>
                              <div style={{ flex: 1, minWidth: '150px' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                                  <span style={{ fontWeight: 600, fontSize: '15px' }}>{g.name}</span>
                                  <span style={{ fontSize: '11px', padding: '2px 10px', borderRadius: '10px', background: sc.bg, color: sc.color, fontWeight: 600, display: 'flex', alignItems: 'center', gap: '4px' }}>{sc.icon}{sc.label}</span>
                                </div>
                                <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', fontSize: '13px', color: 'var(--gray)' }}>
                                  {g.email && <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}><FiMail size={12} /> {g.email}</span>}
                                  {g.phone && <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}><FiPhone size={12} /> {g.phone}</span>}
                                </div>
                                <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginTop: '6px' }}>
                                  {g.guardian_role && (
                                    <span style={{ fontSize: '12px', padding: '3px 10px', borderRadius: '10px', background: '#8B5CF615', color: '#7C3AED', fontWeight: 500 }}>
                                      👤 {g.guardian_role}
                                    </span>
                                  )}
                                  {g.dietary_requirements && (
                                    <span style={{ fontSize: '12px', padding: '3px 10px', borderRadius: '10px', background: '#F59E0B15', color: '#D97706', fontWeight: 500, display: 'flex', alignItems: 'center', gap: '4px' }}>
                                      🍽️ {g.dietary_requirements}
                                    </span>
                                  )}
                                  {guestTicket && (
                                    <span style={{ fontSize: '12px', padding: '3px 10px', borderRadius: '10px', background: guestTicket.checked_in ? '#10B98120' : '#E8A0BF20', color: guestTicket.checked_in ? '#10B981' : '#C97191', fontWeight: 600 }}>
                                      {guestTicket.checked_in ? '✓ Checked In' : `🎫 ${guestTicket.ticket_code}`}
                                    </span>
                                  )}
                                </div>
                                {g.children && g.children.length > 0 && (
                                  <div style={{ marginTop: '8px', padding: '8px 12px', borderRadius: '10px', background: '#F0F9FF', border: '1px solid #E0F2FE' }}>
                                    <span style={{ fontSize: '11px', fontWeight: 600, color: '#0369A1', textTransform: 'uppercase', letterSpacing: '0.5px' }}>🧒 Children ({g.children.length})</span>
                                    <div style={{ marginTop: '4px', display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                                      {g.children.map(c => (
                                        <span key={c.id} style={{ fontSize: '12px', padding: '3px 10px', borderRadius: '10px', background: '#DBEAFE', color: '#1E40AF', fontWeight: 500 }}>
                                          {c.child_name}{c.child_age ? ` (${c.child_age}yr)` : ''}{c.dietary_requirements ? ` — ${c.dietary_requirements}` : ''}
                                        </span>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                              <div style={{ display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap' }}>
                                <select value={g.rsvp_status} onChange={async (e) => { await api.put(`/events/${id}/guests/${g.id}`, { rsvp_status: e.target.value }); loadTabData(); }} style={{ width: 'auto', padding: '6px 10px', fontSize: '12px', borderRadius: '8px', border: '1px solid var(--gray-light)', background: 'var(--bg-off)' }}>
                                  <option value="pending">Pending</option>
                                  <option value="attending">Attending</option>
                                  <option value="maybe">Maybe</option>
                                  <option value="not_attending">Not Attending</option>
                                </select>
                                {guestTicket ? (
                                  <a href={`/ticket/${guestTicket.ticket_code}`} target="_blank" rel="noreferrer" style={{ fontSize: '11px', padding: '5px 10px', borderRadius: '6px', background: 'var(--cream)', border: '1px solid var(--gray-light)', color: 'var(--text)', textDecoration: 'none', fontWeight: 500, display: 'flex', alignItems: 'center', gap: '4px', whiteSpace: 'nowrap' }}>
                                    <FiExternalLink size={12} /> View Ticket
                                  </a>
                                ) : (
                                  <button onClick={async () => { try { await api.post(`/events/${id}/tickets`, { guest_id: g.id }); showToast('Ticket generated!'); loadTabData(); } catch { showToast('Failed to generate ticket', 'error'); } }} style={{ fontSize: '11px', padding: '5px 10px', borderRadius: '6px', background: 'var(--cream)', border: '1px solid var(--gray-light)', cursor: 'pointer', fontWeight: 500, whiteSpace: 'nowrap' }}>
                                    🎫 Generate Ticket
                                  </button>
                                )}
                                <button onClick={() => {
                                  setFormData({
                                    editingGuestId: g.id,
                                    name: g.name || '',
                                    phone: g.phone || '',
                                    email: g.email || '',
                                    rsvp_status: g.rsvp_status || 'pending',
                                    dietary_requirements: g.dietary_requirements || '',
                                    children: (g.children || []).map(c => ({ name: c.child_name || c.name || '', age: c.child_age ?? c.age ?? '' })),
                                  });
                                  setShowModal('guest');
                                }} style={{ background: 'none', border: '1px solid var(--gray-light)', cursor: 'pointer', padding: '5px 8px', borderRadius: '6px', display: 'flex', alignItems: 'center', color: 'var(--gray)' }} title="Edit">
                                  <FiEdit size={14} />
                                </button>
                                <button onClick={async () => {
                                  if (window.confirm(`Remove ${g.name} from the guest list?`)) {
                                    await api.delete(`/events/${id}/guests/${g.id}`);
                                    loadTabData();
                                  }
                                }} style={{ background: 'none', border: '1px solid #EF444440', cursor: 'pointer', padding: '5px 8px', borderRadius: '6px', display: 'flex', alignItems: 'center', color: '#EF4444' }} title="Remove">
                                  <FiTrash2 size={14} />
                                </button>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}

            {/* Empty state when no guests */}
            {guests.length === 0 && (
              <GigiTipCard
                message="GiGi can help you manage and invite your guests."
                action="Help me add guests to my event"
              />
            )}
            </>
          )}

          {/* TIMELINE */}
          {tab === 'timeline' && (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h3>Day Programme</h3>
                <button className="btn btn-primary btn-sm" onClick={() => { setFormData({ title: '', description: '', item_time: '' }); setShowModal('timeline'); }}><FiPlus /> Add Lineup</button>
              </div>
              {timeline.length === 0 ? (
                <>
                  <GigiTipCard
                    message="Let GiGi suggest a day programme for your event."
                    action="suggest_timeline"
                  />
                  <div className="card" style={{ textAlign: 'center', padding: '30px', color: 'var(--gray)' }}>No programme items yet. Add what happens during the day — times, activities, and who participates.</div>
                </>
              ) : (
                <div style={{ position: 'relative', paddingLeft: '32px' }}>
                  <div style={{ position: 'absolute', left: '12px', top: 0, bottom: 0, width: '2px', background: 'var(--gold-light)' }}></div>
                  {timeline.map((item, i) => (
                    <div key={item.id} style={{ position: 'relative', marginBottom: '24px' }}>
                      <div style={{ position: 'absolute', left: '-26px', top: '4px', width: '16px', height: '16px', borderRadius: '50%', background: 'var(--gold)', border: '3px solid var(--white)', boxShadow: 'var(--shadow)' }}></div>
                      <div className="card">
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                          <div>
                            <h4 style={{ fontSize: '16px', marginBottom: '4px' }}>{item.title}</h4>
                            {item.description && <p style={{ color: 'var(--gray)', fontSize: '14px', marginBottom: '8px' }}>{item.description}</p>}
                            {item.item_time && <span className="tag tag-gold"><FiClock style={{ verticalAlign: 'middle' }} /> {item.item_time}{item.till_late ? ' – Till Late' : item.end_time ? ` – ${item.end_time}` : ''}</span>}
                          </div>
                          <div style={{ display: 'flex', gap: '4px' }}>
                            <button className="btn btn-sm" style={{ background: 'none', color: 'var(--gold)' }} onClick={() => { setFormData({ id: item.id, title: item.title, description: item.description || '', item_time: item.item_time || '', end_time: item.end_time || '', till_late: item.till_late || false }); setShowModal('editTimeline'); }}><FiEdit /></button>
                            <button className="btn btn-sm" style={{ background: 'none', color: 'var(--red)' }} onClick={async () => { await api.delete(`/events/${id}/timeline/${item.id}`); loadTabData(); }}><FiTrash2 /></button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* BUDGET */}
          {tab === 'budget' && (hostPlan === 'free' ? (
            <div className="card" style={{ textAlign: 'center', padding: '60px 24px' }}>
              <div style={{ width: '64px', height: '64px', margin: '0 auto 20px', borderRadius: '50%', background: 'rgba(255,107,107,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <CoinIcon size={28} style={{ color: '#FF6B6B', opacity: 0.6 }} />
              </div>
              <h3 style={{ fontSize: '18px', fontWeight: 700, marginBottom: '8px', color: 'var(--text)' }}>Budget Tracker Locked</h3>
              <p style={{ fontSize: '14px', color: 'var(--gray)', marginBottom: '24px', maxWidth: '400px', margin: '0 auto 24px' }}>
                Upgrade to Standard or Premium to unlock the budget tracker and manage your event spending.
              </p>
              <Link to="/upgrade?type=host" style={{
                display: 'inline-flex', alignItems: 'center', gap: '8px',
                padding: '12px 28px', borderRadius: '12px',
                background: 'linear-gradient(135deg, var(--rose-deep), #b13d4e)', color: 'white',
                fontSize: '14px', fontWeight: 700, fontFamily: 'Inter, sans-serif',
                textDecoration: 'none',
              }}><FiZap size={16} /> View Plans</Link>
            </div>
          ) : (
            <div>
              <div className="card" style={{ marginBottom: '20px', background: 'linear-gradient(135deg, rgba(212,165,116,0.08), rgba(139,34,82,0.05))' }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '20px', textAlign: 'center' }}>
                  <div><div style={{ fontSize: '12px', color: 'var(--gray)', marginBottom: '4px' }}>Total Budget</div><div style={{ fontSize: '24px', fontWeight: 700 }}>R{Number(event.budget||0).toLocaleString()}</div></div>
                  <div><div style={{ fontSize: '12px', color: 'var(--gray)', marginBottom: '4px' }}>Allocated</div><div style={{ fontSize: '24px', fontWeight: 700, color: 'var(--blue)' }}>R{totalBudgetAllocated.toLocaleString()}</div></div>
                  <div><div style={{ fontSize: '12px', color: 'var(--gray)', marginBottom: '4px' }}>Spent</div><div style={{ fontSize: '24px', fontWeight: 700, color: 'var(--red)' }}>R{totalBudgetSpent.toLocaleString()}</div></div>
                  <div><div style={{ fontSize: '12px', color: 'var(--gray)', marginBottom: '4px' }}>Remaining</div><div style={{ fontSize: '24px', fontWeight: 700, color: 'var(--green)' }}>R{(Number(event.budget||0)-totalBudgetSpent).toLocaleString()}</div></div>
                </div>
              </div>
              {budget.length === 0 && (
                <GigiTipCard
                  message="GiGi can set up budget categories for your event. One tap!"
                  action="suggest_budget_categories"
                />
              )}
              <div style={{ display: 'grid', gap: '12px' }}>
                {budget.map(b => (
                  <div key={b.id} className="card budget-row" style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
                    <div style={{ flex: '1 1 120px', fontWeight: 600 }}>{b.category}</div>
                    <div style={{ flex: '1 1 200px', display: 'flex', gap: '12px', alignItems: 'center' }}>
                      <div style={{ flex: 1 }}>
                        <label style={{ fontSize: '11px', color: 'var(--gray)' }}>Allocated (R)</label>
                        <input type="number" defaultValue={b.allocated || ''} onBlur={async (e) => { await api.put(`/events/${id}/budget/${b.id}`, { allocated: e.target.value || 0 }); loadTabData(); }} style={{ padding: '8px 12px' }} />
                      </div>
                      <div style={{ flex: 1 }}>
                        <label style={{ fontSize: '11px', color: 'var(--gray)' }}>Spent (R)</label>
                        <input type="number" defaultValue={b.spent || ''} onBlur={async (e) => { await api.put(`/events/${id}/budget/${b.id}`, { spent: e.target.value || 0 }); loadTabData(); }} style={{ padding: '8px 12px' }} />
                      </div>
                    </div>
                    <div style={{ width: '100px', textAlign: 'right' }}>
                      <div style={{ fontSize: '11px', color: 'var(--gray)' }}>Remaining</div>
                      <div style={{ fontWeight: 600, color: (Number(b.allocated||0) - Number(b.spent||0)) >= 0 ? 'var(--green)' : 'var(--red)' }}>
                        R{(Number(b.allocated||0) - Number(b.spent||0)).toLocaleString()}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}

          {/* CHECKLIST */}
          {tab === 'checklist' && (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <div>
                  <h3>Checklist</h3>
                  <p style={{ fontSize: '14px', color: 'var(--gray)', marginTop: '4px' }}>{checklistDone} of {checklist.length} tasks completed</p>
                </div>
                <button className="btn btn-primary btn-sm" onClick={() => { setFormData({ title: '' }); setShowModal('checklist'); }}><FiPlus /> Add Task</button>
              </div>
              <div className="progress-bar" style={{ height: '10px', marginBottom: '20px' }}>
                <div className="progress-fill" style={{ width: `${checklist.length ? (checklistDone/checklist.length)*100 : 0}%` }}></div>
              </div>
              {checklist.length === 0 && (
                <GigiTipCard
                  message="GiGi has a ready-made checklist for your event type."
                  action="generate_checklist"
                />
              )}
              <div style={{ display: 'grid', gap: '8px' }}>
                {checklist.map(item => (
                  <div key={item.id} className="card" style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '14px 20px' }}>
                    <button onClick={async () => { await api.put(`/events/${id}/checklist/${item.id}`, { completed: !item.completed }); loadTabData(); }}
                      style={{ width: '24px', height: '24px', minWidth: '24px', minHeight: '24px', maxWidth: '24px', maxHeight: '24px', borderRadius: '50%', border: item.completed ? 'none' : '2px solid var(--cream-dark)', background: item.completed ? 'var(--green)' : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', cursor: 'pointer', flexShrink: 0, aspectRatio: '1 / 1', padding: 0 }}>
                      {item.completed && <FiCheck size={14} />}
                    </button>
                    <span style={{ flex: 1, textDecoration: item.completed ? 'line-through' : 'none', color: item.completed ? 'var(--gray)' : 'var(--dark)' }}>{item.title}</span>
                    {item.ai_generated && <span className="tag tag-gold" style={{ fontSize: '10px' }}>AI</span>}
                    <button style={{ background: 'none', color: 'var(--red)', cursor: 'pointer', border: 'none' }} onClick={async () => { await api.delete(`/events/${id}/checklist/${item.id}`); loadTabData(); }}><FiTrash2 size={14} /></button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* GIFT REGISTRY */}
          {tab === 'registry' && (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '10px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <h3>Gift Registry</h3>
                  {registry.length > 0 && (
                    <div style={{ background: 'linear-gradient(135deg, #D13A60, #E3425C)', color: 'white', borderRadius: '20px', padding: '3px 10px', fontSize: '12px', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer' }} onClick={() => { setGiftBoxOpen(true); document.getElementById('curated-gift-box')?.scrollIntoView({ behavior: 'smooth', block: 'center' }); }}>
                      <FiGift size={12} /> {registry.length}
                    </div>
                  )}
                </div>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  <button className="btn btn-outline btn-sm" onClick={() => setGiftStores(!giftStores)}><FiShoppingBag /> {giftStores ? 'Hide Stores & Vendors' : 'Browse Stores & Vendors'}</button>
                  <button className="btn btn-primary btn-sm" onClick={() => { setFormData({ title: '', type: 'physical', target_amount: '', description: '' }); setShowModal('registry'); }}><FiPlus /> Add Custom Item</button>
                </div>
              </div>

              {/* Quick Paste Bar — always visible */}
              <div style={{ marginBottom: '16px', padding: '14px 16px', borderRadius: '12px', background: 'linear-gradient(135deg, rgba(201,72,112,0.06), rgba(212,165,116,0.04))', border: '1px solid rgba(201,72,112,0.12)' }}>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <FiGift size={16} style={{ color: 'var(--rose-deep)', flexShrink: 0 }} />
                  <input
                    type="url"
                    value={pasteUrl}
                    onChange={e => { setPasteUrl(e.target.value); setScrapeError(''); setScrapedProduct(null); }}
                    placeholder="Paste any product link here..."
                    style={{ flex: 1, padding: '10px 12px', borderRadius: '8px', border: '1px solid var(--cream-dark)', fontSize: '13px', background: 'var(--white)' }}
                    onKeyDown={e => {
                      if (e.key === 'Enter' && pasteUrl.trim()) {
                        e.preventDefault();
                        (async () => {
                          setScraping(true); setScrapeError(''); setScrapedProduct(null);
                          try {
                            const r = await api.post('/scrape-product', { url: pasteUrl.trim() });
                            setScrapedProduct(r.data.product);
                          } catch { setScrapeError('Could not fetch. Try again or add manually.'); }
                          setScraping(false);
                        })();
                      }
                    }}
                  />
                  <button
                    className="btn btn-primary"
                    disabled={!pasteUrl.trim() || scraping}
                    style={{ padding: '10px 14px', borderRadius: '8px', fontSize: '12px', fontWeight: 600, whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: '4px' }}
                    onClick={async () => {
                      setScraping(true); setScrapeError(''); setScrapedProduct(null);
                      try {
                        const r = await api.post('/scrape-product', { url: pasteUrl.trim() });
                        setScrapedProduct(r.data.product);
                      } catch { setScrapeError('Could not fetch. Try again or add manually.'); }
                      setScraping(false);
                    }}
                  >
                    {scraping ? <><span className="spinner" style={{ width: '12px', height: '12px' }} /> ...</> : <><FiSearch size={13} /> Fetch</>}
                  </button>
                </div>
                {scrapeError && <div style={{ marginTop: '6px', fontSize: '11px', color: '#e65100' }}><FiAlertCircle size={11} /> {scrapeError}</div>}

                {/* Product Preview Card — editable */}
                {scrapedProduct && (
                  <div style={{ marginTop: '12px', background: 'var(--white)', borderRadius: '10px', border: '2px solid var(--rose)', overflow: 'hidden', animation: 'fadeIn 0.3s ease' }}>
                    <div style={{ padding: '14px' }}>
                      {/* Store badge */}
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
                        <div style={{ fontSize: '11px', fontWeight: 700, color: 'var(--rose-deep)', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <FiShoppingBag size={12} /> {scrapedProduct.store}
                        </div>
                        <button style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--gray)', padding: '2px' }} onClick={() => { setScrapedProduct(null); setPasteUrl(''); }}><FiX size={16} /></button>
                      </div>

                      {/* Image section */}
                      <div style={{ display: 'flex', gap: '12px', marginBottom: '12px' }}>
                        <div style={{ width: '80px', height: '80px', borderRadius: '8px', overflow: 'hidden', flexShrink: 0, background: 'var(--gray-light)', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px dashed var(--cream-dark)' }}>
                          {scrapedProduct.image ? (
                            <img src={scrapedProduct.image} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={e => { e.target.style.display = 'none'; e.target.nextSibling && (e.target.nextSibling.style.display = 'flex'); }} />
                          ) : (
                            <div style={{ textAlign: 'center', padding: '4px' }}>
                              <FiImage size={20} style={{ color: 'var(--gray)', opacity: 0.4 }} />
                              <div style={{ fontSize: '8px', color: 'var(--gray)', marginTop: '2px' }}>No image</div>
                            </div>
                          )}
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          {/* Editable title */}
                          <label style={{ fontSize: '10px', fontWeight: 600, color: 'var(--gray)', textTransform: 'uppercase', letterSpacing: '0.3px' }}>Gift Name</label>
                          <input value={scrapedProduct.title} onChange={e => setScrapedProduct({...scrapedProduct, title: e.target.value})}
                            style={{ width: '100%', padding: '7px 10px', borderRadius: '6px', border: '1px solid var(--cream-dark)', fontSize: '13px', fontWeight: 600, marginTop: '2px' }}
                            placeholder="e.g., Red Velvet Cake" />
                        </div>
                      </div>

                      {/* Price and Image URL row */}
                      <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                        <div style={{ flex: '0 0 130px' }}>
                          <label style={{ fontSize: '10px', fontWeight: 600, color: 'var(--gray)', textTransform: 'uppercase', letterSpacing: '0.3px' }}>Price</label>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginTop: '2px' }}>
                            <span style={{ fontSize: '13px', fontWeight: 700, color: 'var(--dark)' }}>R</span>
                            <input type="number" value={scrapedProduct.price || ''} onChange={e => setScrapedProduct({...scrapedProduct, price: e.target.value})}
                              style={{ width: '100%', padding: '7px 10px', borderRadius: '6px', border: !scrapedProduct.price ? '1px solid #F59E0B' : '1px solid var(--cream-dark)', fontSize: '13px', fontWeight: 600, background: !scrapedProduct.price ? 'rgba(245,158,11,0.05)' : 'var(--white)' }}
                              placeholder="0.00" />
                          </div>
                        </div>
                        <div style={{ flex: 1, minWidth: '150px' }}>
                          <label style={{ fontSize: '10px', fontWeight: 600, color: 'var(--gray)', textTransform: 'uppercase', letterSpacing: '0.3px' }}>Image URL <span style={{ fontWeight: 400, textTransform: 'none' }}>(right-click image → copy)</span></label>
                          <input value={scrapedProduct.image || ''} onChange={e => setScrapedProduct({...scrapedProduct, image: e.target.value})}
                            style={{ width: '100%', padding: '7px 10px', borderRadius: '6px', border: '1px solid var(--cream-dark)', fontSize: '12px', marginTop: '2px' }}
                            placeholder="https://..." />
                        </div>
                      </div>

                      {/* Missing info hint */}
                      {(!scrapedProduct.price || !scrapedProduct.image) && (
                        <div style={{ marginTop: '10px', padding: '8px 12px', borderRadius: '6px', background: 'rgba(245,158,11,0.08)', fontSize: '12px', color: '#92400E', display: 'flex', alignItems: 'flex-start', gap: '6px' }}>
                          <FiAlertCircle size={14} style={{ marginTop: '1px', flexShrink: 0 }} />
                          <span>
                            {!scrapedProduct.price && !scrapedProduct.image
                              ? 'Price & image not found. Check the store page and fill them in — or add without and edit later.'
                              : !scrapedProduct.price
                                ? 'Price not found. Type it in from the store page — or add without and edit later.'
                                : 'Image not found. Right-click the product image on the store → "Copy image address" and paste it above.'}
                          </span>
                        </div>
                      )}
                    </div>
                    <div style={{ padding: '10px 14px', borderTop: '1px solid var(--cream-dark)', display: 'flex', gap: '8px', background: 'rgba(201,72,112,0.03)' }}>
                      <button className="btn btn-primary btn-full" style={{ borderRadius: '8px', padding: '11px', fontSize: '14px', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}
                        onClick={async () => {
                          try {
                            if (!scrapedProduct) {
                              showToast('No product data found', 'error');
                              return;
                            }
                            await api.post(`/events/${id}/registry`, {
                              title: scrapedProduct.title || 'Untitled Product',
                              type: 'store',
                              target_amount: Number(scrapedProduct.price) || 0,
                              description: scrapedProduct.description || '',
                              image: scrapedProduct.url || '',
                              product_image: scrapedProduct.image || '',
                              vendor_product_name: scrapedProduct.store || 'Unknown Store',
                              vendor_price: Number(scrapedProduct.price) || 0,
                            });
                            loadTabData();
                            setGiftBoxOpen(true);
                            setPasteUrl('');
                            setScrapedProduct(null);
                            showToast(`"${scrapedProduct.title}" added to your gift box! 🎁`);
                            setTimeout(() => { document.getElementById('curated-gift-box')?.scrollIntoView({ behavior: 'smooth', block: 'center' }); }, 300);
                          } catch (err) {
                            console.error('Add to gift box error:', err);
                            showToast('Failed to add gift', 'error');
                          }
                        }}
                      ><FiPlus size={14} /> Add to Gift Box</button>
                    </div>
                  </div>
                )}
              </div>

              {/* Payment & Delivery Settings — collapsible */}
              <div className="card" style={{ marginBottom: '20px', background: 'var(--white)', cursor: 'pointer' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                  onClick={() => setShowSettings(!showSettings)}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <FiSettings size={16} style={{ color: 'var(--gray)' }} />
                    <h4 style={{ fontSize: '15px', margin: 0 }}>Payment & Delivery Settings</h4>
                    {(event?.bank_details || event?.delivery_address || event?.show_host_contact) && (
                      <FiCheckCircle size={14} style={{ color: '#10B981' }} />
                    )}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    {(!event?.bank_details && !event?.delivery_address && !event?.show_host_contact) && (
                      <span style={{ fontSize: '11px', color: '#F59E0B', fontWeight: 600 }}>Needs setup</span>
                    )}
                    {showSettings ? <FiChevronUp size={16} style={{ color: 'var(--gray)' }} /> : <FiChevronDown size={16} style={{ color: 'var(--gray)' }} />}
                  </div>
                </div>

                {showSettings && (
                  <div style={{ marginTop: '16px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
                    {/* Banking Details */}
                    <div style={{ padding: '14px', borderRadius: '10px', background: 'linear-gradient(135deg, rgba(139,34,82,0.03), rgba(212,165,116,0.03))' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: event?.bank_details ? '10px' : '4px' }}>
                        <h5 style={{ fontSize: '13px', display: 'flex', alignItems: 'center', gap: '6px', margin: 0 }}><CoinIcon size={13} style={{ color: 'var(--rose-deep)' }} /> Banking / EFT Details</h5>
                        <button className="btn btn-outline btn-sm" onClick={(e) => { e.stopPropagation(); setFormData({ bank_details: event?.bank_details || '' }); setShowModal('eventBank'); }}><FiEdit size={11} /> {event?.bank_details ? 'Edit' : 'Set Up'}</button>
                      </div>
                      {event?.bank_details ? (
                        <div style={{ padding: '12px', background: 'var(--white)', borderRadius: '8px', fontSize: '13px', whiteSpace: 'pre-wrap', lineHeight: 1.7 }}>{event.bank_details}</div>
                      ) : (
                        <p style={{ fontSize: '12px', color: 'var(--gray)', margin: 0 }}>Add your banking details so guests can make EFT payments and upload proof of payment when contributing to gifts.</p>
                      )}
                    </div>

                    {/* Delivery & Contact */}
                    <div style={{ padding: '14px', borderRadius: '10px', background: 'linear-gradient(135deg, rgba(59,130,246,0.03), rgba(16,185,129,0.03))' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                        <h5 style={{ fontSize: '13px', display: 'flex', alignItems: 'center', gap: '6px', margin: 0 }}><FiMapPin size={13} style={{ color: '#3B82F6' }} /> Delivery & Contact Settings</h5>
                        <button className="btn btn-outline btn-sm" onClick={(e) => { e.stopPropagation(); setShowModal('deliverySettings'); setFormData({ delivery_address: event?.delivery_address || '', delivery_date: event?.delivery_date ? event.delivery_date.substring(0, 10) : '', delivery_notes: event?.delivery_notes || '', show_host_contact: event?.show_host_contact || false, host_contact_phone: event?.host_contact_phone || '', host_contact_email: event?.host_contact_email || '' }); }}><FiEdit size={11} /> {event?.delivery_address || event?.show_host_contact ? 'Edit' : 'Set Up'}</button>
                      </div>
                      {event?.delivery_address && (
                        <div style={{ padding: '10px 12px', background: 'var(--white)', borderRadius: '8px', fontSize: '13px', lineHeight: 1.6, marginBottom: '8px' }}>
                          <div style={{ fontWeight: 600, fontSize: '11px', color: '#3B82F6', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '3px' }}>Delivery Address</div>
                          <div style={{ whiteSpace: 'pre-wrap' }}>{event.delivery_address}</div>
                          {event.delivery_date && <div style={{ marginTop: '4px', color: 'var(--gray)', fontSize: '12px' }}>Deliver by: {new Date(event.delivery_date).toLocaleDateString('en-ZA', { day: 'numeric', month: 'long', year: 'numeric' })}</div>}
                          {event.delivery_notes && <div style={{ marginTop: '3px', color: 'var(--gray)', fontStyle: 'italic', fontSize: '12px' }}>{event.delivery_notes}</div>}
                        </div>
                      )}
                      {event?.show_host_contact && (
                        <div style={{ padding: '10px 12px', background: 'var(--white)', borderRadius: '8px', fontSize: '12px', display: 'flex', gap: '14px', flexWrap: 'wrap' }}>
                          <div style={{ fontWeight: 600, fontSize: '11px', color: '#10B981', textTransform: 'uppercase', letterSpacing: '0.5px', width: '100%' }}>Contact Visible on Invitation</div>
                          {event.host_contact_phone && <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}><FiPhone size={11} color="#10B981" /> {event.host_contact_phone}</span>}
                          {event.host_contact_email && <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}><FiMail size={11} color="#10B981" /> {event.host_contact_email}</span>}
                        </div>
                      )}
                      {!event?.delivery_address && !event?.show_host_contact && (
                        <p style={{ fontSize: '12px', color: 'var(--gray)', margin: 0 }}>Set your delivery address for online gift purchases and choose whether guests can see your contact details on the invitation.</p>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Contributions Overview Dashboard */}
              {registry.length > 0 && registry.some(r => r.contributions?.length > 0) && (
                <div className="card" style={{ marginBottom: '20px', background: 'linear-gradient(135deg, rgba(139,34,82,0.04), rgba(212,165,116,0.04))' }}>
                  <h4 style={{ fontSize: '15px', marginBottom: '14px', display: 'flex', alignItems: 'center', gap: '8px' }}><FiHeart size={16} style={{ color: 'var(--rose-deep)' }} /> Contributions & Claims</h4>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '12px', marginBottom: '16px' }}>
                    <div style={{ textAlign: 'center', padding: '14px', background: 'var(--bg-off)', borderRadius: '12px' }}>
                      <div style={{ fontSize: '22px', fontWeight: 700, color: 'var(--rose-deep)' }}>{registry.reduce((a, r) => a + (r.contributions?.length || 0), 0)}</div>
                      <div style={{ fontSize: '12px', color: 'var(--gray)' }}>Total Claims</div>
                    </div>
                    <div style={{ textAlign: 'center', padding: '14px', background: 'var(--bg-off)', borderRadius: '12px' }}>
                      <div style={{ fontSize: '22px', fontWeight: 700, color: 'var(--green)' }}>R{registry.reduce((a, r) => a + (r.contributions || []).reduce((b, c) => b + Number(c.amount || 0), 0), 0).toLocaleString()}</div>
                      <div style={{ fontSize: '12px', color: 'var(--gray)' }}>Total Raised</div>
                    </div>
                    <div style={{ textAlign: 'center', padding: '14px', background: 'var(--bg-off)', borderRadius: '12px' }}>
                      <div style={{ fontSize: '22px', fontWeight: 700, color: 'var(--gold)' }}>{registry.filter(r => r.is_fulfilled).length}/{registry.length}</div>
                      <div style={{ fontSize: '12px', color: 'var(--gray)' }}>Fulfilled</div>
                    </div>
                    <div style={{ textAlign: 'center', padding: '14px', background: 'var(--bg-off)', borderRadius: '12px' }}>
                      <div style={{ fontSize: '22px', fontWeight: 700, color: '#F59E0B' }}>{registry.reduce((a, r) => a + (r.contributions || []).filter(c => c.payment_status === 'pending').length, 0)}</div>
                      <div style={{ fontSize: '12px', color: 'var(--gray)' }}>Awaiting Payment</div>
                    </div>
                  </div>

                  {/* Recent activity feed */}
                  {(() => {
                    const allContribs = registry.flatMap(r => (r.contributions || []).map(c => ({ ...c, gift_title: r.title, gift_type: r.type }))).sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
                    if (allContribs.length === 0) return null;
                    return (
                      <div>
                        <div style={{ fontSize: '12px', fontWeight: 600, color: 'var(--gray)', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Recent Activity</div>
                        <div style={{ maxHeight: '200px', overflowY: 'auto', borderRadius: '10px', border: '1px solid var(--gray-light)' }}>
                          {allContribs.slice(0, 15).map(c => (
                            <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 14px', borderBottom: '1px solid var(--gray-light)', fontSize: '13px' }}>
                              <div style={{ width: '28px', height: '28px', borderRadius: '50%', background: 'linear-gradient(135deg, var(--gold-light), var(--gold))', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: '11px', fontWeight: 700, flexShrink: 0 }}>{(c.contributor_name || '?')[0]}</div>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <span style={{ fontWeight: 600 }}>{c.contributor_name}</span>
                                {c.amount > 0 ? <span style={{ color: 'var(--green)' }}> contributed R{Number(c.amount).toLocaleString()}</span> : <span> claimed</span>}
                                <span style={{ color: 'var(--gray)' }}> — {c.gift_title}</span>
                              </div>
                              <div style={{ display: 'flex', gap: '4px', alignItems: 'center', flexShrink: 0 }}>
                                {c.payment_method === 'paystack' && (
                                  <span style={{ fontSize: '9px', padding: '2px 5px', borderRadius: '4px', background: 'rgba(11,164,219,0.1)', color: '#0BA4DB', fontWeight: 700, letterSpacing: '0.3px' }}>PAYSTACK</span>
                                )}
                                {c.paystack_reference && (
                                  <span style={{ fontSize: '9px', padding: '2px 5px', borderRadius: '4px', background: 'rgba(0,0,0,0.04)', color: 'var(--gray)', fontFamily: 'monospace' }}>{c.paystack_reference.slice(0, 12)}</span>
                                )}
                                {c.proof_of_payment && <a href={c.proof_of_payment} target="_blank" rel="noreferrer" style={{ fontSize: '11px', padding: '2px 6px', borderRadius: '6px', background: 'rgba(16,185,129,0.1)', color: 'var(--green)', textDecoration: 'none', fontWeight: 600 }}>📄</a>}
                                <span style={{ fontSize: '10px', padding: '2px 6px', borderRadius: '6px', fontWeight: 600,
                                  background: c.payment_status === 'confirmed' ? 'rgba(16,185,129,0.1)' : c.payment_status === 'submitted' ? 'rgba(59,130,246,0.1)' : 'rgba(245,158,11,0.1)',
                                  color: c.payment_status === 'confirmed' ? '#10B981' : c.payment_status === 'submitted' ? '#3B82F6' : '#F59E0B'
                                }}>{c.payment_status === 'confirmed' ? '✓ Paid' : c.payment_status === 'submitted' ? 'Proof Sent' : '⏳ Pending'}</span>
                              </div>
                              <span style={{ fontSize: '11px', color: 'var(--gray)', flexShrink: 0 }}>{new Date(c.created_at).toLocaleDateString('en-ZA', { day: 'numeric', month: 'short' })}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })()}
                </div>
              )}

              {/* Online Stores & Gift Vendors — combined toggle */}
              {giftStores && (
                <div style={{ marginBottom: '28px' }}>
                  {/* Mini-tabs */}
                  <div style={{ display: 'flex', gap: '6px', marginBottom: '18px' }}>
                    <button
                      onClick={() => setStoreTab('online')}
                      style={{
                        flex: 1, padding: '11px 12px', borderRadius: '10px', border: '1px solid rgba(227,66,92,0.15)',
                        cursor: 'pointer', fontSize: '13px', fontWeight: storeTab === 'online' ? 700 : 500,
                        background: storeTab === 'online' ? 'rgba(227,66,92,0.12)' : 'var(--white)',
                        color: storeTab === 'online' ? 'var(--rose-deep)' : 'var(--dark)',
                        transition: 'all 0.2s', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px'
                      }}
                    ><FiExternalLink size={14} /> Online Stores</button>
                    <button
                      onClick={() => setStoreTab('vendors')}
                      style={{
                        flex: 1, padding: '11px 12px', borderRadius: '10px', border: '1px solid rgba(227,66,92,0.15)',
                        cursor: 'pointer', fontSize: '13px', fontWeight: storeTab === 'vendors' ? 700 : 500,
                        background: storeTab === 'vendors' ? 'rgba(227,66,92,0.12)' : 'var(--white)',
                        color: storeTab === 'vendors' ? 'var(--rose-deep)' : 'var(--dark)',
                        transition: 'all 0.2s', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px'
                      }}
                    ><FiShoppingBag size={14} /> Platform Vendors {giftVendors.length > 0 && <span style={{ fontSize: '10px', background: 'var(--rose)', color: 'white', borderRadius: '8px', padding: '1px 6px', fontWeight: 700 }}>{giftVendors.length}</span>}</button>
                  </div>

                  {/* Online Stores panel */}
                  {storeTab === 'online' && (
                    <div>
                      <p style={{ fontSize: '13px', color: 'var(--gray)', marginBottom: '14px' }}>These are suggested retailers for your convenience. GGR is not affiliated with, endorsed by, or in partnership with any of these stores. Find a gift you love, copy the product link, and paste it above.</p>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '12px' }}>
                        {[
                          { name: 'Takealot', url: 'https://www.takealot.com', color: '#0b79bf', desc: 'SA\'s #1 online store' },
                          { name: 'Woolworths', url: 'https://www.woolworths.co.za', color: '#1a1a1a', desc: 'Homeware & lifestyle' },
                          { name: 'Yuppiechef', url: 'https://www.yuppiechef.com', color: '#e74c3c', desc: 'Kitchen & home gifts' },
                          { name: 'Hyperli', url: 'https://www.hyperli.com', color: '#FF6B00', desc: 'Deals up to 60% off' },
                          { name: 'Daddy\'s Deals', url: 'https://www.daddysdeals.co.za', color: '#2E86AB', desc: 'Travel & experience deals' },
                          { name: 'Travelstart', url: 'https://www.travelstart.co.za', color: '#00B2A9', desc: 'Flights & honeymoon' },
                          { name: 'Sorbet', url: 'https://www.sorbet.co.za', color: '#D4508B', desc: 'Spa & beauty vouchers' },
                          { name: 'Carrol Boyes', url: 'https://www.carrolboyes.com', color: '#2C2C2C', desc: 'Luxury homeware & decor' },
                          { name: 'NetFlorist', url: 'https://www.netflorist.co.za', color: '#e91e63', desc: 'Flowers & hampers' },
                          { name: 'Rain Africa', url: 'https://www.rainafrica.com', color: '#6B8E23', desc: 'Luxury body care' },
                          { name: 'Game', url: 'https://www.game.co.za', color: '#00843D', desc: 'Electronics & home' },
                          { name: 'Toys R Us', url: 'https://www.toysrus.co.za', color: '#004B93', desc: 'Toys & kids gifts' },
                          { name: 'Babies R Us', url: 'https://www.babiesrus.co.za', color: '#E91E9D', desc: 'Baby essentials' },
                          { name: '@home', url: 'https://www.home.co.za', color: '#8B4513', desc: 'Home & living' },
                          { name: 'Mr Price Home', url: 'https://www.mrphome.com', color: '#c62828', desc: 'Affordable homeware' },
                          { name: 'Superbalist', url: 'https://superbalist.com', color: '#000000', desc: 'Fashion & decor' },
                        ].map(store => (
                          <a key={store.name} href={store.url} target="_blank" rel="nofollow noreferrer" className="card" style={{
                            padding: '16px', textDecoration: 'none', color: 'inherit', textAlign: 'center',
                            transition: 'transform 0.2s, box-shadow 0.2s', cursor: 'pointer', border: '1px solid var(--border)'
                          }} onClick={() => { api.post('/brand-clicks', { brand: store.name, product: null, url: store.url, event_id: id }).catch(() => {}); }}
                             onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.08)'; }}
                             onMouseLeave={e => { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = ''; }}>
                            <div style={{ width: '40px', height: '40px', borderRadius: '10px', background: 'var(--border)', color: 'var(--gray)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 10px', fontWeight: 700, fontSize: '16px' }}>
                              <FiExternalLink size={18} />
                            </div>
                            <div style={{ fontWeight: 600, fontSize: '14px', marginBottom: '4px' }}>{store.name}</div>
                            <div style={{ fontSize: '11px', color: 'var(--gray)' }}>{store.desc}</div>
                          </a>
                        ))}
                      </div>
                      <div style={{ marginTop: '12px', padding: '10px 14px', borderRadius: '10px', background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.2)', fontSize: '12px', color: '#92400e', lineHeight: 1.5, fontFamily: 'Inter, sans-serif' }}>
                        <strong>Disclaimer:</strong> These retailers are listed for convenience only. GGR has no partnership, affiliation, or endorsement agreement with any of these stores. Product availability, pricing, and delivery are the sole responsibility of each retailer. GGR earns no commission from these links and is not liable for purchases made on external websites.
                      </div>
                      <div style={{ marginTop: '10px', padding: '12px 16px', borderRadius: '10px', background: 'rgba(201,72,112,0.06)', display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <FiArrowLeft size={16} style={{ color: 'var(--rose-deep)', transform: 'rotate(90deg) scaleX(-1)', flexShrink: 0 }} />
                        <span style={{ fontSize: '13px', color: 'var(--dark)' }}>
                          <strong>Tip:</strong> Find a gift you love, copy the product URL, then paste it in the bar above — we'll fetch the details for you!
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Platform Vendors panel */}
                  {storeTab === 'vendors' && (
                    <div>
                      {giftVendors.length > 0 ? (
                        <>
                          <p style={{ fontSize: '13px', color: 'var(--gray)', marginBottom: '14px' }}>Add curated gift packages from our registered vendors directly to your registry.</p>
                          {giftVendors.map(vendor => (
                            <div key={vendor.id} className="card" style={{ marginBottom: '16px', padding: '20px' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '14px', marginBottom: '16px' }}>
                                <div style={{ width: '48px', height: '48px', borderRadius: '12px', background: 'linear-gradient(135deg, var(--rose-light), var(--rose-deep))', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', flexShrink: 0 }}>
                                  {(vendor.logo_url || vendor.avatar) ? (
                                    <img src={vendor.logo_url || vendor.avatar} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={e => { e.target.style.display = 'none'; }} />
                                  ) : (
                                    <FiShoppingBag size={20} style={{ color: 'white' }} />
                                  )}
                                </div>
                                <div style={{ flex: 1 }}>
                                  <h4 style={{ fontSize: '16px', marginBottom: '2px' }}>{vendor.business_name}</h4>
                                  <p style={{ fontSize: '12px', color: 'var(--gray)' }}>{vendor.business_description?.substring(0, 80)}</p>
                                </div>
                                {Number(vendor.avg_rating) > 0 && (
                                  <span style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '13px', color: 'var(--gray)' }}>
                                    <FiStar size={13} style={{ color: '#F59E0B', fill: '#F59E0B' }} /> {Number(vendor.avg_rating).toFixed(1)}
                                  </span>
                                )}
                                <Link to={`/vendors/${vendor.id}`} style={{ fontSize: '12px', color: 'var(--rose-deep)' }}>View Profile</Link>
                              </div>
                              {vendor.rate_cards?.map(card => (
                                <div key={card.id} style={{ marginBottom: '12px' }}>
                                  {card.items?.length > 0 && (
                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '10px' }}>
                                      {card.items.map(item => (
                                        <div key={item.id} style={{
                                          borderRadius: '10px', border: '1px solid var(--cream-dark)',
                                          background: 'var(--gray-light)', transition: 'border-color 0.2s',
                                          display: 'flex', flexDirection: 'column', overflow: 'hidden'
                                        }}>
                                          {item.image_url && (
                                            <div style={{ height: '130px', overflow: 'hidden' }}>
                                              <img src={item.image_url} alt={item.service_name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={e => { e.target.style.display = 'none'; }} />
                                            </div>
                                          )}
                                          <div style={{ padding: item.image_url ? '12px 14px' : '14px', display: 'flex', flexDirection: 'column', gap: '6px', flex: 1 }}>
                                            <div style={{ fontWeight: 600, fontSize: '14px' }}>{item.service_name}</div>
                                            {item.description && <div style={{ fontSize: '12px', color: 'var(--gray)', lineHeight: 1.4 }}>{item.description}</div>}
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 'auto', paddingTop: '8px' }}>
                                              <span style={{ fontWeight: 700, color: 'var(--rose-deep)', fontSize: '15px' }}>R{Number(item.price).toLocaleString()}</span>
                                              <button className="btn btn-primary" style={{ padding: '6px 14px', fontSize: '12px', borderRadius: '8px' }} onClick={async () => {
                                                await api.post(`/events/${id}/registry`, {
                                                  title: item.service_name,
                                                  type: 'physical',
                                                  target_amount: Number(item.price),
                                                  description: `${item.description || ''}\n\nFrom: ${vendor.business_name}`,
                                                  vendor_id: vendor.id,
                                                  vendor_product_name: item.service_name,
                                                  vendor_price: item.price
                                                });
                                                loadTabData();
                                                setGiftBoxOpen(true);
                                                if (typeof showToast === 'function') showToast(`"${item.service_name}" added! Scroll down to view your curated gifts 🎁`);
                                              }}><FiPlus size={12} /> Add</button>
                                            </div>
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          ))}
                        </>
                      ) : (
                        <div style={{ textAlign: 'center', padding: '32px 16px', color: 'var(--gray)' }}>
                          <FiShoppingBag size={32} style={{ opacity: 0.3, marginBottom: '10px' }} />
                          <p style={{ fontSize: '14px', margin: 0 }}>No gift vendors available yet.</p>
                          <p style={{ fontSize: '12px', marginTop: '4px' }}>Check back soon as we onboard new vendors.</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Featured Retail Partners — Ad Space */}
              {(() => {
                const ADS = [
                  { brand: 'Carrol Boyes', tagline: 'Functional Art for Every Occasion', desc: 'Luxury salad servers, wine glasses & homeware — proudly South African craftsmanship.', cta: 'Shop Collection', url: 'https://www.carrolboyes.com', color: '#2C2C2C', accent: '#C9A84C', img: '/uploads/gift-defaults/carrol.jpg' },
                  { brand: 'Hyperli', tagline: 'Experiences They\'ll Never Forget', desc: 'Spa days, wine tours, hot air balloons & adventures — up to 60% off.', cta: 'View Deals', url: 'https://www.hyperli.com', color: '#FF6B00', accent: '#FFE0CC', img: '/uploads/gift-defaults/hyperli.jpg' },
                  { brand: 'Yuppiechef', tagline: 'The Gift of Great Cooking', desc: 'Premium kitchen hampers, cookware sets & foodie essentials.', cta: 'Browse Gifts', url: 'https://www.yuppiechef.com/gift-ideas.htm', color: '#e74c3c', accent: '#FDE8E8', img: '/uploads/gift-defaults/yuppiechef.jpg' },
                  { brand: 'NetFlorist', tagline: 'Say It With Flowers', desc: 'Luxury bouquets, hampers & thoughtful gifts delivered across SA.', cta: 'Add this Gift', url: 'https://www.netflorist.co.za', color: '#e91e63', accent: '#FCE4EC', img: '/uploads/gift-defaults/netflorist.jpg' },
                  { brand: 'Sorbet', tagline: 'The Gift of Pampering', desc: 'Spa vouchers, beauty treatments & pamper packages for someone special.', cta: 'Get Voucher', url: 'https://www.sorbet.co.za/vouchers', color: '#D4508B', accent: '#FDE7F0', img: '/uploads/gift-defaults/sorbet.jpg' },
                  { brand: 'Travelstart', tagline: 'Gift a Getaway', desc: 'Honeymoon flights, weekend escapes & travel packages at the best prices.', cta: 'Find Flights', url: 'https://www.travelstart.co.za', color: '#00B2A9', accent: '#E0F7F6', img: '/uploads/gift-defaults/travelstart.jpg' },
                ];
                // Rotate ads based on time — changes every 30 seconds
                const adIndex = Math.floor((Date.now() / 30000)) % ADS.length;
                const ad = ADS[adIndex];
                return (
                  <a href={ad.url} target="_blank" rel="noreferrer"
                    onClick={() => { api.post('/brand-clicks', { brand: ad.brand, product: 'featured_ad', url: ad.url, event_id: id }).catch(() => {}); }}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '16px',
                      padding: '16px 20px', marginBottom: '20px',
                      borderRadius: '12px', overflow: 'hidden',
                      background: `linear-gradient(135deg, ${ad.accent}, var(--white))`,
                      border: `1px solid rgba(0,0,0,0.06)`,
                      textDecoration: 'none', color: 'inherit',
                      transition: 'all 0.2s', cursor: 'pointer',
                    }}
                    onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.08)'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
                    onMouseLeave={e => { e.currentTarget.style.boxShadow = ''; e.currentTarget.style.transform = ''; }}
                  >
                    <img src={ad.img} alt={ad.brand}
                      style={{
                        width: '64px', height: '64px', borderRadius: '12px', flexShrink: 0,
                        objectFit: 'cover', boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                      }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '3px' }}>
                        <span style={{ fontWeight: 700, fontSize: '14px', color: ad.color }}>{ad.brand}</span>
                        <span style={{ fontSize: '8px', fontWeight: 700, padding: '1px 6px', borderRadius: '3px', background: 'rgba(0,0,0,0.06)', color: 'var(--gray)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Featured</span>
                      </div>
                      <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--dark)', marginBottom: '2px' }}>{ad.tagline}</div>
                      <div style={{ fontSize: '12px', color: 'var(--gray)', lineHeight: 1.4 }}>{ad.desc}</div>
                    </div>
                    <div style={{
                      padding: '8px 16px', borderRadius: '8px', flexShrink: 0,
                      background: ad.color, color: 'white',
                      fontSize: '12px', fontWeight: 700, whiteSpace: 'nowrap',
                      display: 'flex', alignItems: 'center', gap: '4px',
                      boxShadow: `0 2px 8px ${ad.color}44`,
                    }}>
                      {ad.cta} <FiExternalLink size={11} />
                    </div>
                  </a>
                );
              })()}

              {/* GiGi's Gift Picks — smart suggestions by event type */}
              {(() => {
                const type = (event?.event_type || '').toLowerCase();
                const PICKS = {
                  'baby shower': [
                    { name: 'Baby Travel System', brand: 'Takealot', price: 'R2,499+', url: 'https://www.takealot.com/all?_sb=1&_r=1&ref=search&search=baby+travel+system', why: 'Every new parent needs one' },
                    { name: 'Baby Essentials Hamper', brand: 'Babies R Us', price: 'R499+', url: 'https://www.babiesrus.co.za', why: 'Nappies, wipes & must-haves' },
                    { name: 'Baby Clothing Bundle', brand: 'Cotton On Kids', price: 'R199+', url: 'https://cottonon.com/ZA/kids/', why: 'Adorable newborn outfits' },
                    { name: 'Nursery Décor Set', brand: 'Mr Price Home', price: 'R299+', url: 'https://www.mrphome.com', why: 'Beautiful nursery touches' },
                    { name: 'Pamper Hamper for Mom', brand: 'NetFlorist', price: 'R449+', url: 'https://www.netflorist.co.za', why: 'Mom deserves pampering too' },
                  ],
                  'kids party': [
                    { name: 'Kids Gift Bundle', brand: 'Toys R Us', price: 'R199+', url: 'https://www.toysrus.co.za', why: 'Age-appropriate toys & games' },
                    { name: 'Kids Clothing', brand: 'Cotton On Kids', price: 'R149+', url: 'https://cottonon.com/ZA/kids/', why: 'Cool clothes they\'ll love' },
                    { name: 'Fun Experience Day', brand: 'Hyperli', price: 'R199+', url: 'https://www.hyperli.com/search?q=kids+activities', why: 'Trampoline parks, shows & more' },
                    { name: 'Book Collection', brand: 'Takealot', price: 'R99+', url: 'https://www.takealot.com/all?_sb=1&_r=1&ref=search&search=kids+books', why: 'Stories that inspire' },
                  ],
                  'wedding': [
                    { name: 'Carrol Boyes Wine Glasses', brand: 'Carrol Boyes', price: 'R1,295', url: 'https://www.carrolboyes.com/collections/wine-glasses', why: 'Iconic SA luxury for the couple' },
                    { name: 'Honeymoon Flight Voucher', brand: 'Travelstart', price: 'R2,500+', url: 'https://www.travelstart.co.za', why: 'Help fund their dream trip' },
                    { name: 'Couples Spa Day', brand: 'Hyperli', price: 'R499+', url: 'https://www.hyperli.com/search?q=couples+spa', why: 'Relaxation after the big day' },
                    { name: 'Premium Kitchen Hamper', brand: 'Yuppiechef', price: 'R499+', url: 'https://www.yuppiechef.com/gift-ideas.htm', why: 'For their new home together' },
                    { name: 'Luxury Bouquet', brand: 'NetFlorist', price: 'R399+', url: 'https://www.netflorist.co.za', why: 'Beautiful fresh flowers' },
                  ],
                  'lobola': [
                    { name: 'Serving Bowl Set', brand: 'Carrol Boyes', price: 'R895', url: 'https://www.carrolboyes.com/collections/bowls', why: 'For hosting family gatherings' },
                    { name: 'Homeware Essentials', brand: 'Woolworths', price: 'R350+', url: 'https://www.woolworths.co.za/cat/Home/_/N-1z13s5s', why: 'Quality home starter pieces' },
                    { name: 'Luxury Body Care', brand: 'Rain Africa', price: 'R650+', url: 'https://www.rainafrica.com/collections/gift-sets', why: 'Proudly South African luxury' },
                    { name: 'Kitchen Appliances', brand: 'Takealot', price: 'R299+', url: 'https://www.takealot.com/all?_sb=1&_r=1&ref=search&search=kitchen+appliance', why: 'Practical & appreciated' },
                  ],
                  'umembeso': [
                    { name: 'Luxury Blanket Set', brand: 'Woolworths', price: 'R499+', url: 'https://www.woolworths.co.za/cat/Home/_/N-1z13s5s', why: 'Traditional & meaningful' },
                    { name: 'Candle & Décor Set', brand: '@home', price: 'R249+', url: 'https://www.home.co.za', why: 'Beautiful home touches' },
                    { name: 'Pamper Voucher', brand: 'Sorbet', price: 'R500+', url: 'https://www.sorbet.co.za/vouchers', why: 'A treat for the bride' },
                    { name: 'Salad Servers', brand: 'Carrol Boyes', price: 'R595', url: 'https://www.carrolboyes.com/collections/salad-servers', why: 'Functional art for the home' },
                  ],
                  'birthday': [
                    { name: 'Wine Tasting Experience', brand: 'Hyperli', price: 'R299+', url: 'https://www.hyperli.com/search?q=wine+tasting', why: 'A memorable experience gift' },
                    { name: 'Pamper Voucher', brand: 'Sorbet', price: 'R500+', url: 'https://www.sorbet.co.za/vouchers', why: 'Everyone loves being spoiled' },
                    { name: 'Gift Hamper', brand: 'NetFlorist', price: 'R399+', url: 'https://www.netflorist.co.za', why: 'Chocolate, wine & flowers' },
                    { name: 'Adventure Day Out', brand: 'Hyperli', price: 'R199+', url: 'https://www.hyperli.com/search?q=adventure', why: 'Go karts, paintball & more' },
                  ],
                  'graduation': [
                    { name: 'Laptop / Tablet', brand: 'Takealot', price: 'R4,999+', url: 'https://www.takealot.com/all?_sb=1&_r=1&ref=search&search=laptop', why: 'For the next chapter' },
                    { name: 'Weekend Getaway', brand: "Daddy's Deals", price: 'R999+', url: 'https://www.daddysdeals.co.za/search?q=weekend+away', why: 'They earned a break' },
                    { name: 'Wine Glasses Set', brand: 'Carrol Boyes', price: 'R1,295', url: 'https://www.carrolboyes.com/collections/wine-glasses', why: 'A grown-up celebration gift' },
                    { name: 'Wellness Hamper', brand: 'Wellness Warehouse', price: 'R399+', url: 'https://www.wellnesswarehouse.com/gifts', why: 'Self-care for the future' },
                  ],
                  'engagement': [
                    { name: 'Couples Spa Day', brand: 'Hyperli', price: 'R499+', url: 'https://www.hyperli.com/search?q=couples+spa', why: 'Celebrate together' },
                    { name: 'Wine Glasses Set', brand: 'Carrol Boyes', price: 'R1,295', url: 'https://www.carrolboyes.com/collections/wine-glasses', why: 'Toast to the future' },
                    { name: 'Romantic Getaway', brand: 'Booking.com', price: 'R1,200+/night', url: 'https://www.booking.com/romantic-getaways/country/za.html', why: 'A pre-wedding escape' },
                    { name: 'Luxury Bouquet', brand: 'NetFlorist', price: 'R399+', url: 'https://www.netflorist.co.za', why: 'Classic & beautiful' },
                  ],
                  'corporate event': [
                    { name: 'Team Experience', brand: 'Hyperli', price: 'R199+/person', url: 'https://www.hyperli.com/search?q=team+building', why: 'Team building activities' },
                    { name: 'Gourmet Hamper', brand: 'Yuppiechef', price: 'R499+', url: 'https://www.yuppiechef.com/gift-ideas.htm', why: 'Premium corporate gift' },
                    { name: 'Wine Gift Box', brand: 'Woolworths', price: 'R299+', url: 'https://www.woolworths.co.za/cat/Home/_/N-1z13s5s', why: 'Elegant thank-you gift' },
                    { name: 'Desk Accessories', brand: 'Takealot', price: 'R199+', url: 'https://www.takealot.com/all?_sb=1&_r=1&ref=search&search=desk+accessories+gift', why: 'Practical & stylish' },
                  ],
                  'gender reveal': [
                    { name: 'Baby Essentials Hamper', brand: 'Babies R Us', price: 'R499+', url: 'https://www.babiesrus.co.za', why: 'Get a head start on essentials' },
                    { name: 'Nursery Décor', brand: 'Mr Price Home', price: 'R199+', url: 'https://www.mrphome.com', why: 'Cute nursery touches' },
                    { name: 'Pamper Gift for Mom', brand: 'Sorbet', price: 'R500+', url: 'https://www.sorbet.co.za/vouchers', why: 'Spoil the mom-to-be' },
                    { name: 'Baby Clothing', brand: 'Cotton On Kids', price: 'R149+', url: 'https://cottonon.com/ZA/kids/', why: 'Tiny outfits they\'ll love' },
                  ],
                };
                // Fallback for any event type not listed
                const defaultPicks = [
                  { name: 'Luxury Homeware', brand: 'Carrol Boyes', price: 'R595+', url: 'https://www.carrolboyes.com', why: 'Proudly SA luxury gifts' },
                  { name: 'Experience Voucher', brand: 'Hyperli', price: 'R299+', url: 'https://www.hyperli.com', why: 'Memories over things' },
                  { name: 'Gift Hamper', brand: 'NetFlorist', price: 'R399+', url: 'https://www.netflorist.co.za', why: 'Flowers, chocolate & more' },
                  { name: 'Pamper Voucher', brand: 'Sorbet', price: 'R500+', url: 'https://www.sorbet.co.za/vouchers', why: 'Everyone deserves pampering' },
                ];
                const picks = PICKS[type] || PICKS[Object.keys(PICKS).find(k => type.includes(k))] || defaultPicks;

                return (
                  <div style={{ marginBottom: '20px' }}>
                    {/* Toggle bar */}
                    <div
                      onClick={() => setGigiPicks(!gigiPicks)}
                      style={{
                        display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 16px',
                        borderRadius: gigiPicks ? '12px 12px 0 0' : '12px', cursor: 'pointer',
                        background: 'linear-gradient(135deg, rgba(232,160,191,0.1), rgba(212,165,116,0.08))',
                        border: '1px solid rgba(232,160,191,0.18)',
                        borderBottom: gigiPicks ? '1px solid rgba(232,160,191,0.1)' : undefined,
                        transition: 'all 0.2s',
                      }}
                    >
                      <img src="/gigi-mascot.png" alt="GiGi" style={{ width: '32px', height: '32px', borderRadius: '50%', flexShrink: 0, objectFit: 'contain' }} />
                      <div style={{ flex: 1 }}>
                        <span style={{ fontSize: '13px', fontWeight: 600, color: 'var(--dark)' }}>
                          {gigiPicks ? 'GiGi\'s picks for your ' : 'Ask GiGi — '}
                          {gigiPicks
                            ? <span style={{ color: 'var(--rose-deep)' }}>{event?.event_type || 'event'}</span>
                            : <span style={{ color: 'var(--gray)', fontWeight: 400 }}>Gift suggestions for your {event?.event_type || 'event'}</span>
                          }
                        </span>
                      </div>
                      <div style={{
                        padding: '5px 12px', borderRadius: '6px',
                        background: gigiPicks ? 'var(--cream)' : 'linear-gradient(135deg, var(--rose-deep), #D13A60)',
                        color: gigiPicks ? 'var(--gray)' : 'white',
                        fontSize: '11px', fontWeight: 700,
                        display: 'flex', alignItems: 'center', gap: '4px',
                      }}>
                        <FiZap size={11} /> {gigiPicks ? 'Hide' : 'Show Picks'}
                      </div>
                    </div>

                    {/* Picks panel */}
                    {gigiPicks && (
                      <div style={{
                        padding: '16px', background: 'var(--white)',
                        border: '1px solid rgba(232,160,191,0.18)', borderTop: 'none',
                        borderRadius: '0 0 12px 12px',
                      }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '14px' }}>
                          {picks.map((p, i) => (
                            <a key={i} href={p.url} target="_blank" rel="noreferrer"
                              onClick={() => { api.post('/brand-clicks', { brand: p.brand, product: p.name, url: p.url, event_id: id }).catch(() => {}); }}
                              style={{
                                display: 'flex', alignItems: 'center', gap: '14px',
                                padding: '10px 14px', borderRadius: '10px',
                                background: 'var(--cream)', textDecoration: 'none', color: 'inherit',
                                transition: 'all 0.15s', border: '1px solid transparent',
                              }}
                              onMouseEnter={e => { e.currentTarget.style.background = 'rgba(232,160,191,0.1)'; e.currentTarget.style.borderColor = 'rgba(232,160,191,0.2)'; }}
                              onMouseLeave={e => { e.currentTarget.style.background = 'var(--cream)'; e.currentTarget.style.borderColor = 'transparent'; }}
                            >
                              <div style={{ width: '32px', height: '32px', borderRadius: '8px', background: 'linear-gradient(135deg, var(--rose-light), var(--rose))', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, color: 'white', fontSize: '14px' }}>
                                <FiGift size={15} />
                              </div>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--dark)' }}>{p.name}</div>
                                <div style={{ fontSize: '11px', color: 'var(--gray)' }}>{p.brand} · {p.why}</div>
                              </div>
                              <div style={{ textAlign: 'right', flexShrink: 0 }}>
                                <div style={{ fontSize: '13px', fontWeight: 700, color: 'var(--rose-deep)' }}>{p.price}</div>
                                <div style={{ fontSize: '10px', color: 'var(--rose-deep)', display: 'flex', alignItems: 'center', gap: '2px', justifyContent: 'flex-end' }}><FiExternalLink size={9} /> Shop</div>
                              </div>
                            </a>
                          ))}
                        </div>

                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 14px', borderRadius: '8px', background: 'rgba(212,165,116,0.08)' }}>
                          <FiSearch size={14} style={{ color: 'var(--gold-dark)', flexShrink: 0 }} />
                          <span style={{ fontSize: '12px', color: 'var(--gray)', flex: 1 }}>Want more options? Browse all our partner stores.</span>
                          <button onClick={() => { setGiftStores(true); setGigiPicks(false); }} style={{
                            padding: '5px 12px', borderRadius: '6px', border: 'none', cursor: 'pointer',
                            background: 'var(--dark)', color: 'white', fontSize: '11px', fontWeight: 600,
                            display: 'flex', alignItems: 'center', gap: '4px',
                          }}><FiShoppingBag size={11} /> Browse Stores</button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* Gift Box - Curated Items Indicator */}
              {registry.length > 0 && (
                <>
                  {/* Gift Box Button */}
                  <div
                    id="curated-gift-box"
                    onClick={() => {
                      if (!giftBoxOpen) { setGiftBoxAnimating(true); setTimeout(() => { setGiftBoxOpen(true); setGiftBoxAnimating(false); }, 600); }
                      else setGiftBoxOpen(false);
                    }}
                    style={{
                      cursor: 'pointer', borderRadius: '16px', overflow: 'hidden',
                      background: 'linear-gradient(135deg, #D13A60, #E3425C, #D4A574)',
                      padding: '20px 24px', marginBottom: giftBoxOpen ? '0' : '16px',
                      borderBottomLeftRadius: giftBoxOpen ? 0 : '16px',
                      borderBottomRightRadius: giftBoxOpen ? 0 : '16px',
                      transition: 'all 0.3s ease', position: 'relative',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', minWidth: 0, flex: '1 1 auto' }}>
                        {/* Animated Gift Box */}
                        <div style={{ position: 'relative', width: '48px', height: '48px', flexShrink: 0 }}>
                          {/* Box bottom */}
                          <div style={{
                            position: 'absolute', bottom: 0, left: '3px', right: '3px', height: '28px',
                            background: 'rgba(255,255,255,0.95)', borderRadius: '4px 4px 8px 8px',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                          }} />
                          {/* Ribbon vertical */}
                          <div style={{ position: 'absolute', bottom: 0, left: '50%', transform: 'translateX(-50%)', width: '6px', height: '28px', background: '#D13A60', zIndex: 1 }} />
                          {/* Ribbon horizontal */}
                          <div style={{ position: 'absolute', bottom: '12px', left: '3px', right: '3px', height: '6px', background: '#D13A60', zIndex: 1 }} />
                          {/* Box lid */}
                          <div style={{
                            position: 'absolute', top: giftBoxAnimating ? '-12px' : giftBoxOpen ? '-8px' : '8px',
                            left: 0, right: 0, height: '18px',
                            background: 'rgba(255,255,255,0.95)', borderRadius: '6px 6px 2px 2px',
                            boxShadow: '0 -1px 6px rgba(0,0,0,0.1)',
                            transform: giftBoxAnimating ? 'rotate(-15deg) translateY(-8px)' : giftBoxOpen ? 'rotate(-10deg) translateY(-4px)' : 'rotate(0)',
                            transformOrigin: 'bottom left',
                            transition: 'all 0.5s cubic-bezier(0.34, 1.56, 0.64, 1)',
                            zIndex: 2,
                          }}>
                            <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: '16px', height: '8px', background: '#D13A60', borderRadius: '2px' }} />
                          </div>
                          {/* Sparkles when animating */}
                          {giftBoxAnimating && (
                            <>
                              <div style={{ position: 'absolute', top: '-4px', left: '8px', fontSize: '12px', animation: 'giftSparkle 0.6s ease-out' }}>&#10022;</div>
                              <div style={{ position: 'absolute', top: '0px', right: '4px', fontSize: '10px', animation: 'giftSparkle 0.6s ease-out 0.1s' }}>&#10022;</div>
                              <div style={{ position: 'absolute', top: '-8px', left: '24px', fontSize: '14px', animation: 'giftSparkle 0.6s ease-out 0.2s' }}>&#10022;</div>
                            </>
                          )}
                        </div>
                        <div>
                          <div style={{ color: 'white', fontWeight: 700, fontSize: '17px', marginBottom: '2px' }}>
                            Your Curated Gifts
                          </div>
                          <div style={{ color: 'rgba(255,255,255,0.8)', fontSize: '13px' }}>
                            {giftBoxOpen ? 'Click to close your gift box' : 'Click to open your gift box and view all items'}
                          </div>
                        </div>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
                        {/* Item count badge */}
                        <div style={{
                          background: 'rgba(255,255,255,0.95)', color: '#D13A60',
                          borderRadius: '20px', padding: '4px 12px',
                          fontWeight: 800, fontSize: '13px',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                          display: 'flex', alignItems: 'center', gap: '4px',
                        }}>
                          <FiGift size={14} /> {registry.length}
                        </div>
                        {/* Arrow indicator */}
                        <div style={{
                          color: 'white', fontSize: '18px',
                          transform: giftBoxOpen ? 'rotate(180deg)' : 'rotate(0)',
                          transition: 'transform 0.3s ease',
                        }}>
                          <FiChevronDown />
                        </div>
                      </div>
                    </div>
                    <style>{`
                      @keyframes giftSparkle {
                        0% { opacity: 0; transform: scale(0) translateY(0); }
                        50% { opacity: 1; transform: scale(1.2) translateY(-8px); }
                        100% { opacity: 0; transform: scale(0.8) translateY(-16px); }
                      }
                    `}</style>
                  </div>

                  {/* Gift Box Contents - Animated Drawer */}
                  <div style={{
                    maxHeight: giftBoxOpen ? '5000px' : '0',
                    overflow: 'hidden',
                    transition: giftBoxOpen ? 'max-height 0.6s ease-in' : 'max-height 0.4s ease-out',
                    borderBottomLeftRadius: '16px', borderBottomRightRadius: '16px',
                    background: 'var(--white)',
                    border: giftBoxOpen ? '2px solid rgba(201,72,112,0.2)' : '0px solid transparent',
                    borderTop: 'none',
                    marginBottom: '16px',
                  }}>
                    <div style={{ padding: '20px' }}>
                      {/* Summary bar */}
                      <div style={{ display: 'flex', gap: '12px', marginBottom: '20px', flexWrap: 'wrap' }}>
                        {(() => {
                          const fulfilled = registry.filter(r => r.is_fulfilled || (parseInt(r.quantity_fulfilled)||0) >= (parseInt(r.quantity_needed)||1)).length;
                          const totalContributions = registry.reduce((s, r) => s + (r.contributions?.length || 0), 0);
                          const raised = registry.reduce((s, r) => s + Number(r.current_amount || 0), 0);
                          return (
                            <>
                              <div style={{ padding: '8px 14px', borderRadius: '10px', background: 'rgba(201,72,112,0.06)', fontSize: '13px' }}>
                                <span style={{ fontWeight: 700, color: 'var(--rose-deep)' }}>{fulfilled}</span> <span style={{ color: 'var(--gray)' }}>fulfilled</span>
                              </div>
                              <div style={{ padding: '8px 14px', borderRadius: '10px', background: 'rgba(212,165,116,0.1)', fontSize: '13px' }}>
                                <span style={{ fontWeight: 700, color: 'var(--gold-dark)' }}>{totalContributions}</span> <span style={{ color: 'var(--gray)' }}>contributions</span>
                              </div>
                              {raised > 0 && (
                                <div style={{ padding: '8px 14px', borderRadius: '10px', background: 'rgba(16,185,129,0.08)', fontSize: '13px' }}>
                                  <span style={{ fontWeight: 700, color: '#10B981' }}>R{raised.toLocaleString()}</span> <span style={{ color: 'var(--gray)' }}>raised</span>
                                </div>
                              )}
                            </>
                          );
                        })()}
                      </div>

                      {/* Registry Items Grid — 3 per row on desktop, stacks on mobile */}
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '14px', maxWidth: '920px' }}>
                        {registry.map(item => {
                          const qtyNeeded = parseInt(item.quantity_needed) || 1;
                          const qtyFulfilled = parseInt(item.quantity_fulfilled) || 0;
                          const isFull = item.is_fulfilled || qtyFulfilled >= qtyNeeded;
                          return (
                          <div key={item.id} className="card" style={{ position: 'relative', opacity: isFull ? 0.6 : 1, padding: 0, overflow: 'hidden' }}>
                            {isFull && (
                              <div style={{ position: 'absolute', top: '12px', right: '12px', background: '#10B981', color: 'white', borderRadius: '20px', padding: '4px 12px', fontSize: '11px', fontWeight: 700, zIndex: 2, display: 'flex', alignItems: 'center', gap: '4px' }}><FiCheck size={12} /> Fulfilled</div>
                            )}
                            <div style={{ height: '120px', overflow: 'hidden', position: 'relative' }}>
                              <img src={item.product_image || getGiftDefaultImage(item.title, item.type)} alt={item.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={e => { e.target.src = getGiftDefaultImage(item.title, item.type); }} />
                            </div>
                            <div style={{ padding: '16px 20px 20px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                              <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                                <span className="tag tag-gold">{item.type === 'store' ? 'online' : item.type}</span>
                                {item.vendor_id && <span className="tag" style={{ background: 'rgba(232,160,191,0.15)', color: 'var(--rose-deep)', fontSize: '10px' }}>Vendor Gift</span>}
                              </div>
                              <div style={{ display: 'flex', gap: '4px' }}>
                                <button style={{ background: 'none', color: 'var(--gold)', cursor: 'pointer', border: 'none', padding: '2px' }} title="Edit" onClick={() => { setFormData({ editRegistryId: item.id, title: item.title, type: item.type || 'physical', target_amount: item.target_amount || '', description: item.description || '', image: item.image || '', bank_details: item.bank_details || '', quantity_needed: item.quantity_needed || 1, product_image: item.product_image || '', store_address: item.store_address || '' }); setShowModal('registry'); }}><FiEdit size={14} /></button>
                                <button style={{ background: 'none', color: 'var(--red)', cursor: 'pointer', border: 'none', padding: '2px' }} title="Delete" onClick={async () => { if (window.confirm('Delete this gift item?')) { await api.delete(`/events/${id}/registry/${item.id}`); loadTabData(); } }}><FiTrash2 size={14} /></button>
                              </div>
                            </div>
                            <h4 style={{ marginBottom: '8px' }}>{item.title}</h4>
                            {item.description && <p style={{ fontSize: '14px', color: 'var(--gray)', marginBottom: '12px', lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>{item.description}</p>}
                            {(item.vendor_price || item.target_amount > 0) && (
                              <div style={{ fontSize: '15px', fontWeight: 700, color: 'var(--rose-deep)', marginBottom: '8px' }}>R{Number(item.vendor_price || item.target_amount).toLocaleString()}{qtyNeeded > 1 ? ' each' : ''}</div>
                            )}
                            {qtyNeeded > 1 && (
                              <div style={{ marginBottom: '10px' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', marginBottom: '5px' }}>
                                  <span style={{ fontWeight: 600 }}>{qtyFulfilled} of {qtyNeeded} claimed</span>
                                  <span style={{ color: 'var(--gray)' }}>{qtyNeeded - qtyFulfilled} remaining</span>
                                </div>
                                <div className="progress-bar">
                                  <div className="progress-fill" style={{ width: `${Math.min(100, (qtyFulfilled/qtyNeeded)*100)}%`, background: isFull ? '#10B981' : undefined }}></div>
                                </div>
                              </div>
                            )}
                            {item.image && item.type === 'store' && (
                              <a href={item.image} target="_blank" rel="noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '12px', color: 'var(--rose-deep)', marginBottom: '8px' }}><FiExternalLink size={12} /> View in store</a>
                            )}
                            {item.bank_details && (
                              <div style={{ padding: '10px 12px', background: 'var(--gray-light)', borderRadius: '8px', marginBottom: '12px', fontSize: '13px', whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
                                <strong style={{ fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--rose-deep)' }}>Bank Details</strong><br/>{item.bank_details}
                              </div>
                            )}
                            {qtyNeeded <= 1 && item.target_amount > 0 && item.type !== 'physical' && item.type !== 'store' && (
                              <>
                                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', marginBottom: '6px' }}>
                                  <span>R{Number(item.current_amount||0).toLocaleString()} raised</span>
                                  <span style={{ color: 'var(--gray)' }}>of R{Number(item.target_amount).toLocaleString()}</span>
                                </div>
                                <div className="progress-bar">
                                  <div className="progress-fill" style={{ width: `${Math.min(100, (item.current_amount/item.target_amount)*100)}%` }}></div>
                                </div>
                              </>
                            )}
                            {item.contributions && item.contributions.length > 0 && (
                              <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid var(--cream-dark)' }}>
                                <div style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: 700, color: 'var(--rose-deep)', marginBottom: '8px' }}>
                                  {item.type === 'group' || item.type === 'monetary' ? `${item.contributions.length} Contribution${item.contributions.length > 1 ? 's' : ''}` : `${item.contributions.length} Claim${item.contributions.length > 1 ? 's' : ''}`}
                                </div>
                                {item.contributions.map(c => (
                                  <div key={c.id} style={{ fontSize: '13px', marginBottom: '8px', padding: '8px 10px', background: 'var(--bg-off)', borderRadius: '8px', display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                                    <div style={{ width: '26px', height: '26px', borderRadius: '50%', background: 'linear-gradient(135deg, var(--gold-light), var(--gold))', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: '11px', fontWeight: 700, flexShrink: 0 }}>{(c.contributor_name || '?')[0]}</div>
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                      <div style={{ fontWeight: 600 }}>{c.contributor_name}{c.amount > 0 ? <span style={{ color: 'var(--green)', marginLeft: '6px' }}>R{Number(c.amount).toLocaleString()}</span> : ''}</div>
                                      {c.message && <div style={{ fontSize: '12px', color: 'var(--gray)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.message}</div>}
                                      <div style={{ fontSize: '11px', color: 'var(--gray)', marginTop: '2px' }}>{new Date(c.created_at).toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric' })}</div>
                                    </div>
                                    <div style={{ display: 'flex', gap: '4px', alignItems: 'center', flexShrink: 0, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                                      {c.payment_method === 'paystack' && (
                                        <span style={{ fontSize: '9px', padding: '2px 5px', borderRadius: '4px', background: 'rgba(11,164,219,0.1)', color: '#0BA4DB', fontWeight: 700 }}>PAYSTACK</span>
                                      )}
                                      {c.paystack_reference && (
                                        <span title={c.paystack_reference} style={{ fontSize: '9px', padding: '2px 5px', borderRadius: '4px', background: 'rgba(0,0,0,0.04)', color: 'var(--gray)', fontFamily: 'monospace', cursor: 'help' }}>Ref: {c.paystack_reference.slice(0, 14)}</span>
                                      )}
                                      {c.proof_of_payment && <a href={c.proof_of_payment} target="_blank" rel="noreferrer" style={{ fontSize: '11px', padding: '3px 8px', borderRadius: '6px', background: 'rgba(16,185,129,0.1)', color: 'var(--green)', textDecoration: 'none', fontWeight: 600 }}>Proof</a>}
                                      {c.payment_status === 'confirmed' && <span style={{ fontSize: '10px', padding: '3px 8px', borderRadius: '6px', background: 'rgba(16,185,129,0.15)', color: '#10B981', fontWeight: 700 }}>Paid</span>}
                                      {c.payment_status === 'submitted' && (
                                        <button onClick={async () => { await api.put(`/events/${id}/registry/contributions/${c.id}`, { payment_status: 'confirmed' }); loadTabData(); }} style={{ fontSize: '10px', padding: '3px 8px', borderRadius: '6px', background: 'rgba(16,185,129,0.1)', color: '#10B981', fontWeight: 700, border: '1px solid #10B98133', cursor: 'pointer' }}>Confirm</button>
                                      )}
                                      {c.payment_status === 'pending' && !c.proof_of_payment && <span style={{ fontSize: '10px', padding: '3px 8px', borderRadius: '6px', background: 'rgba(212,165,116,0.1)', color: 'var(--gold-dark)', fontWeight: 600 }}>Awaiting</span>}
                                      {c.payment_status === 'pending' && c.proof_of_payment && (
                                        <button onClick={async () => { await api.put(`/events/${id}/registry/contributions/${c.id}`, { payment_status: 'confirmed' }); loadTabData(); }} style={{ fontSize: '10px', padding: '3px 8px', borderRadius: '6px', background: 'rgba(16,185,129,0.1)', color: '#10B981', fontWeight: 700, border: '1px solid #10B98133', cursor: 'pointer' }}>Confirm</button>
                                      )}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                            {item.gift_codes && item.gift_codes.length > 0 && (
                              <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid var(--cream-dark)' }}>
                                <div style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: 700, color: '#d4a853', marginBottom: '8px' }}>
                                  🔖 Reservations ({item.gift_codes.length})
                                </div>
                                {item.gift_codes.map(gc => (
                                  <div key={gc.id} style={{ fontSize: '13px', marginBottom: '8px', padding: '8px 10px', background: 'rgba(212,168,83,0.08)', borderRadius: '8px' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '6px' }}>
                                      <strong style={{ color: 'var(--gold)', fontFamily: "'Courier New', monospace", letterSpacing: '1px' }}>{gc.code}</strong>
                                      <span style={{ fontSize: '11px', fontWeight: 600, color: gc.status === 'redeemed' ? 'var(--green)' : 'var(--gray)' }}>
                                        {gc.status === 'redeemed' ? 'Redeemed' : 'Reserved'}
                                      </span>
                                    </div>
                                    <div style={{ fontSize: '12px', marginTop: '4px' }}>👤 {gc.buyer_name}{gc.buyer_email ? ` · ${gc.buyer_email}` : ''}{gc.buyer_phone ? ` · ${gc.buyer_phone}` : ''}</div>
                                    {gc.store_name && <div style={{ fontSize: '12px', color: 'var(--gray)', marginTop: '2px' }}>🏬 {gc.store_name}</div>}
                                    {gc.message && <div style={{ fontSize: '12px', marginTop: '4px', fontStyle: 'italic', color: 'var(--gray)' }}>💬 “{gc.message}”</div>}
                                  </div>
                                ))}
                              </div>
                            )}
                            </div>
                          </div>
                        ); })}
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}

          {/* GALLERY WALL */}
          {tab === 'gallery' && (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '10px' }}>
                <h3>Gallery Wall</h3>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <input type="file" ref={fileInputRef} accept="image/*,video/*" multiple style={{ display: 'none' }} onChange={async (e) => {
                    const files = Array.from(e.target.files); if (!files.length) return;
                    for (const file of files) {
                      const fd = new FormData(); fd.append('media', file); fd.append('caption', ''); fd.append('media_type', file.type.startsWith('video') ? 'video' : 'image');
                      await api.post(`/events/${id}/gallery`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
                    }
                    loadTabData(); showToast(`${files.length} file${files.length > 1 ? 's' : ''} uploaded!`);
                  }} />
                  <button className="btn btn-primary btn-sm" onClick={() => fileInputRef.current?.click()}><FiUpload /> Upload Photos</button>
                </div>
              </div>

              {/* Layout selector */}
              {gallery.length > 0 && (
                <div style={{ display: 'flex', gap: '8px', marginBottom: '20px', overflowX: 'auto', paddingBottom: '4px' }}>
                  {GALLERY_LAYOUTS.map(l => (
                    <button key={l.id} onClick={async () => {
                      try {
                        await api.put(`/events/${id}/gallery-layout`, { layout: l.id });
                        loadEvent();
                      } catch {}
                    }} style={{
                      display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 14px',
                      borderRadius: '10px', border: 'none', cursor: 'pointer', whiteSpace: 'nowrap',
                      fontSize: '13px', fontWeight: 500, transition: 'all 0.2s',
                      background: (event.gallery_layout || 'masonry') === l.id ? 'var(--dark)' : 'var(--gray-light)',
                      color: (event.gallery_layout || 'masonry') === l.id ? 'white' : 'var(--gray)',
                    }}>
                      <span>{l.icon}</span> {l.name}
                    </button>
                  ))}
                </div>
              )}

              {gallery.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '60px 20px', borderRadius: '16px', ...getEventBannerStyle(event, ['0.5', '0.65']) }}>
                  <div style={{ fontSize: '52px', marginBottom: '16px' }}>📸</div>
                  <h3 style={{ fontFamily: "'Playfair Display', serif", fontSize: '24px', marginBottom: '12px' }}>Your Gallery Wall</h3>
                  <p style={{ opacity: 0.7, maxWidth: '400px', margin: '0 auto 20px', lineHeight: 1.6 }}>Upload photos and videos to create a beautiful memory wall for your event</p>
                  <button className="btn btn-burgundy" onClick={() => fileInputRef.current?.click()}><FiUpload /> Upload First Photo</button>
                </div>
              ) : (
                <div style={{
                  borderRadius: '16px', overflow: 'hidden',
                  ...getEventBannerStyle(event, ['0.4', '0.55']),
                  padding: '24px'
                }}>
                  <GalleryWall
                    gallery={gallery}
                    layout={event.gallery_layout || 'masonry'}
                    accent="#E3425C"
                    isHost={true}
                    onEdit={(post) => {
                      const sizes = ['small','medium','large'];
                      setFormData({ galleryId: post.id, galleryUrl: post.media_url, galleryCaption: post.caption || '', galleryFrame: post.frame_size || sizes[0], galleryType: post.media_type });
                      setShowModal('galleryEdit');
                    }}
                    onDelete={async (post) => {
                      if (window.confirm('Delete this photo?')) {
                        await api.delete(`/events/${id}/gallery/${post.id}`);
                        loadTabData();
                      }
                    }}
                  />
                  <div style={{ textAlign: 'center', marginTop: '16px' }}>
                    <button className="btn btn-sm" style={{ background: 'rgba(255,255,255,0.15)', color: 'white', border: '1px solid rgba(255,255,255,0.2)' }} onClick={() => fileInputRef.current?.click()}><FiUpload /> Add More</button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* EVENT WALL */}
          {tab === 'wall' && (
            <div style={{ maxWidth: '600px' }}>
              <h3 style={{ marginBottom: '16px' }}>Event Wall</h3>
              <div className="card" style={{ marginBottom: '20px' }}>
                <textarea placeholder="Share an update with your event guests..." value={formData.wallPost || ''} onChange={e => setFormData({ ...formData, wallPost: e.target.value })} rows={3} style={{ marginBottom: '12px' }} />
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <button className="btn btn-primary btn-sm" onClick={async () => {
                    if (!formData.wallPost?.trim()) return;
                    await api.post(`/events/${id}/wall`, { content: formData.wallPost });
                    setFormData({ ...formData, wallPost: '' }); loadTabData();
                  }}><FiSend /> Post</button>
                  <button className="btn btn-sm" style={{ background: 'var(--rose-light)', color: 'var(--rose-deep)' }} onClick={async () => {
                    if (!formData.wallPost?.trim()) return;
                    const r = await api.post(`/events/${id}/wall`, { content: formData.wallPost });
                    await api.put(`/events/${id}/wall/${r.data.id}/pin`, { is_announcement: true, pinned: true });
                    setFormData({ ...formData, wallPost: '' }); loadTabData();
                  }}><FiAlertCircle /> Post as Announcement</button>
                </div>
              </div>
              {/* Pinned/announcements first */}
              {wallPosts.filter(p => p.pinned || p.is_announcement).length > 0 && (
                <div style={{ marginBottom: '16px' }}>
                  {wallPosts.filter(p => p.pinned || p.is_announcement).map(post => (
                    <div key={post.id} className="card" style={{ marginBottom: '12px', border: '2px solid var(--rose)', background: 'rgba(232,160,191,0.04)' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '10px' }}>
                        {post.is_announcement && <span className="tag" style={{ background: 'var(--rose)', color: 'white', fontSize: '11px' }}><FiAlertCircle size={10} /> Announcement</span>}
                        {post.pinned && <span className="tag tag-gold" style={{ fontSize: '11px' }}><FiStar size={10} /> Pinned</span>}
                        <div style={{ marginLeft: 'auto', display: 'flex', gap: '4px' }}>
                          <button style={{ background: 'none', border: 'none', color: 'var(--gold)', cursor: 'pointer', padding: '4px' }} onClick={() => { setFormData({ ...formData, editWallId: post.id, editWallContent: post.content }); }} title="Edit"><FiEdit size={14} /></button>
                          <button style={{ background: 'none', border: 'none', color: 'var(--red)', cursor: 'pointer', padding: '4px' }} onClick={async () => { if (window.confirm('Delete this post?')) { await api.delete(`/events/${id}/wall/${post.id}`); loadTabData(); } }} title="Delete"><FiTrash2 size={14} /></button>
                        </div>
                      </div>
                      {formData.editWallId === post.id ? (
                        <div style={{ marginBottom: '10px' }}>
                          <textarea value={formData.editWallContent || ''} onChange={e => setFormData({ ...formData, editWallContent: e.target.value })} rows={3} style={{ marginBottom: '8px' }} />
                          <div style={{ display: 'flex', gap: '8px' }}>
                            <button className="btn btn-primary btn-sm" onClick={async () => {
                              await api.put(`/events/${id}/wall/${post.id}`, { content: formData.editWallContent });
                              setFormData({ ...formData, editWallId: null, editWallContent: '' }); loadTabData();
                            }}>Save</button>
                            <button className="btn btn-sm btn-secondary" onClick={() => setFormData({ ...formData, editWallId: null, editWallContent: '' })}>Cancel</button>
                          </div>
                        </div>
                      ) : (
                        <p style={{ marginBottom: '10px', whiteSpace: 'pre-wrap', fontWeight: 500 }}>{post.content}</p>
                      )}
                      <div style={{ display: 'flex', gap: '8px', fontSize: '12px' }}>
                        <span style={{ color: 'var(--gray)' }}>{new Date(post.created_at).toLocaleString()}</span>
                        <button style={{ background: 'none', border: 'none', color: 'var(--gray)', cursor: 'pointer', fontSize: '12px' }} onClick={async () => { await api.put(`/events/${id}/wall/${post.id}/pin`, { pinned: !post.pinned }); loadTabData(); }}>{post.pinned ? 'Unpin' : 'Pin'}</button>
                        <button style={{ background: 'none', border: 'none', color: 'var(--gray)', cursor: 'pointer', fontSize: '12px' }} onClick={async () => { await api.put(`/events/${id}/wall/${post.id}/pin`, { is_announcement: !post.is_announcement }); loadTabData(); }}>{post.is_announcement ? 'Remove announcement' : 'Make announcement'}</button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {wallPosts.filter(p => !p.pinned && !p.is_announcement).map(post => (
                <div key={post.id} className="card" style={{ marginBottom: '12px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                    <div style={{ width: '36px', height: '36px', borderRadius: '50%', background: 'linear-gradient(135deg, var(--rose-light), var(--rose))', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontWeight: 600, fontSize: '14px' }}>{(post.user_name || post.guest_name || 'U')[0]}</div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600, fontSize: '14px' }}>{post.user_name || post.guest_name || 'Guest'}</div>
                      <div style={{ fontSize: '12px', color: 'var(--gray)' }}>{new Date(post.created_at).toLocaleString()}</div>
                    </div>
                    <div style={{ display: 'flex', gap: '4px' }}>
                      <button style={{ background: 'none', border: 'none', color: 'var(--gold)', cursor: 'pointer', padding: '4px' }} onClick={() => { setFormData({ ...formData, editWallId: post.id, editWallContent: post.content }); }} title="Edit"><FiEdit size={14} /></button>
                      <button style={{ background: 'none', border: 'none', color: 'var(--red)', cursor: 'pointer', padding: '4px' }} onClick={async () => { if (window.confirm('Delete this post?')) { await api.delete(`/events/${id}/wall/${post.id}`); loadTabData(); } }} title="Delete"><FiTrash2 size={14} /></button>
                    </div>
                  </div>
                  {formData.editWallId === post.id ? (
                    <div style={{ marginBottom: '12px' }}>
                      <textarea value={formData.editWallContent || ''} onChange={e => setFormData({ ...formData, editWallContent: e.target.value })} rows={3} style={{ marginBottom: '8px' }} />
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button className="btn btn-primary btn-sm" onClick={async () => {
                          await api.put(`/events/${id}/wall/${post.id}`, { content: formData.editWallContent });
                          setFormData({ ...formData, editWallId: null, editWallContent: '' }); loadTabData();
                        }}>Save</button>
                        <button className="btn btn-sm btn-secondary" onClick={() => setFormData({ ...formData, editWallId: null, editWallContent: '' })}>Cancel</button>
                      </div>
                    </div>
                  ) : (
                    <p style={{ marginBottom: '12px', whiteSpace: 'pre-wrap' }}>{post.content}</p>
                  )}
                  {/* Emoji reactions on post */}
                  {post.reactions && Object.keys(post.reactions).length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '10px' }}>
                      {Object.entries(post.reactions).map(([emoji, names]) => (
                        <button key={emoji} onClick={async (e) => {
                          const rect = e.currentTarget.getBoundingClientRect();
                          await api.post(`/wall/${post.id}/react`, { emoji, user_name: 'Host' });
                          // Explosion animation
                          const emojis = [];
                          for (let i = 0; i < 7; i++) {
                            const el = document.createElement('span');
                            el.textContent = emoji;
                            el.style.cssText = `position:fixed;left:${rect.left + rect.width/2}px;top:${rect.top}px;font-size:20px;pointer-events:none;z-index:9999;transition:all 0.7s ease-out;`;
                            document.body.appendChild(el);
                            emojis.push(el);
                            requestAnimationFrame(() => {
                              el.style.left = `${rect.left + rect.width/2 + (Math.random()-0.5)*120}px`;
                              el.style.top = `${rect.top - 40 - Math.random()*80}px`;
                              el.style.opacity = '0';
                              el.style.transform = `scale(${0.3 + Math.random()*0.5}) rotate(${(Math.random()-0.5)*60}deg)`;
                            });
                          }
                          setTimeout(() => emojis.forEach(el => el.remove()), 750);
                          loadTabData();
                        }} style={{ background: 'rgba(232,160,191,0.12)', border: '1px solid rgba(232,160,191,0.3)', borderRadius: '16px', padding: '2px 8px', fontSize: '13px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <span style={{ fontSize: '16px' }}>{emoji}</span>
                          <span style={{ color: 'var(--rose-deep)', fontWeight: 600 }}>{names.length}</span>
                        </button>
                      ))}
                    </div>
                  )}
                  <div style={{ display: 'flex', gap: '12px', paddingTop: '12px', borderTop: '1px solid var(--cream-dark)', alignItems: 'center' }}>
                    <button style={{ background: 'none', display: 'flex', alignItems: 'center', gap: '4px', fontSize: '13px', color: 'var(--gray)', cursor: 'pointer', border: 'none' }} onClick={async () => { await api.post(`/wall/${post.id}/like`); loadTabData(); }}>
                      <FiHeart /> {post.likes || 0}
                    </button>
                    {/* Emoji react button with picker */}
                    <div style={{ position: 'relative' }}>
                      <button style={{ background: 'none', border: 'none', fontSize: '13px', color: 'var(--gray)', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }} onClick={() => setFormData({ ...formData, wallEmojiPicker: formData.wallEmojiPicker === post.id ? null : post.id })}>
                        😊 React
                      </button>
                      {formData.wallEmojiPicker === post.id && (
                        <div style={{ position: 'absolute', bottom: '100%', left: 0, background: 'white', borderRadius: '12px', boxShadow: '0 4px 20px rgba(0,0,0,0.15)', padding: '8px', display: 'flex', gap: '4px', zIndex: 100, marginBottom: '4px' }}>
                          {['❤️','😂','🔥','👏','😍','🎉','💯','😢'].map(em => (
                            <button key={em} onClick={async (e) => {
                              const rect = e.currentTarget.getBoundingClientRect();
                              await api.post(`/wall/${post.id}/react`, { emoji: em, user_name: 'Host' });
                              setFormData({ ...formData, wallEmojiPicker: null });
                              // Explosion
                              const emojis = [];
                              for (let i = 0; i < 7; i++) {
                                const el = document.createElement('span');
                                el.textContent = em;
                                el.style.cssText = `position:fixed;left:${rect.left}px;top:${rect.top}px;font-size:22px;pointer-events:none;z-index:9999;transition:all 0.7s ease-out;`;
                                document.body.appendChild(el);
                                emojis.push(el);
                                requestAnimationFrame(() => {
                                  el.style.left = `${rect.left + (Math.random()-0.5)*140}px`;
                                  el.style.top = `${rect.top - 50 - Math.random()*80}px`;
                                  el.style.opacity = '0';
                                  el.style.transform = `scale(${0.3 + Math.random()*0.5}) rotate(${(Math.random()-0.5)*60}deg)`;
                                });
                              }
                              setTimeout(() => emojis.forEach(el => el.remove()), 750);
                              loadTabData();
                            }} style={{ background: 'none', border: 'none', fontSize: '22px', cursor: 'pointer', padding: '4px', transition: 'transform 0.15s' }} onMouseEnter={e => e.target.style.transform='scale(1.3)'} onMouseLeave={e => e.target.style.transform='scale(1)'}>{em}</button>
                          ))}
                        </div>
                      )}
                    </div>
                    <button style={{ background: 'none', display: 'flex', alignItems: 'center', gap: '4px', fontSize: '13px', color: 'var(--gray)', cursor: 'pointer', border: 'none' }} onClick={() => setFormData({ ...formData, wallCommentOpen: formData.wallCommentOpen === post.id ? null : post.id })}>
                      <FiMessageCircle /> {post.comments?.length || 0}
                    </button>
                    <button style={{ background: 'none', border: 'none', color: 'var(--gray)', cursor: 'pointer', fontSize: '12px', marginLeft: 'auto' }} onClick={async () => { await api.put(`/events/${id}/wall/${post.id}/pin`, { pinned: true }); loadTabData(); }}><FiStar size={12} /> Pin</button>
                    <button style={{ background: 'none', border: 'none', color: 'var(--gray)', cursor: 'pointer', fontSize: '12px' }} onClick={async () => { await api.put(`/events/${id}/wall/${post.id}/pin`, { is_announcement: true, pinned: true }); loadTabData(); }}><FiAlertCircle size={12} /> Announce</button>
                  </div>
                  {/* Comments section */}
                  {post.comments && post.comments.length > 0 && (
                    <div style={{ marginTop: '12px', paddingLeft: '16px', borderLeft: '2px solid var(--cream-dark)' }}>
                      {post.comments.filter(c => !c.parent_id).map(c => (
                        <div key={c.id} style={{ marginBottom: '10px' }}>
                          <div style={{ fontSize: '13px' }}>
                            <strong style={{ color: 'var(--rose-deep)' }}>{c.user_name || c.guest_name || 'Guest'}</strong>
                            <span style={{ color: 'var(--gray)', fontSize: '11px', marginLeft: '8px' }}>{new Date(c.created_at).toLocaleString()}</span>
                          </div>
                          <p style={{ fontSize: '13px', margin: '2px 0 4px' }}>{c.content}</p>
                          {/* Comment reactions */}
                          {c.reactions && Object.keys(c.reactions).length > 0 && (
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginBottom: '4px' }}>
                              {Object.entries(c.reactions).map(([emoji, names]) => (
                                <button key={emoji} onClick={async (e) => {
                                  const rect = e.currentTarget.getBoundingClientRect();
                                  await api.post(`/wall/comment/${c.id}/react`, { emoji, user_name: 'Host' });
                                  const emojis = [];
                                  for (let i = 0; i < 6; i++) {
                                    const el = document.createElement('span');
                                    el.textContent = emoji;
                                    el.style.cssText = `position:fixed;left:${rect.left+rect.width/2}px;top:${rect.top}px;font-size:18px;pointer-events:none;z-index:9999;transition:all 0.6s ease-out;`;
                                    document.body.appendChild(el);
                                    emojis.push(el);
                                    requestAnimationFrame(() => {
                                      el.style.left = `${rect.left+rect.width/2+(Math.random()-0.5)*100}px`;
                                      el.style.top = `${rect.top-30-Math.random()*60}px`;
                                      el.style.opacity = '0';
                                      el.style.transform = `scale(${0.3+Math.random()*0.4}) rotate(${(Math.random()-0.5)*50}deg)`;
                                    });
                                  }
                                  setTimeout(() => emojis.forEach(el => el.remove()), 700);
                                  loadTabData();
                                }} style={{ background: 'rgba(232,160,191,0.1)', border: '1px solid rgba(232,160,191,0.2)', borderRadius: '12px', padding: '1px 6px', fontSize: '11px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '3px' }}>
                                  <span style={{ fontSize: '13px' }}>{emoji}</span><span style={{ color: 'var(--rose-deep)', fontWeight: 600 }}>{names.length}</span>
                                </button>
                              ))}
                            </div>
                          )}
                          <div style={{ display: 'flex', gap: '8px', fontSize: '11px' }}>
                            <button style={{ background: 'none', border: 'none', color: 'var(--rose)', cursor: 'pointer', fontSize: '11px' }} onClick={() => setFormData({ ...formData, wallReplyTo: formData.wallReplyTo === c.id ? null : c.id, wallReplyContent: '' })}>Reply</button>
                            <div style={{ position: 'relative' }}>
                              <button style={{ background: 'none', border: 'none', color: 'var(--gray)', cursor: 'pointer', fontSize: '11px' }} onClick={() => setFormData({ ...formData, wallCommentEmojiPicker: formData.wallCommentEmojiPicker === c.id ? null : c.id })}>React</button>
                              {formData.wallCommentEmojiPicker === c.id && (
                                <div style={{ position: 'absolute', bottom: '100%', left: 0, background: 'white', borderRadius: '10px', boxShadow: '0 4px 16px rgba(0,0,0,0.15)', padding: '6px', display: 'flex', gap: '2px', zIndex: 100, marginBottom: '4px' }}>
                                  {['❤️','😂','🔥','👏','😍','🎉'].map(em => (
                                    <button key={em} onClick={async (e) => {
                                      const rect = e.currentTarget.getBoundingClientRect();
                                      await api.post(`/wall/comment/${c.id}/react`, { emoji: em, user_name: 'Host' });
                                      setFormData({ ...formData, wallCommentEmojiPicker: null });
                                      const emojis = [];
                                      for (let i = 0; i < 6; i++) {
                                        const el = document.createElement('span');
                                        el.textContent = em;
                                        el.style.cssText = `position:fixed;left:${rect.left}px;top:${rect.top}px;font-size:18px;pointer-events:none;z-index:9999;transition:all 0.6s ease-out;`;
                                        document.body.appendChild(el);
                                        emojis.push(el);
                                        requestAnimationFrame(() => {
                                          el.style.left = `${rect.left+(Math.random()-0.5)*100}px`;
                                          el.style.top = `${rect.top-30-Math.random()*60}px`;
                                          el.style.opacity = '0';
                                          el.style.transform = `scale(${0.3+Math.random()*0.4}) rotate(${(Math.random()-0.5)*50}deg)`;
                                        });
                                      }
                                      setTimeout(() => emojis.forEach(el => el.remove()), 700);
                                      loadTabData();
                                    }} style={{ background: 'none', border: 'none', fontSize: '18px', cursor: 'pointer', padding: '3px', transition: 'transform 0.15s' }} onMouseEnter={e => e.target.style.transform='scale(1.3)'} onMouseLeave={e => e.target.style.transform='scale(1)'}>{em}</button>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                          {/* Reply input */}
                          {formData.wallReplyTo === c.id && (
                            <div style={{ display: 'flex', gap: '6px', marginTop: '6px' }}>
                              <input placeholder={`Reply to ${c.user_name || c.guest_name || 'Guest'}...`} value={formData.wallReplyContent || ''} onChange={e => setFormData({ ...formData, wallReplyContent: e.target.value })} style={{ flex: 1, fontSize: '12px', padding: '6px 10px' }} onKeyDown={async e => {
                                if (e.key === 'Enter' && formData.wallReplyContent?.trim()) {
                                  await api.post(`/wall/${post.id}/comment`, { content: formData.wallReplyContent, parent_id: c.id });
                                  setFormData({ ...formData, wallReplyTo: null, wallReplyContent: '' }); loadTabData();
                                }
                              }} />
                              <button className="btn btn-primary btn-sm" style={{ fontSize: '11px', padding: '4px 10px' }} onClick={async () => {
                                if (!formData.wallReplyContent?.trim()) return;
                                await api.post(`/wall/${post.id}/comment`, { content: formData.wallReplyContent, parent_id: c.id });
                                setFormData({ ...formData, wallReplyTo: null, wallReplyContent: '' }); loadTabData();
                              }}><FiSend size={10} /></button>
                            </div>
                          )}
                          {/* Nested replies */}
                          {post.comments.filter(r => r.parent_id === c.id).map(reply => (
                            <div key={reply.id} style={{ marginLeft: '16px', marginTop: '6px', paddingLeft: '10px', borderLeft: '2px solid var(--cream-dark)' }}>
                              <div style={{ fontSize: '12px' }}>
                                <strong style={{ color: 'var(--rose)' }}>{reply.user_name || reply.guest_name || 'Guest'}</strong>
                                <span style={{ color: 'var(--gray)', fontSize: '10px', marginLeft: '6px' }}>{new Date(reply.created_at).toLocaleString()}</span>
                              </div>
                              <p style={{ fontSize: '12px', margin: '2px 0 2px' }}>{reply.content}</p>
                              {reply.reactions && Object.keys(reply.reactions).length > 0 && (
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '3px', marginBottom: '2px' }}>
                                  {Object.entries(reply.reactions).map(([emoji, names]) => (
                                    <span key={emoji} style={{ background: 'rgba(232,160,191,0.1)', borderRadius: '10px', padding: '1px 5px', fontSize: '10px' }}>{emoji} {names.length}</span>
                                  ))}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      ))}
                    </div>
                  )}
                  {/* Comment input */}
                  {formData.wallCommentOpen === post.id && (
                    <div style={{ display: 'flex', gap: '8px', marginTop: '12px', paddingTop: '12px', borderTop: '1px solid var(--cream-dark)' }}>
                      <input placeholder="Write a comment..." value={formData.wallCommentContent || ''} onChange={e => setFormData({ ...formData, wallCommentContent: e.target.value })} style={{ flex: 1, fontSize: '13px' }} onKeyDown={async e => {
                        if (e.key === 'Enter' && formData.wallCommentContent?.trim()) {
                          await api.post(`/wall/${post.id}/comment`, { content: formData.wallCommentContent });
                          setFormData({ ...formData, wallCommentContent: '', wallCommentOpen: null }); loadTabData();
                        }
                      }} />
                      <button className="btn btn-primary btn-sm" onClick={async () => {
                        if (!formData.wallCommentContent?.trim()) return;
                        await api.post(`/wall/${post.id}/comment`, { content: formData.wallCommentContent });
                        setFormData({ ...formData, wallCommentContent: '', wallCommentOpen: null }); loadTabData();
                      }}><FiSend size={12} /></button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* TICKETS */}
          {tab === 'tickets' && (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
                <h3>Tickets</h3>
                <div style={{ display: 'flex', gap: '8px' }}>
                  {['setup', 'sales', 'checkin'].map(v => (
                    <button key={v} onClick={() => setTicketView(v)} className="btn btn-sm"
                      style={{
                        background: ticketView === v ? '#C94D5C' : 'transparent',
                        color: ticketView === v ? 'white' : 'var(--gray)',
                        border: `1px solid ${ticketView === v ? '#C94D5C' : '#E0E0E0'}`,
                        fontWeight: ticketView === v ? 600 : 400,
                        textTransform: 'capitalize'
                      }}
                    >{v === 'checkin' ? 'Check-in' : v}</button>
                  ))}
                </div>
              </div>

              {/* SETUP VIEW */}
              {ticketView === 'setup' && (
                <div>
                  {/* Toggle */}
                  <div className="card" style={{ padding: '20px', marginBottom: '16px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div>
                        <div style={{ fontWeight: 600, color: 'var(--dark)' }}>Ticket Sales</div>
                        <div style={{ fontSize: '13px', color: 'var(--gray)', marginTop: '4px' }}>
                          {ticketsEnabled ? 'Guests can purchase tickets for your event' : 'Enable to start selling paid tickets'}
                        </div>
                      </div>
                      <button onClick={async () => {
                        const newVal = !ticketsEnabled;
                        await api.put(`/events/${id}`, { tickets_enabled: newVal });
                        setTicketsEnabled(newVal);
                      }} style={{
                        width: '48px', height: '26px', borderRadius: '13px', border: 'none',
                        background: ticketsEnabled ? '#10B981' : '#E0E0E0', cursor: 'pointer',
                        position: 'relative', transition: 'background 0.2s'
                      }}>
                        <div style={{
                          width: '20px', height: '20px', borderRadius: '50%', background: 'white',
                          position: 'absolute', top: '3px', left: ticketsEnabled ? '25px' : '3px',
                          transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                        }} />
                      </button>
                    </div>
                  </div>

                  {/* Closing dates */}
                  <div className="card" style={{ padding: '20px', marginBottom: '16px' }}>
                    <div style={{ fontWeight: 600, color: 'var(--dark)', marginBottom: '4px' }}>Closing Dates (Optional)</div>
                    <div style={{ fontSize: '13px', color: 'var(--gray)', marginBottom: '16px' }}>
                      Set deadlines for RSVPs and ticket sales. Leave empty for no deadline.
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '14px' }}>
                      <div>
                        <label style={{ fontSize: '12px', fontWeight: 600, marginBottom: '4px', display: 'block' }}>RSVP Deadline</label>
                        <input type="date" className="form-control" value={rsvpDeadline ? rsvpDeadline.slice(0, 10) : ''}
                          onChange={e => {
                            const val = e.target.value ? e.target.value + 'T23:59:59.000Z' : '';
                            setRsvpDeadline(val);
                            api.put(`/events/${id}`, { rsvp_deadline: val || null });
                          }}
                          style={{ fontSize: '14px' }} />
                      </div>
                      <div>
                        <label style={{ fontSize: '12px', fontWeight: 600, marginBottom: '4px', display: 'block' }}>Ticket Sales Deadline</label>
                        <input type="date" className="form-control" value={ticketDeadline ? ticketDeadline.slice(0, 10) : ''}
                          onChange={e => {
                            const val = e.target.value ? e.target.value + 'T23:59:59.000Z' : '';
                            setTicketDeadline(val);
                            api.put(`/events/${id}`, { ticket_deadline: val || null });
                          }}
                          style={{ fontSize: '14px' }} />
                      </div>
                    </div>
                  </div>

                  {ticketsEnabled && (
                    <>
                      {/* Share link */}
                      <div className="card" style={{ padding: '16px', marginBottom: '16px', background: '#C94D5C08', border: '1px solid #C94D5C20' }}>
                        <div style={{ fontSize: '12px', fontWeight: 600, color: '#C94D5C', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '1px' }}>Ticket Purchase Link</div>
                        {invitations.length > 0 && invitations[0].share_link && invitations[0].share_link !== 'null' ? (
                          <>
                            <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '8px' }}>
                              <code style={{ flex: 1, padding: '10px', background: '#1A1A2E', borderRadius: '8px', color: '#E8A0BF', fontSize: '13px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {`${window.location.origin}/buy-tickets/${(event?.title || 'event').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')}/${invitations[0].share_link}`}
                              </code>
                              <button className="btn btn-sm" style={{ background: '#C94D5C', color: 'white', border: 'none', whiteSpace: 'nowrap' }}
                                onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/buy-tickets/${(event?.title || 'event').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')}/${invitations[0].share_link}`); showToast('Link copied!'); }}>
                                <FiCopy size={14} /> Copy
                              </button>
                            </div>
                            <div style={{ display: 'flex', gap: '8px' }}>
                              <button className="btn btn-sm" style={{ background: 'linear-gradient(135deg, #25D366, #128C7E)', color: 'white', border: 'none', whiteSpace: 'nowrap' }}
                                onClick={() => {
                                  const link = `${window.location.origin}/buy-tickets/${(event?.title || 'event').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')}/${invitations[0].share_link}`;
                                  const text = encodeURIComponent(`Get your tickets for ${event?.title || 'our event'}! 🎫\n\n${link}\n\nSee you there!`);
                                  window.open(`https://wa.me/?text=${text}`, '_blank');
                                }}>
                                <FiShare2 size={14} /> Share via WhatsApp
                              </button>
                              <button className="btn btn-sm" style={{ background: '#1A1A2E', color: '#E8A0BF', border: '1px solid #E8A0BF33', whiteSpace: 'nowrap' }}
                                onClick={() => {
                                  const link = `${window.location.origin}/buy-tickets/${(event?.title || 'event').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')}/${invitations[0].share_link}`;
                                  const subject = encodeURIComponent(`Get your tickets for ${event?.title || 'our event'}!`);
                                  const body = encodeURIComponent(`Hey!\n\nGet your tickets here: ${link}\n\nSee you there! 🎫`);
                                  window.open(`mailto:?subject=${subject}&body=${body}`, '_blank');
                                }}>
                                <FiSend size={14} /> Email
                              </button>
                            </div>
                          </>
                        ) : (
                          <div style={{ padding: '12px', background: '#1A1A2E', borderRadius: '8px', color: '#FFB347', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <FiInfo size={16} />
                            Create an invitation first to get a ticket link. Go to the <b>Invites</b> tab to set up your invitation.
                          </div>
                        )}
                      </div>

                      {/* Tier list */}
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                        <h4 style={{ margin: 0 }}>Ticket Tiers ({ticketTiers.length})</h4>
                        <button className="btn btn-primary btn-sm" onClick={() => { setEditingTier(null); setTierForm({ name: '', price: '', quantity: '', description: '', color: '#E3425C', target_count: '', notional_price: '' }); setShowTierForm(true); }}>
                          <FiPlus size={14} /> Add Tier
                        </button>
                      </div>

                      {ticketTiers.length === 0 ? (
                        <div className="card" style={{ textAlign: 'center', padding: '40px', color: 'var(--gray)' }}>
                          <IoTicketOutline size={32} style={{ marginBottom: '12px', opacity: 0.4 }} />
                          <p>No ticket tiers yet. Create your first tier to start selling.</p>
                        </div>
                      ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                          {ticketTiers.map(tier => {
                            const remaining = tier.quantity !== null ? tier.quantity - tier.sold : null;
                            const soldOut = remaining !== null && remaining <= 0;
                            return (
                              <div key={tier.id} className="card" style={{ padding: '20px', opacity: soldOut ? 0.6 : 1 }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px' }}>
                                  <div style={{ flex: 1 }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                                      <div style={{ width: '12px', height: '12px', borderRadius: '50%', background: tier.color, flexShrink: 0 }} />
                                      <span style={{ fontWeight: 600, color: 'var(--dark)', fontSize: '16px' }}>{tier.name}</span>
                                      {tier.visible === false && <span className="tag" style={{ background: '#F59E0B20', color: '#F59E0B', fontSize: '10px' }}>Hidden</span>}
                                    </div>
                                    <div style={{ fontSize: '20px', fontWeight: 700, color: '#C94D5C' }}>
                                      R{Number(tier.price).toFixed(2)}
                                      {Number(tier.price) <= 0 && <span style={{ fontSize: '11px', fontWeight: 400, color: 'var(--gray)', marginLeft: '8px' }}>Free</span>}
                                    </div>
                                    {tier.description && <div style={{ fontSize: '13px', color: 'var(--gray)', marginTop: '4px' }}>{tier.description}</div>}
                                    {/* Free tiers are immediately claimable — no upfront fee required */}
                                    <div style={{ display: 'flex', gap: '16px', marginTop: '10px', fontSize: '12px', color: 'var(--gray)' }}>
                                      <span>Sold: <strong style={{ color: 'var(--dark)' }}>{tier.sold}</strong></span>
                                      <span>Available: <strong style={{ color: soldOut ? '#E3425C' : 'var(--dark)' }}>{remaining !== null ? remaining : 'Unlimited'}</strong></span>
                                    </div>
                                    {tier.quantity !== null && (
                                      <div style={{ marginTop: '8px', height: '6px', borderRadius: '3px', background: '#E0E0E0', overflow: 'hidden' }}>
                                        <div style={{ height: '100%', borderRadius: '3px', background: soldOut ? '#E3425C' : tier.color, width: `${Math.min(100, (tier.sold / tier.quantity) * 100)}%`, transition: 'width 0.3s' }} />
                                      </div>
                                    )}
                                  </div>
                                  <div style={{ display: 'flex', gap: '6px' }}>
                                    <button className="btn btn-sm" style={{ border: '1px solid #E0E0E0', background: 'transparent', color: 'var(--gray)' }}
                                      onClick={() => { setEditingTier(tier); setTierForm({ name: tier.name, price: String(tier.price), quantity: tier.quantity !== null ? String(tier.quantity) : '', description: tier.description || '', color: tier.color, target_count: tier.target_count ? String(tier.target_count) : '', notional_price: tier.notional_price ? String(tier.notional_price) : '' }); setShowTierForm(true); }}>
                                      <FiEdit2 size={12} />
                                    </button>
                                    <button className="btn btn-sm" style={{ border: '1px solid #E0E0E0', background: 'transparent', color: tier.sold > 0 ? 'var(--gray)' : '#E3425C' }}
                                      disabled={tier.sold > 0}
                                      onClick={async () => {
                                        if (!window.confirm(`Delete "${tier.name}" tier?`)) return;
                                        await api.delete(`/ticket-tiers/${tier.id}`);
                                        setTicketTiers(prev => prev.filter(t => t.id !== tier.id));
                                      }}>
                                      <FiTrash2 size={12} />
                                    </button>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}

                      {/* Tier form modal */}
                      {showTierForm && (
                        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' }} onClick={e => e.target === e.currentTarget && setShowTierForm(false)}>
                          <div className="card" style={{ width: '100%', maxWidth: '460px', padding: '28px' }} onClick={e => e.stopPropagation()}>
                            <h4 style={{ marginBottom: '20px' }}>{editingTier ? 'Edit Tier' : 'New Ticket Tier'}</h4>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                              <div>
                                <label style={{ fontSize: '12px', fontWeight: 600, marginBottom: '4px', display: 'block' }}>Name</label>
                                <input type="text" className="form-control" value={tierForm.name} onChange={e => setTierForm(prev => ({ ...prev, name: e.target.value }))} placeholder="e.g. General Admission" />
                              </div>
                              <div>
                                <label style={{ fontSize: '12px', fontWeight: 600, marginBottom: '4px', display: 'block' }}>Price (R)</label>
                                <input type="number" className="form-control" value={tierForm.price} onChange={e => setTierForm(prev => ({ ...prev, price: e.target.value }))} placeholder="0 for free" min="0" step="0.01" />
                              </div>
                              <div>
                                <label style={{ fontSize: '12px', fontWeight: 600, marginBottom: '4px', display: 'block' }}>Quantity (leave empty for unlimited)</label>
                                <input type="number" className="form-control" value={tierForm.quantity} onChange={e => setTierForm(prev => ({ ...prev, quantity: e.target.value }))} placeholder="Unlimited" min="0" />
                              </div>
                              <div>
                                <label style={{ fontSize: '12px', fontWeight: 600, marginBottom: '4px', display: 'block' }}>Description</label>
                                <input type="text" className="form-control" value={tierForm.description} onChange={e => setTierForm(prev => ({ ...prev, description: e.target.value }))} placeholder="What this tier includes..." />
                              </div>
                              {/* Free tiers are immediately claimable — no upfront fee required */}
                              <div>
                                <label style={{ fontSize: '12px', fontWeight: 600, marginBottom: '4px', display: 'block' }}>Color</label>
                                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                                  {['#E3425C', '#8B2252', '#C97191', '#E8A0BF', '#10B981', '#3B82F6', '#8B5CF6', '#F59E0B', '#EC4899', '#1A1A2E'].map(c => (
                                    <div key={c} onClick={() => setTierForm(prev => ({ ...prev, color: c }))} style={{
                                      width: '32px', height: '32px', borderRadius: '50%', background: c, cursor: 'pointer',
                                      border: tierForm.color === c ? '3px solid var(--dark)' : '3px solid transparent',
                                      boxShadow: tierForm.color === c ? '0 0 0 2px white' : 'none'
                                    }} />
                                  ))}
                                </div>
                              </div>
                            </div>
                            <div style={{ display: 'flex', gap: '10px', marginTop: '24px' }}>
                              <button className="btn" style={{ flex: 1, background: '#E0E0E0', color: 'var(--dark)' }} onClick={() => setShowTierForm(false)}>Cancel</button>
                              <button className="btn btn-primary" style={{ flex: 1 }} onClick={async () => {
                                if (!tierForm.name.trim()) return alert('Tier name is required');
                                const body = {
                                  name: tierForm.name.trim(),
                                  price: parseFloat(tierForm.price) || 0,
                                  quantity: tierForm.quantity ? parseInt(tierForm.quantity) : null,
                                  description: tierForm.description,
                                  color: tierForm.color,
                                  target_count: tierForm.target_count ? parseInt(tierForm.target_count) : null,
                                  notional_price: tierForm.notional_price ? parseFloat(tierForm.notional_price) : null
                                };
                                if (editingTier) {
                                  const r = await api.put(`/ticket-tiers/${editingTier.id}`, body);
                                  setTicketTiers(prev => prev.map(t => t.id === editingTier.id ? r.data : t));
                                } else {
                                  const r = await api.post(`/events/${id}/ticket-tiers`, body);
                                  setTicketTiers(prev => [...prev, r.data]);
                                }
                                setShowTierForm(false);
                              }}>{editingTier ? 'Update' : 'Create'}</button>
                            </div>
                          </div>
                        </div>
                      )}
                    </>
                  )}

                  {!ticketsEnabled && (
                    <div className="card" style={{ textAlign: 'center', padding: '60px 40px', color: 'var(--gray)' }}>
                      <IoTicketOutline size={48} style={{ marginBottom: '16px', opacity: 0.3 }} />
                      <h4 style={{ color: 'var(--dark)', marginBottom: '8px' }}>Start Selling Tickets</h4>
                      <p style={{ marginBottom: '20px', maxWidth: '400px', marginLeft: 'auto', marginRight: 'auto' }}>
                        Create paid or free ticket tiers. Guests buy through a simple page. You keep ~97% of ticket price — GGR takes 3% and buyers pay a R3 booking fee per ticket.
                      </p>
                      <button className="btn btn-primary" onClick={async () => {
                        await api.put(`/events/${id}`, { tickets_enabled: true });
                        setTicketsEnabled(true);
                      }}>Enable Ticket Sales</button>
                    </div>
                  )}
                </div>
              )}

              {/* SALES VIEW */}
              {ticketView === 'sales' && (
                <div>
                  {ticketSales ? (
                    <>
                      {/* Summary cards */}
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '12px', marginBottom: '20px' }}>
                        {[
                          { icon: <FiDollarSign size={16} />, label: 'Total Revenue', value: `R${Number(ticketSales.total_revenue).toFixed(2)}`, color: '#10B981', sub: `${ticketSales.total_sold} ticket${ticketSales.total_sold !== 1 ? 's' : ''} sold` },
                          { icon: <FiTrendingUp size={16} />, label: 'GGR Commission (3% + R3/ticket)', value: `R${Number(ticketSales.total_commission).toFixed(2)}`, color: '#C94D5C', sub: 'Platform + buyer booking fee' },
                          { icon: <FiCreditCard size={16} />, label: 'Your Payout (~97%)', value: `R${Number(ticketSales.total_payout).toFixed(2)}`, color: '#3B82F6', sub: 'Credited to wallet' },
                          { icon: <IoTicketOutline size={16} />, label: 'Tickets Sold', value: ticketSales.total_sold, color: '#8B5CF6', sub: `${ticketSales.total_revenue > 0 ? 'Avg R' + (ticketSales.total_revenue / ticketSales.total_sold).toFixed(0) : '—'} per ticket` },
                        ].map((card, i) => (
                          <div key={i} className="card" style={{ padding: '16px', textAlign: 'center' }}>
                            <div style={{ marginBottom: '8px', color: card.color, display: 'flex', justifyContent: 'center' }}>{card.icon}</div>
                            <div style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '1px', color: 'var(--gray)', marginBottom: '6px' }}>{card.label}</div>
                            <div style={{ fontSize: '22px', fontWeight: 700, color: card.color }}>{card.value}</div>
                            {card.sub && <div style={{ fontSize: '11px', color: 'var(--gray)', marginTop: '4px' }}>{card.sub}</div>}
                          </div>
                        ))}
                      </div>

                      {/* Commission breakdown note */}
                      <div className="card" style={{ padding: '14px 18px', marginBottom: '20px', background: '#FDF2F8', border: '1px solid #F9D4E3', borderRadius: '12px', display: 'flex', alignItems: 'center', gap: '12px', fontSize: '13px' }}>
                        <span style={{ fontSize: '20px' }}>💡</span>
                        <div>
                          <strong>How it works:</strong> GGR takes 3% commission on ticket price + a R3 buyer-paid booking fee per ticket.
                          Of R{Number(ticketSales.total_revenue).toFixed(2)} total ticket sales, <span style={{ color: '#C94D5C', fontWeight: 600 }}>R{Number(ticketSales.total_commission).toFixed(2)} goes to GGR</span> (commission + booking fees) and{' '}
                          <span style={{ color: '#3B82F6', fontWeight: 600 }}>R{Number(ticketSales.total_payout).toFixed(2)} is credited to your wallet</span>.
                        </div>
                      </div>

                      {/* Per-tier breakdown */}
                      {ticketSales.tiers && ticketSales.tiers.length > 0 && (
                        <div style={{ marginBottom: '20px' }}>
                          <h4 style={{ marginBottom: '12px' }}>By Tier</h4>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                            {ticketSales.tiers.map(tier => (
                              <div key={tier.id} className="card" style={{ padding: '16px' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                  <div>
                                    <div style={{ fontWeight: 600, color: 'var(--dark)' }}>{tier.name}</div>
                                    <div style={{ fontSize: '12px', color: 'var(--gray)' }}>R{Number(tier.price).toFixed(2)} each</div>
                                  </div>
                                  <div style={{ textAlign: 'right' }}>
                                    <div style={{ fontWeight: 600, color: '#10B981' }}>R{Number(tier.revenue).toFixed(2)}</div>
                                    <div style={{ fontSize: '12px', color: 'var(--gray)' }}>{tier.sold} sold{tier.quantity ? ` / ${tier.quantity}` : ''}</div>
                                  </div>
                                </div>
                                {tier.quantity && (
                                  <div style={{ marginTop: '8px', height: '4px', borderRadius: '2px', background: '#E0E0E0' }}>
                                    <div style={{ height: '100%', borderRadius: '2px', background: '#10B981', width: `${Math.min(100, (tier.sold / tier.quantity) * 100)}%` }} />
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Recent transactions */}
                      {ticketSales.recent && ticketSales.recent.length > 0 && (
                        <div>
                          <h4 style={{ marginBottom: '12px' }}>Recent Sales</h4>
                          <div className="card" style={{ overflow: 'hidden' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                              <thead>
                                <tr style={{ background: '#F5F5F6' }}>
                                  <th style={{ padding: '10px 16px', textAlign: 'left', fontWeight: 600 }}>Buyer</th>
                                  <th style={{ padding: '10px 16px', textAlign: 'left', fontWeight: 600 }}>Tier</th>
                                  <th style={{ padding: '10px 16px', textAlign: 'right', fontWeight: 600 }}>Amount</th>
                                  <th style={{ padding: '10px 16px', textAlign: 'right', fontWeight: 600 }}>Date</th>
                                </tr>
                              </thead>
                              <tbody>
                                {ticketSales.recent.map((row, i) => (
                                  <tr key={i} style={{ borderTop: '1px solid #F1F1F2' }}>
                                    <td style={{ padding: '10px 16px', color: 'var(--dark)', fontWeight: 500 }}>{row.buyer_name || '-'}</td>
                                    <td style={{ padding: '10px 16px' }}>{row.tier_name}</td>
                                    <td style={{ padding: '10px 16px', textAlign: 'right', fontWeight: 500 }}>R{Number(row.price_paid).toFixed(2)}</td>
                                    <td style={{ padding: '10px 16px', textAlign: 'right', color: 'var(--gray)', fontSize: '12px' }}>{new Date(row.created_at).toLocaleDateString('en-ZA', { day: 'numeric', month: 'short' })}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="card" style={{ textAlign: 'center', padding: '40px', color: 'var(--gray)' }}>
                      <IoTicketOutline size={32} style={{ marginBottom: '12px', opacity: 0.4 }} />
                      <p>{ticketsEnabled ? 'No sales data yet. Sales will appear here once tickets are purchased.' : 'Enable ticket sales above to get started.'}</p>
                    </div>
                  )}
                </div>
              )}

              {/* CHECK-IN VIEW */}
              {ticketView === 'checkin' && (
                <div>
                  {/* Search + Scan */}
                  <div style={{ display: 'flex', gap: '10px', marginBottom: '16px' }}>
                    <input type="text" className="form-control" placeholder="Search by name or ticket code..." value={checkinSearch} onChange={e => setCheckinSearch(e.target.value)}
                      style={{ padding: '12px 16px', borderRadius: '12px', flex: 1 }} />
                    <button className="btn btn-sm" onClick={() => {
                      setQrScanning(true);
                      // Start scanner after mount
                      setTimeout(async () => {
                        try {
                          const ref = document.getElementById('qr-reader');
                          if (!ref || qrScannerRef.current) return;
                          const scanner = new Html5Qrcode('qr-reader');
                          qrScannerRef.current = scanner;
                          await scanner.start(
                            { facingMode: 'environment' },
                            { fps: 10, qrbox: { width: 250, height: 250 } },
                            async (decoded) => {
                              // Extract ticket code from URL
                              let ticketCode = decoded;
                              try {
                                const url = new URL(decoded);
                                const parts = url.pathname.split('/');
                                ticketCode = parts[parts.length - 1];
                              } catch {}
                              // Stop scanner
                              await scanner.stop();
                              setQrScanning(false);
                              qrScannerRef.current = null;
                              // Call check-in
                              try {
                                await api.put(`/tickets/${ticketCode}/checkin`);
                                setScanResult({ success: true, message: `Checked in! Code: ${ticketCode}` });
                                setTickets(prev => prev.map(t => t.ticket_code === ticketCode ? { ...t, checked_in: true, checked_in_at: new Date().toISOString() } : t));
                              } catch (err) {
                                setScanResult({ success: false, message: err.response?.data?.error || 'Check-in failed. Invalid ticket?' });
                              }
                              setTimeout(() => setScanResult(null), 4000);
                            },
                            () => {} // scan error
                          ).catch(err => {
                            console.log('QR scan start error:', err);
                            if (scanner.isScanning) { scanner.stop().catch(() => {}); }
                            setQrScanning(false);
                            qrScannerRef.current = null;
                          });
                        } catch {}
                      }, 100);
                    }} style={{ background: 'var(--dark)', color: 'white', whiteSpace: 'nowrap', borderRadius: '12px', padding: '0 16px', display: 'flex', alignItems: 'center', gap: '6px', fontFamily: 'Inter, sans-serif' }}>
                      <FiCamera size={14} /> Scan QR
                    </button>
                  </div>

                  {/* QR Scanner modal */}
                  {qrScanning && (
                    <div style={{ position: 'relative', marginBottom: '16px', borderRadius: '16px', overflow: 'hidden', background: '#000', maxWidth: '400px', margin: '0 auto 16px' }}>
                      <div id="qr-reader" style={{ width: '100%' }} />
                      <button onClick={async () => {
                        if (qrScannerRef.current) {
                          await qrScannerRef.current.stop().catch(() => {});
                          qrScannerRef.current = null;
                        }
                        setQrScanning(false);
                      }} style={{ position: 'absolute', top: '8px', right: '8px', background: 'rgba(0,0,0,0.7)', color: 'white', border: 'none', borderRadius: '50%', width: '32px', height: '32px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10 }}>
                        <FiX size={18} />
                      </button>
                    </div>
                  )}

                  {/* Scan result */}
                  {scanResult && (
                    <div className="card" style={{
                      padding: '16px', marginBottom: '16px',
                      background: scanResult.success ? '#10B98110' : '#E3425C10',
                      border: `1px solid ${scanResult.success ? '#10B98130' : '#E3425C30'}`,
                      animation: 'fadeIn 0.3s ease'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        {scanResult.success ? <FiCheckCircle size={20} color="#10B981" /> : <FiX size={20} color="#E3425C" />}
                        <div>
                          <div style={{ fontWeight: 600, color: scanResult.success ? '#10B981' : '#E3425C' }}>{scanResult.message}</div>
                          {scanResult.guest_name && <div style={{ fontSize: '13px', color: 'var(--gray)' }}>{scanResult.guest_name} — {scanResult.tier_name || 'General'}</div>}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Ticket list */}
                  <div className="card" style={{ overflow: 'hidden' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                      <thead>
                        <tr style={{ background: '#F5F5F6' }}>
                          <th style={{ padding: '10px 16px', textAlign: 'left', fontWeight: 600 }}>Guest</th>
                          <th style={{ padding: '10px 16px', textAlign: 'left', fontWeight: 600 }}>Ticket Code</th>
                          <th style={{ padding: '10px 16px', textAlign: 'left', fontWeight: 600 }}>Tier</th>
                          <th style={{ padding: '10px 16px', textAlign: 'center', fontWeight: 600 }}>Status</th>
                          <th style={{ padding: '10px 16px', textAlign: 'right', fontWeight: 600 }}>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {tickets.filter(t => {
                          if (!checkinSearch) return true;
                          const s = checkinSearch.toLowerCase();
                          return (t.guest_name || '').toLowerCase().includes(s) || (t.ticket_code || '').toLowerCase().includes(s);
                        }).map(ticket => (
                          <tr key={ticket.id} style={{ borderTop: '1px solid #F1F1F2', background: ticket.checked_in ? '#10B98105' : 'transparent' }}>
                            <td style={{ padding: '10px 16px', color: 'var(--dark)', fontWeight: 500 }}>{ticket.guest_name || '-'}</td>
                            <td style={{ padding: '10px 16px', fontFamily: "'Courier New', monospace", fontSize: '12px' }}>{ticket.ticket_code}</td>
                            <td style={{ padding: '10px 16px' }}>
                              {ticket.tier_name ? (
                                <span className="tag" style={{ background: `${ticket.tier_color || '#E3425C'}20`, color: ticket.tier_color || '#E3425C', fontSize: '11px' }}>{ticket.tier_name}</span>
                              ) : <span style={{ color: 'var(--gray)', fontSize: '12px' }}>Free</span>}
                            </td>
                            <td style={{ padding: '10px 16px', textAlign: 'center' }}>
                              {ticket.checked_in ? (
                                <span style={{ color: '#10B981', fontWeight: 600, fontSize: '12px' }}><FiCheck size={12} /> Checked In</span>
                              ) : (
                                <span style={{ color: 'var(--gray)', fontSize: '12px' }}>Pending</span>
                              )}
                            </td>
                            <td style={{ padding: '10px 16px', textAlign: 'right' }}>
                              {!ticket.checked_in ? (
                                <button className="btn btn-sm" style={{ background: '#10B981', color: 'white', border: 'none' }}
                                  onClick={async () => {
                                    await api.put(`/tickets/${ticket.ticket_code}/checkin`);
                                    setTickets(prev => prev.map(t => t.id === ticket.id ? { ...t, checked_in: true, checked_in_at: new Date().toISOString() } : t));
                                    setScanResult({ success: true, message: `Checked in!`, guest_name: ticket.guest_name, tier_name: ticket.tier_name });
                                    setTimeout(() => setScanResult(null), 4000);
                                  }}>
                                  <FiCheck size={14} /> Check In
                                </button>
                              ) : (
                                <button className="btn btn-sm" style={{ background: 'transparent', border: '1px solid #E0E0E0', color: 'var(--gray)' }}
                                  onClick={async () => {
                                    await api.put(`/tickets/${ticket.ticket_code}/undo-checkin`);
                                    setTickets(prev => prev.map(t => t.id === ticket.id ? { ...t, checked_in: false, checked_in_at: null } : t));
                                  }}>
                                  Undo
                                </button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {tickets.length === 0 && (
                      <div style={{ textAlign: 'center', padding: '40px', color: 'var(--gray)' }}>
                        <IoTicketOutline size={32} style={{ marginBottom: '12px', opacity: 0.4 }} />
                        <p>No tickets yet. {ticketsEnabled ? 'Tickets will appear here once purchased.' : 'Enable ticket sales to get started.'}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* DELEGATION */}
          {/* SPEAKERS */}
          {tab === 'speakers' && (
            <div>
              {/* SPEAKERS SECTION */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '8px' }}>
                <h3>Speakers</h3>
                <button className="btn btn-primary btn-sm" onClick={() => { setFormData({ name: '', title: '', bio: '', photoFile: null }); setShowModal('speaker'); }}><FiPlus /> Add Speaker</button>
              </div>
              {speakers.length === 0 ? (
                <div className="card" style={{ textAlign: 'center', padding: '30px', color: 'var(--gray)' }}>
                  <FiMic size={30} style={{ marginBottom: '10px', opacity: 0.4 }} />
                  <p>No speakers yet. Add speakers, MCs, or keynote presenters.</p>
                </div>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '16px' }}>
                  {speakers.map(s => (
                    <div key={s.id} className="card" style={{ padding: 0, overflow: 'hidden' }}>
                      <div style={{ display: 'flex', gap: '16px', padding: '20px' }}>
                        <div style={{ width: '80px', height: '80px', borderRadius: '50%', overflow: 'hidden', flexShrink: 0, background: 'linear-gradient(135deg, var(--rose-light), var(--rose-deep))' }}>
                          {s.photo ? <img src={s.photo} alt={s.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: '28px', fontWeight: 700 }}>{s.name[0]}</div>}
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <h4 style={{ fontSize: '16px', marginBottom: '2px' }}>{s.name}</h4>
                          {s.title && <span className="tag tag-gold" style={{ marginBottom: '8px', display: 'inline-block' }}>{s.title}</span>}
                          {s.bio && <p style={{ fontSize: '13px', color: 'var(--gray)', lineHeight: 1.5, marginTop: '6px' }}>{s.bio}</p>}
                        </div>
                      </div>
                      <div style={{ padding: '0 20px 16px', display: 'flex', gap: '8px' }}>
                        <button className="btn btn-sm btn-outline" onClick={() => { setFormData({ id: s.id, name: s.name, title: s.title || '', bio: s.bio || '', existingPhoto: s.photo, photoFile: null }); setShowModal('speaker'); }}><FiEdit size={12} /> Edit</button>
                        <button style={{ background: 'none', color: 'var(--red)', cursor: 'pointer', border: 'none', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '4px' }} onClick={async () => { await api.delete(`/events/${id}/speakers/${s.id}`); loadTabData(); }}><FiTrash2 size={12} /> Remove</button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* DELEGATES SECTION */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '32px', marginBottom: '16px', paddingTop: '24px', borderTop: '1px solid var(--border)', flexWrap: 'wrap', gap: '8px' }}>
                <h3>Delegates</h3>
                <button className="btn btn-primary btn-sm" onClick={() => { setFormData({ delegate_name: '', delegate_email: '', role: '', task: '', bio: '' }); setShowModal('delegation'); }}><FiPlus /> Add Delegate</button>
              </div>
              {delegations.length === 0 ? (
                <div className="card" style={{ textAlign: 'center', padding: '30px', color: 'var(--gray)' }}>
                  <FiUserCheck size={30} style={{ marginBottom: '10px', opacity: 0.4 }} />
                  <p>No delegates yet. Add family members, MCs, coordinators, or anyone involved in the event.</p>
                </div>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '16px' }}>
                  {delegations.map(d => (
                    <div key={d.id} className="card" style={{ textAlign: 'center', padding: '24px' }}>
                      <div style={{ width: '80px', height: '80px', borderRadius: '50%', overflow: 'hidden', margin: '0 auto 12px', border: '3px solid var(--gold-light)', background: 'var(--cream)' }}>
                        {d.photo ? <img src={d.photo} alt={d.delegate_name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '28px', fontWeight: 700, color: 'var(--gold-dark)' }}>{d.delegate_name[0]}</div>}
                      </div>
                      <h4 style={{ fontSize: '16px', marginBottom: '4px' }}>{d.delegate_name}</h4>
                      <span className="tag tag-gold" style={{ marginBottom: '8px', display: 'inline-block' }}>{d.role}</span>
                      {d.bio && <p style={{ fontSize: '13px', color: 'var(--gray)', marginBottom: '8px', lineHeight: 1.5 }}>{d.bio}</p>}
                      {d.delegate_email && <div style={{ fontSize: '12px', color: 'var(--gray)', marginBottom: '8px' }}>{d.delegate_email}</div>}
                      {d.task && <div style={{ fontSize: '13px', padding: '10px', background: 'var(--cream)', borderRadius: '8px', marginBottom: '8px', textAlign: 'left' }}>{d.task}</div>}
                      <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginTop: '8px' }}>
                        <select value={d.status} onChange={async (e) => { await api.put(`/events/${id}/delegations/${d.id}`, { status: e.target.value }); loadTabData(); }} style={{ width: 'auto', padding: '4px 8px', fontSize: '12px' }}>
                          <option value="pending">Pending</option>
                          <option value="in_progress">In Progress</option>
                          <option value="completed">Completed</option>
                        </select>
                        <button style={{ background: 'none', color: 'var(--red)', cursor: 'pointer', border: 'none', fontSize: '13px' }} onClick={async () => { await api.delete(`/events/${id}/delegations/${d.id}`); loadTabData(); }}><FiTrash2 size={12} /></button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* SEATING ARRANGEMENT */}
          {tab === 'seating' && (hostPlan === 'free' ? (
            <div className="card" style={{ textAlign: 'center', padding: '60px 24px' }}>
              <div style={{ width: '64px', height: '64px', margin: '0 auto 20px', borderRadius: '50%', background: 'rgba(255,107,107,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <FiMapPin size={28} style={{ color: '#FF6B6B', opacity: 0.6 }} />
              </div>
              <h3 style={{ fontSize: '18px', fontWeight: 700, marginBottom: '8px', color: 'var(--text)' }}>Seating Planner Locked</h3>
              <p style={{ fontSize: '14px', color: 'var(--gray)', marginBottom: '24px', maxWidth: '400px', margin: '0 auto 24px' }}>
                Upgrade to Standard or Premium to arrange table seating for your guests.
              </p>
              <Link to="/upgrade?type=host" style={{
                display: 'inline-flex', alignItems: 'center', gap: '8px',
                padding: '12px 28px', borderRadius: '12px',
                background: 'linear-gradient(135deg, var(--rose-deep), #b13d4e)', color: 'white',
                fontSize: '14px', fontWeight: 700, fontFamily: 'Inter, sans-serif',
                textDecoration: 'none',
              }}><FiZap size={16} /> View Plans</Link>
            </div>
          ) : (() => {
            const assignedGuestIds = new Set(tables.flatMap(t => t.guests.map(g => g.guest_id)));
            const unseatedGuests = guests.filter(g => !assignedGuestIds.has(g.id) && (g.rsvp_status === 'attending' || g.rsvp_status === 'maybe'));
            const filteredUnseated = seatingSearch ? unseatedGuests.filter(g => g.name.toLowerCase().includes(seatingSearch.toLowerCase())) : unseatedGuests;
            const totalSeats = tables.reduce((a, t) => a + t.capacity, 0);
            const totalSeated = tables.reduce((a, t) => a + t.guests.length, 0);

            const addTable = async () => {
              try {
                const num = tables.length + 1;
                const r = await api.post(`/events/${id}/tables`, { table_name: `Table ${num}`, capacity: 8, table_shape: 'round' });
                setTables([...tables, r.data]);
                showToast('Table added!');
              } catch { showToast('Failed', 'error'); }
            };
            const deleteTable = async (tid) => {
              try {
                await api.delete(`/events/${id}/tables/${tid}`);
                setTables(tables.filter(t => t.id !== tid));
                showToast('Table removed');
              } catch { showToast('Failed', 'error'); }
            };
            const updateTable = async (tid, data) => {
              try {
                const r = await api.put(`/events/${id}/tables/${tid}`, data);
                setTables(tables.map(t => t.id === tid ? r.data : t));
              } catch { showToast('Failed', 'error'); }
            };
            const assignGuest = async (tableId, guestId) => {
              try {
                const r = await api.post(`/events/${id}/tables/${tableId}/assign`, { guest_id: guestId });
                setTables(tables.map(t => t.id === tableId ? { ...t, guests: [...t.guests, r.data] } : t.guests.some(g => g.guest_id === guestId) ? { ...t, guests: t.guests.filter(g => g.guest_id !== guestId) } : t));
              } catch (e) { showToast(e.response?.data?.error || 'Failed', 'error'); }
            };
            const unassignGuest = async (tableId, guestId) => {
              try {
                await api.delete(`/events/${id}/tables/${tableId}/unassign/${guestId}`);
                setTables(tables.map(t => t.id === tableId ? { ...t, guests: t.guests.filter(g => g.guest_id !== guestId) } : t));
              } catch { showToast('Failed', 'error'); }
            };

            return (
            <div>
              {/* Header stats */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
                <div>
                  <h3 style={{ marginBottom: '4px' }}>Seating Arrangement</h3>
                  <p style={{ fontSize: '13px', color: 'var(--gray)' }}>
                    {tables.length} tables · {totalSeats} seats · {totalSeated} seated · {unseatedGuests.length} unseated
                  </p>
                </div>
                <button className="btn btn-primary btn-sm" onClick={addTable}><FiPlus /> Add Table</button>
              </div>

              {/* Progress bar */}
              {totalSeats > 0 && (
                <div style={{ marginBottom: '20px', background: 'var(--cream)', borderRadius: '8px', overflow: 'hidden', height: '8px' }}>
                  <div style={{ width: `${Math.min(100, (totalSeated / totalSeats) * 100)}%`, height: '100%', background: totalSeated === totalSeats ? 'var(--green, #22C55E)' : '#E3425C', borderRadius: '8px', transition: 'width 0.3s' }} />
                </div>
              )}

              {tables.length === 0 ? (
                <div className="card" style={{ textAlign: 'center', padding: '60px' }}>
                  <FiMapPin size={48} style={{ color: 'var(--gray)', marginBottom: '16px', opacity: 0.3 }} />
                  <h3 style={{ marginBottom: '8px' }}>No Tables Yet</h3>
                  <p style={{ color: 'var(--gray)', marginBottom: '24px', fontSize: '14px' }}>Add tables and assign your confirmed guests to their seats.</p>
                  <button className="btn btn-primary" onClick={addTable}><FiPlus /> Add Your First Table</button>
                </div>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '16px', marginBottom: '24px' }}>
                  {tables.map(table => {
                    const isFull = table.guests.length >= table.capacity;
                    return (
                    <div key={table.id} className="card" style={{ padding: '0', overflow: 'hidden' }}>
                      {/* Table header */}
                      <div style={{ padding: '14px 16px', background: isFull ? 'linear-gradient(135deg, #22C55E, #16A34A)' : 'linear-gradient(135deg, var(--dark), #2a2a2e)', color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <div style={{ width: '36px', height: '36px', borderRadius: table.table_shape === 'round' ? '50%' : '8px', border: '2px solid rgba(255,255,255,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '13px', fontWeight: 700 }}>
                            {table.guests.length}/{table.capacity}
                          </div>
                          <div>
                            <input
                              defaultValue={table.table_name}
                              onBlur={e => { if (e.target.value !== table.table_name) updateTable(table.id, { table_name: e.target.value }); }}
                              style={{ background: 'transparent', border: 'none', color: 'white', fontWeight: 600, fontSize: '14px', padding: 0, width: '120px' }}
                            />
                            <div style={{ fontSize: '10px', opacity: 0.7 }}>
                              {table.table_shape} · {table.capacity} seats
                            </div>
                          </div>
                        </div>
                        <div style={{ display: 'flex', gap: '4px' }}>
                          <select
                            value={table.capacity}
                            onChange={e => updateTable(table.id, { capacity: parseInt(e.target.value) })}
                            style={{ background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: '6px', color: 'white', fontSize: '11px', padding: '4px 6px', cursor: 'pointer' }}
                          >
                            {[4,6,8,10,12,14,16,20].map(n => <option key={n} value={n} style={{ color: '#333' }}>{n} seats</option>)}
                          </select>
                          <button onClick={() => deleteTable(table.id)} style={{ background: 'rgba(255,255,255,0.1)', border: 'none', borderRadius: '6px', padding: '4px 8px', cursor: 'pointer', color: 'rgba(255,255,255,0.7)' }}><FiTrash2 size={12} /></button>
                        </div>
                      </div>
                      {/* Seated guests */}
                      <div style={{ padding: '8px 12px', minHeight: '60px' }}>
                        {table.guests.length === 0 ? (
                          <div style={{ padding: '16px', textAlign: 'center', color: 'var(--gray)', fontSize: '12px' }}>
                            No guests assigned yet. Drag or click from the unseated list below.
                          </div>
                        ) : (
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', padding: '4px 0' }}>
                            {table.guests.map(g => (
                              <div key={g.guest_id} style={{ display: 'flex', alignItems: 'center', gap: '6px', background: 'var(--cream)', borderRadius: '20px', padding: '5px 10px 5px 12px', fontSize: '12px' }}>
                                <span style={{ fontWeight: 500 }}>{g.guest_name}</span>
                                {g.dietary_requirements && <span title={g.dietary_requirements} style={{ fontSize: '10px', background: '#FEF3C7', color: '#92400E', padding: '1px 5px', borderRadius: '4px' }}>diet</span>}
                                <button onClick={() => unassignGuest(table.id, g.guest_id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--gray)', padding: '2px', display: 'flex' }}><FiX size={12} /></button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                    );
                  })}
                </div>
              )}

              {/* Unseated guests */}
              {unseatedGuests.length > 0 && (
                <div className="card" style={{ padding: '0', overflow: 'hidden' }}>
                  <div style={{ padding: '14px 16px', borderBottom: '1px solid var(--cream-dark)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
                    <div>
                      <h4 style={{ fontSize: '14px', marginBottom: '2px' }}>Unseated Guests ({unseatedGuests.length})</h4>
                      <p style={{ fontSize: '11px', color: 'var(--gray)' }}>Click a guest to assign them to a table</p>
                    </div>
                    <div style={{ position: 'relative' }}>
                      <FiSearch size={14} style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: 'var(--gray)' }} />
                      <input value={seatingSearch} onChange={e => setSeatingSearch(e.target.value)} placeholder="Search guests..." style={{ paddingLeft: '30px', fontSize: '12px', width: '180px', borderRadius: '8px', border: '1px solid var(--cream-dark)', padding: '6px 10px 6px 30px' }} />
                    </div>
                  </div>
                  <div style={{ padding: '12px 16px', display: 'flex', flexWrap: 'wrap', gap: '6px', maxHeight: '200px', overflowY: 'auto' }}>
                    {filteredUnseated.map(g => (
                      <div key={g.id} style={{ position: 'relative' }}>
                        <div
                          style={{ display: 'flex', alignItems: 'center', gap: '6px', background: g.rsvp_status === 'attending' ? '#DCFCE7' : '#FEF9C3', borderRadius: '20px', padding: '6px 12px', fontSize: '12px', cursor: 'pointer', border: '1px solid transparent', transition: 'border-color 0.15s' }}
                          onClick={(e) => {
                            // Show a quick table picker dropdown
                            const existing = document.querySelector('.seating-picker-dropdown');
                            if (existing) existing.remove();
                            const dd = document.createElement('div');
                            dd.className = 'seating-picker-dropdown';
                            dd.style.cssText = 'position:fixed;z-index:9999;background:white;border-radius:10px;box-shadow:0 8px 30px rgba(0,0,0,0.15);padding:6px;min-width:160px;max-height:200px;overflow-y:auto;';
                            const rect = e.currentTarget.getBoundingClientRect();
                            dd.style.top = (rect.bottom + 4) + 'px';
                            dd.style.left = rect.left + 'px';
                            tables.filter(t => t.guests.length < t.capacity).forEach(t => {
                              const opt = document.createElement('div');
                              opt.textContent = `${t.table_name} (${t.guests.length}/${t.capacity})`;
                              opt.style.cssText = 'padding:8px 12px;cursor:pointer;border-radius:6px;font-size:12px;';
                              opt.onmouseenter = () => opt.style.background = 'var(--cream)';
                              opt.onmouseleave = () => opt.style.background = 'none';
                              opt.onclick = () => { assignGuest(t.id, g.id); dd.remove(); };
                              dd.appendChild(opt);
                            });
                            if (tables.filter(t => t.guests.length < t.capacity).length === 0) {
                              const msg = document.createElement('div');
                              msg.textContent = 'All tables are full';
                              msg.style.cssText = 'padding:8px 12px;font-size:12px;color:var(--gray);';
                              dd.appendChild(msg);
                            }
                            document.body.appendChild(dd);
                            const closeDD = (ev) => { if (!dd.contains(ev.target)) { dd.remove(); document.removeEventListener('click', closeDD); } };
                            setTimeout(() => document.addEventListener('click', closeDD), 10);
                          }}
                        >
                          <div style={{ width: '22px', height: '22px', borderRadius: '50%', background: g.rsvp_status === 'attending' ? '#22C55E' : '#EAB308', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px', fontWeight: 700, flexShrink: 0 }}>
                            {g.name.charAt(0).toUpperCase()}
                          </div>
                          <span style={{ fontWeight: 500 }}>{g.name}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {unseatedGuests.length === 0 && tables.length > 0 && guests.filter(g => g.rsvp_status === 'attending' || g.rsvp_status === 'maybe').length > 0 && (
                <div className="card" style={{ textAlign: 'center', padding: '24px', background: '#F0FDF4', border: '1px solid #BBF7D0' }}>
                  <FiCheck size={24} style={{ color: '#22C55E', marginBottom: '8px' }} />
                  <p style={{ fontWeight: 600, color: '#166534', fontSize: '14px' }}>All confirmed guests are seated!</p>
                </div>
              )}
            </div>
            );
          })())}

          {/* INVITATIONS */}
          {tab === 'invitations' && (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '10px' }}>
                <h3>Invitations</h3>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  <button className="btn btn-sm" style={{ background: 'linear-gradient(135deg, #8B5CF6, #EC4899)', color: 'white', border: 'none' }} onClick={() => setShowAIDesigner(true)}><FiStar /> AI Designer</button>
                  <button className="btn btn-primary btn-sm" onClick={() => { const matched = findClosestPalette(event.event_type); const p = INVITATION_PALETTES.find(x => x.key === matched); setFormData({ theme: matched, colors: p ? { bg: p.bg, accent: p.accent, text: p.text } : {}, selectedPalette: matched, visible_tabs: ['wall','lineup','registry','polls','qa','gallery'] }); setShowModal('invitation'); }}><FiPlus /> Custom Invitation</button>
                </div>
              </div>
              <div style={{ background: 'linear-gradient(135deg, #1C1C1E, #2D1F2B)', borderRadius: '14px', padding: '18px 20px', marginBottom: '20px', color: '#fff', border: '1px solid #C9719140' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px', flexWrap: 'wrap' }}>
                  <div style={{ flex: 1, minWidth: '220px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                      <FiStar style={{ color: '#E8A0BF' }} />
                      <strong style={{ fontSize: '15px' }}>AI Event Media Package</strong>
                      <span style={{ background: '#C97191', color: '#fff', fontSize: '11px', fontWeight: 700, padding: '2px 10px', borderRadius: '12px' }}>R400</span>
                    </div>
                    <p style={{ fontSize: '13px', color: 'rgba(255,255,255,0.75)', margin: '0 0 6px', lineHeight: 1.6 }}>
                      Done-for-you by an AI design assistant: <strong>AI video invitation</strong> · tailored <strong>AI invitations</strong> · printable <strong>welcome board</strong> · <strong>event programme</strong>. You'll be contacted to get started.
                    </p>
                  </div>
                  <button className="btn btn-sm" style={{ background: 'linear-gradient(135deg, #C97191, #8B5CF6)', color: 'white', border: 'none', whiteSpace: 'nowrap' }} onClick={() => { setFormData({ ...formData, sr_event_name: event?.title || '', sr_event_date: event?.event_date ? String(event.event_date).slice(0, 10) : '', sr_style: '', sr_notes: '', sr_phone: user?.phone || '' }); setSrPhotos([]); setShowModal('serviceRequest'); }}>Request Package</button>
                </div>
              </div>
              {invitations.length === 0 ? (
                <div className="card" style={{ textAlign: 'center', padding: '40px' }}>
                  <div style={{ fontSize: '48px', marginBottom: '16px' }}>💌</div>
                  <h4>No Invitations Yet</h4>
                  <p style={{ color: 'var(--gray)', marginTop: '8px' }}>Create a beautiful invitation for your event</p>
                </div>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(280px, 100%), 1fr))', gap: '20px' }}>
                  {invitations.map(inv => (
                    <div key={inv.id} className="card" style={{ padding: 0, overflow: 'hidden', width: '100%' }}>
                      <div style={{ position: 'relative' }}>
                        {inv.custom_image ? (
                          <div style={{ background: '#1a1a1a', display: 'flex', alignItems: 'center', justifyContent: 'center', aspectRatio: '9/16' }}>
                            <img src={inv.custom_image} alt="Your Invitation" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                          </div>
                        ) : inv.video_url ? (
                          <div style={{ background: '#000', display: 'flex', alignItems: 'center', justifyContent: 'center', aspectRatio: '9/16' }}>
                            <video src={inv.video_url} controls style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                          </div>
                        ) : (
                          <div style={{ aspectRatio: '9/16', background: 'linear-gradient(135deg, var(--rose), var(--rose-deep))', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', textAlign: 'center', padding: '20px 20px 60px' }}>
                            <div>
                              <div style={{ fontSize: '12px', textTransform: 'uppercase', letterSpacing: '2px', marginBottom: '8px', opacity: 0.8 }}>{inv.invitation_heading || "You're Invited"}</div>
                              <h3 style={{ fontSize: '22px' }}>{event.title}</h3>
                              <div style={{ marginTop: '8px', fontSize: '14px', opacity: 0.9 }}>
                                {event.event_date && new Date(event.event_date).toLocaleDateString('en-ZA', { day:'numeric', month:'long', year:'numeric' })}
                              </div>
                            </div>
                          </div>
                        )}
                        {/* Delete button — top-right corner */}
                        <button
                          onClick={async (e) => {
                            e.stopPropagation();
                            if (!window.confirm('Delete this invitation? This cannot be undone.')) return;
                            try { await api.delete(`/events/${id}/invitations/${inv.id}`); showToast('Invitation deleted'); loadTabData(); } catch { showToast('Failed to delete', 'error'); }
                          }}
                          style={{
                            position: 'absolute', top: '8px', right: '8px',
                            width: '28px', height: '28px', borderRadius: '50%',
                            background: 'rgba(0,0,0,0.35)', border: 'none',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            color: '#fff', cursor: 'pointer', zIndex: 2,
                          }}
                          title="Delete invitation"
                        ><FiTrash2 size={13} /></button>
                        {/* Action buttons overlay — clean icon row */}
                        <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, background: 'linear-gradient(transparent, rgba(0,0,0,0.75) 35%)', padding: '32px 10px 10px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                          <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap' }}>
                            <span className="tag tag-gold" style={{ fontSize: '10px', background: 'rgba(234,179,8,0.25)', color: '#FDE68A' }}>{inv.theme}</span>
                            {inv.ai_template && <span className="tag" style={{ background: 'rgba(139,92,246,0.25)', color: '#C4B5FD', fontSize: '10px' }}>AI Design</span>}
                            {inv.video_url && <span className="tag" style={{ background: 'rgba(236,72,153,0.25)', color: '#FDA4AF', fontSize: '10px' }}>Video</span>}
                          </div>
                          <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', alignItems: 'center' }}>
                            <a href={`https://wa.me/?text=${encodeURIComponent(`You're invited to ${event.title}! ${window.location.origin}/invite/${inv.share_link}`)}`} target="_blank" rel="noreferrer" title="Share via WhatsApp"
                              style={{ width: '36px', height: '36px', borderRadius: '50%', background: '#25D366', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', flexShrink: 0 }}>
                              <FiSend size={14} />
                            </a>
                            <button onClick={() => { navigator.clipboard?.writeText(`${window.location.origin}/invite/${inv.share_link}`); showToast('Link copied!'); }}
                              title="Copy link"
                              style={{ width: '36px', height: '36px', borderRadius: '50%', background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', cursor: 'pointer', flexShrink: 0 }}>
                              <FiCopy size={14} />
                            </button>
                            <button onClick={() => {
                              const tabs = inv.visible_tabs || ['wall','lineup','registry','polls','qa','gallery'];
                              const isCustom = inv.theme === 'custom' && inv.colors && inv.colors.bg;
                              setFormData({ invId: inv.id, theme: inv.theme || 'elegant', colors: inv.colors || {}, selectedPalette: isCustom ? 'auto' : (inv.theme || 'elegant'), visible_tabs: typeof tabs === 'string' ? JSON.parse(tabs) : tabs, existingBanner: inv.custom_image || '', dress_code: inv.dress_code || '', invitation_heading: inv.invitation_heading || '', host_name: inv.host_name || '', videoUrl: inv.video_url || '', audioUrl: inv.audio_url || '' });
                              setShowModal('editInvitation');
                            }}
                              title="Edit invitation"
                              style={{ width: '36px', height: '36px', borderRadius: '50%', background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', cursor: 'pointer', flexShrink: 0 }}>
                              <FiEdit size={14} />
                            </button>
                            <button onClick={() => window.open(`/invite/${inv.share_link}`, '_blank')}
                              title="Preview invitation"
                              style={{ width: '36px', height: '36px', borderRadius: '50%', background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', cursor: 'pointer', flexShrink: 0 }}>
                              <FiExternalLink size={14} />
                            </button>
                            <button onClick={async () => {
                              const guestsWithEmail = guests.filter(g => g.email);
                              if (guestsWithEmail.length === 0) { showToast('No guests with email addresses', 'error'); return; }
                              if (!window.confirm(`Send invitation via Email to ${guestsWithEmail.length} guests?`)) return;
                              try {
                                const r = await api.post(`/events/${id}/send-invites-email`);
                                showToast(r.data.sent > 0 ? `Emailed ${r.data.sent} invitations!` : 'No emails sent');
                              } catch { showToast('Failed to send emails', 'error'); }
                            }}
                              title="Email invitation"
                              style={{ width: '36px', height: '36px', borderRadius: '50%', background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', cursor: 'pointer', flexShrink: 0 }}>
                              <FiMail size={14} />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {/* Event Extras removed — now part of R280 Premium Package in AI Invitation Designer */}
              {/* ═══ THANK YOU CARDS ═══ */}
              <div style={{ marginTop: '40px', borderTop: '1px solid rgba(255,255,255,0.08)', paddingTop: '32px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '10px' }}>
                  <div>
                    <h3 style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><FiHeart style={{ color: '#E3425C' }} /> Thank You Cards</h3>
                    <p style={{ color: 'var(--gray)', fontSize: '13px', marginTop: '4px' }}>Auto-send personalized thank you cards to guests after your event</p>
                  </div>
                  <button className="btn btn-sm" disabled={gigiWriting} style={{ background: 'linear-gradient(135deg, #8B5CF6, #EC4899)', color: 'white', border: 'none', opacity: gigiWriting ? 0.7 : 1 }}
                    onClick={async () => {
                      setGigiWriting(true);
                      try {
                        const res = await api.post('/ai/chat', {
                          message: 'generate_thankyou',
                          event_type: event.event_type,
                          event_context: {
                            title: event.title,
                            hostName: user?.name,
                            invitationTheme: invitations[0]?.theme,
                            invitationColors: invitations[0]?.colors,
                          }
                        });
                        if (res.data.fillData) {
                          setThankYouData(d => ({
                            ...d,
                            message: res.data.fillData.message,
                            host_signature: res.data.fillData.host_signature || d.host_signature,
                            card_style: res.data.fillData.card_style || d.card_style,
                          }));
                          showToast('GiGi wrote your thank you message!');
                        }
                      } catch {
                        showToast('Failed to generate message', 'error');
                      }
                      setGigiWriting(false);
                    }}>
                    <FiStar /> {gigiWriting ? 'GiGi is writing...' : 'Ask GiGi to Write'}
                  </button>
                </div>

                {thankYouSent && (
                  <div className="card" style={{ background: 'rgba(16,185,129,0.08)', border: '1px solid rgba(16,185,129,0.2)', marginBottom: '16px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <FiCheck style={{ color: '#10B981', fontSize: '20px' }} />
                      <div>
                        <div style={{ fontWeight: 600, color: '#10B981', fontSize: '14px' }}>Thank you cards sent!</div>
                        <div style={{ fontSize: '12px', color: 'var(--gray)' }}>Your guests received their personalized thank you cards</div>
                      </div>
                    </div>
                  </div>
                )}

                <div className="card">
                  {/* Card Style — matches invitation theme */}
                  <div style={{ marginBottom: '16px' }}>
                    <label style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text)', marginBottom: '8px', display: 'block' }}>Card Style</label>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '8px' }}>
                      {[
                        { key: 'elegant', label: 'Elegant', bg: '#1C1C1E', color: '#C9A84C' },
                        { key: 'floral', label: 'Floral', bg: '#FFF0F3', color: '#E91E63' },
                        { key: 'golden', label: 'Golden', bg: 'linear-gradient(135deg, #C9A84C, #E8D59A)', color: '#FFF' },
                        { key: 'minimal', label: 'Minimal', bg: '#FAFAF9', color: '#333' },
                        { key: 'festive', label: 'Festive', bg: '#E3425C', color: '#FFD700' },
                        { key: 'botanical', label: 'Botanical', bg: '#5C7A5A', color: '#F0F7F0' },
                        { key: 'roseGold', label: 'Rose Gold', bg: 'linear-gradient(135deg, #B76E79, #E8B4B8)', color: '#FFF' },
                        { key: 'african', label: 'African', bg: '#4A2C1A', color: '#CC6633' },
                        { key: 'midnight', label: 'Midnight', bg: 'linear-gradient(135deg, #0D1B2A, #1A0A2E)', color: '#C0C0FF' },
                        { key: 'garden', label: 'Garden', bg: '#F0F7F0', color: '#5C7A5A' },
                      ].map(s => (
                        <button key={s.key} onClick={() => setThankYouData(d => ({ ...d, card_style: s.key }))}
                          style={{
                            padding: '12px 6px', borderRadius: '10px',
                            border: thankYouData.card_style === s.key ? '2px solid var(--rose)' : '1px solid rgba(0,0,0,0.08)',
                            background: s.bg, cursor: 'pointer', textAlign: 'center', transition: 'all 0.2s',
                            transform: thankYouData.card_style === s.key ? 'scale(1.05)' : 'scale(1)',
                          }}>
                          <div style={{ fontSize: '11px', fontWeight: 600, color: s.color }}>{s.label}</div>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Message */}
                  <div style={{ marginBottom: '16px' }}>
                    <label style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text)', marginBottom: '8px', display: 'block' }}>
                      Thank You Message
                      <span style={{ fontWeight: 400, color: 'var(--gray)', marginLeft: '8px' }}>Use {'{guest_name}'} and {'{event_name}'}</span>
                    </label>
                    <textarea
                      value={thankYouData.message}
                      onChange={e => setThankYouData(d => ({ ...d, message: e.target.value }))}
                      placeholder={`Hi {guest_name},\n\nThank you so much for celebrating with us at {event_name}. Your presence made our day truly special.\n\nWith all our love and appreciation.`}
                      rows={6}
                      style={{
                        width: '100%', padding: '12px', borderRadius: '10px', fontSize: '14px', lineHeight: 1.7,
                        background: 'var(--cream)', border: '1px solid rgba(0,0,0,0.08)',
                        color: 'var(--text)', resize: 'vertical', fontFamily: "'DM Sans', sans-serif",
                      }}
                    />
                  </div>

                  {/* Signature */}
                  <div style={{ marginBottom: '16px' }}>
                    <label style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text)', marginBottom: '8px', display: 'block' }}>Your Signature</label>
                    <input
                      value={thankYouData.host_signature}
                      onChange={e => setThankYouData(d => ({ ...d, host_signature: e.target.value }))}
                      placeholder={`e.g., The ${user?.name?.split(' ')[1] || user?.name || ''} Family`}
                      style={{
                        width: '100%', padding: '10px 12px', borderRadius: '10px', fontSize: '14px',
                        background: 'var(--cream)', border: '1px solid rgba(0,0,0,0.08)',
                        color: 'var(--text)',
                      }}
                    />
                  </div>

                  {/* Auto-send toggle */}
                  <div style={{ marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 12px', borderRadius: '10px', background: 'var(--cream)' }}>
                    <button
                      onClick={() => setThankYouData(d => ({ ...d, auto_send: !d.auto_send }))}
                      style={{
                        width: '38px', height: '22px', borderRadius: '11px', border: 'none', cursor: 'pointer',
                        background: thankYouData.auto_send ? '#10B981' : '#ccc',
                        position: 'relative', transition: 'background 0.3s', flexShrink: 0,
                      }}>
                      <div style={{
                        width: '16px', height: '16px', borderRadius: '50%', background: 'white',
                        position: 'absolute', top: '3px', transition: 'left 0.3s',
                        left: thankYouData.auto_send ? '19px' : '3px',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                      }} />
                    </button>
                    <div>
                      <div style={{ fontSize: '12px', fontWeight: 600 }}>Auto-send via WhatsApp</div>
                      <div style={{ fontSize: '10px', color: 'var(--gray)' }}>Send to attending guests after your event at 9:00 AM</div>
                    </div>
                  </div>

                  {/* Send delay selector */}
                  {thankYouData.auto_send && (
                    <div style={{ marginBottom: '16px', padding: '12px', borderRadius: '10px', background: 'var(--cream)' }}>
                      <label style={{ fontSize: '13px', fontWeight: 600, display: 'block', marginBottom: '6px' }}>Send after event</label>
                      <select
                        value={thankYouData.send_delay_days || 1}
                        onChange={e => setThankYouData(d => ({ ...d, send_delay_days: parseInt(e.target.value) }))}
                        style={{ width: '100%', padding: '8px 12px', borderRadius: '8px', border: '1px solid rgba(0,0,0,0.08)', background: 'white', fontSize: '13px', color: 'var(--text)' }}>
                        <option value={1}>1 day after event (recommended)</option>
                        <option value={2}>2 days after event</option>
                        <option value={3}>3 days after event</option>
                        <option value={7}>1 week after event</option>
                      </select>
                    </div>
                  )}

                  {/* Post-event follow-up toggle */}
                  <div style={{ marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 12px', borderRadius: '10px', background: 'var(--cream)' }}>
                    <button
                      onClick={() => setThankYouData(d => ({ ...d, post_event_followup: !d.post_event_followup }))}
                      style={{
                        width: '38px', height: '22px', borderRadius: '11px', border: 'none', cursor: 'pointer',
                        background: thankYouData.post_event_followup !== false ? '#8B5CF6' : '#ccc',
                        position: 'relative', transition: 'background 0.3s', flexShrink: 0,
                      }}>
                      <div style={{
                        width: '16px', height: '16px', borderRadius: '50%', background: 'white',
                        position: 'absolute', top: '3px', transition: 'left 0.3s',
                        left: thankYouData.post_event_followup !== false ? '19px' : '3px',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                      }} />
                    </button>
                    <div>
                      <div style={{ fontSize: '12px', fontWeight: 600 }}>Post-event cleanup message</div>
                      <div style={{ fontSize: '10px', color: 'var(--gray)' }}>3 days after event, ask guests if they want to keep or clear their messages</div>
                    </div>
                  </div>

                  {/* Preview */}
                  <ThankYouCardPreview
                    cardStyle={thankYouData.card_style}
                    message={thankYouData.message}
                    signature={thankYouData.host_signature}
                    eventDate={event?.event_date}
                    eventTitle={event?.title}
                    userName={user?.name}
                  />

                  {/* Save + Send Actions — compact icon row */}
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '10px', alignItems: 'center' }}>
                    <button className="btn btn-sm btn-primary" style={{ padding: '8px 14px', fontSize: '12px', borderRadius: '20px' }}
                      onClick={async () => {
                        try {
                          await api.post(`/events/${id}/thank-you`, thankYouData);
                          setThankYouSaved(true);
                          showToast('Thank you card saved!');
                        } catch (err) {
                          showToast('Failed to save', 'error');
                        }
                      }}>
                      <FiCheck size={12} /> {thankYouSaved ? 'Update' : 'Save'}
                    </button>
                    {thankYouSaved && guests.length > 0 && (
                      <button onClick={() => window.open(`/thank-you/${id}/${btoa(String(guests[0]?.id || '0'))}`, '_blank')}
                        title="Preview thank you card"
                        style={{ width: '36px', height: '36px', borderRadius: '50%', background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: ['elegant', 'golden', 'roseGold', 'african', 'midnight', 'festive'].includes(thankYouData.card_style) ? '#fff' : '#333', cursor: 'pointer', flexShrink: 0 }}>
                        <FiExternalLink size={14} />
                      </button>
                    )}
                    {thankYouSaved && !thankYouSent && (
                      <button
                        style={{ width: '36px', height: '36px', borderRadius: '50%', background: '#25D366', color: '#fff', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, opacity: thankYouSending ? 0.6 : 1 }}
                        disabled={thankYouSending}
                        title="Send via WhatsApp"
                        onClick={async () => {
                          if (!window.confirm('Send thank you cards to all attending guests now via WhatsApp?')) return;
                          setThankYouSending(true);
                          try {
                            const res = await api.post(`/events/${id}/send-thank-you`);
                            setThankYouSent(true);
                            showToast(`Sent to ${res.data.sent} guests!`);
                          } catch (err) {
                            showToast('Failed to send', 'error');
                          } finally {
                            setThankYouSending(false);
                          }
                        }}>
                        <FiSend size={14} />
                      </button>
                    )}
                    {thankYouSaved && (
                      <button
                        style={{ width: '36px', height: '36px', borderRadius: '50%', background: '#4A90D9', color: '#fff', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, opacity: thankYouSending ? 0.6 : 1 }}
                        disabled={thankYouSending}
                        title="Send via Email"
                        onClick={async () => {
                          const emailCount = guests.filter(g => g.rsvp_status === 'attending' && g.email).length;
                          if (emailCount === 0) { showToast('No attending guests have email addresses', 'error'); return; }
                          if (!window.confirm(`Send thank you cards via Email to ${emailCount} attending guests?`)) return;
                          setThankYouSending(true);
                          try {
                            const res = await api.post(`/events/${id}/send-thank-you-email`);
                            showToast(`Emailed ${res.data.sent} guests!`);
                          } catch (err) {
                            showToast('Failed to send emails', 'error');
                          } finally {
                            setThankYouSending(false);
                          }
                        }}>
                        <FiMail size={14} />
                      </button>
                    )}
                    {thankYouSaved && (
                      <button
                        style={{ width: '36px', height: '36px', borderRadius: '50%', background: '#128C7E', color: '#fff', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}
                        title="Open WhatsApp for each guest"
                          onClick={() => {
                            const attendingWithPhone = guests.filter(g => g.rsvp_status === 'attending' && g.phone && !thankYouSentGuests.includes(g.id));
                            if (attendingWithPhone.length === 0) { showToast('No guests to send to'); return; }
                            if (!window.confirm(`Open WhatsApp for ${attendingWithPhone.length} guests? You'll send each one individually.`)) return;
                            const baseUrl = window.location.origin;
                            attendingWithPhone.forEach((guest, i) => {
                              const guestToken = btoa(String(guest.id));
                              const cardLink = `${baseUrl}/thank-you/${id}/${guestToken}`;
                              const personalMsg = (thankYouData.message || 'Thank you for being part of our special day!')
                                .replace('{guest_name}', guest.name || 'Friend')
                                .replace('{event_name}', event?.title || 'our event');
                              const waMsg = `💝 *A Thank You from ${event?.title}*\n\nHi ${guest.name || 'Friend'},\n\n${personalMsg}\n\n✨ View your personal thank you card:\n${cardLink}\n\nWith love,\n${thankYouData.host_signature || event?.title}\n\n— Grand Gather Registry ✦`;
                              setTimeout(() => openWhatsApp(guest.phone, waMsg), i * 1500);
                            });
                            setThankYouSentGuests(prev => [...prev, ...attendingWithPhone.map(g => g.id)]);
                            showToast(`Opening WhatsApp for ${attendingWithPhone.length} guests...`);
                          }}>
                          <FiShare2 size={11} /> Open WA ({guests.filter(g => g.rsvp_status === 'attending' && g.phone && !thankYouSentGuests.includes(g.id)).length})
                        </button>
                    )}
                    {thankYouSaved && (
                        <button className="btn btn-sm btn-outline" style={{ padding: '8px 14px', fontSize: '12px' }}
                          onClick={() => {
                            const attending = guests.filter(g => g.rsvp_status === 'attending' && g.phone);
                            const allMessages = attending.map(guest => {
                              const guestToken = btoa(String(guest.id));
                              const cardLink = `${window.location.origin}/thank-you/${id}/${guestToken}`;
                              return `To: ${guest.name} (${guest.phone})\n${cardLink}`;
                            }).join('\n\n');
                            navigator.clipboard?.writeText(allMessages);
                            showToast(`${attending.length} links copied!`);
                          }}>
                          Copy Links
                        </button>
                    )}
                    {thankYouSent && <span style={{ fontSize: '11px', color: '#10B981', fontWeight: 600, padding: '8px 0' }}>✓ Sent</span>}
                  </div>

                  {/* ─── Individual Guest List (collapsible) ─── */}
                  {thankYouSaved && (
                    <details style={{ marginTop: '4px' }}>
                      <summary style={{ fontSize: '13px', fontWeight: 600, cursor: 'pointer', color: 'var(--text)', padding: '8px 0', userSelect: 'none' }}>
                        Send to Individual Guests ({guests.filter(g => g.rsvp_status === 'attending').length})
                      </summary>
                      <div style={{ marginTop: '8px' }}>
                        {/* Filter tabs */}
                        <div style={{ display: 'flex', gap: '6px', marginBottom: '10px', flexWrap: 'wrap' }}>
                          {['attending', 'all'].map(f => (
                            <button key={f} className={`btn btn-sm ${(thankYouFilter || 'attending') === f ? 'btn-primary' : 'btn-outline'}`}
                              onClick={() => setThankYouFilter(f)}
                              style={{ textTransform: 'capitalize', fontSize: '12px' }}>
                              {f === 'attending' ? `Attending (${guests.filter(g => g.rsvp_status === 'attending').length})` : `All Guests (${guests.length})`}
                            </button>
                          ))}
                        </div>

                        {/* Guest list */}
                        <div style={{ maxHeight: '300px', overflowY: 'auto', borderRadius: '10px', border: '1px solid rgba(0,0,0,0.06)' }}>
                          {guests
                            .filter(g => (thankYouFilter || 'attending') === 'all' ? true : g.rsvp_status === 'attending')
                            .map(guest => {
                              const isSent = thankYouSentGuests.includes(guest.id);
                              const baseUrl = window.location.origin;
                              const guestToken = btoa(String(guest.id));
                              const cardLink = `${baseUrl}/thank-you/${id}/${guestToken}`;
                              const personalMsg = (thankYouData.message || 'Thank you for being part of our special day!')
                                .replace('{guest_name}', guest.name || 'Friend')
                                .replace('{event_name}', event?.title || 'our event');
                              const waMsg = `💝 *A Thank You from ${event?.title}*\n\nHi ${guest.name || 'Friend'},\n\n${personalMsg}\n\n✨ View your personal thank you card:\n${cardLink}\n\nWith love,\n${thankYouData.host_signature || event?.title}\n\n— Grand Gather Registry ✦`;
                              const waLink = buildWaLink(guest.phone, waMsg);

                              return (
                                <div key={guest.id} style={{
                                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                  padding: '10px 12px', borderBottom: '1px solid rgba(0,0,0,0.04)',
                                  background: isSent ? 'rgba(16,185,129,0.04)' : 'transparent',
                                }}>
                                  <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{ fontSize: '13px', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                      {guest.name}
                                      {isSent && <span style={{ marginLeft: '6px', color: '#10B981', fontSize: '11px' }}>✓ Sent</span>}
                                    </div>
                                    <div style={{ fontSize: '11px', color: 'var(--gray)' }}>
                                      {guest.phone || 'No phone'} · {guest.rsvp_status}
                                    </div>
                                  </div>
                                  <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
                                    {guest.phone && !isSent && (
                                      <a href={waLink} target="_blank" rel="noreferrer"
                                        onClick={() => setThankYouSentGuests(prev => [...prev, guest.id])}
                                        className="btn btn-sm" style={{ background: '#25D366', color: 'white', border: 'none', fontSize: '11px', padding: '5px 10px', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                                        <FiSend size={11} /> Send
                                      </a>
                                    )}
                                    {!guest.phone && (
                                      <span style={{ fontSize: '11px', color: '#ccc' }}>No WhatsApp</span>
                                    )}
                                    <button className="btn btn-sm btn-outline" style={{ fontSize: '11px', padding: '5px 10px' }}
                                      onClick={() => {
                                        navigator.clipboard?.writeText(waMsg);
                                        showToast('Message copied!');
                                      }}>
                                      Copy
                                    </button>
                                  </div>
                                </div>
                              );
                            })
                          }
                        </div>
                      </div>
                    </details>
                  )}

                  <p style={{ fontSize: '11px', color: 'var(--gray)', marginTop: '12px', textAlign: 'center' }}>
                    {thankYouData.auto_send ? `✨ Cards will auto-send via WhatsApp ${thankYouData.send_delay_days === 1 ? '1 day' : thankYouData.send_delay_days === 7 ? '1 week' : thankYouData.send_delay_days + ' days'} after your event at 9:00 AM${thankYouData.post_event_followup !== false ? ' · Follow-up cleanup message 3 days later' : ''}` : 'Auto-send is off — use the Send Now button after your event'}
                  </p>
                </div>
              </div>
            </div>
          )}

          
          {tab === 'vendors' && (
            <ErrorBoundary message="An error occurred while loading the Vendors tab.">
            <div>
              <div style={{ marginTop: '24px' }}>
            <VendorChatTab
              conversations={conversations}
              activeChat={activeChat}
              setActiveChat={setActiveChat}
              chatMessages={chatMessages}
              setChatMessages={setChatMessages}
              chatInput={chatInput}
              setChatInput={setChatInput}
              chatEndRef={chatEndRef}
              chatPollRef={chatPollRef}
              user={user}
              showToast={showToast}
              eventId={id}
              eventTitle={event.title}
              autoOpenConvId={autoOpenConvId}
              setTab={setTab}
              loadConversations={async () => { const c = await api.get('/chat/conversations', { params: { event_id: id, role: 'client' } }).catch(() => ({ data: [] })); setConversations(c.data); }}
              bookedRateCards={bookedRateCards}
              setBookedRateCards={setBookedRateCards}
              isMobile={isMobile}
              verifyingPayment={verifyingPayment}
            />
              </div>
            </div>
            </ErrorBoundary>
          )}

          {tab === 'deliveries' && (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '8px' }}>
                <div>
                  <h3 style={{ fontSize: '20px', fontWeight: 700 }}>Vendor Deliveries</h3>
                  <p style={{ fontSize: '13px', color: 'var(--gray)' }}>Track what vendors are bringing to your event</p>
                </div>
                {deliveries.length > 0 && (
                  <span style={{ fontSize: '12px', color: 'var(--gray)' }}>{deliveries.length} delivery{deliveries.length !== 1 ? 'ies' : ''}</span>
                )}
              </div>
              {deliveries.length === 0 ? (
                <div className="card" style={{ textAlign: 'center', padding: '60px', color: 'var(--gray)' }}>
                  <div style={{ fontSize: '48px', marginBottom: '12px' }}>&#128666;</div>
                  <h4 style={{ marginBottom: '6px', color: 'var(--dark)' }}>No deliveries tracked yet</h4>
                  <p style={{ fontSize: '13px' }}>When vendors set up delivery tracking for your event, you'll see it here and can confirm when items arrive.</p>
                </div>
              ) : (
                <div style={{ display: 'grid', gap: '16px' }}>
                  {deliveries.map(d => {
                    const statusOrder = ['preparation','on_the_way','arrived','completed'];
                    const currentIdx = d.status === 'cancelled' ? -1 : statusOrder.indexOf(d.status);
                    return (
                      <div key={d.id} className="card" style={{ padding: '24px' }}>
                        {/* Top row: vendor info + status */}
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '16px', marginBottom: '16px', flexWrap: 'wrap' }}>
                          <div style={{ display: 'flex', gap: '14px', alignItems: 'center' }}>
                            <div style={{
                              width: '48px', height: '48px', borderRadius: '12px', background: 'var(--cream)',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              overflow: 'hidden', flexShrink: 0
                            }}>
                              {d.vendor_logo ? (
                                <img src={d.vendor_logo} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                              ) : (
                                <span style={{ fontSize: '20px' }}>&#128666;</span>
                              )}
                            </div>
                            <div>
                              <h4 style={{ marginBottom: '2px', fontSize: '16px' }}>{d.vendor_business || d.vendor_name}</h4>
                              <div style={{ fontSize: '12px', color: 'var(--gray)' }}>{d.vendor_location || 'Location not listed'}</div>
                            </div>
                          </div>
                          <span className={`tag ${d.status === 'completed' ? 'tag-green' : d.status === 'on_the_way' ? 'tag-blue' : d.status === 'arrived' ? 'tag-gold' : d.status === 'cancelled' ? 'tag-red' : ''}`} style={{ textTransform: 'capitalize' }}>
                            {d.status.replace(/_/g, ' ')}
                          </span>
                        </div>

                        {/* Status flow */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0', marginBottom: '16px', flexWrap: 'wrap', justifyContent: 'center' }}>
                          {['preparation','on_the_way','arrived','completed'].map((s, i, arr) => {
                            const stepIdx = statusOrder.indexOf(s);
                            const isActive = stepIdx <= currentIdx;
                            const isCurrent = s === d.status;
                            const labels = { preparation: 'Prep', on_the_way: 'On the Way', arrived: 'Arrived', completed: 'Done' };
                            const icons = { preparation: '\u{1F528}', on_the_way: '\u{1F698}', arrived: '\u{1F3C1}', completed: '\u{2705}' };
                            return (
                              <React.Fragment key={s}>
                                {i > 0 && (
                                  <div style={{ width: '25px', height: '3px', borderRadius: '2px', background: d.status === 'cancelled' ? 'var(--cream-dark)' : stepIdx <= currentIdx ? 'var(--green)' : 'var(--cream-dark)', margin: '0 2px', flexShrink: 0 }} />
                                )}
                                <div style={{
                                  display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px',
                                  opacity: d.status === 'cancelled' ? 0.4 : isActive ? 1 : 0.4,
                                  flex: '0 0 auto'
                                }}>
                                  <div style={{
                                    width: '40px', height: '40px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    background: isCurrent ? 'var(--green)' : isActive ? '#D1FAE5' : 'var(--cream)',
                                    border: isCurrent ? '3px solid var(--green)' : '2px solid ' + (isActive ? 'var(--green)' : 'var(--cream-dark)'),
                                    fontSize: '16px', transition: 'all 0.3s'
                                  }}>
                                    {icons[s]}
                                  </div>
                                  <span style={{ fontSize: '10px', fontWeight: isCurrent ? 700 : 400, color: isCurrent ? 'var(--green)' : 'var(--gray)', whiteSpace: 'nowrap' }}>{labels[s]}</span>
                                </div>
                              </React.Fragment>
                            );
                          })}
                        </div>

                        {/* Action area */}
                        {d.status === 'cancelled' && (
                          <div style={{ padding: '10px 14px', background: '#FEE2E2', borderRadius: '8px', fontSize: '13px', color: '#991B1B', marginBottom: '12px' }}>
                            &#10060; This delivery was cancelled by the vendor.
                          </div>
                        )}
                        {d.status === 'completed' && (
                          <div style={{ padding: '10px 14px', background: '#D1FAE5', borderRadius: '8px', fontSize: '13px', color: '#065F46', marginBottom: '12px' }}>
                            &#10003; Delivery completed and confirmed.
                          </div>
                        )}
                        {d.status === 'arrived' && !d.confirmed_by_host && (
                          <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
                            <div style={{ padding: '8px 14px', background: '#FEF3C7', borderRadius: '8px', fontSize: '13px', color: '#92400E', display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}>
                              <span>&#128276;</span> The vendor says they've arrived. Confirm when you've received everything!
                            </div>
                            <button className="btn btn-primary btn-sm" onClick={() => confirmDelivery(d.id)} style={{ background: 'var(--green)' }}>
                              &#10003; Confirm Delivery
                            </button>
                          </div>
                        )}
                        {d.status !== 'cancelled' && d.status !== 'completed' && d.status !== 'arrived' && (
                          <div style={{ fontSize: '13px', color: 'var(--gray)', fontStyle: 'italic' }}>
                            {d.status === 'preparation' && '\u{1F528} The vendor is preparing your order.'}
                            {d.status === 'on_the_way' && '\u{1F698} Your items are on the way!'}
                          </div>
                        )}

                        {/* Availability details */}
                        {d.availability_date && (
                          <div style={{ padding: '10px 14px', background: 'var(--cream)', borderRadius: '8px', fontSize: '13px', display: 'flex', gap: '16px', flexWrap: 'wrap', marginTop: '16px' }}>
                            <span><strong>Delivery Date:</strong> {new Date(d.availability_date).toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                            {d.availability_start_time && <span><strong>From:</strong> {d.availability_start_time}</span>}
                            {d.availability_end_time && <span><strong>To:</strong> {d.availability_end_time}</span>}
                          </div>
                        )}
                        {d.notes && <p style={{ fontSize: '13px', color: 'var(--gray)', marginTop: '12px' }}><strong>Vendor Notes:</strong> {d.notes}</p>}

                        {/* Contact vendor */}
                        {d.vendor_phone && (
                          <div style={{ marginTop: '16px', display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                            <a href={`tel:${d.vendor_phone}`} className="btn btn-sm" style={{ display: 'flex', alignItems: 'center', gap: '6px', textDecoration: 'none', background: 'var(--cream)', color: 'var(--dark)', borderRadius: '8px', padding: '8px 14px', fontSize: '13px' }}>
                              <FiPhone size={14} /> Call {d.vendor_business || 'Vendor'}
                            </a>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}


          {tab === 'bookings' && (
            <div>
                <div>
                  <h3 style={{ marginBottom: '16px', fontSize: '16px' }}>Your Vendor Bookings</h3>
                  {hostBookings.length === 0 ? (
                    <div className="card" style={{ textAlign: 'center', padding: '40px', color: 'var(--gray)' }}>
                      <FiShoppingBag size={32} style={{ marginBottom: '8px', opacity: 0.3 }} />
                      <p>No vendor bookings yet. Browse the <Link to="/vendors" state={{ fromEvent: id }} style={{ color: 'var(--rose-deep)' }}>Vendor Marketplace</Link> to find vendors for your event.</p>
                    </div>
                  ) : (
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px', flexWrap: 'wrap' }}>
                        <select
                          value={hostBookingsFilter || 'active'}
                          onChange={e => setHostBookingsFilter(e.target.value)}
                          style={{ padding: '8px 14px', fontSize: '13px', fontWeight: 600, borderRadius: '8px', border: '1px solid var(--cream-dark)', background: 'white', color: 'var(--charcoal)', cursor: 'pointer' }}
                        >
                          <option value="active">Active Bookings</option>
                          <option value="pending">Pending</option>
                          <option value="confirmed">Confirmed</option>
                          <option value="paid">Paid</option>
                          <option value="cancelled">Cancelled/Declined</option>
                          <option value="archived">Archived</option>
                          <option value="all">All</option>
                        </select>
                        <span style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px', color: 'var(--gray)' }}>
                          <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#F59E0B', display: 'inline-block' }} /> {sortedHostBookings.filter(b => b.status === 'pending').length} pending
                        </span>
                        <span style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px', color: 'var(--gray)' }}>
                          <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#10B981', display: 'inline-block' }} /> {sortedHostBookings.filter(b => b.status === 'confirmed').length} confirmed
                        </span>
                      </div>
                      {sortedHostBookings.map(bk => (
                        <div key={bk.id} className="card" style={{
                          marginBottom: '12px', padding: '20px',
                          borderLeft: bk.is_archived ? '3px solid #9CA3AF' : bk.status === 'pending' ? '3px solid #F59E0B' : bk.status === 'confirmed' && bk.payment_status !== 'paid' ? '3px solid #3B82F6' : 'none'
                        }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '12px' }}>
                            <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                              {bk.avatar ? (
                                <img src={bk.avatar} alt="" style={{ width: '44px', height: '44px', borderRadius: '50%', objectFit: 'cover' }} />
                              ) : (
                                <div style={{ width: '44px', height: '44px', borderRadius: '50%', background: 'var(--rose-light)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--rose-deep)', fontWeight: 700 }}>
                                  {(bk.business_name || bk.vendor_name || '?')[0]}
                                </div>
                              )}
                              <div>
                                <div style={{ fontWeight: 700, fontSize: '15px' }}>{bk.business_name || bk.vendor_name}</div>
                                <div style={{ fontSize: '12px', color: 'var(--gray)' }}>{bk.business_category} &bull; {bk.event_title}</div>
                              </div>
                            </div>
                            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                              <span className="tag" style={{
                                background: bk.status === 'confirmed' ? 'rgba(16,185,129,0.1)' : bk.status === 'cancelled' ? 'rgba(239,68,68,0.1)' : bk.status === 'declined' ? 'rgba(239,68,68,0.1)' : 'rgba(245,158,11,0.1)',
                                color: bk.status === 'confirmed' ? '#10B981' : bk.status === 'cancelled' ? '#EF4444' : bk.status === 'declined' ? '#EF4444' : '#F59E0B',
                                fontSize: '11px'
                              }}>{bk.status}</span>
                              <span className="tag" style={{
                                background: bk.payment_status === 'paid' ? 'rgba(16,185,129,0.1)' : 'rgba(107,114,128,0.1)',
                                color: bk.payment_status === 'paid' ? '#10B981' : '#6B7280',
                                fontSize: '11px'
                              }}>{bk.payment_status === 'paid' ? 'Paid' : 'Unpaid'}</span>
                              {bk.refund_status && (
                                <span className="tag" style={{
                                  background: bk.refund_status === 'processed' ? 'rgba(16,185,129,0.1)' : 'rgba(245,158,11,0.1)',
                                  color: bk.refund_status === 'processed' ? '#10B981' : '#D97706',
                                  fontSize: '11px'
                                }}>Refund: {bk.refund_status === 'processed' ? 'Processed' : 'Processing'}</span>
                              )}
                            </div>
                          </div>

                          {/* Cancel/Reschedule/Chat buttons for pending/confirmed bookings */}
                          {['pending', 'confirmed'].includes(bk.status) && !bk.is_archived && (
                            <div style={{ marginTop: '10px', display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
                              {bk.reschedule_requested ? (
                                <span style={{ padding: '5px 12px', fontSize: '11px', fontWeight: 600, borderRadius: '6px', background: 'rgba(245,158,11,0.12)', color: '#D97706' }}>Reschedule requested</span>
                              ) : (
                                <button
                                  onClick={() => { setRescheduleBookingId(bk.id); setRescheduleDate(''); setRescheduleNotes(''); }}
                                  style={{ padding: '6px 14px', fontSize: '12px', fontWeight: 600, borderRadius: '6px', border: '1px solid #F59E0B', color: '#D97706', background: 'transparent', cursor: 'pointer' }}
                                >Reschedule</button>
                              )}
                              <button
                                onClick={() => { setCancelBookingId(bk.id); setCancelReason(''); setCancelCustomReason(''); }}
                                style={{ padding: '6px 14px', fontSize: '12px', fontWeight: 600, borderRadius: '6px', border: '1px solid #EF4444', color: '#EF4444', background: 'transparent', cursor: 'pointer' }}
                              >Cancel Booking</button>
                              <button
                                onClick={() => openChatWithVendor(bk.vendor_id, bk.business_name || bk.vendor_name)}
                                style={{ padding: '6px 14px', fontSize: '12px', fontWeight: 600, borderRadius: '6px', border: '1px solid var(--rose-deep)', color: 'var(--rose-deep)', background: 'transparent', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}
                              ><FiMessageCircle size={12} /> Chat with Vendor</button>
                              {['confirmed'].includes(bk.status) && (
                                disputes.some(d => d.booking_id === bk.id) ? (
                                  <span style={{ padding: '5px 12px', fontSize: '11px', fontWeight: 600, borderRadius: '6px', background: 'rgba(245,158,11,0.12)', color: '#D97706' }}><FiAlertTriangle size={12} /> Issue Reported</span>
                                ) : (
                                  <button
                                    onClick={() => setReportModal({ booking_id: bk.id, vendor_name: bk.business_name || bk.vendor_name })}
                                    style={{ padding: '6px 14px', fontSize: '12px', fontWeight: 600, borderRadius: '6px', border: '1px solid #F59E0B', color: '#D97706', background: 'transparent', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}
                                  ><FiAlertTriangle size={12} /> Report Issue</button>
                                )
                              )}
                            </div>
                          )}

                          {/* Archive button — for cancelled/declined or confirmed+paid */}
                          {((bk.status === 'cancelled' || bk.status === 'declined') || (bk.status === 'confirmed' && bk.payment_status === 'paid')) && (
                            <div style={{ marginTop: '10px' }}>
                              <button
                                onClick={() => toggleHostArchive(bk.id)}
                                style={{ padding: '6px 14px', fontSize: '11px', fontWeight: 600, borderRadius: '6px', border: '1px solid #9CA3AF', color: '#6B7280', background: 'transparent', cursor: 'pointer' }}
                              >{bk.is_archived ? 'Unarchive' : 'Archive'}</button>
                            </div>
                          )}

                          {/* Delivery Info — address first (Takealot flow), then pay */}
                          {bk.delivery_type && (() => {
                            const isVendorDelivers = bk.delivery_type === 'flat_fee' || bk.delivery_type === 'free';
                            const isComeToProvider = bk.delivery_type === 'come_to_provider';
                            return (
                              <div style={{ marginTop: '12px', padding: '12px', background: 'rgba(227,66,92,0.03)', borderRadius: '8px', borderLeft: '3px solid #E3425C' }}>
                                {/* Delivery method badge */}
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                                  {isVendorDelivers ? <FiTruck size={14} color="#E3425C" /> : <FiMapPin size={14} color="#E3425C" />}
                                  <span style={{ fontSize: '12px', fontWeight: 600, color: 'var(--charcoal)' }}>
                                    {isVendorDelivers
                                      ? `Vendor Delivers${bk.delivery_type === 'flat_fee' ? ` — R${Number(bk.delivery_fee || 0).toLocaleString()}` : ' — Free Delivery'}`
                                      : 'Collect from Vendor'}
                                  </span>
                                </div>
                                {/* Content area */}
                                {isVendorDelivers && editingAddressId === bk.id ? (
                                  <div style={{ marginTop: '8px' }}>
                                    
  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px' }}>
    <div style={{ gridColumn: '1 / -1' }}>
      <input value={editDeliveryFields.street} onChange={e => setEditDeliveryFields(prev => ({ ...prev, street: e.target.value }))} placeholder='Street name & number' style={{ width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid var(--cream-dark)', fontSize: '13px', boxSizing: 'border-box' }} />
    </div>
    <input value={editDeliveryFields.town} onChange={e => setEditDeliveryFields(prev => ({ ...prev, town: e.target.value }))} placeholder='Town / City' style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid var(--cream-dark)', fontSize: '13px', boxSizing: 'border-box' }} />
    <input value={editDeliveryFields.region} onChange={e => setEditDeliveryFields(prev => ({ ...prev, region: e.target.value }))} placeholder='Region / Suburb' style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid var(--cream-dark)', fontSize: '13px', boxSizing: 'border-box' }} />
    <input value={editDeliveryFields.province} onChange={e => setEditDeliveryFields(prev => ({ ...prev, province: e.target.value }))} placeholder='Province' style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid var(--cream-dark)', fontSize: '13px', boxSizing: 'border-box' }} />
    <input value={editDeliveryFields.postal_code} onChange={e => setEditDeliveryFields(prev => ({ ...prev, postal_code: e.target.value }))} placeholder='Postal code' style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid var(--cream-dark)', fontSize: '13px', boxSizing: 'border-box' }} />
    <input value={editDeliveryFields.contact_person} onChange={e => setEditDeliveryFields(prev => ({ ...prev, contact_person: e.target.value }))} placeholder='Contact person' style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid var(--cream-dark)', fontSize: '13px', boxSizing: 'border-box' }} />
    <input value={editDeliveryFields.contact_mobile} onChange={e => setEditDeliveryFields(prev => ({ ...prev, contact_mobile: e.target.value }))} placeholder='Contact mobile number' style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid var(--cream-dark)', fontSize: '13px', boxSizing: 'border-box' }} />
  </div>
                                    <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                                      <button
                                        onClick={() => saveDeliveryAddress(bk.id)}
                                        disabled={savingDeliveryId === bk.id}
                                        style={{ padding: '6px 16px', fontSize: '12px', fontWeight: 600, borderRadius: '6px', border: 'none', background: '#E3425C', color: 'white', cursor: 'pointer' }}
                                      >{savingDeliveryId === bk.id ? 'Saving...' : 'Save'}</button>
                                      <button
                                        onClick={() => { setEditingAddressId(null); }}
                                        style={{ padding: '6px 12px', fontSize: '12px', borderRadius: '6px', border: '1px solid var(--cream-dark)', background: 'transparent', color: 'var(--gray)', cursor: 'pointer' }}
                                      >Cancel</button>
                                    </div>
                                  </div>
                                ) : isVendorDelivers && bk.delivery_address ? (
                                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', padding: '8px 12px', background: 'rgba(16,185,129,0.06)', borderRadius: '6px', marginTop: '8px' }}>
                                    <FiCheckCircle size={14} color="#10B981" style={{ marginTop: '2px', flexShrink: 0 }} />
                                    <div style={{ flex: 1 }}>
                                      <div style={{ fontSize: '11px', fontWeight: 600, color: '#10B981', marginBottom: '2px' }}>Delivery Address</div>
                                      <div style={{ fontSize: '13px', color: 'var(--charcoal)', whiteSpace: 'pre-wrap' }}>{bk.delivery_address}</div>
                                    </div>
                                    <button
                                      onClick={() => { setEditingAddressId(bk.id); if (hostDeliveryInfo) { setEditDeliveryFields({ street: hostDeliveryInfo.street || '', town: hostDeliveryInfo.town || '', region: hostDeliveryInfo.region || '', province: hostDeliveryInfo.province || '', postal_code: hostDeliveryInfo.postal_code || '', contact_person: hostDeliveryInfo.contact_person || '', contact_mobile: hostDeliveryInfo.contact_mobile || '' }); } }}
                                      style={{ padding: '2px 8px', fontSize: '11px', fontWeight: 600, borderRadius: '4px', border: 'none', background: 'transparent', color: 'var(--rose-deep)', cursor: 'pointer', flexShrink: 0 }}
                                    >Edit</button>
                                  </div>
                                ) : isVendorDelivers && !bk.delivery_address ? (
                                  <div style={{ marginTop: '8px' }}>
                                    <div style={{ fontSize: '11px', fontWeight: 600, color: '#D97706', marginBottom: '6px' }}>Delivery Address Needed</div>
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px' }}>
                                      <div style={{ gridColumn: '1 / -1' }}>
                                        <input value={editDeliveryFields.street} onChange={e => setEditDeliveryFields(prev => ({ ...prev, street: e.target.value }))} placeholder='Street name & number' style={{ width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid var(--cream-dark)', fontSize: '13px', boxSizing: 'border-box' }} />
                                      </div>
                                      <input value={editDeliveryFields.town} onChange={e => setEditDeliveryFields(prev => ({ ...prev, town: e.target.value }))} placeholder='Town / City' style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid var(--cream-dark)', fontSize: '13px', boxSizing: 'border-box' }} />
                                      <input value={editDeliveryFields.region} onChange={e => setEditDeliveryFields(prev => ({ ...prev, region: e.target.value }))} placeholder='Region / Suburb' style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid var(--cream-dark)', fontSize: '13px', boxSizing: 'border-box' }} />
                                      <input value={editDeliveryFields.province} onChange={e => setEditDeliveryFields(prev => ({ ...prev, province: e.target.value }))} placeholder='Province' style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid var(--cream-dark)', fontSize: '13px', boxSizing: 'border-box' }} />
                                      <input value={editDeliveryFields.postal_code} onChange={e => setEditDeliveryFields(prev => ({ ...prev, postal_code: e.target.value }))} placeholder='Postal code' style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid var(--cream-dark)', fontSize: '13px', boxSizing: 'border-box' }} />
                                      <input value={editDeliveryFields.contact_person} onChange={e => setEditDeliveryFields(prev => ({ ...prev, contact_person: e.target.value }))} placeholder='Contact person' style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid var(--cream-dark)', fontSize: '13px', boxSizing: 'border-box' }} />
                                      <input value={editDeliveryFields.contact_mobile} onChange={e => setEditDeliveryFields(prev => ({ ...prev, contact_mobile: e.target.value }))} placeholder='Contact mobile number' style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid var(--cream-dark)', fontSize: '13px', boxSizing: 'border-box' }} />
                                    </div>
                                    <button
                                      onClick={() => saveDeliveryAddress(bk.id)}
                                      disabled={savingDeliveryId === bk.id}
                                      style={{ padding: '6px 16px', fontSize: '12px', fontWeight: 600, borderRadius: '6px', border: 'none', background: '#E3425C', color: 'white', cursor: 'pointer', marginTop: '8px' }}
                                    >{savingDeliveryId === bk.id ? 'Saving...' : 'Save Address'}</button>
                                  </div>
                                ) : null}
                                {isComeToProvider && bk.business_address ? (
                                  <div style={{ marginTop: '8px', padding: '8px 12px', background: 'rgba(59,130,246,0.05)', borderRadius: '6px' }}>
                                    <div style={{ fontSize: '11px', fontWeight: 600, color: '#3B82F6', marginBottom: '4px' }}>Vendor Location</div>
                                    <div style={{ fontSize: '13px', color: 'var(--charcoal)', whiteSpace: 'pre-wrap' }}><FiMapPin size={12} style={{ marginRight: '4px', verticalAlign: 'middle' }} />{bk.business_address}</div>
                                  </div>
                                ) : isComeToProvider && !bk.business_address ? (
                                  <div style={{ marginTop: '8px', fontSize: '12px', color: 'var(--gray)', fontStyle: 'italic' }}>
                                    Business address not listed. Please contact the vendor directly.
                                  </div>
                                ) : null}
                              </div>
                            );
                          })()}

                          {/* Items being prepared — for confirmed package bookings */}
                          {bk.status === 'confirmed' && bk.items && bk.items.length > 0 && (
                            <div style={{ marginTop: '12px', padding: '10px 14px', background: 'rgba(16,185,129,0.04)', borderRadius: '8px', borderLeft: '3px solid #10B981' }}>
                              <div style={{ fontSize: '11px', fontWeight: 600, color: '#10B981', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                Items Being Prepared
                              </div>
                              {bk.items.map((item, i) => (
                                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', padding: '4px 0', borderBottom: i < bk.items.length - 1 ? '1px dashed rgba(0,0,0,0.06)' : 'none' }}>
                                  <span>{item.service_name} {item.unit ? `(${item.unit})` : ''}</span>
                                  <span style={{ fontWeight: 600, color: 'var(--rose-deep)' }}>R{Number(item.price).toLocaleString()}</span>
                                </div>
                              ))}
                            </div>
                          )}

                          {/* Pay section — shown after address for natural flow */}
                          {Number(bk.quote_amount) > 0 && (
                            <div style={{ marginTop: '16px', padding: '16px', background: 'rgba(227,66,92,0.04)', borderRadius: '10px', border: '2px solid rgba(227,66,92,0.15)' }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
                                <div>
                                  <div style={{ fontSize: '12px', color: 'var(--gray)' }}>Total Amount</div>
                                  <div style={{ fontSize: '22px', fontWeight: 700, color: 'var(--rose-deep)' }}>R{Number(bk.quote_amount).toLocaleString()}</div>
                                  {Number(bk.booking_fee || 0) > 0 && (
                                    <div style={{ fontSize: '11px', color: 'var(--gray)', marginTop: '2px' }}>Incl. R{Number(bk.booking_fee).toLocaleString()} booking fee</div>
                                  )}
                                </div>

                                {['pending', 'confirmed'].includes(bk.status) && bk.payment_status !== 'paid' && !bk.is_archived && (
                                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', justifyContent: 'center' }}>
                                  <button onClick={async () => {
                                    try {
                                      const res = await api.post('/ozow/vendor-booking/initialize', {
                                        booking_id: bk.id,
                                        amount: Number(bk.quote_amount),
                                        event_id: id,
                                        vendor_id: bk.vendor_id,
                                      });
                                      const data = res.data;
                                      if (!data?.success || (!data?.url && !data?.paymentUrl)) {
                                        alert('Payment setup failed: ' + (data?.error || 'No payment URL returned'));
                                        return;
                                      }
                                      if (data.url && data.params) {
                                        // Form-based Ozow flow — POST hidden form directly to pay.ozow.com
                                        const f = document.createElement('form');
                                        f.method = 'POST';
                                        f.action = data.url;
                                        f.style.display = 'none';
                                        Object.entries(data.params).forEach(([k, v]) => {
                                          const inp = document.createElement('input');
                                          inp.type = 'hidden'; inp.name = k; inp.value = v;
                                          f.appendChild(inp);
                                        });
                                        document.body.appendChild(f);
                                        sessionStorage.setItem('ggr_ozow_pending', JSON.stringify({
                                          booking_id: bk.id,
                                          event_id: id,
                                          reference: data.reference,
                                        }));
                                        f.submit();
                                        return; // Browser redirects to Ozow
                                      }
                                      // Fallback: direct redirect
                                      window.location.href = data.paymentUrl;
                                    } catch (err) {
                                      const msg = err.response?.data?.error || err.message || 'Payment failed';
                                      alert('Payment error: ' + msg);
                                      showToast(msg, 'error');
                                    }
                                  }} style={{
                                    display: 'flex', alignItems: 'center', gap: '8px',
                                    padding: '14px 36px',
                                    background: 'linear-gradient(135deg, var(--rose-deep), #c94d5c)',
                                    color: 'white', border: 'none', borderRadius: '10px',
                                    fontSize: '15px', fontWeight: 700, cursor: 'pointer',
                                    boxShadow: '0 4px 16px rgba(227,66,92,0.35)'
                                  }}>
                                    <CoinIcon size={18} /> Pay Vendor
                                  </button>
                                  {window.location.search.includes('demo=true') && (
                                    <button onClick={async () => {
                                      try {
                                        const res = await api.post('/ozow/demo/pay', {
                                          booking_id: bk.id,
                                          amount: Number(bk.quote_amount),
                                          event_id: id,
                                          vendor_id: bk.vendor_id,
                                        });
                                        if (res.data?.success) {
                                          showToast('Demo payment successful!', 'success');
                                          api.get('/host/bookings', { params: { event_id: id } }).then(r => setHostBookings(r.data)).catch(() => {});
                                        }
                                      } catch (err) {
                                        showToast('Demo payment failed', 'error');
                                      }
                                    }} style={{
                                      display: 'flex', alignItems: 'center', gap: '6px',
                                      padding: '14px 24px',
                                      background: 'linear-gradient(135deg, #10B981, #059669)',
                                      color: 'white', border: '1px dashed rgba(255,255,255,0.4)', borderRadius: '10px',
                                      fontSize: '13px', fontWeight: 700, cursor: 'pointer',
                                      boxShadow: '0 4px 12px rgba(16,185,129,0.3)'
                                    }}>
                                      🧪 Demo Pay
                                    </button>
                                  )}
                                  </div>
                                )}

                                {bk.payment_status === 'paid' && bk.paystack_reference && (
                                  <div style={{ fontSize: '11px', color: '#10B981', fontFamily: 'monospace' }}>
                                    REF: {bk.paystack_reference}
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                          {bk.message && (
                            <div style={{ marginTop: '8px', fontSize: '13px', color: 'var(--gray)', fontStyle: 'italic' }}>"{bk.message}"</div>
                          )}
                          <div style={{ marginTop: '8px', fontSize: '11px', color: 'var(--gray)' }}>
                            Booked {new Date(bk.created_at).toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric' })}
                            {bk.paid_at && <> &bull; Paid {new Date(bk.paid_at).toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric' })}</>}
                          </div>
                        </div>
                      ))}
                      {sortedHostBookings.length === 0 && hostBookings.length > 0 && (
                        <div className="card" style={{ textAlign: 'center', padding: '24px', color: 'var(--gray)', fontSize: '13px' }}>
                          No bookings match this filter.
                        </div>
                      )}
                    </div>
                  )}
                </div>
              {/* Report Issue Modal */}
              {reportModal && (() => {
                const submitReport = async () => {
                  if (!reportSubject.trim() || !reportDescription.trim()) return showToast('Please fill in all fields', 'error');
                  setReporting(true);
                  try {
                    await api.post('/disputes', { booking_id: reportModal.booking_id, subject: reportSubject, description: reportDescription });
                    showToast('Issue reported — we will review and respond', 'success');
                    setReportModal(null);
                    setReportSubject('');
                    setReportDescription('');
                    api.get('/disputes', { params: { event_id: id } }).then(r => setDisputes(r.data)).catch(() => {});
                  } catch (err) {
                    showToast(err.response?.data?.error || 'Failed to submit report', 'error');
                  } finally { setReporting(false); }
                };
                return (
                  <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={() => { setReportModal(null); setReportSubject(''); setReportDescription(''); }}>
                    <div style={{ background: 'white', borderRadius: '16px', padding: '28px', maxWidth: '460px', width: '90%', maxHeight: '80vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
                      <h3 style={{ fontSize: '18px', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '8px' }}><FiAlertTriangle color="#F59E0B" /> Report an Issue</h3>
                      <p style={{ fontSize: '13px', color: 'var(--gray)', marginBottom: '6px' }}>Booking with <strong>{reportModal.vendor_name}</strong></p>
                      <p style={{ fontSize: '12px', color: 'var(--gray)', marginBottom: '16px' }}>GGR will review your booking records, chat history, and payment status to help resolve the issue.</p>
                      <input
                        value={reportSubject}
                        onChange={e => setReportSubject(e.target.value)}
                        placeholder="Subject (e.g. Vendor not responding)"
                        style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', border: '1px solid var(--cream-dark)', fontSize: '13px', marginBottom: '12px', boxSizing: 'border-box' }}
                      />
                      <textarea
                        value={reportDescription}
                        onChange={e => setReportDescription(e.target.value)}
                        placeholder="Describe the issue in detail..."
                        rows={4}
                        style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', border: '1px solid var(--cream-dark)', fontSize: '13px', marginBottom: '16px', resize: 'vertical', boxSizing: 'border-box' }}
                      />
                      <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                        <button onClick={() => { setReportModal(null); setReportSubject(''); setReportDescription(''); }} className="btn" style={{ background: 'var(--cream)', color: 'var(--gray)' }}>Close</button>
                        <button onClick={submitReport} disabled={reporting} className="btn" style={{ background: '#F59E0B', color: 'white' }}>
                          {reporting ? 'Submitting...' : 'Submit Report'}
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })()}

              {/* Cancel Booking Modal */}
              {cancelBookingId && (() => {
                const reasonTemplates = [
                  'Vendor is delayed',
                  'Package/service won\'t arrive on time',
                  'Changed my mind',
                  'Found another vendor',
                  'Event details changed',
                  'Budget constraints',
                  'Other'
                ];
                const doCancel = async () => {
                  const reason = cancelReason === 'Other' ? cancelCustomReason : cancelReason;
                  if (!reason || !reason.trim()) return showToast('Please select or enter a reason', 'error');
                  setCancelling(true);
                  try {
                    await api.post(`/vendor/bookings/${cancelBookingId}/cancel`, { reason });
                    showToast('Booking cancelled', 'success');
                    setCancelBookingId(null);
                    api.get('/host/bookings', { params: { event_id: id } }).then(r => setHostBookings(r.data)).catch(() => {});
                  } catch (err) {
                    showToast(err.response?.data?.error || 'Failed to cancel booking', 'error');
                  } finally { setCancelling(false); }
                };
                return (
                  <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={() => setCancelBookingId(null)}>
                    <div style={{ background: 'white', borderRadius: '16px', padding: '28px', maxWidth: '460px', width: '90%', maxHeight: '80vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
                      <h3 style={{ fontSize: '18px', marginBottom: '6px' }}>Cancel Booking</h3>
                      <p style={{ fontSize: '13px', color: 'var(--gray)', marginBottom: '20px' }}>Please select a reason for cancellation:</p>
                      <div style={{ display: 'grid', gap: '8px', marginBottom: '16px' }}>
                        {reasonTemplates.map(r => (
                          <label key={r} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 14px', borderRadius: '8px', border: cancelReason === r ? '2px solid #E3425C' : '1px solid var(--cream-dark)', cursor: 'pointer', fontSize: '14px', background: cancelReason === r ? 'rgba(227,66,92,0.04)' : 'transparent' }}>
                            <input type="radio" name="cancelReason" value={r} checked={cancelReason === r} onChange={() => setCancelReason(r)} style={{ accentColor: '#E3425C' }} />
                            {r}
                          </label>
                        ))}
                      </div>
                      {cancelReason === 'Other' && (
                        <textarea
                          value={cancelCustomReason}
                          onChange={e => setCancelCustomReason(e.target.value)}
                          placeholder="Please describe the reason..."
                          rows={3}
                          style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', border: '1px solid var(--cream-dark)', fontSize: '13px', marginBottom: '16px', resize: 'vertical', boxSizing: 'border-box' }}
                        />
                      )}
                      <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                        <button onClick={() => setCancelBookingId(null)} className="btn" style={{ background: 'var(--cream)', color: 'var(--gray)' }}>Close</button>
                        <button onClick={doCancel} disabled={cancelling} className="btn" style={{ background: '#EF4444', color: 'white' }}>
                          {cancelling ? 'Cancelling...' : 'Confirm Cancellation'}
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })()}

              {/* Reschedule Modal */}
              {rescheduleBookingId && (() => {
                const doReschedule = async () => {
                  if (!rescheduleDate) return showToast('Please select a new date', 'error');
                  setRescheduling(true);
                  try {
                    await api.post(`/vendor/bookings/${rescheduleBookingId}/reschedule-request`, {
                      new_date: rescheduleDate,
                      notes: rescheduleNotes || undefined
                    });
                    showToast('Reschedule requested', 'success');
                    setRescheduleBookingId(null);
                    api.get('/host/bookings', { params: { event_id: id } }).then(r => setHostBookings(r.data)).catch(() => {});
                  } catch (err) {
                    showToast(err.response?.data?.error || 'Failed to request reschedule', 'error');
                  } finally { setRescheduling(false); }
                };
                return (
                  <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', zIndex: 10001, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={() => setRescheduleBookingId(null)}>
                    <div style={{ background: 'white', borderRadius: '16px', padding: '28px', maxWidth: '460px', width: '90%' }} onClick={e => e.stopPropagation()}>
                      <h3 style={{ fontSize: '18px', marginBottom: '6px' }}>Request Reschedule</h3>
                      <p style={{ fontSize: '13px', color: 'var(--gray)', marginBottom: '20px' }}>Propose a new date for this booking:</p>
                      <div style={{ marginBottom: '16px' }}>
                        <label style={{ fontSize: '12px', fontWeight: 600, marginBottom: '4px', display: 'block' }}>New Date</label>
                        <input type="date" value={rescheduleDate} onChange={e => setRescheduleDate(e.target.value)}
                          min={new Date().toISOString().split('T')[0]}
                          style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', border: '1px solid var(--cream-dark)', fontSize: '14px', boxSizing: 'border-box' }} />
                      </div>
                      <div style={{ marginBottom: '20px' }}>
                        <label style={{ fontSize: '12px', fontWeight: 600, marginBottom: '4px', display: 'block' }}>Notes (optional)</label>
                        <textarea
                          value={rescheduleNotes}
                          onChange={e => setRescheduleNotes(e.target.value)}
                          placeholder="Reason for reschedule..."
                          rows={3}
                          style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', border: '1px solid var(--cream-dark)', fontSize: '13px', resize: 'vertical', boxSizing: 'border-box' }}
                        />
                      </div>
                      <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                        <button onClick={() => setRescheduleBookingId(null)} className="btn" style={{ background: 'var(--cream)', color: 'var(--gray)' }}>Close</button>
                        <button onClick={doReschedule} disabled={rescheduling} className="btn" style={{ background: '#F59E0B', color: 'white' }}>
                          {rescheduling ? 'Requesting...' : 'Request Reschedule'}
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })()}
            </div>
          )}

          {tab === 'corporate' && (
            <div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
                {/* Polls */}
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                    <h3>Audience Polls</h3>
                    <button className="btn btn-primary btn-sm" onClick={() => { setFormData({ question: '', options: ['', ''] }); setShowModal('poll'); }}><FiPlus /> New Poll</button>
                  </div>
                  {polls.map(poll => (
                    <div key={poll.id} className="card" style={{ marginBottom: '12px' }}>
                      <h4 style={{ marginBottom: '12px' }}>{poll.question}</h4>
                      {(poll.options || []).map((opt, i) => {
                        const totalVotes = Object.keys(poll.votes || {}).length;
                        const optVotes = Object.values(poll.votes || {}).filter(v => v === i).length;
                        return (
                          <div key={i} style={{ marginBottom: '8px', cursor: 'pointer' }} onClick={async () => { await api.post(`/polls/${poll.id}/vote`, { option_index: i }); loadTabData(); }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px', marginBottom: '4px' }}>
                              <span>{opt}</span><span>{totalVotes ? Math.round((optVotes/totalVotes)*100) : 0}%</span>
                            </div>
                            <div className="progress-bar"><div className="progress-fill" style={{ width: `${totalVotes ? (optVotes/totalVotes)*100 : 0}%` }}></div></div>
                          </div>
                        );
                      })}
                    </div>
                  ))}
                </div>
                {/* Q&A */}
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                    <h3>Live Q&A</h3>
                    <button className="btn btn-primary btn-sm" onClick={() => { setFormData({ question: '' }); setShowModal('qa'); }}><FiPlus /> Ask Question</button>
                  </div>
                  {qa.map(q => (
                    <div key={q.id} className="card" style={{ marginBottom: '12px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <div style={{ flex: 1 }}>
                          <p style={{ fontWeight: 600, marginBottom: '4px' }}>{q.question}</p>
                          <span style={{ fontSize: '12px', color: 'var(--gray)' }}>by {q.user_name || q.guest_name || 'Guest'}</span>
                        </div>
                        <button className="btn btn-sm btn-outline" onClick={async () => { await api.post(`/qa/${q.id}/upvote`); loadTabData(); }}>▲ {q.upvotes}</button>
                      </div>
                      {q.answer ? (
                        <div style={{ marginTop: '8px', padding: '12px', background: 'var(--cream)', borderRadius: '8px', fontSize: '14px' }}><strong>A:</strong> {q.answer}</div>
                      ) : (
                        <div style={{ marginTop: '8px' }}>
                          <div style={{ display: 'flex', gap: '8px' }}>
                            <input placeholder="Type your answer..." value={formData[`qa_${q.id}`] || ''} onChange={e => setFormData({...formData, [`qa_${q.id}`]: e.target.value})} style={{ flex: 1, padding: '8px 12px', fontSize: '13px' }} />
                            <button className="btn btn-sm btn-primary" onClick={async () => {
                              if (!formData[`qa_${q.id}`]?.trim()) return;
                              await api.put(`/qa/${q.id}/answer`, { answer: formData[`qa_${q.id}`] });
                              setFormData({...formData, [`qa_${q.id}`]: ''}); loadTabData();
                            }}>Answer</button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

        </div>
      </div>

      {/* MODALS */}
      {showModal && showModal !== 'eventSettings' && (
        <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && setShowModal(null)}>
          <div className="modal">
            {showModal === 'timeline' && (
              <>
                <h2>Add Lineup</h2>
                <div className="form-group"><label>What's Happening</label><input value={formData.title||''} onChange={e => setFormData({...formData, title: e.target.value})} placeholder="e.g., Welcome & Registration" /></div>
                <div className="form-group"><label>Details / Who's Participating</label><textarea value={formData.description||''} onChange={e => setFormData({...formData, description: e.target.value})} rows={3} placeholder="e.g., MC opens the event, keynote by John Smith" /></div>
                <div className="form-group"><label>Start Time</label><input type="time" value={formData.item_time||''} onChange={e => setFormData({...formData, item_time: e.target.value})} /></div>
                <div className="form-group"><label>End Time (Optional)</label><input type="time" value={formData.end_time||''} onChange={e => setFormData({...formData, end_time: e.target.value, till_late: false})} disabled={formData.till_late} /></div>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '13px', color: 'var(--gray)', marginTop: '4px' }}>
                  <input type="checkbox" checked={formData.till_late || false} onChange={e => setFormData({...formData, till_late: e.target.checked, end_time: e.target.checked ? '' : formData.end_time})} style={{ width: '16px', height: '16px', accentColor: 'var(--rose)' }} />
                  Till Late
                </label>
                <div style={{display:'flex',gap:'12px'}}>
                  <button className="btn btn-secondary btn-full" onClick={() => setShowModal(null)}>Cancel</button>
                  <button className="btn btn-primary btn-full" onClick={async () => { await api.post(`/events/${id}/timeline`, formData); setShowModal(null); loadTabData(); }}>Add</button>
                </div>
              </>
            )}
            {showModal === 'editTimeline' && (
              <>
                <h2>Edit Lineup</h2>
                <div className="form-group"><label>What's Happening</label><input value={formData.title||''} onChange={e => setFormData({...formData, title: e.target.value})} placeholder="e.g., Welcome & Registration" /></div>
                <div className="form-group"><label>Details / Who's Participating</label><textarea value={formData.description||''} onChange={e => setFormData({...formData, description: e.target.value})} rows={3} placeholder="e.g., MC opens the event, keynote by John Smith" /></div>
                <div className="form-group"><label>Start Time</label><input type="time" value={formData.item_time||''} onChange={e => setFormData({...formData, item_time: e.target.value})} /></div>
                <div className="form-group"><label>End Time (Optional)</label><input type="time" value={formData.end_time||''} onChange={e => setFormData({...formData, end_time: e.target.value, till_late: false})} disabled={formData.till_late} /></div>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '13px', color: 'var(--gray)', marginTop: '4px' }}>
                  <input type="checkbox" checked={formData.till_late || false} onChange={e => setFormData({...formData, till_late: e.target.checked, end_time: e.target.checked ? '' : formData.end_time})} style={{ width: '16px', height: '16px', accentColor: 'var(--rose)' }} />
                  Till Late
                </label>
                <div style={{display:'flex',gap:'12px'}}>
                  <button className="btn btn-secondary btn-full" onClick={() => setShowModal(null)}>Cancel</button>
                  <button className="btn btn-primary btn-full" onClick={async () => { await api.put(`/events/${id}/timeline/${formData.id}`, formData); setShowModal(null); loadTabData(); }}>Save</button>
                </div>
              </>
            )}
            {showModal === 'guest' && (
              <>
                <h2>{formData.editingGuestId ? 'Edit Guest' : 'Add Guest'}</h2>
                <div className="form-group"><label>Name</label><input value={formData.name||''} onChange={e => setFormData({...formData, name: e.target.value})} placeholder="Guest name" /></div>
                <div className="form-group"><label>Phone</label><input value={formData.phone||''} onChange={e => setFormData({...formData, phone: e.target.value})} placeholder="Phone number" /></div>
                <div className="form-group"><label>Email</label><input value={formData.email||''} onChange={e => setFormData({...formData, email: e.target.value})} placeholder="Email address" /></div>
                <div className="form-group"><label>RSVP Status</label>
                  <select value={formData.rsvp_status||'pending'} onChange={e => setFormData({...formData, rsvp_status: e.target.value})}>
                    <option value="pending">Pending</option><option value="attending">Attending</option><option value="maybe">Maybe</option><option value="not_attending">Not Attending</option>
                  </select>
                </div>
                <div className="form-group"><label>Dietary Requirements</label><input value={formData.dietary_requirements||''} onChange={e => setFormData({...formData, dietary_requirements: e.target.value})} placeholder="e.g., Vegetarian, Halal" /></div>
                <div className="form-group" style={{ background: 'var(--gray-light)', padding: '14px', borderRadius: '10px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>🎈 Kids Coming <span style={{ fontSize: '11px', color: 'var(--gray)', fontWeight: 400 }}>— for party packs, add each child's name & age</span></label>
                  {(formData.children || []).map((kid, idx) => (
                    <div key={idx} style={{ display: 'flex', gap: '8px', alignItems: 'center', marginTop: '8px' }}>
                      <input value={kid.name||''} onChange={e => { const kids = [...(formData.children || [])]; kids[idx] = { ...kid, name: e.target.value }; setFormData({ ...formData, children: kids }); }} placeholder="Child's name" style={{ flex: 1, padding: '8px 10px', borderRadius: '8px', border: '1px solid var(--cream-dark)', fontSize: '13px' }} />
                      <input type="number" min="0" value={kid.age||''} onChange={e => { const kids = [...(formData.children || [])]; kids[idx] = { ...kid, age: e.target.value }; setFormData({ ...formData, children: kids }); }} placeholder="Age" style={{ width: '70px', padding: '8px 10px', borderRadius: '8px', border: '1px solid var(--cream-dark)', fontSize: '13px' }} />
                      <button onClick={() => setFormData({ ...formData, children: (formData.children || []).filter((_, i) => i !== idx) })} style={{ background: 'none', border: 'none', color: 'var(--rose-deep)', cursor: 'pointer', fontSize: '16px', fontWeight: 700 }} title="Remove">✕</button>
                    </div>
                  ))}
                  <button onClick={() => setFormData({ ...formData, children: [...(formData.children || []), { name: '', age: '' }] })} className="btn btn-sm" style={{ marginTop: '10px', background: 'var(--rose-light)', color: 'var(--rose-deep)', border: 'none', borderRadius: '8px', cursor: 'pointer', padding: '6px 12px', fontSize: '13px', fontWeight: 600 }}>
                    + Add Kid
                  </button>
                </div>
                <div style={{display:'flex',gap:'12px'}}>
                  <button className="btn btn-secondary btn-full" onClick={() => { setShowModal(null); setFormData({...formData, editingGuestId: null}); }}>Cancel</button>
                  <button className="btn btn-primary btn-full" onClick={async () => {
                    if (formData.editingGuestId) {
                      const { editingGuestId, ...guestData } = formData;
                      await api.put(`/events/${id}/guests/${editingGuestId}`, guestData);
                    } else {
                      await api.post(`/events/${id}/guests`, formData);
                    }
                    setShowModal(null);
                    setFormData({...formData, editingGuestId: null});
                    loadTabData();
                  }}>{formData.editingGuestId ? 'Save Changes' : 'Add Guest'}</button>
                </div>
              </>
            )}
            {showModal === 'checklist' && (
              <>
                <h2>Add Task</h2>
                <div className="form-group"><label>Task</label><input value={formData.title||''} onChange={e => setFormData({...formData, title: e.target.value})} placeholder="What needs to be done?" /></div>
                <div style={{display:'flex',gap:'12px'}}>
                  <button className="btn btn-secondary btn-full" onClick={() => setShowModal(null)}>Cancel</button>
                  <button className="btn btn-primary btn-full" onClick={async () => { await api.post(`/events/${id}/checklist`, formData); setShowModal(null); loadTabData(); }}>Add</button>
                </div>
              </>
            )}
            {showModal === 'registry' && (
              <>
                <h2>{formData.editRegistryId ? 'Edit Registry Item' : 'Add Registry Item'}</h2>
                <div className="form-group"><label>Title</label><input value={formData.title||''} onChange={e => setFormData({...formData, title: e.target.value})} placeholder="e.g., Kitchen Aid Mixer, Honeymoon Fund" /></div>
                <div className="form-group"><label>Type</label>
                  <select value={formData.type||'physical'} onChange={e => setFormData({...formData, type: e.target.value})}>
                    <option value="physical">Physical Gift</option><option value="cash">Cash Contribution</option><option value="group">Group Contribution</option><option value="store">Online Store Gift</option>
                  </select>
                </div>
                {formData.type === 'store' && (
                  <>
                    <div className="form-group">
                      <label style={{ display: 'flex', alignItems: 'center', gap: '6px' }}><FiExternalLink size={14} /> Product Link</label>
                      <input value={formData.image||''} onChange={e => setFormData({...formData, image: e.target.value})} placeholder="https://www.takealot.com/product/..." />
                      <p style={{ fontSize: '11px', color: 'var(--gray)', marginTop: '4px' }}>Paste the product URL from any online store</p>
                    </div>
                    <div className="form-group">
                      <label style={{ display: 'flex', alignItems: 'center', gap: '6px' }}><FiMapPin size={14} /> Physical Store Address (optional)</label>
                      <input value={formData.store_address||''} onChange={e => setFormData({...formData, store_address: e.target.value})} placeholder="e.g. Cotton On, Menlyn Park Shopping Centre, Pretoria" />
                      <p style={{ fontSize: '11px', color: 'var(--gray)', marginTop: '4px' }}>Leave empty if there's no physical store — guests will be told to buy online</p>
                    </div>
                  </>
                )}
                <div className="form-group">
                  <label>Product Image URL (optional)</label>
                  <input value={formData.product_image||''} onChange={e => setFormData({...formData, product_image: e.target.value})} placeholder="https://... (right-click product image → Copy image address)" />
                  {formData.product_image && (
                    <div style={{ marginTop: '8px', width: '80px', height: '80px', borderRadius: '8px', overflow: 'hidden', border: '1px solid var(--cream-dark)' }}>
                      <img src={formData.product_image} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={e => { e.target.style.display = 'none'; }} />
                    </div>
                  )}
                </div>
                <div style={{ display: 'flex', gap: '12px' }}>
                  <div className="form-group" style={{ flex: 1 }}><label>Price (R)</label><input type="number" value={formData.target_amount||''} onChange={e => setFormData({...formData, target_amount: e.target.value})} placeholder="0" /></div>
                  {(formData.type === 'physical' || formData.type === 'store') && (
                    <div className="form-group" style={{ width: '120px' }}><label>Quantity</label><input type="number" min="1" value={formData.quantity_needed||1} onChange={e => setFormData({...formData, quantity_needed: parseInt(e.target.value) || 1})} /></div>
                  )}
                </div>
                <div className="form-group"><label>Description</label><textarea value={formData.description||''} onChange={e => setFormData({...formData, description: e.target.value})} rows={2} placeholder={formData.type === 'store' ? 'Describe the gift, size, color preference, etc.' : ''} /></div>
                {(formData.type === 'cash' || formData.type === 'group') && (
                  <div className="form-group" style={{ background: 'var(--gray-light)', padding: '16px', borderRadius: 'var(--radius-sm)' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '6px' }}><CoinIcon size={14} /> Bank Account Details</label>
                    <p style={{ fontSize: '12px', color: 'var(--gray)', marginBottom: '8px' }}>Guests will see this when they want to contribute</p>
                    <textarea value={formData.bank_details||''} onChange={e => setFormData({...formData, bank_details: e.target.value})} rows={4} placeholder={"Bank: FNB / Capitec / Standard Bank\nAccount Holder: John Doe\nAccount Number: 123456789\nBranch Code: 250655\nReference: Your Name"} />
                  </div>
                )}
                <div style={{display:'flex',gap:'12px'}}>
                  <button className="btn btn-secondary btn-full" onClick={() => setShowModal(null)}>Cancel</button>
                  <button className="btn btn-primary btn-full" onClick={async () => {
                    if (formData.editRegistryId) {
                      await api.put(`/events/${id}/registry/${formData.editRegistryId}`, formData);
                      setShowModal(null); loadTabData();
                      showToast('Gift updated!');
                    } else {
                      await api.post(`/events/${id}/registry`, formData);
                      setShowModal(null); loadTabData();
                      setGiftBoxOpen(true);
                      showToast(`"${formData.title}" added! Scroll down to view your curated gifts 🎁`);
                    }
                  }}>{formData.editRegistryId ? 'Save Changes' : 'Add'}</button>
                </div>
              </>
            )}
            {showModal === 'deliverySettings' && (
              <>
                <h2>Delivery & Contact Settings</h2>
                <p style={{ fontSize: '13px', color: 'var(--gray)', marginBottom: '20px', lineHeight: 1.6 }}>
                  Set where online gift purchases should be delivered and whether guests can see your contact details on the invitation.
                </p>

                <div style={{ background: '#EFF6FF', borderRadius: '12px', padding: '16px', marginBottom: '20px', border: '1px solid #3B82F622' }}>
                  <h3 style={{ fontSize: '14px', margin: '0 0 12px', display: 'flex', alignItems: 'center', gap: '6px', color: '#1E40AF' }}><FiMapPin size={14} /> Delivery Address</h3>
                  <div className="form-group" style={{ marginBottom: '12px' }}>
                    <label>Physical Address</label>
                    <textarea value={formData.delivery_address || ''} onChange={e => setFormData({...formData, delivery_address: e.target.value})} rows={3} placeholder="e.g. 12 Main Road, Sandton, Johannesburg, 2196" />
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label>Preferred Delivery Date</label>
                      <input type="date" value={formData.delivery_date || ''} onChange={e => setFormData({...formData, delivery_date: e.target.value})} />
                    </div>
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label>Delivery Notes</label>
                      <input value={formData.delivery_notes || ''} onChange={e => setFormData({...formData, delivery_notes: e.target.value})} placeholder="e.g. Ring bell at Gate 2" />
                    </div>
                  </div>
                </div>

                <div style={{ background: '#F0FDF4', borderRadius: '12px', padding: '16px', marginBottom: '20px', border: '1px solid #10B98122' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                    <h3 style={{ fontSize: '14px', margin: 0, display: 'flex', alignItems: 'center', gap: '6px', color: '#065F46' }}><FiPhone size={14} /> Contact on Invitation</h3>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '13px', color: formData.show_host_contact ? '#10B981' : 'var(--gray)' }}>
                      <input type="checkbox" checked={formData.show_host_contact || false} onChange={e => setFormData({...formData, show_host_contact: e.target.checked})} style={{ width: '18px', height: '18px', accentColor: '#10B981' }} />
                      {formData.show_host_contact ? 'Visible' : 'Hidden'}
                    </label>
                  </div>
                  {formData.show_host_contact && (
                    <div style={{ display: 'grid', gap: '12px' }}>
                      <div className="form-group" style={{ marginBottom: 0 }}>
                        <label>Contact Person Name</label>
                        <input value={formData.host_contact_name || ''} onChange={e => setFormData({...formData, host_contact_name: e.target.value})} placeholder="e.g. Lesego" />
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                        <div className="form-group" style={{ marginBottom: 0 }}>
                          <label><FiPhone size={12} /> WhatsApp Number</label>
                          <input value={formData.host_contact_phone || ''} onChange={e => setFormData({...formData, host_contact_phone: e.target.value})} placeholder="e.g. 071 234 5678" />
                        </div>
                        <div className="form-group" style={{ marginBottom: 0 }}>
                          <label><FiMail size={12} /> Email (optional)</label>
                          <input type="email" value={formData.host_contact_email || ''} onChange={e => setFormData({...formData, host_contact_email: e.target.value})} placeholder="your@email.com" />
                        </div>
                      </div>
                    </div>
                  )}
                  {!formData.show_host_contact && (
                    <p style={{ fontSize: '12px', color: 'var(--gray)', margin: 0 }}>Turn this on so guests can contact you directly. This builds trust and reduces fraud concerns.</p>
                  )}
                </div>

                <div style={{ display: 'flex', gap: '12px' }}>
                  <button className="btn btn-secondary btn-full" onClick={() => setShowModal(null)}>Cancel</button>
                  <button className="btn btn-primary btn-full" onClick={async () => {
                    try {
                      await api.put(`/events/${id}/delivery-settings`, {
                        delivery_address: formData.delivery_address || null,
                        delivery_date: formData.delivery_date || null,
                        delivery_notes: formData.delivery_notes || null,
                        show_host_contact: formData.show_host_contact || false,
                        host_contact_name: formData.host_contact_name || null,
                        host_contact_phone: formData.host_contact_phone || null,
                        host_contact_email: formData.host_contact_email || null
                      });
                      setEvent(prev => ({
                        ...prev,
                        delivery_address: formData.delivery_address,
                        delivery_date: formData.delivery_date,
                        delivery_notes: formData.delivery_notes,
                        show_host_contact: formData.show_host_contact,
                        host_contact_name: formData.host_contact_name,
                        host_contact_phone: formData.host_contact_phone,
                        host_contact_email: formData.host_contact_email
                      }));
                      setShowModal(null);
                    } catch { alert('Failed to save settings'); }
                  }}>Save Settings</button>
                </div>
              </>
            )}
            {showModal === 'eventBank' && (
              <>
                <h2>Banking / EFT Details</h2>
                <p style={{ fontSize: '13px', color: 'var(--gray)', marginBottom: '16px' }}>These details will be shown to all guests when they contribute to any gift. They'll be asked to make an EFT payment and upload proof.</p>
                <div className="form-group">
                  <label style={{ display: 'flex', alignItems: 'center', gap: '6px' }}><CoinIcon size={14} /> Bank Account Details</label>
                  <textarea value={formData.bank_details||''} onChange={e => setFormData({...formData, bank_details: e.target.value})} rows={6} placeholder={"Bank: FNB / Capitec / Standard Bank\nAccount Holder: John Doe\nAccount Number: 123456789\nBranch Code: 250655\nAccount Type: Cheque / Savings\nReference: Guest Name"} />
                </div>
                <div style={{display:'flex',gap:'12px'}}>
                  <button className="btn btn-secondary btn-full" onClick={() => setShowModal(null)}>Cancel</button>
                  <button className="btn btn-primary btn-full" onClick={async () => {
                    await api.put(`/events/${id}/bank-details`, { bank_details: formData.bank_details });
                    setEvent(prev => ({ ...prev, bank_details: formData.bank_details }));
                    setShowModal(null);
                  }}>Save Banking Details</button>
                </div>
              </>
            )}
            {showModal === 'speaker' && (
              <>
                <h2>{formData.id ? 'Edit Speaker' : 'Add Speaker'}</h2>
                <div className="form-group"><label>Name *</label><input value={formData.name||''} onChange={e => setFormData({...formData, name: e.target.value})} placeholder="Speaker's full name" /></div>
                <div className="form-group"><label>Title / Role</label><input value={formData.title||''} onChange={e => setFormData({...formData, title: e.target.value})} placeholder="e.g., Keynote Speaker, MC, Pastor" /></div>
                <div className="form-group"><label>Bio</label><textarea value={formData.bio||''} onChange={e => setFormData({...formData, bio: e.target.value})} rows={3} placeholder="Brief description about the speaker..." /></div>
                <div className="form-group">
                  <label>Photo</label>
                  {(formData.existingPhoto || formData.photoFile) && (
                    <div style={{ position: 'relative', marginBottom: '10px', display: 'inline-block' }}>
                      <img src={formData.photoFile ? URL.createObjectURL(formData.photoFile) : formData.existingPhoto} alt="Speaker" style={{ width: '80px', height: '80px', borderRadius: '50%', objectFit: 'cover' }} />
                      <button onClick={() => setFormData({...formData, photoFile: null, existingPhoto: ''})} style={{ position: 'absolute', top: '-4px', right: '-4px', background: 'var(--red)', color: 'white', border: 'none', borderRadius: '50%', width: '20px', height: '20px', cursor: 'pointer', fontSize: '11px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><FiX size={10} /></button>
                    </div>
                  )}
                  <input type="file" accept="image/*" onChange={e => { if (e.target.files[0]) setFormData({...formData, photoFile: e.target.files[0]}); }} style={{ padding: '10px' }} />
                </div>
                <div style={{display:'flex',gap:'12px'}}>
                  <button className="btn btn-secondary btn-full" onClick={() => setShowModal(null)}>Cancel</button>
                  <button className="btn btn-primary btn-full" disabled={!formData.name} onClick={async () => {
                    try {
                      const fd = new FormData();
                      fd.append('name', formData.name);
                      fd.append('title', formData.title || '');
                      fd.append('bio', formData.bio || '');
                      if (formData.photoFile) fd.append('photo', formData.photoFile);
                      if (formData.id) {
                        await api.put(`/events/${id}/speakers/${formData.id}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
                      } else {
                        await api.post(`/events/${id}/speakers`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
                      }
                      setShowModal(null); loadTabData(); showToast(formData.id ? 'Speaker updated!' : 'Speaker added!');
                    } catch { showToast('Failed to save speaker', 'error'); }
                  }}>{formData.id ? 'Save Changes' : 'Add Speaker'}</button>
                </div>
              </>
            )}
            {showModal === 'delegation' && (
              <>
                <h2>Add Delegate</h2>
                <div className="form-group"><label>Name</label><input value={formData.delegate_name||''} onChange={e => setFormData({...formData, delegate_name: e.target.value})} placeholder="Person's name" /></div>
                <div className="form-group"><label>Role</label>
                  <select value={formData.role||''} onChange={e => setFormData({...formData, role: e.target.value})}>
                    <option value="">Select role</option><option value="Spokesperson">Spokesperson</option><option value="Treasurer">Treasurer</option><option value="Logistics Coordinator">Logistics Coordinator</option><option value="MC">MC</option><option value="Family Representative">Family Representative</option><option value="Coordinator">Coordinator</option><option value="Other">Other</option>
                  </select>
                </div>
                <div className="form-group"><label>Bio</label><textarea value={formData.bio||''} onChange={e => setFormData({...formData, bio: e.target.value})} placeholder="Brief bio or description of this person..." rows={2} /></div>
                <div className="form-group">
                  <label>Photo</label>
                  {formData.photoFile && (
                    <div style={{ marginBottom: '8px', textAlign: 'center' }}>
                      <img src={URL.createObjectURL(formData.photoFile)} alt="Preview" style={{ width: '80px', height: '80px', borderRadius: '50%', objectFit: 'cover' }} />
                    </div>
                  )}
                  <input type="file" accept="image/*" onChange={e => { if (e.target.files[0]) setFormData({...formData, photoFile: e.target.files[0]}); }} style={{ padding: '10px' }} />
                </div>
                <div className="form-group"><label>Email (optional)</label><input value={formData.delegate_email||''} onChange={e => setFormData({...formData, delegate_email: e.target.value})} placeholder="Email address" /></div>
                <div className="form-group"><label>Task (optional)</label><textarea value={formData.task||''} onChange={e => setFormData({...formData, task: e.target.value})} placeholder="Describe their responsibility..." rows={2} /></div>
                <div style={{display:'flex',gap:'12px'}}>
                  <button className="btn btn-secondary btn-full" onClick={() => setShowModal(null)}>Cancel</button>
                  <button className="btn btn-primary btn-full" onClick={async () => {
                    try {
                      const fd = new FormData();
                      fd.append('delegate_name', formData.delegate_name || '');
                      fd.append('delegate_email', formData.delegate_email || '');
                      fd.append('role', formData.role || '');
                      fd.append('task', formData.task || '');
                      fd.append('bio', formData.bio || '');
                      if (formData.photoFile) fd.append('photo', formData.photoFile);
                      await api.post(`/events/${id}/delegations`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
                      setShowModal(null); loadTabData(); showToast('Delegate added!');
                    } catch { showToast('Failed to add delegate', 'error'); }
                  }}>Add Delegate</button>
                </div>
              </>
            )}
            {showModal === 'invitation' && (
              <>
                <h2>Create Invitation</h2>
                <div className="form-group">
                  <label>Color Theme</label>
                  <p style={{ fontSize: '13px', color: 'var(--gray)', marginBottom: '10px' }}>Choose a palette that matches your event — these are the same themes your guests will see</p>
                  <button
                    className="btn btn-sm"
                    style={{ marginBottom: '10px', width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', padding: '10px', borderRadius: '10px', border: 'none', background: 'linear-gradient(135deg, #E3425C, #C71585)', color: '#fff', fontWeight: 600 }}
                    onClick={() => {
                      const imgSrc = formData.custom_image || event.cover_image;
                      if (!imgSrc) { alert('Please upload an invitation image first so we can extract its colors'); return; }
                      extractImageColors(imgSrc, (colors) => {
                        if (colors) {
                          setFormData({...formData, theme: 'custom', colors, selectedPalette: 'custom'});
                        } else {
                          alert('Could not extract colors from the image. Please try another image or pick a palette below.');
                        }
                      });
                    }}
                  >
                    🎨 Match Image Colors
                  </button>
                  <button
                    className="btn btn-sm btn-outline"
                    style={{ marginBottom: '14px', width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', padding: '8px', borderRadius: '10px', border: '1px dashed var(--rose)', color: 'var(--rose)', fontWeight: 500, fontSize: '12px' }}
                    onClick={() => {
                      const matched = findClosestPalette(event.event_type);
                      const p = INVITATION_PALETTES.find(x => x.key === matched);
                      setFormData({...formData, theme: matched, colors: p ? { bg: p.bg, accent: p.accent, text: p.text } : {}, selectedPalette: matched});
                    }}
                  >
                    <FiZap size={12} /> Match Event Type</button>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(72px, 100%), 1fr))', gap: '6px' }}>
                    {INVITATION_PALETTES.map(p => {
                      const isSelected = formData.selectedPalette === p.key;
                      const isAutoMatched = formData.selectedPalette !== 'auto' ? false : findClosestPalette(event.event_type) === p.key;
                      return (
                        <div
                          key={p.key}
                          onClick={() => setFormData({...formData, theme: p.key, colors: { bg: p.bg, accent: p.accent, text: p.text }, selectedPalette: p.key})}
                          style={{
                            padding: '10px 6px',
                            borderRadius: '12px',
                            cursor: 'pointer',
                            textAlign: 'center',
                            border: isSelected ? '2px solid var(--rose)' : '2px solid transparent',
                            background: isSelected ? 'rgba(232,160,191,0.1)' : 'var(--card-bg)',
                            transition: 'all 0.2s ease',
                            position: 'relative',
                          }}
                        >
                          {isAutoMatched && formData.selectedPalette === 'auto' && (
                            <div style={{ position: 'absolute', top: '-6px', right: '-6px', background: 'var(--rose)', color: 'white', borderRadius: '50%', width: '18px', height: '18px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px' }}><FiCheck size={10} /></div>
                          )}
                          <div style={{ display: 'flex', gap: '3px', justifyContent: 'center', marginBottom: '6px' }}>
                            <span style={{ width: '18px', height: '18px', borderRadius: '50%', background: p.bg, border: '1px solid rgba(255,255,255,0.15)', display: 'block', flexShrink: 0 }} />
                            <span style={{ width: '18px', height: '18px', borderRadius: '50%', background: p.accent, border: '1px solid rgba(255,255,255,0.15)', display: 'block', flexShrink: 0 }} />
                            <span style={{ width: '18px', height: '18px', borderRadius: '50%', background: p.text, border: '1px solid rgba(255,255,255,0.15)', display: 'block', flexShrink: 0 }} />
                          </div>
                          <span style={{ fontSize: '11px', fontWeight: isSelected ? 600 : 400, color: isSelected ? 'var(--text)' : 'var(--gray)' }}>{p.label}</span>
                        </div>
                      );
                    })}
                  </div>
                  {/* Custom color pickers */}
                  <div style={{ marginTop: '12px', padding: '12px', borderRadius: '10px', background: 'var(--card-bg)', border: '1px solid var(--border-color)' }}>
                    <div style={{ fontSize: '12px', fontWeight: 600, marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '6px' }}>🎯 Custom Colors</div>
                    <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                      {[
                        { key: 'bg', label: 'Background', val: (formData.colors && formData.colors.bg) || '#2D1F2B' },
                        { key: 'accent', label: 'Accent', val: (formData.colors && formData.colors.accent) || '#E8A0BF' },
                        { key: 'text', label: 'Text', val: (formData.colors && formData.colors.text) || '#FFF5F8' },
                      ].map(c => (
                        <label key={c.key} style={{ flex: 1, minWidth: '80px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px', cursor: 'pointer' }}>
                          <span style={{ fontSize: '10px', color: 'var(--gray)', fontWeight: 500 }}>{c.label}</span>
                          <input type="color" value={c.val} onChange={e => {
                            const newColors = { ...(formData.colors || {}), [c.key]: e.target.value };
                            if (!newColors.bg) newColors.bg = '#2D1F2B';
                            if (!newColors.accent) newColors.accent = '#E8A0BF';
                            if (!newColors.text) newColors.text = '#FFF5F8';
                            setFormData({...formData, theme: 'custom', colors: newColors, selectedPalette: 'custom'});
                          }} style={{ width: '100%', height: '32px', borderRadius: '6px', border: '1px solid var(--border-color)', cursor: 'pointer', padding: '2px' }} />
                          <input type="text" value={c.val} onInput={e => {
                            let v = e.target.value.trim();
                            if (!v.startsWith('#')) v = '#' + v;
                            if (v.length > 7) v = v.slice(0, 7);
                            const newColors = { ...(formData.colors || {}), [c.key]: v };
                            if (!newColors.bg) newColors.bg = '#2D1F2B';
                            if (!newColors.accent) newColors.accent = '#E8A0BF';
                            if (!newColors.text) newColors.text = '#FFF5F8';
                            setFormData({...formData, theme: 'custom', colors: newColors, selectedPalette: 'custom'});
                          }} placeholder="#E3425C" maxLength={7} style={{ width: '100%', fontSize: '11px', fontWeight: 600, padding: '4px 6px', borderRadius: '6px', border: '1px solid var(--border-color)', textAlign: 'center', fontFamily: 'monospace', background: c.val || '#fff' }} />
                        </label>
                      ))}
                    </div>
                    {/* Opacity / Transparency slider */}
                    <div style={{ marginTop: '12px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                        <span style={{ fontSize: '10px', color: 'var(--gray)', fontWeight: 500 }}>Overlay</span>
                        <span style={{ fontSize: '10px', color: 'var(--text)', fontWeight: 600 }}>
                          {Math.round(((formData.colors && formData.colors.overlayOpacity) || 0.85) * 100)}%
                        </span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ fontSize: '10px', color: 'var(--gray)', whiteSpace: 'nowrap' }}>Transparent</span>
                        <input
                          type="range"
                          min="0.2" max="1" step="0.05"
                          value={(formData.colors && formData.colors.overlayOpacity) || 0.85}
                          onChange={e => {
                            const newColors = { ...(formData.colors || {}), overlayOpacity: parseFloat(e.target.value) };
                            if (!newColors.bg) newColors.bg = '#2D1F2B';
                            if (!newColors.accent) newColors.accent = '#E8A0BF';
                            if (!newColors.text) newColors.text = '#FFF5F8';
                            setFormData({...formData, theme: 'custom', colors: newColors, selectedPalette: 'custom'});
                          }}
                          style={{ flex: 1, accentColor: 'var(--rose)', height: '4px' }}
                        />
                        <span style={{ fontSize: '10px', color: 'var(--gray)', whiteSpace: 'nowrap' }}>Bold</span>
                      </div>
                    </div>
                  </div>
                  {(() => {
                    const sel = INVITATION_PALETTES.find(x => x.key === (formData.selectedPalette === 'auto' ? findClosestPalette(event.event_type) : formData.selectedPalette)) || INVITATION_PALETTES[0];
                    const useColors = (formData.selectedPalette !== 'auto' && formData.colors && formData.colors.bg) ? formData.colors : { bg: sel.bg, accent: sel.accent, text: sel.text };
                    const overlayOpacity = useColors.overlayOpacity !== undefined ? useColors.overlayOpacity : 0.85;
                    const previewBg = formData.custom_image || formData.imageFile ? (formData.imageFile ? URL.createObjectURL(formData.imageFile) : formData.custom_image) : (event.cover_image || getDefaultEventBanner(event.event_type));
                    return (
                      <div style={{ marginTop: '14px', borderRadius: '14px', overflow: 'hidden', border: `2px solid ${useColors.accent}40`, position: 'relative' }}>
                        <div style={{
                          position: 'relative', minHeight: '200px',
                          backgroundColor: useColors.bg,
                          backgroundImage: previewBg ? `url(${previewBg})` : 'none',
                          backgroundSize: 'cover', backgroundPosition: 'center',
                          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                          padding: '24px 20px', textAlign: 'center',
                        }}>
                          {previewBg && (
                            <div style={{ position: 'absolute', inset: 0, backgroundColor: useColors.bg, opacity: overlayOpacity, pointerEvents: 'none', zIndex: 0 }} />
                          )}
                          <div style={{ position: 'absolute', top: '-40px', right: '-40px', width: '100px', height: '100px', borderRadius: '50%', border: `1px solid ${useColors.accent}22`, pointerEvents: 'none' }} />
                          <div style={{ position: 'absolute', bottom: '-30px', left: '-30px', width: '80px', height: '80px', borderRadius: '50%', border: `1px solid ${useColors.accent}18`, pointerEvents: 'none' }} />
                          {/* Heading — matches InvitationView line 1041 */}
                          <div style={{ fontSize: '9px', textTransform: 'uppercase', letterSpacing: '4px', color: useColors.accent, marginBottom: '12px', fontWeight: 500, opacity: 0.75, zIndex: 1, fontFamily: 'Inter, sans-serif' }}>
                            {formData.invitation_heading || 'You\'re Invited to'}
                          </div>
                          {/* Star accent — matches InvitationView line 1061 */}
                          <div style={{ fontSize: '16px', color: useColors.accent, marginBottom: '10px', opacity: 0.6, zIndex: 1 }}>&#10022;</div>
                          {/* Event title — matches InvitationView line 1064 */}
                          <div style={{ fontFamily: "'Playfair Display', serif", fontSize: '20px', fontWeight: 700, letterSpacing: '-0.3px', color: useColors.text, zIndex: 1, marginBottom: '6px', lineHeight: 1.2, textShadow: previewBg && overlayOpacity < 0.6 ? '0 1px 4px rgba(0,0,0,0.6)' : 'none' }}>{event.title || 'Your Event'}</div>
                          {/* Hosted by — matches InvitationView line 1070 */}
                          <div style={{ fontSize: '11px', color: useColors.text, opacity: 0.85, zIndex: 1, marginBottom: '10px', fontFamily: 'Inter, sans-serif' }}>
                            Hosted by <span style={{ color: useColors.accent, fontWeight: 600 }}>{formData.host_name || event.host_name || 'Host'}</span>
                          </div>
                          {/* Divider — matches InvitationView line 1075 */}
                          <div style={{ width: '50px', height: '1px', background: `linear-gradient(90deg, ${useColors.accent}00, ${useColors.accent}88, ${useColors.accent}00)`, marginBottom: '10px', zIndex: 1 }} />
                          {/* Event details — date, venue, dress code — matches InvitationView lines 1077-1122 */}
                          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px', marginBottom: '10px', zIndex: 1 }}>
                            {event.event_date && (
                              <div style={{ textAlign: 'center' }}>
                                <div style={{ fontSize: '8px', opacity: 0.6, textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '1px', fontFamily: 'Inter, sans-serif' }}>Date</div>
                                <div style={{ fontSize: '12px', fontFamily: "'Playfair Display', serif" }}>
                                  {new Date(event.event_date).toLocaleDateString('en-ZA', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })}
                                </div>
                              </div>
                            )}
                            {event.location && (
                              <div style={{ textAlign: 'center' }}>
                                <div style={{ fontSize: '8px', opacity: 0.6, textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '1px', fontFamily: 'Inter, sans-serif' }}>Venue</div>
                                <div style={{ fontSize: '12px', fontFamily: "'Playfair Display', serif" }}>{event.location}</div>
                              </div>
                            )}
                            {(formData.dress_code || event.dress_code) && (
                              <div style={{ textAlign: 'center' }}>
                                <div style={{ fontSize: '8px', opacity: 0.6, textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '1px', fontFamily: 'Inter, sans-serif' }}>Dress Code</div>
                                <div style={{ fontSize: '12px', fontFamily: "'Playfair Display', serif" }}>{formData.dress_code || event.dress_code}</div>
                              </div>
                            )}
                          </div>
                          {/* Event description — matches InvitationView line 1125-1127 */}
                          {(formData.description || event.description) && (
                            <div style={{ fontSize: '10px', color: useColors.text, opacity: 0.75, zIndex: 1, lineHeight: 1.7, maxWidth: '92%', fontFamily: 'Inter, sans-serif', whiteSpace: 'pre-line' }}>
                              {formData.description || event.description}
                            </div>
                          )}
                        </div>
                        <div style={{ display: 'flex', height: '8px' }}>
                          <div style={{ flex: 1, background: useColors.bg }} />
                          <div style={{ flex: 1, background: useColors.accent }} />
                          <div style={{ flex: 1, background: useColors.text, borderLeft: '1px solid rgba(0,0,0,0.1)' }} />
                        </div>
                      </div>
                    );
                  })()}
                </div>
                <div className="form-group">
                  <label>Display Name (Hosted By)</label>
                  <p style={{ fontSize: '13px', color: 'var(--gray)', marginBottom: '8px' }}>The name shown as the host on your invitation (e.g., "Sarah & Thabo", "The Molefe Family")</p>
                  <input value={formData.host_name || ''} onChange={e => setFormData({...formData, host_name: e.target.value})} placeholder={event.host_name || 'Your name'} />
                </div>
                <div className="form-group">
                  <label>Invitation Heading</label>
                  <p style={{ fontSize: '13px', color: 'var(--gray)', marginBottom: '8px' }}>The greeting text shown at the top of your invitation</p>
                  <select value={formData.invitation_heading || ''} onChange={e => setFormData({...formData, invitation_heading: e.target.value === 'custom' ? 'custom' : e.target.value})}>
                    <option value="">You're Invited to (default)</option>
                    <option value="You Are Cordially Invited to">You Are Cordially Invited to</option>
                    <option value="You're Warmly Invited to">You're Warmly Invited to</option>
                    <option value="We Request the Honour of Your Presence at">We Request the Honour of Your Presence at</option>
                    <option value="Join Us in Celebrating">Join Us in Celebrating</option>
                    <option value="Save the Date">Save the Date</option>
                    <option value="In Loving Memory of">In Loving Memory of</option>
                    <option value="Umemiwe">Umemiwe (Zulu)</option>
                    <option value="O Memeletswe">O Memeletswe (Sotho/Tswana)</option>
                    <option value="U Rambiwa">U Rambiwa (Venda)</option>
                    <option value="custom">Custom...</option>
                  </select>
                  {(formData.invitation_heading === 'custom' || (formData.invitation_heading && !['','You Are Cordially Invited to',"You're Warmly Invited to",'We Request the Honour of Your Presence at','Join Us in Celebrating','Save the Date','In Loving Memory of','Umemiwe','O Memeletswe','U Rambiwa'].includes(formData.invitation_heading))) && (
                    <input value={formData.invitation_heading === 'custom' ? '' : formData.invitation_heading || ''} onChange={e => setFormData({...formData, invitation_heading: e.target.value})} placeholder="e.g., Woza Sizobungaza, Le a Laletšwa..." style={{ marginTop: '8px' }} />
                  )}
                </div>
                <div className="form-group">
                  <label>Dress Code</label>
                  <p style={{ fontSize: '13px', color: 'var(--gray)', marginBottom: '8px' }}>Let your guests know what to wear</p>
                  <select value={formData.dress_code || ''} onChange={e => setFormData({...formData, dress_code: e.target.value === 'custom' ? '' : e.target.value})} style={{ marginBottom: formData.dress_code === 'custom' || (formData.dress_code && !['Formal / Black Tie','Semi-Formal','Smart Casual','Casual','Traditional / Cultural Attire','All White','All Black','Cocktail Attire','Garden Party','Themed'].includes(formData.dress_code)) ? '8px' : 0 }}>
                    <option value="">No dress code</option>
                    <option value="Formal / Black Tie">Formal / Black Tie</option>
                    <option value="Semi-Formal">Semi-Formal</option>
                    <option value="Smart Casual">Smart Casual</option>
                    <option value="Casual">Casual</option>
                    <option value="Traditional / Cultural Attire">Traditional / Cultural Attire</option>
                    <option value="All White">All White</option>
                    <option value="All Black">All Black</option>
                    <option value="Cocktail Attire">Cocktail Attire</option>
                    <option value="Garden Party">Garden Party</option>
                    <option value="Themed">Themed</option>
                    <option value="custom">Custom...</option>
                  </select>
                  {(formData.dress_code === 'custom' || (formData.dress_code && !['','Formal / Black Tie','Semi-Formal','Smart Casual','Casual','Traditional / Cultural Attire','All White','All Black','Cocktail Attire','Garden Party','Themed'].includes(formData.dress_code))) && (
                    <input value={formData.dress_code === 'custom' ? '' : formData.dress_code} onChange={e => setFormData({...formData, dress_code: e.target.value})} placeholder="e.g., Red and Gold, African Print..." style={{ marginTop: '8px' }} />
                  )}
                </div>
                <div className="form-group">
                  <label>Upload Your Invitation</label>
                  <p style={{ fontSize: '13px', color: 'var(--gray)', marginBottom: '8px' }}>Already have a designed invitation? Upload your own invitation image here — it will be shown to guests on the invitation page</p>
                  <input type="file" accept="image/*" onChange={e => { if (e.target.files[0]) { setFormData({ ...formData, imageFile: e.target.files[0] }); } }} style={{ padding: '10px' }} />
                  {formData.imageFile && (
                    <div style={{ marginTop: '12px', position: 'relative' }}>
                      <img src={URL.createObjectURL(formData.imageFile)} alt="Your Invitation" style={{ width: '100%', maxHeight: '400px', objectFit: 'contain', borderRadius: '8px', background: '#f8f8f8' }} />
                      <button onClick={() => setFormData({...formData, imageFile: null})} style={{ position: 'absolute', top: '8px', right: '8px', background: 'rgba(0,0,0,0.6)', color: 'white', border: 'none', borderRadius: '50%', width: '36px', height: '36px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><FiX size={18} /></button>
                    </div>
                  )}
                </div>
                <div className="form-group">
                  <label>Video Invitation (Optional)</label>
                  <p style={{ fontSize: '13px', color: 'var(--gray)', marginBottom: '8px' }}>Add a personal video message for your guests</p>
                  <input type="file" accept="video/*" onChange={async (e) => {
                    const file = e.target.files[0]; if (!file) return;
                    const fd = new FormData(); fd.append('video', file);
                    try {
                      const res = await api.post(`/events/${id}/invitations/video`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
                      setFormData({...formData, videoUrl: res.data.video_url}); showToast('Video uploaded!');
                    } catch { showToast('Failed to upload video', 'error'); }
                  }} style={{ padding: '10px' }} />
                  {formData.videoUrl && (
                    <div style={{ marginTop: '10px', borderRadius: '10px', overflow: 'hidden', position: 'relative' }}>
                      <video src={formData.videoUrl} controls style={{ width: '100%', maxHeight: '180px', background: '#000' }} />
                      <button onClick={() => setFormData({...formData, videoUrl: ''})} style={{ position: 'absolute', top: '8px', right: '8px', background: 'rgba(0,0,0,0.6)', color: 'white', border: 'none', borderRadius: '50%', width: '36px', height: '36px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><FiX size={18} /></button>
                    </div>
                  )}
                </div>
                <div className="form-group">
                  <label>Event Music (Optional)</label>
                  <p style={{ fontSize: '13px', color: 'var(--gray)', marginBottom: '8px' }}>Pick a song or voice note from your phone — guests can play it on the invitation page. Use music you have the rights to.</p>
                  <input type="file" accept="audio/*" onChange={async (e) => {
                    const file = e.target.files[0]; if (!file) return;
                    if (file.size > 15 * 1024 * 1024) { showToast('Music file too large — max 15MB', 'error'); return; }
                    const fd = new FormData(); fd.append('audio', file);
                    try {
                      const res = await api.post(`/events/${id}/invitations/audio`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
                      setFormData({...formData, audioUrl: res.data.audio_url}); showToast('Music uploaded!');
                    } catch { showToast('Failed to upload music', 'error'); }
                  }} style={{ padding: '10px' }} />
                  {formData.audioUrl && (
                    <div style={{ marginTop: '10px', position: 'relative' }}>
                      <audio src={formData.audioUrl} controls style={{ width: '100%' }} />
                      <button onClick={() => setFormData({...formData, audioUrl: ''})} style={{ position: 'absolute', top: '8px', right: '8px', background: 'rgba(0,0,0,0.6)', color: 'white', border: 'none', borderRadius: '50%', width: '36px', height: '36px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><FiX size={18} /></button>
                    </div>
                  )}
                </div>
                <div className="form-group">
                  <label>What Guests Can See</label>
                  <p style={{ fontSize: '13px', color: 'var(--gray)', marginBottom: '10px' }}>Choose which sections are visible to your guests on the invitation</p>
                  {[
                    { key: 'wall', label: 'Event Wall' },
                    { key: 'lineup', label: 'Lineup & Programme' },
                    { key: 'registry', label: 'Gift Registry' },
                    { key: 'polls', label: 'Polls' },
                    { key: 'qa', label: 'Q&A' },
                    { key: 'gallery', label: 'Gallery' },
                    { key: 'tickets', label: 'Digital Tickets' },
                  ].map(opt => (
                    <label key={opt.key} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 12px', borderRadius: '8px', background: (formData.visible_tabs||[]).includes(opt.key) ? 'rgba(232,160,191,0.1)' : 'transparent', border: `1px solid ${(formData.visible_tabs||[]).includes(opt.key) ? 'var(--rose)' : 'var(--gray-light)'}`, marginBottom: '6px', cursor: 'pointer', transition: 'all 0.2s' }}>
                      <input type="checkbox" checked={(formData.visible_tabs||[]).includes(opt.key)} onChange={e => {
                        const tabs = formData.visible_tabs || [];
                        setFormData({...formData, visible_tabs: e.target.checked ? [...tabs, opt.key] : tabs.filter(t => t !== opt.key)});
                      }} style={{ accentColor: 'var(--rose)' }} />
                      <span style={{ fontSize: '14px' }}>{opt.label}</span>
                    </label>
                  ))}
                </div>
                <div style={{display:'flex',gap:'12px'}}>
                  <button className="btn btn-secondary btn-full" onClick={() => setShowModal(null)}>Cancel</button>
                  <button className="btn btn-primary btn-full" onClick={async () => {
                    try {
                      const fd = new FormData();
                      fd.append('theme', formData.theme || 'elegant');
                      fd.append('colors', JSON.stringify(formData.colors || {}));
                      fd.append('visible_tabs', JSON.stringify(formData.visible_tabs || []));
                      fd.append('dress_code', formData.dress_code || '');
                      if (formData.invitation_heading) fd.append('invitation_heading', formData.invitation_heading);
                      if (formData.host_name) fd.append('host_name', formData.host_name);
                      if (formData.videoUrl) fd.append('video_url', formData.videoUrl);
                      if (formData.audioUrl) fd.append('audio_url', formData.audioUrl);
                      if (formData.imageFile) fd.append('image', formData.imageFile);
                      await api.post(`/events/${id}/invitations`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
                      setShowModal(null); loadTabData(); showToast('Invitation created!');
                    } catch { showToast('Failed to create invitation', 'error'); }
                  }}>Create</button>
                </div>
              </>
            )}
            {showModal === 'editInvitation' && (
              <>
                <h2>Edit Invitation</h2>
                <div className="form-group">
                  <label>Color Theme</label>
                  <p style={{ fontSize: '13px', color: 'var(--gray)', marginBottom: '10px' }}>Choose a palette that matches your event — these are the same themes your guests will see</p>
                  <button
                    className="btn btn-sm"
                    style={{ marginBottom: '10px', width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', padding: '10px', borderRadius: '10px', border: 'none', background: 'linear-gradient(135deg, #E3425C, #C71585)', color: '#fff', fontWeight: 600 }}
                    onClick={() => {
                      const imgSrc = formData.custom_image || event.cover_image;
                      if (!imgSrc) { alert('Please upload an invitation image first so we can extract its colors'); return; }
                      extractImageColors(imgSrc, (colors) => {
                        if (colors) {
                          setFormData({...formData, theme: 'custom', colors, selectedPalette: 'custom'});
                        } else {
                          alert('Could not extract colors from the image. Please try another image or pick a palette below.');
                        }
                      });
                    }}
                  >
                    🎨 Match Image Colors
                  </button>
                  <button
                    className="btn btn-sm btn-outline"
                    style={{ marginBottom: '14px', width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', padding: '8px', borderRadius: '10px', border: '1px dashed var(--rose)', color: 'var(--rose)', fontWeight: 500, fontSize: '12px' }}
                    onClick={() => {
                      const matched = findClosestPalette(event.event_type);
                      const p = INVITATION_PALETTES.find(x => x.key === matched);
                      setFormData({...formData, theme: matched, colors: p ? { bg: p.bg, accent: p.accent, text: p.text } : {}, selectedPalette: matched});
                    }}
                  >
                    <FiZap size={12} /> Match Event Type</button>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(72px, 100%), 1fr))', gap: '6px' }}>
                    {INVITATION_PALETTES.map(p => {
                      const isSelected = formData.selectedPalette === p.key;
                      const isAutoMatched = formData.selectedPalette !== 'auto' ? false : findClosestPalette(event.event_type) === p.key;
                      return (
                        <div
                          key={p.key}
                          onClick={() => setFormData({...formData, theme: p.key, colors: { bg: p.bg, accent: p.accent, text: p.text }, selectedPalette: p.key})}
                          style={{
                            padding: '10px 6px',
                            borderRadius: '12px',
                            cursor: 'pointer',
                            textAlign: 'center',
                            border: isSelected ? '2px solid var(--rose)' : '2px solid transparent',
                            background: isSelected ? 'rgba(232,160,191,0.1)' : 'var(--card-bg)',
                            transition: 'all 0.2s ease',
                            position: 'relative',
                          }}
                        >
                          {isAutoMatched && formData.selectedPalette === 'auto' && (
                            <div style={{ position: 'absolute', top: '-6px', right: '-6px', background: 'var(--rose)', color: 'white', borderRadius: '50%', width: '18px', height: '18px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px' }}><FiCheck size={10} /></div>
                          )}
                          <div style={{ display: 'flex', gap: '3px', justifyContent: 'center', marginBottom: '6px' }}>
                            <span style={{ width: '18px', height: '18px', borderRadius: '50%', background: p.bg, border: '1px solid rgba(255,255,255,0.15)', display: 'block', flexShrink: 0 }} />
                            <span style={{ width: '18px', height: '18px', borderRadius: '50%', background: p.accent, border: '1px solid rgba(255,255,255,0.15)', display: 'block', flexShrink: 0 }} />
                            <span style={{ width: '18px', height: '18px', borderRadius: '50%', background: p.text, border: '1px solid rgba(255,255,255,0.15)', display: 'block', flexShrink: 0 }} />
                          </div>
                          <span style={{ fontSize: '11px', fontWeight: isSelected ? 600 : 400, color: isSelected ? 'var(--text)' : 'var(--gray)' }}>{p.label}</span>
                        </div>
                      );
                    })}
                  </div>
                  {/* Custom color pickers */}
                  <div style={{ marginTop: '12px', padding: '12px', borderRadius: '10px', background: 'var(--card-bg)', border: '1px solid var(--border-color)' }}>
                    <div style={{ fontSize: '12px', fontWeight: 600, marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '6px' }}>🎯 Custom Colors</div>
                    <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                      {[
                        { key: 'bg', label: 'Background', val: (formData.colors && formData.colors.bg) || '#2D1F2B' },
                        { key: 'accent', label: 'Accent', val: (formData.colors && formData.colors.accent) || '#E8A0BF' },
                        { key: 'text', label: 'Text', val: (formData.colors && formData.colors.text) || '#FFF5F8' },
                      ].map(c => (
                        <label key={c.key} style={{ flex: 1, minWidth: '80px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px', cursor: 'pointer' }}>
                          <span style={{ fontSize: '10px', color: 'var(--gray)', fontWeight: 500 }}>{c.label}</span>
                          <input type="color" value={c.val} onChange={e => {
                            const newColors = { ...(formData.colors || {}), [c.key]: e.target.value };
                            if (!newColors.bg) newColors.bg = '#2D1F2B';
                            if (!newColors.accent) newColors.accent = '#E8A0BF';
                            if (!newColors.text) newColors.text = '#FFF5F8';
                            setFormData({...formData, theme: 'custom', colors: newColors, selectedPalette: 'custom'});
                          }} style={{ width: '100%', height: '32px', borderRadius: '6px', border: '1px solid var(--border-color)', cursor: 'pointer', padding: '2px' }} />
                          <input type="text" value={c.val} onInput={e => {
                            let v = e.target.value.trim();
                            if (!v.startsWith('#')) v = '#' + v;
                            if (v.length > 7) v = v.slice(0, 7);
                            const newColors = { ...(formData.colors || {}), [c.key]: v };
                            if (!newColors.bg) newColors.bg = '#2D1F2B';
                            if (!newColors.accent) newColors.accent = '#E8A0BF';
                            if (!newColors.text) newColors.text = '#FFF5F8';
                            setFormData({...formData, theme: 'custom', colors: newColors, selectedPalette: 'custom'});
                          }} placeholder="#E3425C" maxLength={7} style={{ width: '100%', fontSize: '11px', fontWeight: 600, padding: '4px 6px', borderRadius: '6px', border: '1px solid var(--border-color)', textAlign: 'center', fontFamily: 'monospace', background: c.val || '#fff' }} />
                        </label>
                      ))}
                    </div>
                    {/* Opacity / Transparency slider */}
                    <div style={{ marginTop: '12px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                        <span style={{ fontSize: '10px', color: 'var(--gray)', fontWeight: 500 }}>Overlay</span>
                        <span style={{ fontSize: '10px', color: 'var(--text)', fontWeight: 600 }}>
                          {Math.round(((formData.colors && formData.colors.overlayOpacity) || 0.85) * 100)}%
                        </span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ fontSize: '10px', color: 'var(--gray)', whiteSpace: 'nowrap' }}>Transparent</span>
                        <input
                          type="range"
                          min="0.2" max="1" step="0.05"
                          value={(formData.colors && formData.colors.overlayOpacity) || 0.85}
                          onChange={e => {
                            const newColors = { ...(formData.colors || {}), overlayOpacity: parseFloat(e.target.value) };
                            if (!newColors.bg) newColors.bg = '#2D1F2B';
                            if (!newColors.accent) newColors.accent = '#E8A0BF';
                            if (!newColors.text) newColors.text = '#FFF5F8';
                            setFormData({...formData, theme: 'custom', colors: newColors, selectedPalette: 'custom'});
                          }}
                          style={{ flex: 1, accentColor: 'var(--rose)', height: '4px' }}
                        />
                        <span style={{ fontSize: '10px', color: 'var(--gray)', whiteSpace: 'nowrap' }}>Bold</span>
                      </div>
                    </div>
                  </div>
                  {(() => {
                    const sel = INVITATION_PALETTES.find(x => x.key === (formData.selectedPalette === 'auto' ? findClosestPalette(event.event_type) : formData.selectedPalette)) || INVITATION_PALETTES[0];
                    const useColors = (formData.selectedPalette !== 'auto' && formData.colors && formData.colors.bg) ? formData.colors : { bg: sel.bg, accent: sel.accent, text: sel.text };
                    const overlayOpacity = useColors.overlayOpacity !== undefined ? useColors.overlayOpacity : 0.85;
                    const previewBg = formData.custom_image || formData.imageFile ? (formData.imageFile ? URL.createObjectURL(formData.imageFile) : formData.custom_image) : (event.cover_image || getDefaultEventBanner(event.event_type));
                    return (
                      <div style={{ marginTop: '14px', borderRadius: '14px', overflow: 'hidden', border: `2px solid ${useColors.accent}40`, position: 'relative' }}>
                        <div style={{
                          position: 'relative', minHeight: '200px',
                          backgroundColor: useColors.bg,
                          backgroundImage: previewBg ? `url(${previewBg})` : 'none',
                          backgroundSize: 'cover', backgroundPosition: 'center',
                          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                          padding: '24px 20px', textAlign: 'center',
                        }}>
                          {previewBg && (
                            <div style={{ position: 'absolute', inset: 0, backgroundColor: useColors.bg, opacity: overlayOpacity, pointerEvents: 'none', zIndex: 0 }} />
                          )}
                          <div style={{ position: 'absolute', top: '-40px', right: '-40px', width: '100px', height: '100px', borderRadius: '50%', border: `1px solid ${useColors.accent}22`, pointerEvents: 'none' }} />
                          <div style={{ position: 'absolute', bottom: '-30px', left: '-30px', width: '80px', height: '80px', borderRadius: '50%', border: `1px solid ${useColors.accent}18`, pointerEvents: 'none' }} />
                          {/* Heading — matches InvitationView line 1041 */}
                          <div style={{ fontSize: '9px', textTransform: 'uppercase', letterSpacing: '4px', color: useColors.accent, marginBottom: '12px', fontWeight: 500, opacity: 0.75, zIndex: 1, fontFamily: 'Inter, sans-serif' }}>
                            {formData.invitation_heading || 'You\'re Invited to'}
                          </div>
                          {/* Star accent — matches InvitationView line 1061 */}
                          <div style={{ fontSize: '16px', color: useColors.accent, marginBottom: '10px', opacity: 0.6, zIndex: 1 }}>&#10022;</div>
                          {/* Event title — matches InvitationView line 1064 */}
                          <div style={{ fontFamily: "'Playfair Display', serif", fontSize: '20px', fontWeight: 700, letterSpacing: '-0.3px', color: useColors.text, zIndex: 1, marginBottom: '6px', lineHeight: 1.2, textShadow: previewBg && overlayOpacity < 0.6 ? '0 1px 4px rgba(0,0,0,0.6)' : 'none' }}>{event.title || 'Your Event'}</div>
                          {/* Hosted by — matches InvitationView line 1070 */}
                          <div style={{ fontSize: '11px', color: useColors.text, opacity: 0.85, zIndex: 1, marginBottom: '10px', fontFamily: 'Inter, sans-serif' }}>
                            Hosted by <span style={{ color: useColors.accent, fontWeight: 600 }}>{formData.host_name || event.host_name || 'Host'}</span>
                          </div>
                          {/* Divider — matches InvitationView line 1075 */}
                          <div style={{ width: '50px', height: '1px', background: `linear-gradient(90deg, ${useColors.accent}00, ${useColors.accent}88, ${useColors.accent}00)`, marginBottom: '10px', zIndex: 1 }} />
                          {/* Event details — date, venue, dress code — matches InvitationView lines 1077-1122 */}
                          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px', marginBottom: '10px', zIndex: 1 }}>
                            {event.event_date && (
                              <div style={{ textAlign: 'center' }}>
                                <div style={{ fontSize: '8px', opacity: 0.6, textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '1px', fontFamily: 'Inter, sans-serif' }}>Date</div>
                                <div style={{ fontSize: '12px', fontFamily: "'Playfair Display', serif" }}>
                                  {new Date(event.event_date).toLocaleDateString('en-ZA', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })}
                                </div>
                              </div>
                            )}
                            {event.location && (
                              <div style={{ textAlign: 'center' }}>
                                <div style={{ fontSize: '8px', opacity: 0.6, textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '1px', fontFamily: 'Inter, sans-serif' }}>Venue</div>
                                <div style={{ fontSize: '12px', fontFamily: "'Playfair Display', serif" }}>{event.location}</div>
                              </div>
                            )}
                            {(formData.dress_code || event.dress_code) && (
                              <div style={{ textAlign: 'center' }}>
                                <div style={{ fontSize: '8px', opacity: 0.6, textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '1px', fontFamily: 'Inter, sans-serif' }}>Dress Code</div>
                                <div style={{ fontSize: '12px', fontFamily: "'Playfair Display', serif" }}>{formData.dress_code || event.dress_code}</div>
                              </div>
                            )}
                          </div>
                          {/* Event description — matches InvitationView line 1125-1127 */}
                          {(formData.description || event.description) && (
                            <div style={{ fontSize: '10px', color: useColors.text, opacity: 0.75, zIndex: 1, lineHeight: 1.7, maxWidth: '92%', fontFamily: 'Inter, sans-serif', whiteSpace: 'pre-line' }}>
                              {formData.description || event.description}
                            </div>
                          )}
                        </div>
                        <div style={{ display: 'flex', height: '8px' }}>
                          <div style={{ flex: 1, background: useColors.bg }} />
                          <div style={{ flex: 1, background: useColors.accent }} />
                          <div style={{ flex: 1, background: useColors.text, borderLeft: '1px solid rgba(0,0,0,0.1)' }} />
                        </div>
                      </div>
                    );
                  })()}
                </div>
                <div className="form-group">
                  <label>Display Name (Hosted By)</label>
                  <p style={{ fontSize: '13px', color: 'var(--gray)', marginBottom: '8px' }}>The name shown as the host on your invitation (e.g., "Sarah & Thabo", "The Molefe Family")</p>
                  <input value={formData.host_name || ''} onChange={e => setFormData({...formData, host_name: e.target.value})} placeholder={event.host_name || 'Your name'} />
                </div>
                <div className="form-group">
                  <label>Invitation Heading</label>
                  <p style={{ fontSize: '13px', color: 'var(--gray)', marginBottom: '8px' }}>The greeting text shown at the top of your invitation</p>
                  <select value={formData.invitation_heading || ''} onChange={e => setFormData({...formData, invitation_heading: e.target.value === 'custom' ? 'custom' : e.target.value})}>
                    <option value="">You're Invited to (default)</option>
                    <option value="You Are Cordially Invited to">You Are Cordially Invited to</option>
                    <option value="You're Warmly Invited to">You're Warmly Invited to</option>
                    <option value="We Request the Honour of Your Presence at">We Request the Honour of Your Presence at</option>
                    <option value="Join Us in Celebrating">Join Us in Celebrating</option>
                    <option value="Save the Date">Save the Date</option>
                    <option value="In Loving Memory of">In Loving Memory of</option>
                    <option value="Umemiwe">Umemiwe (Zulu)</option>
                    <option value="O Memeletswe">O Memeletswe (Sotho/Tswana)</option>
                    <option value="U Rambiwa">U Rambiwa (Venda)</option>
                    <option value="custom">Custom...</option>
                  </select>
                  {(formData.invitation_heading === 'custom' || (formData.invitation_heading && !['','You Are Cordially Invited to',"You're Warmly Invited to",'We Request the Honour of Your Presence at','Join Us in Celebrating','Save the Date','In Loving Memory of','Umemiwe','O Memeletswe','U Rambiwa'].includes(formData.invitation_heading))) && (
                    <input value={formData.invitation_heading === 'custom' ? '' : formData.invitation_heading || ''} onChange={e => setFormData({...formData, invitation_heading: e.target.value})} placeholder="e.g., Woza Sizobungaza, Le a Laletšwa..." style={{ marginTop: '8px' }} />
                  )}
                </div>
                <div className="form-group">
                  <label>Dress Code</label>
                  <p style={{ fontSize: '13px', color: 'var(--gray)', marginBottom: '8px' }}>Let your guests know what to wear</p>
                  <select value={['Formal / Black Tie','Semi-Formal','Smart Casual','Casual','Traditional / Cultural Attire','All White','All Black','Cocktail Attire','Garden Party','Themed'].includes(formData.dress_code) ? formData.dress_code : formData.dress_code ? 'custom' : ''} onChange={e => setFormData({...formData, dress_code: e.target.value === 'custom' ? (formData.dress_code && !['Formal / Black Tie','Semi-Formal','Smart Casual','Casual','Traditional / Cultural Attire','All White','All Black','Cocktail Attire','Garden Party','Themed'].includes(formData.dress_code) ? formData.dress_code : '') : e.target.value})}>
                    <option value="">No dress code</option>
                    <option value="Formal / Black Tie">Formal / Black Tie</option>
                    <option value="Semi-Formal">Semi-Formal</option>
                    <option value="Smart Casual">Smart Casual</option>
                    <option value="Casual">Casual</option>
                    <option value="Traditional / Cultural Attire">Traditional / Cultural Attire</option>
                    <option value="All White">All White</option>
                    <option value="All Black">All Black</option>
                    <option value="Cocktail Attire">Cocktail Attire</option>
                    <option value="Garden Party">Garden Party</option>
                    <option value="Themed">Themed</option>
                    <option value="custom">Custom...</option>
                  </select>
                  {(formData.dress_code && !['','Formal / Black Tie','Semi-Formal','Smart Casual','Casual','Traditional / Cultural Attire','All White','All Black','Cocktail Attire','Garden Party','Themed'].includes(formData.dress_code)) && (
                    <input value={formData.dress_code} onChange={e => setFormData({...formData, dress_code: e.target.value})} placeholder="e.g., Red and Gold, African Print..." style={{ marginTop: '8px' }} />
                  )}
                </div>
                <div className="form-group">
                  <label>Upload Your Invitation</label>
                  <p style={{ fontSize: '13px', color: 'var(--gray)', marginBottom: '8px' }}>Already have a designed invitation? Upload your own invitation image here — it will be shown to guests on the invitation page</p>
                  {(formData.existingBanner || formData.imageFile) && (
                    <div style={{ position: 'relative', marginBottom: '8px' }}>
                      <img src={formData.imageFile ? URL.createObjectURL(formData.imageFile) : formData.existingBanner} alt="Your Invitation" style={{ width: '100%', maxHeight: '400px', objectFit: 'contain', borderRadius: '8px', background: '#f8f8f8' }} />
                      <button onClick={() => setFormData({...formData, imageFile: null, existingBanner: ''})} style={{ position: 'absolute', top: '8px', right: '8px', background: 'rgba(0,0,0,0.6)', color: 'white', border: 'none', borderRadius: '50%', width: '36px', height: '36px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><FiX size={18} /></button>
                    </div>
                  )}
                  <input type="file" accept="image/*" onChange={e => { if (e.target.files[0]) { setFormData({ ...formData, imageFile: e.target.files[0] }); } }} style={{ padding: '10px' }} />
                </div>
                <div className="form-group">
                  <label>Video Invitation (Optional)</label>
                  <p style={{ fontSize: '13px', color: 'var(--gray)', marginBottom: '8px' }}>Add a personal video message for your guests — upload a new one to replace</p>
                  <input type="file" accept="video/*" onChange={async (e) => {
                    const file = e.target.files[0]; if (!file) return;
                    const fd = new FormData(); fd.append('video', file);
                    try {
                      const res = await api.post(`/events/${id}/invitations/video`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
                      setFormData({...formData, videoUrl: res.data.video_url}); showToast('Video uploaded!');
                    } catch { showToast('Failed to upload video', 'error'); }
                  }} style={{ padding: '10px' }} />
                  {formData.videoUrl && (
                    <div style={{ marginTop: '10px', borderRadius: '10px', overflow: 'hidden', position: 'relative' }}>
                      <video src={formData.videoUrl} controls style={{ width: '100%', maxHeight: '180px', background: '#000' }} />
                      <button onClick={() => setFormData({...formData, videoUrl: ''})} style={{ position: 'absolute', top: '8px', right: '8px', background: 'rgba(0,0,0,0.6)', color: 'white', border: 'none', borderRadius: '50%', width: '36px', height: '36px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><FiX size={18} /></button>
                    </div>
                  )}
                </div>
                <div className="form-group">
                  <label>Event Music (Optional)</label>
                  <p style={{ fontSize: '13px', color: 'var(--gray)', marginBottom: '8px' }}>Pick a song or voice note from your phone — guests can play it on the invitation page. Use music you have the rights to.</p>
                  <input type="file" accept="audio/*" onChange={async (e) => {
                    const file = e.target.files[0]; if (!file) return;
                    if (file.size > 15 * 1024 * 1024) { showToast('Music file too large — max 15MB', 'error'); return; }
                    const fd = new FormData(); fd.append('audio', file);
                    try {
                      const res = await api.post(`/events/${id}/invitations/audio`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
                      setFormData({...formData, audioUrl: res.data.audio_url}); showToast('Music uploaded!');
                    } catch { showToast('Failed to upload music', 'error'); }
                  }} style={{ padding: '10px' }} />
                  {formData.audioUrl && (
                    <div style={{ marginTop: '10px', position: 'relative' }}>
                      <audio src={formData.audioUrl} controls style={{ width: '100%' }} />
                      <button onClick={() => setFormData({...formData, audioUrl: ''})} style={{ position: 'absolute', top: '8px', right: '8px', background: 'rgba(0,0,0,0.6)', color: 'white', border: 'none', borderRadius: '50%', width: '36px', height: '36px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><FiX size={18} /></button>
                    </div>
                  )}
                </div>
                <div className="form-group">
                  <label>RSVP Deadline (Optional)</label>
                  <p style={{ fontSize: '13px', color: 'var(--gray)', marginBottom: '10px' }}>Guests cannot RSVP after this date. Leave empty for no deadline.</p>
                  <input type="date" className="form-control" value={rsvpDeadline ? rsvpDeadline.slice(0, 10) : ''}
                    onChange={e => {
                      const val = e.target.value ? e.target.value + 'T23:59:59.000Z' : '';
                      setRsvpDeadline(val);
                      api.put(`/events/${id}`, { rsvp_deadline: val || null });
                    }}
                    style={{ fontSize: '14px' }} />
                </div>
                <div className="form-group">
                  <label>What Guests Can See</label>
                  <p style={{ fontSize: '13px', color: 'var(--gray)', marginBottom: '10px' }}>Choose which sections are visible to your guests</p>
                  {[
                    { key: 'wall', label: 'Event Wall' },
                    { key: 'lineup', label: 'Lineup & Programme' },
                    { key: 'registry', label: 'Gift Registry' },
                    { key: 'polls', label: 'Polls' },
                    { key: 'qa', label: 'Q&A' },
                    { key: 'gallery', label: 'Gallery' },
                    { key: 'tickets', label: 'Digital Tickets' },
                  ].map(opt => (
                    <label key={opt.key} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '20px', padding: '10px 12px', borderRadius: '8px', background: (formData.visible_tabs||[]).includes(opt.key) ? 'rgba(232,160,191,0.1)' : 'transparent', border: `1px solid ${(formData.visible_tabs||[]).includes(opt.key) ? 'var(--rose)' : 'var(--gray-light)'}`, marginBottom: '6px', cursor: 'pointer', transition: 'all 0.2s' }}>
                      <span style={{ fontSize: '14px', flex: 1 }}>{opt.label}</span>
                      <input type="checkbox" checked={(formData.visible_tabs||[]).includes(opt.key)} onChange={e => {
                        const tabs = formData.visible_tabs || [];
                        setFormData({...formData, visible_tabs: e.target.checked ? [...tabs, opt.key] : tabs.filter(t => t !== opt.key)});
                      }} style={{ accentColor: 'var(--rose)', flexShrink: 0, marginLeft: '16px' }} />
                    </label>
                  ))}
                </div>
                <div style={{display:'flex',gap:'12px'}}>
                  <button className="btn btn-secondary btn-full" onClick={() => setShowModal(null)}>Cancel</button>
                  <button className="btn btn-primary btn-full" onClick={async () => {
                    try {
                      const fd = new FormData();
                      fd.append('theme', formData.theme || 'elegant');
                      fd.append('colors', JSON.stringify(formData.colors || {}));
                      fd.append('visible_tabs', JSON.stringify(formData.visible_tabs || []));
                      fd.append('dress_code', formData.dress_code || '');
                      fd.append('invitation_heading', formData.invitation_heading || '');
                      fd.append('host_name', formData.host_name || '');
                      fd.append('video_url', formData.videoUrl || '');
                      fd.append('audio_url', formData.audioUrl || '');
                      if (formData.imageFile) fd.append('image', formData.imageFile);
                      await api.put(`/events/${id}/invitations/${formData.invId}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
                      setShowModal(null); loadTabData(); showToast('Invitation updated!');
                    } catch { showToast('Failed to update invitation', 'error'); }
                  }}>Save Changes</button>
                </div>
              </>
            )}
            {showModal === 'galleryEdit' && (
              <>
                <h2>Edit Photo</h2>
                <div style={{ marginBottom: '16px', borderRadius: '12px', overflow: 'hidden' }}>
                  {formData.galleryType === 'video' ? (
                    <video src={formData.galleryUrl} controls style={{ width: '100%', maxHeight: '300px', objectFit: 'contain', background: '#000' }} />
                  ) : (
                    <img src={formData.galleryUrl} alt="" style={{ width: '100%', maxHeight: '300px', objectFit: 'contain', background: '#f5f5f5', borderRadius: '12px' }} />
                  )}
                </div>
                <div className="form-group">
                  <label>Caption</label>
                  <input value={formData.galleryCaption || ''} onChange={e => setFormData({...formData, galleryCaption: e.target.value})} placeholder="Add a caption..." />
                </div>
                <div className="form-group">
                  <label>Frame Size</label>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    {[{k:'small',l:'Small',h:'180px'},{k:'medium',l:'Medium',h:'260px'},{k:'large',l:'Large',h:'340px'}].map(s => (
                      <button key={s.k} type="button" onClick={() => setFormData({...formData, galleryFrame: s.k})} style={{ flex: 1, padding: '12px', borderRadius: '10px', background: formData.galleryFrame === s.k ? 'var(--dark)' : 'var(--gray-light)', color: formData.galleryFrame === s.k ? 'white' : 'var(--dark)', border: 'none', cursor: 'pointer', fontSize: '13px', fontWeight: 600, transition: 'all 0.2s' }}>
                        <div style={{ width: '100%', height: s.h === '180px' ? '30px' : s.h === '260px' ? '45px' : '60px', background: formData.galleryFrame === s.k ? 'rgba(255,255,255,0.2)' : 'var(--cream-dark)', borderRadius: '4px', marginBottom: '6px' }} />
                        {s.l}
                      </button>
                    ))}
                  </div>
                </div>
                <div style={{display:'flex',gap:'12px'}}>
                  <button className="btn btn-secondary btn-full" onClick={() => setShowModal(null)}>Cancel</button>
                  <button className="btn btn-primary btn-full" onClick={async () => {
                    try {
                      await api.put(`/events/${id}/gallery/${formData.galleryId}`, { caption: formData.galleryCaption, frame_size: formData.galleryFrame });
                      setShowModal(null); loadTabData(); showToast('Photo updated!');
                    } catch { showToast('Failed to update', 'error'); }
                  }}>Save</button>
                </div>
              </>
            )}
            {/* AI Invitation Designer is now a fullscreen component rendered outside the modal */}
            {showModal === 'serviceRequest' && (
              <>
                <h2>AI Event Media Package — R400</h2>
                <p style={{ fontSize: '13px', color: 'var(--gray)', margin: '-8px 0 16px' }}>You'll be directed to an AI design assistant who will create your AI video invitation, tailored AI invitations, printable welcome board and event programme. They'll get in touch with you after payment.</p>
                <div className="form-group"><label>Event Name</label><input value={formData.sr_event_name || ''} onChange={e => setFormData({...formData, sr_event_name: e.target.value})} /></div>
                <div className="form-group"><label>Event Date</label><input type="date" value={formData.sr_event_date || ''} onChange={e => setFormData({...formData, sr_event_date: e.target.value})} /></div>
                <div className="form-group"><label>Style Preferences (optional)</label>
                  <textarea value={formData.sr_style || ''} onChange={e => setFormData({...formData, sr_style: e.target.value})} rows={2} placeholder="e.g. Elegant gold & cream, traditional African print, soft pink pastels..." />
                </div>
                <div className="form-group"><label>Your Photos (optional — for personalised invitations & welcome board)</label>
                  <input type="file" accept="image/*" multiple onChange={e => setSrPhotos(Array.from(e.target.files || []).slice(0, 6))} style={{ padding: '10px' }} />
                  {srPhotos.length > 0 && <p style={{ fontSize: '12px', color: 'var(--gray)', marginTop: '6px' }}>{srPhotos.length} photo(s) selected</p>}
                </div>
                <div className="form-group"><label>Contact Number</label><input value={formData.sr_phone || ''} onChange={e => setFormData({...formData, sr_phone: e.target.value})} placeholder="e.g. 071 234 5678" /></div>
                <div className="form-group"><label>Anything Else? (optional)</label>
                  <textarea value={formData.sr_notes || ''} onChange={e => setFormData({...formData, sr_notes: e.target.value})} rows={2} placeholder="Special requests, timeline, notes for your assistant..." />
                </div>
                <div style={{display:'flex',gap:'12px'}}>
                  <button className="btn btn-secondary btn-full" onClick={() => setShowModal(null)}>Cancel</button>
                  <button className="btn btn-primary btn-full" disabled={srSubmitting} onClick={async () => {
                    if (srSubmitting) return;
                    setSrSubmitting(true);
                    try {
                      const fd = new FormData();
                      fd.append('event_id', id);
                      fd.append('event_name', formData.sr_event_name || '');
                      fd.append('event_date', formData.sr_event_date || '');
                      fd.append('style_preferences', formData.sr_style || '');
                      fd.append('notes', formData.sr_notes || '');
                      fd.append('contact_phone', formData.sr_phone || '');
                      srPhotos.forEach(p => fd.append('photos', p));
                      const created = await api.post('/service-requests', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
                      const pay = await api.post('/ozow/service-request/initialize', { request_id: created.data.id, event_id: id });
                      if (pay.data?.success && pay.data?.url && pay.data?.params) {
                        const f = document.createElement('form');
                        f.method = 'POST'; f.action = pay.data.url; f.style.display = 'none';
                        Object.entries(pay.data.params).forEach(([k, v]) => { const inp = document.createElement('input'); inp.type = 'hidden'; inp.name = k; inp.value = v; f.appendChild(inp); });
                        document.body.appendChild(f); f.submit();
                        return;
                      }
                      alert(pay.data?.error || 'Failed to start payment.');
                    } catch (err) { alert(err.response?.data?.error || 'Failed to submit request.'); }
                    setSrSubmitting(false);
                  }}>{srSubmitting ? 'Starting payment...' : 'Pay R400 & Submit'}</button>
                </div>
              </>
            )}
            {showModal === 'poll' && (
              <>
                <h2>Create Poll</h2>
                <div className="form-group"><label>Question</label><input value={formData.question||''} onChange={e => setFormData({...formData, question: e.target.value})} placeholder="Your poll question" /></div>
                {(formData.options||[]).map((opt, i) => (
                  <div className="form-group" key={i}><label>Option {i+1}</label>
                    <input value={opt} onChange={e => { const opts = [...formData.options]; opts[i] = e.target.value; setFormData({...formData, options: opts}); }} />
                  </div>
                ))}
                <button className="btn btn-sm btn-secondary" style={{marginBottom:'16px'}} onClick={() => setFormData({...formData, options: [...(formData.options||[]), '']})}>+ Add Option</button>
                <div style={{display:'flex',gap:'12px'}}>
                  <button className="btn btn-secondary btn-full" onClick={() => setShowModal(null)}>Cancel</button>
                  <button className="btn btn-primary btn-full" onClick={async () => { await api.post(`/events/${id}/polls`, formData); setShowModal(null); loadTabData(); }}>Create Poll</button>
                </div>
              </>
            )}
            {showModal === 'qa' && (
              <>
                <h2>Ask a Question</h2>
                <div className="form-group"><label>Question</label><textarea value={formData.question||''} onChange={e => setFormData({...formData, question: e.target.value})} rows={3} placeholder="Your question..." /></div>
                <div style={{display:'flex',gap:'12px'}}>
                  <button className="btn btn-secondary btn-full" onClick={() => setShowModal(null)}>Cancel</button>
                  <button className="btn btn-primary btn-full" onClick={async () => { await api.post(`/events/${id}/qa`, { question: formData.question }); setShowModal(null); loadTabData(); }}>Submit</button>
                </div>
              </>
            )}
            {showModal === 'editEvent' && (
              <>
                <h2>Edit Event</h2>
                <div className="form-group"><label>Event Title</label><input value={formData.title||''} onChange={e => setFormData({...formData, title: e.target.value})} /></div>
                <div className="form-group"><label>Event Type</label>
                  <select value={formData.event_type||''} onChange={e => setFormData({...formData, event_type: e.target.value})}>
                    {[...STANDARD_EVENT_TYPES, 'Custom Event'].map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                  {formData.event_type === 'Custom Event' && (
                    <input value={formData.custom_event_type || ''} onChange={e => setFormData({...formData, custom_event_type: e.target.value})} placeholder="Enter your celebration type" style={{ marginTop: '8px' }} />
                  )}
                </div>
                <div className="form-group"><label>Event Date</label><input type="date" value={formData.event_date||''} onChange={e => setFormData({...formData, event_date: e.target.value})} /></div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div className="form-group" style={{ flex: 1, marginBottom: 0 }}><label>Start Time</label><input type="time" value={formData.event_time||''} onChange={e => setFormData({...formData, event_time: e.target.value})} /></div>
                  <div className="form-group" style={{ flex: 1, marginBottom: 0 }}><label>End Time</label><input type="time" value={formData.end_time||''} onChange={e => setFormData({...formData, end_time: e.target.value})} /></div>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', marginTop: '20px', fontSize: '13px', color: 'var(--gray)', whiteSpace: 'nowrap' }}>
                    <input type="checkbox" checked={formData.till_late || false} onChange={e => setFormData({...formData, till_late: e.target.checked})} style={{ width: '16px', height: '16px' }} />
                    Till Late
                  </label>
                </div>
                <div className="form-group"><label>Location</label><input value={formData.location||''} onChange={e => setFormData({...formData, location: e.target.value})} placeholder="e.g., Pretoria, Gauteng" /></div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                  <div className="form-group"><label>Estimated Guests</label><input type="number" value={formData.estimated_guests||''} onChange={e => setFormData({...formData, estimated_guests: e.target.value})} /></div>
                  <div className="form-group"><label>Budget (R)</label><input type="number" value={formData.budget||''} onChange={e => setFormData({...formData, budget: e.target.value})} /></div>
                </div>
                <div className="form-group">
                  <label style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px' }}>
                    <span>Description</span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <select
                        value={descTone}
                        onChange={e => setDescTone(e.target.value)}
                        style={{ fontSize: '10px', padding: '4px 8px', borderRadius: '12px', border: '1px solid var(--border)', background: 'var(--bg)', color: 'var(--text)', cursor: 'pointer', outline: 'none', fontFamily: 'Inter, sans-serif' }}
                        title="Select tone for AI description"
                      >
                        <option value="warm">Warm</option>
                        <option value="professional">Professional</option>
                        <option value="funny">Funny</option>
                        <option value="formal">Formal</option>
                      </select>
                      <button type="button" className="btn btn-sm"
                        disabled={genDesc || !formData.title}
                        onClick={async () => {
                          setGenDesc(true);
                          try {
                            const ctx = [];
                            if (formData.title) ctx.push(`Event: "${formData.title}"`);
                            if (formData.event_type) ctx.push(`Type: ${formData.event_type}`);
                            if (formData.location) ctx.push(`Location: ${formData.location}`);
                            if (formData.estimated_guests) ctx.push(`Guests: ${formData.estimated_guests}`);
                            if (formData.theme_color) ctx.push(`Theme: ${formData.theme_color}`);
                            if (formData.dress_code) ctx.push(`Dress code: ${formData.dress_code}`);
                            const toneMap = {
                              warm: 'warm, engaging',
                              professional: 'professional, polished',
                              funny: 'light-hearted, humorous',
                              formal: 'elegant, formal'
                            };
                            const prompt = `You are writing an event description. Output ONLY the raw description text — no quotes, no "Here is", no markdown, no preamble, no suggestions, no commentary. Just the description.\n\nEvent details:\n${ctx.join('\n')}\n\nWrite a ${toneMap[descTone] || 'warm, engaging'} 2-3 sentence event description in South African English:`;
                            const res = await api.post('/ai/chat', { message: prompt, language: 'en', event_type: formData.event_type || 'Event', event_context: ctx.join('; ') });
                            const text = cleanAiText(res.data?.response || '');
                            setFormData(prev => ({ ...prev, description: text }));
                          } catch {}
                          setGenDesc(false);
                        }}
                        style={{ fontSize: '11px', padding: '5px 12px', background: genDesc ? 'var(--gray-light)' : 'linear-gradient(135deg, #e3425c, #d4a853)', color: genDesc ? 'var(--gray)' : 'white', border: 'none', borderRadius: '20px', fontWeight: 600, cursor: genDesc || !formData.title ? 'not-allowed' : 'pointer', opacity: genDesc || !formData.title ? 0.6 : 1 }}>
                        <FiZap size={12} style={{ marginRight: '4px', verticalAlign: 'middle' }} />
                        {genDesc ? 'Generating...' : formData.description ? 'Regenerate' : 'AI Generate'}
                      </button>
                    </div>
                  </label>
                  <textarea value={formData.description||''} onChange={e => setFormData({...formData, description: e.target.value})} rows={3} placeholder="Describe your event..." />
                </div>
                <div className="form-group">
                  <label>Dress Code</label>
                  <select value={['Formal / Black Tie','Semi-Formal','Smart Casual','Casual','Traditional / Cultural Attire','All White','All Black','Cocktail Attire','Garden Party','Themed'].includes(formData.dress_code) ? formData.dress_code : formData.dress_code ? 'custom' : ''} onChange={e => setFormData({...formData, dress_code: e.target.value === 'custom' ? (formData.dress_code && !['Formal / Black Tie','Semi-Formal','Smart Casual','Casual','Traditional / Cultural Attire','All White','All Black','Cocktail Attire','Garden Party','Themed'].includes(formData.dress_code) ? formData.dress_code : '') : e.target.value})}>
                    <option value="">No dress code</option>
                    <option value="Formal / Black Tie">Formal / Black Tie</option>
                    <option value="Semi-Formal">Semi-Formal</option>
                    <option value="Smart Casual">Smart Casual</option>
                    <option value="Casual">Casual</option>
                    <option value="Traditional / Cultural Attire">Traditional / Cultural Attire</option>
                    <option value="All White">All White</option>
                    <option value="All Black">All Black</option>
                    <option value="Cocktail Attire">Cocktail Attire</option>
                    <option value="Garden Party">Garden Party</option>
                    <option value="Themed">Themed</option>
                    <option value="custom">Custom...</option>
                  </select>
                  {(formData.dress_code && !['','Formal / Black Tie','Semi-Formal','Smart Casual','Casual','Traditional / Cultural Attire','All White','All Black','Cocktail Attire','Garden Party','Themed'].includes(formData.dress_code)) && (
                    <input value={formData.dress_code} onChange={e => setFormData({...formData, dress_code: e.target.value})} placeholder="e.g., Red and Gold, African Print..." style={{ marginTop: '8px' }} />
                  )}
                </div>
                <div className="form-group">
                  <label>Event Theme Colors</label>
                  <input value={formData.theme_color || ''} onChange={e => setFormData({...formData, theme_color: e.target.value})} placeholder="e.g., Gold and Rose Pink, Navy Blue and Silver..." />
                  <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginTop: '8px' }}>
                    {['Gold & White','Black & Gold','Rose Pink & Gold','Navy Blue & Silver','Burgundy & Gold','All White','Green & Gold','Purple & Silver','Red & Black','Emerald & Cream'].map(t => (
                      <button type="button" key={t} onClick={() => setFormData({...formData, theme_color: t})} style={{ padding: '5px 12px', borderRadius: '20px', fontSize: '12px', background: formData.theme_color === t ? 'var(--dark)' : 'var(--gray-light)', color: formData.theme_color === t ? 'white' : 'var(--gray)', border: 'none', cursor: 'pointer', fontWeight: 500, transition: 'all 0.2s' }}>{t}</button>
                    ))}
                  </div>
                </div>
                <div className="form-group" style={{ background: 'var(--cream)', padding: '16px', borderRadius: '12px', border: '1px solid rgba(0,0,0,0.06)' }}>
                  <label style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: formData.show_host_contact ? '12px' : 0 }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><FiPhone size={14} /> Contact Details on Invitation</span>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '13px', color: formData.show_host_contact ? '#10B981' : 'var(--gray)', margin: 0 }}>
                      <input type="checkbox" checked={formData.show_host_contact || false} onChange={e => setFormData({...formData, show_host_contact: e.target.checked})} style={{ width: '18px', height: '18px', accentColor: '#10B981' }} />
                      {formData.show_host_contact ? 'Visible' : 'Hidden'}
                    </label>
                  </label>
                  {formData.show_host_contact && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                      <div>
                        <label style={{ fontSize: '12px', color: 'var(--gray)', marginBottom: '4px' }}>Contact Name</label>
                        <input value={formData.host_contact_name || ''} onChange={e => setFormData({...formData, host_contact_name: e.target.value})} placeholder="e.g. Lesego" />
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                        <div>
                          <label style={{ fontSize: '12px', color: 'var(--gray)', marginBottom: '4px' }}>WhatsApp Number</label>
                          <input value={formData.host_contact_phone || ''} onChange={e => setFormData({...formData, host_contact_phone: e.target.value})} placeholder="e.g. 071 234 5678" />
                        </div>
                        <div>
                          <label style={{ fontSize: '12px', color: 'var(--gray)', marginBottom: '4px' }}>Email (optional)</label>
                          <input type="email" value={formData.host_contact_email || ''} onChange={e => setFormData({...formData, host_contact_email: e.target.value})} placeholder="your@email.com" />
                        </div>
                      </div>
                    </div>
                  )}
                  {!formData.show_host_contact && (
                    <p style={{ fontSize: '12px', color: 'var(--gray)', margin: '8px 0 0' }}>Enable to show a "For Enquiries" section on your invitation with your contact details.</p>
                  )}
                </div>
                <div className="form-group">
                  <label>Cover Image</label>
                  {(formData.existingImage || formData.coverFile) && (
                    <div style={{ position: 'relative', marginBottom: '10px' }}>
                      <img src={formData.coverFile ? URL.createObjectURL(formData.coverFile) : formData.existingImage} alt="Cover" style={{ width: '100%', height: '160px', objectFit: 'cover', borderRadius: '8px' }} />
                      <button onClick={() => setFormData({...formData, coverFile: null, existingImage: '', removeImage: true})} style={{ position: 'absolute', top: '8px', right: '8px', background: 'rgba(0,0,0,0.6)', color: 'white', border: 'none', borderRadius: '50%', width: '36px', height: '36px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><FiX size={18} /></button>
                    </div>
                  )}
                  <input type="file" accept="image/*" onChange={e => { if (e.target.files[0]) { setCropperImage({ src: URL.createObjectURL(e.target.files[0]), field: 'coverFile', aspect: 16/9, title: 'Crop Cover Image' }); } }} style={{ padding: '10px' }} />
                </div>
                <div style={{display:'flex',gap:'12px'}}>
                  <button className="btn btn-secondary btn-full" onClick={() => setShowModal(null)}>Cancel</button>
                  <button className="btn btn-primary btn-full" onClick={async () => {
                    try {
                      const fd = new FormData();
                      fd.append('title', formData.title || '');
                      fd.append('event_type', formData.event_type === 'Custom Event' ? (formData.custom_event_type || '') : (formData.event_type || ''));
                      if (formData.event_date) fd.append('event_date', formData.event_date);
                      if (formData.event_time) fd.append('event_time', formData.event_time);
                      if (formData.end_time) fd.append('end_time', formData.end_time);
                      fd.append('till_late', formData.till_late ? 'true' : 'false');
                      fd.append('location', formData.location || '');
                      fd.append('estimated_guests', formData.estimated_guests || '');
                      fd.append('budget', formData.budget || '');
                      fd.append('description', formData.description || '');
                      fd.append('dress_code', formData.dress_code || '');
                      fd.append('theme_color', formData.theme_color || '');
                      fd.append('show_host_contact', formData.show_host_contact ? 'true' : 'false');
                      fd.append('host_contact_name', formData.host_contact_name || '');
                      fd.append('host_contact_phone', formData.host_contact_phone || '');
                      fd.append('host_contact_email', formData.host_contact_email || '');
                      if (formData.coverFile) {
                        fd.append('cover_image', formData.coverFile);
                        fd.append('removeImage', 'false');
                      } else if (formData.removeImage) {
                        fd.append('cover_image_url', '');
                        fd.append('removeImage', 'true');
                      }
                      const response = await api.put(`/events/${id}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
                      console.log('Event update response:', response.data);
                      setShowModal(null); loadEvent(); showToast('Event updated!');
                    } catch (err) {
                      console.error('Failed to update event:', err);
                      console.error('Error details:', err.response?.data);
                      showToast(err.response?.data?.error || err.message || 'Failed to update event', 'error');
                    }
                  }}>Save Changes</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* GiGi Bot */}
      <GigiBot
        userName={user?.name}
        event={event}
        context={{
          tab,
          guestCount: guests.length,
          budgetItems: budget.length,
          checklistItems: checklist.length,
          timelineItems: timeline.length,
          invitationCount: invitations.length,
          speakerCount: speakers.length,
          registryItems: registry.length,
          hostName: user?.name,
          invitationTheme: invitations[0]?.theme,
          invitationColors: invitations[0]?.colors,
        }}
        onAction={async (type, data) => {
          try {
            if (type === 'fill_description') {
              const cleaned = cleanAiText(data.description);
              await api.put(`/events/${id}`, { description: cleaned });
              loadEvent();
              showToast('Description updated by GiGi!');
            } else if (type === 'add_budget_batch') {
              // Batch mode: replace all budget items at once
              await api.post(`/events/${id}/budget`, { batch: data });
              loadTabData();
            } else if (type === 'add_budget') {
              await api.post(`/events/${id}/budget`, data);
              loadTabData();
            } else if (type === 'add_checklist_batch') {
              await api.post(`/events/${id}/checklist`, { batch: data });
              loadTabData();
            } else if (type === 'add_checklist') {
              await api.post(`/events/${id}/checklist`, { title: data.title, ai_generated: true });
              loadTabData();
            } else if (type === 'add_timeline_batch') {
              await api.post(`/events/${id}/timeline`, { batch: data });
              loadTabData();
            } else if (type === 'add_timeline') {
              await api.post(`/events/${id}/timeline`, data);
              loadTabData();
            } else if (type === 'remove_timeline') {
              await api.delete(`/events/${id}/timeline/${data.id}`);
              loadTabData();
            } else if (type === 'update_timeline') {
              await api.put(`/events/${id}/timeline/${data.id}`, { item_time: data.item_time, title: data.title, description: data.description });
              loadTabData();
            } else if (type === 'reload_event') {
              loadEvent();
              loadTabData();
              showToast('Event date updated by GiGi!');
              return;
            } else if (type === 'reload_guests') {
              loadTabData();
              return;
            } else if (type === 'show_toast') {
              showToast(data.message);
              if (data.tab) setTab(data.tab);
              loadTabData();
              return;
            } else if (type === 'fill_thankyou') {
              setThankYouData(d => ({ ...d, message: data.message, host_signature: data.host_signature || d.host_signature, card_style: data.card_style || d.card_style }));
              setTab('invitations');
              showToast('GiGi wrote your thank you message! Scroll down to see it.');
            }
          } catch {}
        }}
      />

      {/* AI Invitation Designer */}
      {showAIDesigner && (
        <AIInvitationDesigner
          eventId={id}
          eventType={event?.event_type || ''}
          onClose={() => setShowAIDesigner(false)}
          onUseDesign={async (imageFile) => {
            try {
              const fd = new FormData();
              fd.append('theme', 'custom');
              fd.append('colors', JSON.stringify({
                bg: '#1a1a2e',
                accent: '#E3425C',
                text: '#ffffff',
              }));
              fd.append('visible_tabs', JSON.stringify(['wall','lineup','registry','polls','qa','gallery']));
              fd.append('dress_code', '');
              fd.append('invitation_type', 'ai_designer');
              fd.append('image', imageFile);
              await api.post(`/events/${id}/invitations`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
              setShowAIDesigner(false);
              loadTabData();
              showToast('AI Invitation created!');
            } catch { showToast('Failed to create invitation', 'error'); }
          }}
        />
      )}

      {/* Image Cropper Modal */}
      {cropperImage && (
        <ImageCropper
          imageSrc={cropperImage.src}
          aspect={cropperImage.aspect}
          title={cropperImage.title}
          onCancel={() => setCropperImage(null)}
          onCropDone={(croppedBlob) => {
            const file = new File([croppedBlob], 'cropped-image.jpg', { type: 'image/jpeg' });
            if (cropperImage.field === 'coverFile') {
              setFormData(prev => ({ ...prev, coverFile: file, removeImage: false }));
              setCropperImage(null);
              showToast('Image cropped successfully');
            } else {
              setFormData(prev => ({ ...prev, [cropperImage.field]: file }));
              setCropperImage(null);
              showToast('Image updated successfully');
            }
          }}
        />
      )}
    </div>
  );
}

// ─── VENDOR CHAT TAB ───
function VendorChatTab({ conversations, activeChat, setActiveChat, chatMessages, setChatMessages, chatInput, setChatInput, chatEndRef, chatPollRef, user, showToast, eventId, eventTitle, autoOpenConvId, setTab, loadConversations, bookedRateCards, setBookedRateCards, isMobile, verifyingPayment }) {
  const [vendors, setVendors] = useState([]);
  const [vendorSearch, setVendorSearch] = useState('');
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const recordingTimerRef = useRef(null);
  const [bookingInProgress, setBookingInProgress] = useState(null); // rate_card_id currently being booked
  const [editingMessageId, setEditingMessageId] = useState(null); // message id currently being edited
  const [editContent, setEditContent] = useState('');

  const [showTemplates, setShowTemplates] = useState(false);
  const [templates, setTemplates] = useState([]);

  const getPresence = (lastSeen) => {
    if (!lastSeen) return { color: 'var(--red)', label: 'offline' };
    const diff = Date.now() - new Date(lastSeen).getTime();
    if (diff < 5 * 60 * 1000) return { color: '#10b981', label: 'online' };
    if (diff < 30 * 60 * 1000) return { color: '#f59e0b', label: 'away' };
    return { color: 'var(--red)', label: 'offline' };
  };

  useEffect(() => {
    api.get('/vendors').then(r => setVendors(r.data)).catch(() => {});
  }, []);

  // 30s presence polling — refresh vendors and conversations for live last_seen
  useEffect(() => {
    const interval = setInterval(() => {
      api.get('/vendors').then(r => setVendors(r.data)).catch(() => {});
      loadConversations();
    }, 30000);
    return () => clearInterval(interval);
  }, [loadConversations]);

  // Load host bookings to mark rate cards as booked persistently
  useEffect(() => {
    if (!eventId || user?.role !== 'host') return;
    api.get('/host/bookings', { params: { event_id: eventId } }).then(r => {
      const map = {};
      (r.data || []).forEach(b => { if (b.rate_card_id) map[b.rate_card_id] = true; });
      // Merge with existing — don't wipe parent state that may already have a freshly-booked card
      setBookedRateCards(prev => ({ ...prev, ...map }));
    }).catch(() => {});
  }, [eventId, user?.role]);

  // Auto-open a conversation when navigated from vendor profile
  useEffect(() => {
    if (autoOpenConvId) {
      openChat(autoOpenConvId);
      // Clean URL param without page reload
      window.history.replaceState({}, '', window.location.pathname + '?tab=vendors');
    }
  }, [autoOpenConvId]);

  const openChat = async (convId) => {
    try {
      const r = await api.get(`/chat/conversations/${convId}/messages`);
      setActiveChat(r.data.conversation);
      setChatMessages(r.data.messages);
      loadConversations();
    } catch { showToast('Failed to load chat', 'error'); }
  };

  const bookRateCard = async (message) => {
    const rc = message.rate_card_data;
    const rcId = rc?.id || message.rate_card_id;
    if (!rcId || !eventId) return;
    if (bookedRateCards[rcId] || bookingInProgress === rcId) return;

    // Defensive: if rate_card_data is null (deleted rate card), fall back to rate_card_id only
    if (!rc) {
      showToast('This rate card is no longer available.', 'error');
      return;
    }

    setBookingInProgress(rcId);
    try {
      const res = await api.post(`/chat/rate-card/${rcId}/book`, { event_id: eventId });
      setBookedRateCards(prev => ({ ...prev, [rcId]: true }));
      showToast('Booking request sent! Redirecting to Bookings...', 'success');
      // Navigate to Bookings tab so host can pay
      if (setTab) setTab('bookings');
      // Post confirmation message in chat so both host and vendor can see it
      if (activeChat?.id) {
        try {
          const bookingMsg = `\uD83D\uDCCB Booking request sent!\n\nPackage: ${res.data.rate_card_title}\nAmount: R${Number(res.data.quote_amount).toLocaleString()}\nVendor: ${res.data.business_name}\n\nTrack this in Vendors \u2192 Bookings`;
          await api.post(`/chat/conversations/${activeChat.id}/messages`, { content: bookingMsg });
          const chatRes = await api.get(`/chat/conversations/${activeChat.id}/messages`);
          setChatMessages(chatRes.data.messages || []);
        } catch { /* silently skip — booking succeeded */ }
      }
    } catch (err) {
      const data = err.response?.data;
      if (data?.error === 'Already booked') {
        setBookedRateCards(prev => ({ ...prev, [rcId]: true }));
        showToast('You already booked this package — pay below.', 'info');
        if (setTab) setTab('bookings');
      } else {
        showToast(data?.error || 'Booking failed. Try again.', 'error');
      }
    } finally {
      setBookingInProgress(null);
    }
  };

  const archiveConversation = async (conv) => {
    if (!conv) return;
    try {
      await api.put(`/chat/conversations/${conv.id}/archive`, { archived: !conv.archived });
      showToast(conv.archived ? 'Chat unarchived' : 'Chat archived', 'success');
      loadConversations();
    } catch { showToast('Failed', 'error'); }
  };

  const deleteConversation = async (conv) => {
    if (!conv || !window.confirm('Delete this conversation and all messages? This cannot be undone.')) return;
    try {
      await api.delete(`/chat/conversations/${conv.id}`);
      showToast('Conversation deleted', 'success');
      setActiveChat(null);
      setChatMessages([]);
      loadConversations();
    } catch { showToast('Failed to delete', 'error'); }
  };

  const editMessage = async (msg, newContent) => {
    if (!newContent.trim() || newContent.trim() === (msg.content || '')) return;
    try {
      const r = await api.put(`/chat/conversations/${activeChat.id}/messages/${msg.id}`, { content: newContent });
      setChatMessages(prev => prev.map(m => m.id === msg.id ? { ...m, content: r.data.content, edited_at: r.data.edited_at } : m));
    } catch { showToast('Failed to edit message', 'error'); }
  };

  const sendMessage = async () => {
    if (!chatInput.trim() || !activeChat) return;
    try {
      const r = await api.post(`/chat/conversations/${activeChat.id}/messages`, { content: chatInput });
      setChatMessages(prev => [...prev, { ...r.data, sender_name: user?.name }]);
      setChatInput('');
      if (r.data.off_platform_warning) {
        showToast(r.data.off_platform_warning, 'warning');
      }
      setTimeout(() => chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
    } catch { showToast('Failed to send', 'error'); }
  };

  const loadTemplatesHost = async () => {
    try {
      const r = await api.get('/message-templates?role=host');
      setTemplates(r.data);
    } catch (e) { /* silent */ }
  };
  const insertTemplateHost = (tpl) => {
    setChatInput(tpl.body);
    setShowTemplates(false);
  };

  const startChat = async (vendorId, vendorName) => {
    try {
      const r = await api.post('/chat/conversations', { vendor_id: vendorId, subject: `Inquiry about ${eventTitle}`, event_id: eventId });
      await loadConversations();
      openChat(r.data.id); // handles its own errors
      showToast(`Chat started with ${vendorName}!`);
    } catch { showToast('Failed to start chat', 'error'); }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream, { mimeType: MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : 'audio/mp4' });
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];
      mediaRecorder.ondataavailable = (e) => { if (e.data.size > 0) audioChunksRef.current.push(e.data); };
      mediaRecorder.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        const blob = new Blob(audioChunksRef.current, { type: mediaRecorder.mimeType });
        if (blob.size < 1000) { showToast('Recording too short', 'error'); return; }
        try {
          const fd = new FormData();
          fd.append('audio', blob, `voice-${Date.now()}.webm`);
          const upload = await api.post('/chat/voice-note', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
          const r = await api.post(`/chat/conversations/${activeChat.id}/messages`, { content: upload.data.url, message_type: 'voice_note' });
          setChatMessages(prev => [...prev, { ...r.data, sender_name: user?.name }]);
          setTimeout(() => chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
        } catch { showToast('Failed to send voice note', 'error'); }
      };
      mediaRecorder.start();
      setIsRecording(true);
      setRecordingTime(0);
      recordingTimerRef.current = setInterval(() => setRecordingTime(t => t + 1), 1000);
    } catch { showToast('Microphone access denied', 'error'); }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.stop();
    }
    setIsRecording(false);
    clearInterval(recordingTimerRef.current);
  };

  const cancelRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.ondataavailable = null;
      mediaRecorderRef.current.onstop = () => { mediaRecorderRef.current.stream?.getTracks().forEach(t => t.stop()); };
      mediaRecorderRef.current.stop();
    }
    setIsRecording(false);
    setRecordingTime(0);
    clearInterval(recordingTimerRef.current);
  };

  // Track whether user has manually scrolled up in chat
  const chatScrolledUpRef = useRef(false);

  // Poll for new messages
  useEffect(() => {
    let prevMsgCount = 0;
    if (activeChat) {
      chatScrolledUpRef.current = false;
      chatPollRef.current = setInterval(async () => {
        try {
          const r = await api.get(`/chat/conversations/${activeChat.id}/messages`);
          if (r.data.messages.length > prevMsgCount && prevMsgCount > 0) { /* new messages — badge updates silently */ }
          prevMsgCount = r.data.messages.length;
          // Only update if messages actually changed (by id comparison) to avoid unnecessary re-renders
          setChatMessages(prev => {
            const next = r.data.messages;
            if (prev.length === next.length && prev.every((m, i) => m.id === next[i]?.id)) {
              return prev;
            }
            return next;
          });
        } catch {}
      }, 5000);
    }
    return () => clearInterval(chatPollRef.current);
  }, [activeChat]);

  // Scroll to bottom only on new messages, and only if user hasn't scrolled up
  useEffect(() => {
    if (!chatScrolledUpRef.current) {
      setTimeout(() => chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
    }
  }, [chatMessages.length]);

  // Direct onScroll handler — no DOM attachment needed, always active once rendered
  const handleChatScroll = (e) => {
    const el = e.target;
    const isNearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
    chatScrolledUpRef.current = !isNearBottom;
  };

  const filteredVendors = vendors.filter(v =>
    (v.business_name || '').toLowerCase().includes(vendorSearch.toLowerCase()) ||
    (v.business_category || '').toLowerCase().includes(vendorSearch.toLowerCase())
  );

  const unreadTotal = conversations.reduce((a, c) => a + (parseInt(c.unread_count) || 0), 0);

  return (
    <div>
      {/* Verifying payment overlay */}
      {verifyingPayment && (
        <>
        <style>{`@keyframes spin{to{transform:rotate(360deg)}}@keyframes fadeInUp{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}}`}</style>
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: 'white', borderRadius: '16px', padding: '32px 40px', textAlign: 'center', boxShadow: '0 20px 60px rgba(0,0,0,0.15)' }}>
            <div style={{ width: '40px', height: '40px', border: '4px solid var(--cream-dark)', borderTop: '4px solid #E3425C', borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto 16px' }} />
            <div style={{ fontSize: '16px', fontWeight: 600, color: 'var(--charcoal)', marginBottom: '4px' }}>Verifying payment...</div>
            <div style={{ fontSize: '13px', color: 'var(--gray)' }}>Please wait a moment.</div>
          </div>
        </div>
        </>
      )}
      {/* Browse Vendors — Messenger-style horizontal scrolling circle strip */}
      <div className="card" style={{ padding: '14px 16px', marginBottom: '16px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
          <span style={{ fontWeight: 600, fontSize: '14px', display: 'flex', alignItems: 'center', gap: '6px' }}>
            <HiOutlineBuildingStorefront /> Browse Vendors
          </span>
          <input
            value={vendorSearch}
            onChange={e => setVendorSearch(e.target.value)}
            placeholder="Search vendors..."
            style={{ width: '180px', padding: '6px 10px', borderRadius: '8px', border: '1px solid var(--cream-dark)', fontSize: '12px' }}
          />
        </div>
        <div style={{ display: 'flex', gap: '16px', overflowX: 'auto', paddingBottom: '4px', WebkitOverflowScrolling: 'touch' }}>
          {filteredVendors.length === 0 ? (
            <div style={{ padding: '20px 0', textAlign: 'center', color: 'var(--gray)', fontSize: '13px', width: '100%' }}>No vendors found</div>
          ) : (
            filteredVendors.map(v => {
              const vendorImg = v.logo_url || (v.business_gallery && v.business_gallery.length > 0 ? v.business_gallery[0] : null) || v.avatar || null;
              const presence = getPresence(v.last_seen);
              return (
                <div key={v.id} onClick={() => startChat(v.id, v.business_name || v.name)} style={{
                  flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'center',
                  width: '80px', cursor: 'pointer', paddingTop: '2px'
                }}>
                  <div style={{ position: 'relative', marginBottom: '6px' }}>
                    {vendorImg ? (
                      <img src={vendorImg} alt="" style={{ width: '60px', height: '60px', borderRadius: '50%', objectFit: 'cover', border: '2px solid var(--cream-dark)' }} />
                    ) : (
                      <div style={{ width: '60px', height: '60px', borderRadius: '50%', background: 'linear-gradient(135deg, var(--rose-deep), var(--gold))', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 700, fontSize: '22px' }}>
                        {(v.business_name || v.name || '?')[0]}
                      </div>
                    )}
                    <span title={presence.label} style={{
                      position: 'absolute', bottom: '2px', right: '2px',
                      width: '14px', height: '14px', borderRadius: '50%',
                      background: presence.color, border: '2.5px solid white'
                    }} />
                  </div>
                  <div style={{ fontWeight: 600, fontSize: '11px', textAlign: 'center', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '80px', lineHeight: '1.3' }}>
                    {v.business_name || v.name}
                  </div>
                  <span style={{
                    fontSize: '10px', color: 'var(--gray)', textAlign: 'center',
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    maxWidth: '80px', marginTop: '1px'
                  }}>
                    {v.business_category}
                  </span>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Two column layout: vendor list + chat */}
      <div style={{ display: 'grid', gridTemplateColumns: activeChat && !isMobile ? '320px 1fr' : '1fr', gap: '16px', minHeight: '500px' }}>
        {/* Left: Conversation list */}
        <div style={{ display: activeChat && isMobile ? 'none' : 'block' }}>
          {conversations.length === 0 ? (
            <div className="card" style={{ padding: '30px 16px', textAlign: 'center', color: 'var(--gray)' }}>
              <FiMessageCircle size={24} style={{ marginBottom: '8px', opacity: 0.5 }} />
              <div style={{ fontSize: '14px', fontWeight: 600 }}>No conversations yet</div>
              <div style={{ fontSize: '12px', marginTop: '4px' }}>Browse vendors above to start a chat</div>
            </div>
          ) : (
            <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
              <div style={{ padding: '14px 16px', borderBottom: '1px solid var(--cream-dark)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontWeight: 600, fontSize: '14px', display: 'flex', alignItems: 'center', gap: '6px' }}><FiMessageCircle /> My Chats</span>
                {unreadTotal > 0 && (
                  <span style={{
                    background: '#E3425C', color: 'white', borderRadius: '10px', padding: '1px 8px', fontSize: '11px', fontWeight: 700,
                    animation: 'rsvpPulse 1.5s ease-in-out infinite'
                  }}>{unreadTotal} unread</span>
                )}
              </div>
              <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
                {conversations.map(c => {
                  const isActive = activeChat?.id === c.id;
                  const hasUnread = parseInt(c.unread_count) > 0;
                  const unreadCount = parseInt(c.unread_count) || 0;
                  const lastMsgTime = c.last_message_at ? new Date(c.last_message_at).toLocaleTimeString('en-ZA', { hour: '2-digit', minute: '2-digit' }) : '';
                  return (
                    <div key={c.id} onClick={() => openChat(c.id)} style={{
                      padding: '14px 16px', cursor: 'pointer',
                      background: isActive ? 'rgba(227,66,92,0.06)' : '#fff',
                      display: 'flex', alignItems: 'center', gap: '12px',
                      transition: 'background 0.15s',
                      borderBottom: '1px solid var(--cream)'
                    }}
                    onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = 'rgba(0,0,0,0.02)'; }}
                    onMouseLeave={e => { if (!isActive) e.currentTarget.style.background = '#fff'; }}
                    >
                      <div style={{ position: 'relative', flexShrink: 0 }}>
                        {c.other_avatar ? (
                          <img src={c.other_avatar} alt="" style={{ width: '48px', height: '48px', borderRadius: '50%', objectFit: 'cover', border: '2px solid var(--cream)' }} />
                        ) : (
                          <div style={{ width: '48px', height: '48px', borderRadius: '50%', background: 'linear-gradient(135deg, var(--rose-deep), var(--gold))', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 700, fontSize: '18px' }}>
                            {(c.other_business || c.other_name || '?')[0]}
                          </div>
                        )}
                        {(() => { const p = getPresence(c.other_user_last_seen); return <span title={p.label} style={{ position: 'absolute', bottom: '2px', right: '2px', width: '12px', height: '12px', borderRadius: '50%', background: p.color, border: '2px solid white' }} />; })()}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span style={{ fontWeight: hasUnread ? 700 : 600, fontSize: '14px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {c.other_business || c.other_name}
                          </span>
                          {lastMsgTime && (
                            <span style={{ fontSize: '11px', color: hasUnread ? 'var(--rose-deep)' : 'var(--gray)', flexShrink: 0, marginLeft: '8px' }}>{lastMsgTime}</span>
                          )}
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '3px' }}>
                          <span style={{
                            fontSize: '13px', color: hasUnread ? 'var(--dark)' : 'var(--gray)',
                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                            fontWeight: hasUnread ? 500 : 400, flex: 1
                          }}>
                            {c.last_message || 'No messages yet'}
                          </span>
                          {hasUnread && (
                            <span style={{
                              minWidth: '20px', height: '20px', borderRadius: '10px',
                              background: 'var(--rose-deep)', color: 'white',
                              fontSize: '11px', fontWeight: 700,
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              padding: '0 6px', flexShrink: 0, marginLeft: '8px'
                            }}>
                              {unreadCount > 99 ? '99+' : unreadCount}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Right: Chat area */}
        {activeChat && (
          <div className="card" style={{ padding: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            {(() => {
              const conv = conversations.find(c => c.id === activeChat.id);
              const vendorName = conv?.other_business || conv?.other_name || 'Vendor';
              const vendorId = conv?.other_user_id;
              return (
              <div style={{ borderBottom: '1px solid var(--cream-dark)' }}>
                <div style={{ padding: isMobile ? '10px 12px' : '14px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1, minWidth: 0 }}>
                    {isMobile && (
                      <button onClick={() => setActiveChat(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--gray)', flexShrink: 0, padding: '4px' }}>
                        <FiArrowLeft size={20} />
                      </button>
                    )}
                    {conv?.other_avatar ? (
                      <img src={conv.other_avatar} alt="" style={{ width: isMobile ? '32px' : '40px', height: isMobile ? '32px' : '40px', borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
                    ) : (
                      <div style={{ width: isMobile ? '32px' : '40px', height: isMobile ? '32px' : '40px', borderRadius: '50%', background: 'linear-gradient(135deg, var(--rose-deep), var(--gold))', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 700, fontSize: isMobile ? '14px' : '16px', flexShrink: 0 }}>{vendorName[0]}</div>
                    )}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 700, fontSize: isMobile ? '14px' : '15px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        {vendorName}
                        {vendorId && <a href={`/vendors/${vendorId}`} target="_blank" rel="noopener noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: '3px', fontSize: '11px', color: 'var(--rose-deep)', textDecoration: 'none', padding: '2px 8px', borderRadius: '6px', background: 'rgba(139,34,82,0.06)', border: '1px solid rgba(139,34,82,0.12)', fontWeight: 600 }}><FiExternalLink size={10} /> Profile</a>}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', color: 'var(--gray)', marginTop: '2px', flexWrap: 'wrap' }}>
                        {conv?.vendor_category && <span className="tag tag-gold" style={{ fontSize: '10px', padding: '1px 6px', margin: 0 }}>{conv.vendor_category}</span>}
                        {conv?.vendor_location && <span style={{ display: 'flex', alignItems: 'center', gap: '3px' }}><FiMapPin size={10} /> {conv.vendor_location}</span>}
                        {conv?.vendor_price_range && <span>{conv.vendor_price_range}</span>}
                      </div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '4px', alignItems: 'center', position: 'relative' }}>
                    <button onClick={() => archiveConversation(conv)} title={conv?.archived ? 'Unarchive' : 'Archive'} style={{ background: 'none', border: 'none', cursor: 'pointer', color: conv?.archived ? 'var(--gold)' : 'var(--gray)', flexShrink: 0, fontSize: '11px', display: 'flex', alignItems: 'center', gap: '4px', padding: '4px 8px', borderRadius: '6px' }}>
                      <FiArchive size={14} />
                    </button>
                    <button onClick={() => deleteConversation(conv)} title="Delete" style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--gray)', flexShrink: 0, fontSize: '11px', display: 'flex', alignItems: 'center', gap: '4px', padding: '4px 8px', borderRadius: '6px' }}>
                      <FiTrash2 size={14} />
                    </button>
                    {!isMobile && <button onClick={() => setActiveChat(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--gray)', flexShrink: 0 }}><FiX size={18} /></button>}
                  </div>
                </div>
              </div>
              );
            })()}
            <div onScroll={handleChatScroll} style={{ flex: 1, overflowY: 'auto', padding: isMobile ? '10px' : '16px', display: 'flex', flexDirection: 'column', gap: '10px', background: 'linear-gradient(rgba(255,255,255,0.12), rgba(255,255,255,0.12)), url("/screenshot-bg-plain.png"), var(--cream)', backgroundSize: 'cover', backgroundPosition: 'center', minHeight: isMobile ? 'calc(100vh - 200px)' : '350px' }}>
              {chatMessages.map((m) => (
                <div key={m.id || m.created_at} style={{ display: 'flex', flexDirection: m.sender_id === user?.id ? 'row-reverse' : 'row', gap: '8px' }}>
                  {m.message_type === 'rate_card' ? (
                    // Always render rate card — use data if available, fallback to content parsing
                    (() => {
                      const rcd = m.rate_card_data || {};
                      const title = rcd.title || (m.content || '').replace('📋 Rate Card: ', '');
                      const items = rcd.items || [];
                      return (
                    <div style={{ maxWidth: '85%', borderRadius: '12px', overflow: 'hidden', background: 'white', border: '1px solid #e8e5df', boxShadow: '0 2px 16px rgba(0,0,0,0.06)' }}>
                      {/* Header */}
                      <div style={{ padding: '14px 16px', display: 'flex', alignItems: 'center', gap: '12px', borderBottom: '1px solid #f0ede6' }}>
                        {rcd.logo_url ? (
                          <img src={rcd.logo_url} alt="" style={{ width: '40px', height: '40px', borderRadius: '8px', objectFit: 'cover', border: '1px solid #f0ede6' }} />
                        ) : (
                          <div style={{ width: '40px', height: '40px', borderRadius: '8px', background: 'linear-gradient(135deg, var(--rose-deep), #f08c98)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '16px', fontWeight: 700, color: 'white' }}>{(rcd.business_name || 'V')[0]}</div>
                        )}
                        <div style={{ flex: 1 }}>
                          {rcd.business_name && <div style={{ fontWeight: 700, fontSize: '14px', color: 'var(--dark)' }}>{rcd.business_name}</div>}
                          <div style={{ fontSize: '12px', color: 'var(--gray)', fontWeight: 500 }}>{title}</div>
                        </div>
                        {activeChat?.client_id === user?.id && (
                          <div style={{ padding: '4px 10px', borderRadius: '20px', background: '#fdf2f4', fontSize: '11px', fontWeight: 600, color: 'var(--rose-deep)' }}>{rcd.is_quotation ? 'Quotation' : 'Quote'}</div>
                        )}
                      </div>
                      {/* Body */}
                      {items.length > 0 ? (
                        <>
                          {items.map((item, j) => (
                            <div key={j} style={{ padding: '12px 16px', borderBottom: j < items.length - 1 ? '1px solid #f0ede6' : 'none', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px' }}>
                              {item.image_url && (
                                <img src={item.image_url} alt="" style={{ width: '48px', height: '48px', borderRadius: '8px', objectFit: 'cover', flexShrink: 0, border: '1px solid #f0ede6' }} onError={e => { e.target.style.display = 'none'; }} />
                              )}
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--dark)' }}>{item.service_name}</div>
                                {item.description && <div style={{ fontSize: '11px', color: 'var(--gray)', marginTop: '3px', lineHeight: 1.4 }}>{item.description}</div>}
                              </div>
                              <div style={{ textAlign: 'right', flexShrink: 0 }}>
                                <div style={{ fontWeight: 700, fontSize: '14px', color: '#1c1c1e' }}>R{Number(item.price).toLocaleString()}</div>
                                <div style={{ fontSize: '10px', color: 'var(--gray)' }}>{item.unit || 'per item'}</div>
                              </div>
                            </div>
                          ))}
                          {items.length > 1 && (
                            <div style={{ padding: '12px 16px', background: '#faf9f6', display: 'flex', justifyContent: 'space-between', fontWeight: 700, fontSize: '14px', borderTop: '2px solid #f0ede6' }}>
                              <span>Package Total</span>
                              <span style={{ color: 'var(--rose-deep)' }}>R{items.reduce((a, it) => a + Number(it.price || 0), 0).toLocaleString()}</span>
                            </div>
                          )}
                        </>
                      ) : (
                        <div style={{ padding: '16px' }}>
                          {rcd.description && <div style={{ fontSize: '12px', color: 'var(--gray)', marginBottom: '12px', lineHeight: 1.5 }}>{rcd.description}</div>}
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            {rcd.image_url && (
                              <img src={rcd.image_url} alt="" style={{ width: '56px', height: '56px', borderRadius: '10px', objectFit: 'cover', flexShrink: 0, border: '1px solid #f0ede6' }} onError={e => { e.target.style.display = 'none'; }} />
                            )}
                            <div style={{ textAlign: 'right', flex: 1 }}>
                              {rcd.price != null ? (
                                <div style={{ fontWeight: 700, fontSize: '22px', color: '#1c1c1e' }}>R{Number(rcd.price).toLocaleString()}</div>
                              ) : rcd.description ? null : (
                                <div style={{ fontSize: '13px', color: 'var(--gray)' }}>View in Vendor Profile</div>
                              )}
                              <div style={{ fontSize: '12px', color: 'var(--gray)', fontWeight: 500 }}>{rcd.unit || 'per event'}</div>
                            </div>
                          </div>
                        </div>
                      )}
                      {/* Delivery info */}
                      {rcd.delivery_type && rcd.delivery_type !== 'free' && (
                        <div style={{ padding: '8px 16px', borderTop: '1px solid #f0ede6', background: '#fffbf0', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px' }}>
                          {rcd.delivery_type === 'flat_fee' ? (
                            <><FiTruck size={13} style={{ color: '#B45309' }} /><span style={{ color: '#92400E' }}>Delivery: <strong>R{Number(rcd.delivery_fee || 0).toLocaleString()}</strong></span></>
                          ) : rcd.delivery_type === 'come_to_provider' ? (
                            <><FiMapPin size={13} style={{ color: '#B45309' }} /><span style={{ color: '#92400E' }}>Come to the provider's location</span></>
                          ) : null}
                        </div>
                      )}
                      {/* Booking fee info */}
                      {Number(rcd.booking_fee || 0) > 0 && (
                        <div style={{ padding: '8px 16px', borderTop: '1px solid #f0ede6', background: 'rgba(227,66,92,0.03)', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px' }}>
                          <CoinIcon size={13} style={{ color: '#E3425C' }} />
                          <span style={{ color: '#B91C1C' }}>
                            Booking Fee: <strong>R{Number(rcd.booking_fee).toLocaleString()}</strong>
                            {rcd.booking_fee_description ? ` (${rcd.booking_fee_description})` : ''}
                          </span>
                        </div>
                      )}
                      {/* Book This Package button — only for hosts (client in the conversation) */}
                      {activeChat?.client_id === user?.id && (
                        <div style={{ padding: '12px 16px', borderTop: '1px solid #f0ede6', background: '#faf9f6', display: 'flex', justifyContent: 'flex-end' }}>
                          {bookedRateCards[rcd.id || m.rate_card_id] ? (
                            <span style={{ fontSize: '13px', color: '#10B981', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 16px', background: '#ecfdf5', borderRadius: '8px' }}>
                              <FiCheckCircle size={16} /> Booked — check Wallet
                            </span>
                          ) : (
                            <button
                              onClick={() => bookRateCard(m)}
                              disabled={bookingInProgress === (rcd.id || m.rate_card_id)}
                              style={{
                                display: 'flex', alignItems: 'center', gap: '8px',
                                padding: '10px 24px',
                                background: bookingInProgress === (rcd.id || m.rate_card_id) ? '#f0b0b8' : 'var(--rose-deep)',
                                color: 'white', border: 'none', borderRadius: '8px',
                                fontSize: '13px', fontWeight: 600, cursor: 'pointer',
                                transition: 'all 0.2s ease', boxShadow: '0 2px 8px rgba(227,66,92,0.25)'
                              }}
                            >
                              {bookingInProgress === (rcd.id || m.rate_card_id) ? (
                                <>Booking...</>
                              ) : (
                                <><FiShoppingBag size={14} /> Book This Package</>
                              )}
                            </button>
                          )}
                        </div>
                      )}
                      {/* Timestamp */}
                      <div style={{ padding: '6px 16px', fontSize: '10px', color: '#b0ada5', textAlign: m.sender_id !== user?.id ? 'left' : 'right' }}>
                        {new Date(m.created_at).toLocaleTimeString('en-ZA', { hour: '2-digit', minute: '2-digit' })}
                      </div>
                    </div>
                      );
                    })()
                  ) : m.message_type === 'voice_note' ? (
                    <div style={{
                      maxWidth: '75%', padding: '8px 14px',
                      borderRadius: m.sender_id === user?.id ? '18px 18px 6px 18px' : '18px 18px 18px 6px',
                      background: m.sender_id === user?.id ? '#DCF8C6' : '#fff',
                      color: 'var(--dark)',
                      boxShadow: '0 1px 2px rgba(0,0,0,0.06)'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: '180px' }}>
                        <div style={{ width: '36px', height: '36px', borderRadius: '50%', background: 'rgba(227,66,92,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                          <FiMic size={14} style={{ color: '#E3425C' }} />
                        </div>
                        <audio src={m.content} controls style={{ height: '32px', flex: 1, minWidth: 0 }} />
                      </div>
                      <div style={{ fontSize: '11px', color: '#8696a0', marginTop: '4px', textAlign: 'right', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '4px' }}>
                        {new Date(m.created_at).toLocaleTimeString('en-ZA', { hour: '2-digit', minute: '2-digit' })}
                        {m.sender_id === user?.id && <FiCheck size={12} style={{ color: '#8696a0' }} />}
                      </div>
                    </div>
                  ) : m.message_type === 'payment_prompt' ? (
                    (() => {
                      let bookingId = null;
                      let amount = 0;
                      let displayContent = m.content_filtered || m.content || '';
                      try {
                        const newlineIdx = displayContent.indexOf('\n');
                        const firstLine = newlineIdx >= 0 ? displayContent.substring(0, newlineIdx) : displayContent;
                        const parsed = JSON.parse(firstLine);
                        if (parsed.booking_id) {
                          bookingId = parsed.booking_id;
                          amount = parsed.amount || 0;
                          displayContent = newlineIdx >= 0 ? displayContent.substring(newlineIdx + 1) : '';
                        }
                      } catch {}
                      return (
                        <div style={{ maxWidth: '85%', borderRadius: '14px', overflow: 'hidden', background: 'white', border: '1px solid #d1fae5', boxShadow: '0 2px 16px rgba(16,185,129,0.08)' }}>
                          <div style={{ padding: '14px 16px', background: 'linear-gradient(135deg, #059669, #10B981)', display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <FiCheckCircle size={18} style={{ color: 'white' }} />
                            <span style={{ fontWeight: 700, fontSize: '14px', color: 'white' }}>Booking Confirmed</span>
                          </div>
                          <div style={{ padding: '16px', fontSize: '14px', lineHeight: 1.6, whiteSpace: 'pre-wrap', color: 'var(--dark)' }}>
                            {displayContent}
                          </div>
                          {activeChat?.client_id === user?.id && bookingId && (
                            <div style={{ padding: '12px 16px', borderTop: '1px solid #d1fae5', background: '#ecfdf5', display: 'flex', justifyContent: 'center', gap: '8px', flexWrap: 'wrap' }}>
                              <button onClick={async () => {
                                try {
                                  const res = await api.post('/ozow/vendor-booking/initialize', {
                                    booking_id: bookingId,
                                    amount: Number(amount),
                                    event_id: id,
                                    vendor_id: activeChat?.vendor_id,
                                  });
                                  const data = res.data;
                                  if (!data?.success || (!data?.url && !data?.paymentUrl)) {
                                    alert('Payment setup failed: ' + (data?.error || 'No payment URL'));
                                    return;
                                  }
                                  if (data.url && data.params) {
                                    const f = document.createElement('form');
                                    f.method = 'POST';
                                    f.action = data.url;
                                    f.style.display = 'none';
                                    Object.entries(data.params).forEach(([k, v]) => {
                                      const inp = document.createElement('input');
                                      inp.type = 'hidden'; inp.name = k; inp.value = v;
                                      f.appendChild(inp);
                                    });
                                    document.body.appendChild(f);
                                    sessionStorage.setItem('ggr_ozow_pending', JSON.stringify({
                                      booking_id: bookingId,
                                      event_id: id,
                                      reference: data.reference,
                                    }));
                                    f.submit();
                                    return;
                                  }
                                  window.location.href = data.paymentUrl;
                                } catch (err) {
                                  const msg = err.response?.data?.error || err.message || 'Payment failed';
                                  alert('Payment error: ' + msg);
                                  showToast(msg, 'error');
                                }
                              }} style={{
                                display: 'flex', alignItems: 'center', gap: '8px',
                                padding: '12px 32px',
                                background: 'linear-gradient(135deg, #059669, #10B981)',
                                color: 'white', border: 'none', borderRadius: '8px',
                                fontSize: '14px', fontWeight: 700, cursor: 'pointer',
                                boxShadow: '0 4px 12px rgba(16,185,129,0.3)'
                              }}>
                                <CoinIcon size={16} /> Pay R{Number(amount).toLocaleString()} Now
                              </button>
                              {window.location.search.includes('demo=true') && (
                                <button onClick={async () => {
                                  try {
                                    const res = await api.post('/ozow/demo/pay', {
                                      booking_id: bookingId,
                                      amount: Number(amount),
                                      event_id: id,
                                      vendor_id: activeChat?.vendor_id,
                                    });
                                    if (res.data?.success) {
                                      showToast('Demo payment successful!', 'success');
                                      api.get('/host/bookings', { params: { event_id: id } }).then(r => setHostBookings(r.data)).catch(() => {});
                                    }
                                  } catch (err) {
                                    showToast('Demo payment failed', 'error');
                                  }
                                }} style={{
                                  display: 'flex', alignItems: 'center', gap: '6px',
                                  padding: '12px 20px',
                                  background: '#10B981',
                                  color: 'white', border: '1px dashed rgba(255,255,255,0.4)', borderRadius: '8px',
                                  fontSize: '12px', fontWeight: 700, cursor: 'pointer'
                                }}>
                                  🧪 Demo Pay
                                </button>
                              )}
                            </div>
                          )}
                          <div style={{ padding: '6px 16px', fontSize: '10px', color: '#b0ada5', textAlign: 'right' }}>
                            {new Date(m.created_at).toLocaleTimeString('en-ZA', { hour: '2-digit', minute: '2-digit' })}
                          </div>
                        </div>
                      );
                    })()
                  ) : (
                    <div style={{
                      maxWidth: '75%', padding: '8px 12px',
                      borderRadius: m.sender_id === user?.id ? '18px 18px 6px 18px' : '18px 18px 18px 6px',
                      background: m.sender_id === user?.id ? '#DCF8C6' : '#fff',
                      color: 'var(--dark)',
                      fontSize: '14px', lineHeight: 1.5,
                      boxShadow: '0 1px 2px rgba(0,0,0,0.06)'
                    }}>
                      {m.is_filtered && <div style={{ fontSize: '11px', opacity: 0.5, marginBottom: '4px', fontStyle: 'italic' }}>Contact info filtered for security</div>}
                      {editingMessageId === m.id ? (
                        <div>
                          <input
                            value={editContent}
                            onChange={e => setEditContent(e.target.value)}
                            onKeyDown={e => { if (e.key === 'Enter') { editMessage(m, editContent); setEditingMessageId(null); } if (e.key === 'Escape') setEditingMessageId(null); }}
                            style={{ width: '100%', padding: '6px 10px', borderRadius: '8px', border: '1px solid var(--cream-dark)', fontSize: '14px', background: 'white', color: 'var(--dark)' }}
                            autoFocus
                          />
                          <div style={{ display: 'flex', gap: '6px', marginTop: '6px', justifyContent: 'flex-end' }}>
                            <button onClick={() => setEditingMessageId(null)} style={{ background: 'none', border: '1px solid var(--cream-dark)', borderRadius: '6px', padding: '4px 10px', cursor: 'pointer', fontSize: '12px', color: 'var(--gray)' }}>Cancel</button>
                            <button onClick={() => { editMessage(m, editContent); setEditingMessageId(null); }} style={{ background: 'var(--dark)', color: 'white', border: 'none', borderRadius: '6px', padding: '4px 12px', cursor: 'pointer', fontSize: '12px', fontWeight: 600 }}>Save</button>
                          </div>
                        </div>
                      ) : (
                        <>
                          {m.content_filtered || m.content}
                          {m.edited_at && <span style={{ fontSize: '10px', opacity: 0.6, marginLeft: '4px' }}>(edited)</span>}
                        </>
                      )}
                      <div style={{ fontSize: '11px', color: '#8696a0', marginTop: '2px', display: 'flex', alignItems: 'center', gap: '4px', justifyContent: 'flex-end' }}>
                        <span>{new Date(m.created_at).toLocaleTimeString('en-ZA', { hour: '2-digit', minute: '2-digit' })}</span>
                        {m.sender_id === user?.id && (
                          <FiCheck size={12} style={{ color: m.read_at ? '#53bdeb' : '#8696a0' }} />
                        )}
                        {m.sender_id === user?.id && editingMessageId !== m.id && (
                          <button onClick={(e) => { e.stopPropagation(); setEditingMessageId(m.id); setEditContent(m.content || ''); }} title="Edit" style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px', display: 'flex', alignItems: 'center', marginLeft: '2px' }}>
                            <FiEdit2 size={10} style={{ color: '#8696a0' }} />
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))}
              <div ref={chatEndRef} />
            </div>
            <div style={{ padding: isMobile ? '8px' : '10px 16px', borderTop: '1px solid var(--cream-dark)', display: 'flex', gap: '8px', alignItems: 'center', background: '#f0f0f0' }}>
              {isRecording ? (
                <>
                  <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 16px', background: 'rgba(227,66,92,0.08)', borderRadius: '24px', border: '1px solid rgba(227,66,92,0.2)' }}>
                    <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: '#E3425C', animation: 'pulse 1s infinite' }} />
                    <span style={{ fontSize: '14px', color: '#E3425C', fontWeight: 600 }}>Recording... {Math.floor(recordingTime / 60)}:{String(recordingTime % 60).padStart(2, '0')}</span>
                  </div>
                  <button onClick={cancelRecording} style={{ borderRadius: '50%', width: '40px', height: '40px', padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.05)', border: 'none', cursor: 'pointer', color: 'var(--gray)' }}><FiX size={18} /></button>
                  <button onClick={stopRecording} style={{ borderRadius: '50%', width: '40px', height: '40px', padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--rose-deep)', color: 'white', border: 'none', cursor: 'pointer' }}><FiSend size={16} /></button>
                </>
              ) : (
                <>
                  {/* Templates Button */}
                  <div style={{ position: 'relative', flexShrink: 0 }}>
                    <button onClick={() => { if (templates.length === 0) loadTemplatesHost(); setShowTemplates(!showTemplates); }} title="Message Templates" style={{ background: showTemplates ? 'var(--rose-deep)' : 'rgba(0,0,0,0.05)', color: showTemplates ? 'white' : 'var(--gray)', border: 'none', borderRadius: '50%', width: '40px', height: '40px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s', flexShrink: 0 }}><FiMessageSquare size={16} /></button>
                    {showTemplates && (
                      <div style={{ position: 'absolute', bottom: '100%', left: 0, marginBottom: '8px', background: 'white', borderRadius: '12px', boxShadow: '0 -4px 24px rgba(0,0,0,0.12)', border: '1px solid var(--cream-dark)', width: '280px', maxHeight: '280px', overflowY: 'auto', zIndex: 10 }}>
                        <div style={{ padding: '10px 14px', borderBottom: '1px solid var(--cream)', fontWeight: 600, fontSize: '13px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span>Quick Templates</span>
                          <button onClick={() => setShowTemplates(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--gray)' }}><FiX size={14} /></button>
                        </div>
                        {templates.map((tpl, i) => (
                          <div key={i} onClick={() => insertTemplateHost(tpl)} style={{ padding: '10px 14px', cursor: 'pointer', borderBottom: '1px solid var(--cream)', transition: 'background 0.15s' }}
                            onMouseEnter={e => e.currentTarget.style.background = 'var(--cream)'}
                            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                            <div style={{ fontWeight: 600, fontSize: '13px' }}>{tpl.label}</div>
                            <div style={{ fontSize: '11px', color: 'var(--gray)', marginTop: '2px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{tpl.body.substring(0, 70)}{tpl.body.length > 70 ? '...' : ''}</div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  {/* WhatsApp-style pill input */}
                  <div style={{ flex: 1, display: 'flex', alignItems: 'center', background: 'white', borderRadius: '24px', padding: '4px 8px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
                    <input value={chatInput} onChange={e => setChatInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && sendMessage()} placeholder="Type a message..." style={{ flex: 1, padding: '8px 8px', border: 'none', borderRadius: '24px', fontSize: '14px', outline: 'none', background: 'transparent' }} />
                    <button onClick={() => startRecording()} title="Record voice note" style={{ borderRadius: '50%', width: '34px', height: '34px', padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--gray)', flexShrink: 0 }}>
                      <FiMic size={16} />
                    </button>
                  </div>
                  <button onClick={sendMessage} disabled={!chatInput.trim()} style={{ borderRadius: '50%', width: '40px', height: '40px', padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: chatInput.trim() ? 'var(--rose-deep)' : 'rgba(0,0,0,0.08)', color: chatInput.trim() ? 'white' : 'var(--gray)', border: 'none', cursor: chatInput.trim() ? 'pointer' : 'default', flexShrink: 0, transition: 'all 0.2s' }}><FiSend size={16} /></button>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
